import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Inbox, Send, Star, Trash2, Archive, FileText, AlertOctagon,
  Search, Plus, X, Reply, ReplyAll, Forward, MoreVertical,
  Sun, Moon, Settings, Tag, Paperclip, Bold, Italic, Underline,
  Link, Image, Check, CheckCircle, AlertCircle, Info,
  Mail, MailOpen, Pen, RefreshCw, Filter, Ban,
  ChevronDown, Menu, Printer, Copy, Eye, EyeOff, FolderInput,
  Download, ArrowLeft, Edit3, Loader, ShieldCheck, Trash, Save,
  List, AlignLeft, AlignCenter, AlignRight, Strikethrough, Undo, Redo, Type,
  Sparkles, MessageSquare, ThumbsUp, Wand2, ClipboardCopy,
  Calendar, LayoutList, LayoutGrid, ChevronUp, AlertTriangle,
  ChevronLeft, ChevronRight, GripVertical, Users, BarChart3, ShieldAlert,
  Globe, AtSign, Hash, TrendingUp, HardDrive, UserCheck, Monitor,
  Zap, Crown, Rocket, Shield, Clock, Headphones, ArrowRight,
  Database, Lock, Cpu, MailPlus, Server, Layers, Building2, Gift
} from 'lucide-react';
import { FOLDERS, TAG_COLORS, TAG_COLOR_HEX } from './data';
import api from './services/api';
import { formatDate, formatFullDate, getInitials, formatBytes } from './utils/format';
import {
  escapeHtml, textToHtml, cleanPreview, htmlToText,
  replySubject, forwardSubject, base64ToUtf8,
} from './utils/emailText';
import { sanitizeEmailHtml } from './utils/sanitizeHtml';
import EmailBodyFrame from './components/EmailBodyFrame';
import { AI_MODELS, DEFAULT_AI_MODEL, getModelInfo } from './constants/aiModels';

const FOLDER_ICONS = {
  Inbox, Star, Send, FileText, AlertOctagon, Trash2, Archive
};

// Sequência para ids de toast (Date.now() colide quando dois toasts disparam no mesmo ms)
let toastIdSeq = 0;

export default function App() {
  // ===== AUTH STATE =====
  const [authUser, setAuthUser] = useState(() => api.getStoredUser());
  const [authLoading, setAuthLoading] = useState(true);
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [showLoginForm, setShowLoginForm] = useState(false);
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [adminUsers, setAdminUsers] = useState([]);
  const [adminLoading, setAdminLoading] = useState(false);
  const [showAddUser, setShowAddUser] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userForm, setUserForm] = useState({ name: '', email: '', password: '', max_accounts: 0, storage_limit_mb: 0 });
  const [userFormStorageUnit, setUserFormStorageUnit] = useState('MB'); // 'MB' or 'GB'
  const [userFormLoading, setUserFormLoading] = useState(false);
  const [adminSearch, setAdminSearch] = useState('');
  const [adminFilter, setAdminFilter] = useState('all'); // all, admin, user, active, inactive
  const [adminView, setAdminView] = useState('list'); // list, grid
  const [adminSort, setAdminSort] = useState('name'); // name, email, recent, created
  const [adminTab, setAdminTab] = useState('users'); // users, companies
  const [companies, setCompanies] = useState([]);
  const [companiesLoading, setCompaniesLoading] = useState(false);
  const [showAddCompany, setShowAddCompany] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [companyForm, setCompanyForm] = useState({ name:'', plan:'custom', max_users:0, max_accounts_per_user:0, storage_limit_mb:0, ai_credits_per_user:0, ai_enabled:true, notes:'' });
  const [companyFormLoading, setCompanyFormLoading] = useState(false);

  const [theme, setTheme] = useState(() => localStorage.getItem('coremail-theme') || 'light');
  const [emailBodyTheme, setEmailBodyTheme] = useState(() => localStorage.getItem('coremail-body-theme') || 'auto');
  const [currentFolder, setCurrentFolder] = useState('inbox');
  const [emails, setEmails] = useState([]);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [emailsLoading, setEmailsLoading] = useState(false);
  const [emailBodyLoading, setEmailBodyLoading] = useState(false);
  const [emailPage, setEmailPage] = useState(1);
  const [emailPagination, setEmailPagination] = useState({ total: 0, total_pages: 1 });
  const [emailSyncing, setEmailSyncing] = useState(false);
  const [syncProgress, setSyncProgress] = useState(null); // null or { total, synced, percent, folder }
  const [folderCounts, setFolderCounts] = useState({}); // { inbox: { total, unread }, sent: {...}, ... }
  const [showCompose, setShowCompose] = useState(false);
  const [compose, setCompose] = useState({ to: [], cc: [], bcc: [], subject: '', body: '' });
  const [composeAttachments, setComposeAttachments] = useState([]); // File[]
  const [composeToInput, setComposeToInput] = useState('');
  const composeFileRef = useRef(null);
  const [composeCcInput, setComposeCcInput] = useState('');
  const [composeBccInput, setComposeBccInput] = useState('');
  const [showCc, setShowCc] = useState(false);
  const [showBcc, setShowBcc] = useState(false);
  const [contactSuggestions, setContactSuggestions] = useState([]);
  const [contactsField, setContactsField] = useState(null);
  const [highlightedContact, setHighlightedContact] = useState(-1);
  const [editingDraftId, setEditingDraftId] = useState(null);
  const [draftSaving, setDraftSaving] = useState(false);
  const editorRef = useRef(null);
  const [showTagManager, setShowTagManager] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showAccountModal, setShowAccountModal] = useState(false);
  const [tags, setTags] = useState([]);
  const [newTagName, setNewTagName] = useState('');
  const [newTagColor, setNewTagColor] = useState('#3B82F6');
  const [tagColorPicker, setTagColorPicker] = useState(false);
  const [tagViewMode, setTagViewMode] = useState(() => localStorage.getItem('coremail-tag-view') || 'list');
  const [editingTag, setEditingTag] = useState(null); // { id, name, color }
  const [tagRules, setTagRules] = useState([]);
  const [tagRulesLoading, setTagRulesLoading] = useState(false);
  const [showTagRules, setShowTagRules] = useState(false);
  const [newRule, setNewRule] = useState({ tag_id: 0, rule_type: 'domain', rule_value: '' });
  const [aiSummary, setAiSummary] = useState(null); // { result, mode, count } or null
  const [aiSummaryLoading, setAiSummaryLoading] = useState(false);
  const [aiCreditsLimit, setAiCreditsLimit] = useState(0); // 0 = unlimited
  const [aiCreditsUsed, setAiCreditsUsed] = useState(0);
  const [aiCreditsRemaining, setAiCreditsRemaining] = useState(-1); // -1 = unlimited
  const [showAiCreditsPanel, setShowAiCreditsPanel] = useState(false);
  const [showAdminAiPopup, setShowAdminAiPopup] = useState(null); // null or user object
  const [adminAiForm, setAdminAiForm] = useState({ ai_enabled: true, ai_credits_limit: 0 });
  const [showPermissionsPopup, setShowPermissionsPopup] = useState(null); // null or user object
  const [permissionsForm, setPermissionsForm] = useState({});
  const [contextMenu, setContextMenu] = useState(null);
  const [toasts, setToasts] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [accountsLoading, setAccountsLoading] = useState(true);
  const [activeAccount, setActiveAccount] = useState(null);
  const [signature, setSignature] = useState('Ramon Oliveira\nRamcore Tecnologia\nramcore.com.br');
  const [signatureScope, setSignatureScope] = useState('user'); // 'user' or 'account'
  const [settings, setSettings] = useState({ notifications: true, autoRead: true, confirmDelete: true, useSignature: true, syncInterval: 5, fontSize: parseInt(localStorage.getItem('coremail-fontsize') || '14') });
  const [showAddAccount, setShowAddAccount] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [accountProvider, setAccountProvider] = useState('none'); // 'none' | 'hostgator' | 'gmail' | 'gmail_setup' | 'manual'
  const [accountForm, setAccountForm] = useState({ name: '', email: '', imap_host: '', imap_port: '993', smtp_host: '', smtp_port: '587', password: '' });
  const [accountLoading, setAccountLoading] = useState(false);
  const [accountTesting, setAccountTesting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [accountSearch, setAccountSearch] = useState('');
  const [accountUnreads, setAccountUnreads] = useState({}); // { accountId: unreadCount }
  const [accountUnreadsLoading, setAccountUnreadsLoading] = useState(false);
  const [accountViewMode, setAccountViewMode] = useState(() => localStorage.getItem('coremail-acc-view') || 'list'); // 'list' | 'grid' | 'compact'
  // Account groups (loaded from DB)
  const [accountGroups, setAccountGroups] = useState([]);
  const [showGroupManager, setShowGroupManager] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [groupForm, setGroupForm] = useState({ name: '', color: '#3B82F6', accountIds: [] });
  const [filterGroup, setFilterGroup] = useState(null); // null = all, 'ungrouped', or group id
  const [tagDropdown, setTagDropdown] = useState(null);
  const [filterTag, setFilterTag] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [attachPreview, setAttachPreview] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [settingsLoading, setSettingsLoading] = useState(false);
  // New features
  const [emailsPerPage, setEmailsPerPage] = useState(() => parseInt(localStorage.getItem('coremail-per-page')) || 50);
  const [viewMode, setViewMode] = useState(() => localStorage.getItem('coremail-view-mode') || 'comfortable'); // comfortable, compact
  const [filterUnreadOnly, setFilterUnreadOnly] = useState(false);
  const [storageUsedBytes, setStorageUsedBytes] = useState(0);
  const [storageLimitMb, setStorageLimitMb] = useState(0);
  const [storageExceeded, setStorageExceeded] = useState(false);
  const [searchActive, setSearchActive] = useState(false);
  const [searchDateFrom, setSearchDateFrom] = useState('');
  const [searchDateTo, setSearchDateTo] = useState('');
  const [showDateFilter, setShowDateFilter] = useState(false);
  const [bulkConfirm, setBulkConfirm] = useState(null); // null or { action, count, label }
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => localStorage.getItem('coremail-sidebar-collapsed') === 'true');
  const [listWidth, setListWidth] = useState(() => parseInt(localStorage.getItem('coremail-list-width')) || 380);
  const listResizing = useRef(false);
  const syncIntervalRef = useRef(null);

  // AI State (email detail)
  const [showAiPanel, setShowAiPanel] = useState(false);
  const [aiAction, setAiAction] = useState(null);
  const [aiStyle, setAiStyle] = useState('');
  const [aiExtra, setAiExtra] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiResult, setAiResult] = useState('');
  const [aiModel, setAiModel] = useState('gpt-5-mini');

  // AI State (compose)
  const [showComposeAi, setShowComposeAi] = useState(false);
  const [composeAiStyle, setComposeAiStyle] = useState('formal');
  const [composeAiPrompt, setComposeAiPrompt] = useState('');
  const [composeAiLoading, setComposeAiLoading] = useState(false);
  const [composeAiResult, setComposeAiResult] = useState('');
  const [composeAiModel, setComposeAiModel] = useState('gpt-5-mini');

  // AI batch summary model
  const [batchAiModel, setBatchAiModel] = useState('gpt-5-mini');

  // ===== SECTIONS: email (default), contacts, dashboard =====
  const [currentSection, setCurrentSection] = useState('email');

  // Contacts state
  const [contactsList, setContactsList] = useState([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [contactsPage, setContactsPage] = useState(1);
  const [contactsTotal, setContactsTotal] = useState(0);
  const [contactsTotalPages, setContactsTotalPages] = useState(1);
  const [contactsSearch, setContactsSearch] = useState('');
  const [contactsSort, setContactsSort] = useState('times_contacted');
  const [contactsSortOrder, setContactsSortOrder] = useState('DESC');
  const [contactStats, setContactStats] = useState(null);

  // Dashboard state
  const [dashboardData, setDashboardData] = useState(null);
  const [dashboardLoading, setDashboardLoading] = useState(false);
  const [dashTopAccountFilter, setDashTopAccountFilter] = useState('all');
  const [dashTopData, setDashTopData] = useState(null);
  const [dashTopLoading, setDashTopLoading] = useState(false);
  const [chartHoverIdx, setChartHoverIdx] = useState(null);
  const [dashVolumeFilter, setDashVolumeFilter] = useState('all');
  const [dashVolumeData, setDashVolumeData] = useState(null);
  const [dashVolumeLoading, setDashVolumeLoading] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [editContactForm, setEditContactForm] = useState({ name: '', email: '' });
  const [editContactLoading, setEditContactLoading] = useState(false);
  const [customTheme, setCustomTheme] = useState(() => {
    try { return JSON.parse(localStorage.getItem('coremail-custom-theme')) || {}; } catch { return {}; }
  });
  const [themeScope, setThemeScope] = useState(() => localStorage.getItem('coremail-theme-scope') || 'user'); // 'user' or 'account'

  // Admin: viewing user dashboard
  const [adminViewDashboard, setAdminViewDashboard] = useState(null); // null or { userId, userName }
  const [adminDashboardData, setAdminDashboardData] = useState(null);
  const [adminDashboardLoading, setAdminDashboardLoading] = useState(false);

  // Spam rules
  const [showSpamRules, setShowSpamRules] = useState(false);
  const [spamRules, setSpamRules] = useState([]);
  const [spamRulesLoading, setSpamRulesLoading] = useState(false);
  const [newSpamRule, setNewSpamRule] = useState({ type: 'domain', pattern: '' });

  // Cleanup / Email management
  const [cleanupData, setCleanupData] = useState(null);
  const [cleanupLoading, setCleanupLoading] = useState(false);
  const [cleanupActionLoading, setCleanupActionLoading] = useState(null); // string key of action in progress
  const [cleanupExpandedSender, setCleanupExpandedSender] = useState(null);
  const [activeRequests, setActiveRequests] = useState([]);

  const ctxRef = useRef(null);

  // Track active API requests
  useEffect(() => {
    const unsub = api.onRequestChange((reqs) => setActiveRequests([...reqs]));
    return unsub;
  }, []);

  // Theme
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('coremail-theme', theme);
  }, [theme]);

  // Apply font size globally via CSS custom property
  useEffect(() => {
    document.documentElement.style.setProperty('--fs-scale', (settings.fontSize / 14).toFixed(3));
    localStorage.setItem('coremail-fontsize', String(settings.fontSize));
  }, [settings.fontSize]);

  // Custom theme overrides - map each property to correct CSS variables
  useEffect(() => {
    const root = document.documentElement;
    // All vars we may override
    const ALL_CUSTOM_VARS = [
      '--accent', '--accent-hover', '--unread-dot', '--bg-sidebar',
      '--text-sidebar', '--text-sidebar-active', '--text-primary',
      '--text-secondary', '--text-tertiary',
      '--btn-gradient-from', '--btn-gradient-to', '--btn-shadow',
      '--sidebar-icon-color',
    ];
    // Clear all custom overrides first so theme defaults work
    ALL_CUSTOM_VARS.forEach(v => root.style.removeProperty(v));

    const ct = customTheme || {};
    const hasCustom = Object.values(ct).some(v => v);
    if (!hasCustom) {
      localStorage.setItem('coremail-custom-theme', '{}');
      return;
    }

    // Helper: hex to rgba string
    const hexToRgba = (hex, alpha = 0.3) => {
      const r = parseInt(hex.slice(1, 3), 16);
      const g = parseInt(hex.slice(3, 5), 16);
      const b = parseInt(hex.slice(5, 7), 16);
      return `rgba(${r},${g},${b},${alpha})`;
    };

    if (ct.accent) {
      root.style.setProperty('--accent', ct.accent);
      root.style.setProperty('--accent-hover', ct.accent);
      root.style.setProperty('--unread-dot', ct.accent);
    }
    if (ct.sidebar) {
      root.style.setProperty('--bg-sidebar', ct.sidebar);
    }
    if (ct.sidebarIcons) {
      root.style.setProperty('--sidebar-icon-color', ct.sidebarIcons);
      root.style.setProperty('--text-sidebar', ct.sidebarIcons);
      // Active icons are brighter version — use white or the set color
      root.style.setProperty('--text-sidebar-active', '#FFFFFF');
    }
    if (ct.buttons) {
      root.style.setProperty('--btn-gradient-from', ct.buttons);
      root.style.setProperty('--btn-gradient-to', ct.buttons);
      root.style.setProperty('--btn-shadow', hexToRgba(ct.buttons, 0.3));
    }
    if (ct.fontPrimary) {
      root.style.setProperty('--text-primary', ct.fontPrimary);
    }
    if (ct.fontSecondary) {
      root.style.setProperty('--text-secondary', ct.fontSecondary);
      root.style.setProperty('--text-tertiary', ct.fontSecondary + '99'); // slightly transparent
    }

    localStorage.setItem('coremail-custom-theme', JSON.stringify(ct));
  }, [customTheme, theme]);

  // ===== AUTH: Validate token on mount =====
  useEffect(() => {
    const validateAuth = async () => {
      if (api.getToken()) {
        const user = await api.validateToken();
        setAuthUser(user);
        if (user?.storage_used_bytes !== undefined) setStorageUsedBytes(user.storage_used_bytes);
        if (user?.storage_limit_mb !== undefined) setStorageLimitMb(user.storage_limit_mb);
        if (user?.ai_credits_limit !== undefined) setAiCreditsLimit(user.ai_credits_limit);
        if (user?.ai_credits_used !== undefined) setAiCreditsUsed(user.ai_credits_used);
        if (user?.ai_credits_remaining !== undefined) setAiCreditsRemaining(user.ai_credits_remaining);
      } else {
        setAuthUser(null);
      }
      setAuthLoading(false);
    };
    validateAuth();
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    setLoginLoading(true);
    try {
      const data = await api.login(loginForm.email, loginForm.password);
      if (data.success) {
        setAuthUser(data.user);
        if (data.user.storage_used_bytes !== undefined) setStorageUsedBytes(data.user.storage_used_bytes);
        if (data.user.storage_limit_mb !== undefined) setStorageLimitMb(data.user.storage_limit_mb);
        if (data.user.ai_credits_limit !== undefined) setAiCreditsLimit(data.user.ai_credits_limit);
        if (data.user.ai_credits_used !== undefined) setAiCreditsUsed(data.user.ai_credits_used);
        if (data.user.ai_credits_remaining !== undefined) setAiCreditsRemaining(data.user.ai_credits_remaining);
        setLoginForm({ email: '', password: '' });
      } else {
        setLoginError(data.error || 'Erro ao fazer login');
      }
    } catch (err) {
      setLoginError(err.message || 'Erro de conexão com o servidor');
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = () => {
    api.logout();
    setAuthUser(null);
    setEmails([]);
    setSelectedEmail(null);
    setAccounts([]);
    setActiveAccount(null);
    setFolderCounts({});
  };

  // ===== ADMIN: User Management =====
  const loadUsers = async () => {
    setAdminLoading(true);
    try {
      const data = await api.getUsers();
      if (data.success) setAdminUsers(data.users || []);
    } catch (err) {
      toast('Erro ao carregar usuários', 'error');
    } finally {
      setAdminLoading(false);
    }
  };

  const openAdminPanel = () => {
    setShowAdminPanel(true);
    setAdminTab('users');
    loadUsers();
    loadCompanies();
  };

  const openAddUser = () => {
    setEditingUser(null);
    setUserForm({ name: '', email: '', password: '', max_accounts: 0, storage_limit_mb: 0, company_id: '' });
    setUserFormStorageUnit('MB');
    setShowAddUser(true);
  };

  const openEditUser = (user) => {
    setEditingUser(user);
    const slm = user.storage_limit_mb || 0;
    setUserForm({ name: user.name, email: user.email, password: '', max_accounts: user.max_accounts || 0, storage_limit_mb: slm, company_id: user.company_id || '' });
    setUserFormStorageUnit(slm >= 1024 ? 'GB' : 'MB');
    setShowAddUser(true);
  };

  const saveUser = async () => {
    if (!userForm.name || !userForm.email) { toast('Nome e email são obrigatórios', 'error'); return; }
    if (!editingUser && !userForm.password) { toast('Senha é obrigatória para novos usuários', 'error'); return; }
    if (userForm.password && userForm.password.length < 6) { toast('Senha deve ter pelo menos 6 caracteres', 'error'); return; }

    setUserFormLoading(true);
    try {
      let data;
      if (editingUser) {
        const body = { name: userForm.name, email: userForm.email, max_accounts: userForm.max_accounts, storage_limit_mb: userForm.storage_limit_mb, company_id: userForm.company_id || null };
        if (userForm.password) body.password = userForm.password;
        data = await api.updateUser(editingUser.id, body);
      } else {
        data = await api.createUser(userForm);
      }
      if (data.success) {
        toast(editingUser ? 'Usuário atualizado' : 'Usuário criado', 'success');
        setShowAddUser(false);
        loadUsers();
      } else {
        toast(data.error || 'Erro', 'error');
      }
    } catch (err) {
      toast(err.message || 'Erro ao salvar', 'error');
    } finally {
      setUserFormLoading(false);
    }
  };

  const deleteUser = async (id, name) => {
    if (!window.confirm(`Excluir o usuário "${name}" e TODOS os seus dados? Esta ação é irreversível.`)) return;
    try {
      const data = await api.deleteUser(id);
      if (data.success) {
        toast('Usuário excluído', 'success');
        loadUsers();
      } else {
        toast(data.error || 'Erro', 'error');
      }
    } catch (err) {
      toast(err.message || 'Erro ao excluir', 'error');
    }
  };

  // ===== COMPANY MANAGEMENT =====
  const loadCompanies = async () => {
    setCompaniesLoading(true);
    try {
      const data = await api.getCompanies();
      if (data.success) setCompanies(data.companies || []);
    } catch (err) {
      toast('Erro ao carregar empresas', 'error');
    } finally {
      setCompaniesLoading(false);
    }
  };

  const openAddCompany = () => {
    setEditingCompany(null);
    setCompanyForm({ name:'', plan:'custom', max_users:0, max_accounts_per_user:0, storage_limit_mb:0, ai_credits_per_user:0, ai_enabled:true, notes:'' });
    setShowAddCompany(true);
  };

  const openEditCompany = (company) => {
    setEditingCompany(company);
    setCompanyForm({
      name: company.name || '',
      plan: company.plan || 'custom',
      max_users: company.max_users || 0,
      max_accounts_per_user: company.max_accounts_per_user || 0,
      storage_limit_mb: company.storage_limit_mb || 0,
      ai_credits_per_user: company.ai_credits_per_user || 0,
      ai_enabled: company.ai_enabled !== false,
      notes: company.notes || '',
    });
    setShowAddCompany(true);
  };

  const saveCompany = async () => {
    if (!companyForm.name.trim()) { toast('Nome da empresa é obrigatório', 'error'); return; }
    setCompanyFormLoading(true);
    try {
      let data;
      if (editingCompany) {
        data = await api.updateCompany(editingCompany.id, companyForm);
      } else {
        data = await api.createCompany(companyForm);
      }
      if (data.success) {
        toast(editingCompany ? 'Empresa atualizada' : 'Empresa criada', 'success');
        setShowAddCompany(false);
        loadCompanies();
      } else {
        toast(data.error || 'Erro', 'error');
      }
    } catch (err) {
      toast(err.message || 'Erro ao salvar', 'error');
    } finally {
      setCompanyFormLoading(false);
    }
  };

  const deleteCompany = async (id, name) => {
    if (!window.confirm(`Excluir a empresa "${name}"? Usuários serão desvinculados mas não excluídos.`)) return;
    try {
      const data = await api.deleteCompany(id);
      if (data.success) {
        toast('Empresa excluída', 'success');
        loadCompanies();
        loadUsers();
      } else {
        toast(data.error || 'Erro', 'error');
      }
    } catch (err) {
      toast(err.message || 'Erro ao excluir', 'error');
    }
  };

  const propagateCompanyLimits = async (companyId) => {
    try {
      const data = await api.updateCompany(companyId, { propagate: true });
      if (data.success) {
        toast('Limites propagados para todos os usuários da empresa', 'success');
        loadUsers();
      }
    } catch (err) {
      toast('Erro ao propagar limites', 'error');
    }
  };

  const getCompanyName = (companyId) => {
    const c = companies.find(co => co.id === companyId);
    return c ? c.name : null;
  };

  // Filtered + sorted admin users
  const filteredAdminUsers = useMemo(() => {
    let list = adminUsers;
    // Search
    if (adminSearch) {
      const q = adminSearch.toLowerCase();
      list = list.filter(u =>
        u.name?.toLowerCase().includes(q) ||
        u.email?.toLowerCase().includes(q)
      );
    }
    // Filter
    if (adminFilter === 'admin') list = list.filter(u => u.role === 'admin');
    else if (adminFilter === 'user') list = list.filter(u => u.role === 'user');
    else if (adminFilter === 'active') list = list.filter(u => u.is_active);
    else if (adminFilter === 'inactive') list = list.filter(u => !u.is_active);
    // Sort
    list = [...list].sort((a, b) => {
      if (adminSort === 'name') return (a.name || '').localeCompare(b.name || '');
      if (adminSort === 'email') return (a.email || '').localeCompare(b.email || '');
      if (adminSort === 'recent') return new Date(b.last_login || 0) - new Date(a.last_login || 0);
      if (adminSort === 'created') return new Date(b.created_at || 0) - new Date(a.created_at || 0);
      return 0;
    });
    return list;
  }, [adminUsers, adminSearch, adminFilter, adminSort]);

  // Admin stats
  const adminStats = useMemo(() => ({
    total: adminUsers.length,
    admins: adminUsers.filter(u => u.role === 'admin').length,
    users: adminUsers.filter(u => u.role === 'user').length,
    active: adminUsers.filter(u => u.is_active).length,
    inactive: adminUsers.filter(u => !u.is_active).length,
    totalAccounts: adminUsers.reduce((sum, u) => sum + (u.email_accounts_count || 0), 0),
  }), [adminUsers]);

  // Close menus on outside click
  useEffect(() => {
    const handler = () => { setContextMenu(null); setTagDropdown(null); setTagColorPicker(false); setShowAiCreditsPanel(false); };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, []);

  // Toast system
  const toast = useCallback((message, type = 'info') => {
    const id = ++toastIdSeq;
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 4000);
  }, []);

  // ===== LOAD EMAILS (from DB cache) =====
  const folderAbortRef = useRef(null); // AbortController for current folder operations

  // Snapshot do estado atual da lista, para o auto-sync não recarregar a
  // página 1 por cima de uma busca/tag/página em que o usuário está.
  const listStateRef = useRef({ page: 1, searchActive: false, filterTag: null, unreadOnly: false });
  useEffect(() => {
    listStateRef.current = { page: emailPage, searchActive, filterTag, unreadOnly: filterUnreadOnly };
  }, [emailPage, searchActive, filterTag, filterUnreadOnly]);

  const abortFolderOps = useCallback(() => {
    if (folderAbortRef.current) {
      folderAbortRef.current.abort();
    }
    folderAbortRef.current = new AbortController();
    isSyncingRef.current = false;
    return folderAbortRef.current.signal;
  }, []);

  const loadEmails = useCallback(async (folder = currentFolder, page = 1, signal = null, unreadOnly = false) => {
    if (!activeAccount?.id) return;
    setEmailsLoading(true);
    try {
      const data = await api.getEmails(folder, page, emailsPerPage, activeAccount.id, signal, unreadOnly);
      if (data.success) {
        setEmails(data.emails || []);
        setEmailPagination(data.pagination || { total: 0, total_pages: 1 });
        setEmailPage(page);
      }
    } catch (err) {
      if (err.name === 'AbortError') return; // silently ignore aborted requests
      console.error('loadEmails error:', err);
    } finally {
      setEmailsLoading(false);
    }
  }, [activeAccount, currentFolder, emailsPerPage]);

  // Sync IMAP → DB with batch progress for large mailboxes
  const SYNC_BATCH_SIZE = 500;
  const SYNC_BATCH_THRESHOLD = 20;
  const isSyncingRef = useRef(false);
  const syncAbortRef = useRef(null); // dedicated AbortController for sync

  const cancelSync = useCallback(() => {
    if (syncAbortRef.current) {
      syncAbortRef.current.abort();
      syncAbortRef.current = null;
    }
    isSyncingRef.current = false;
    setEmailSyncing(false);
    setSyncProgress(null);
    toast('Sincronização cancelada', 'info');
  }, [toast]);

  const syncFolder = useCallback(async (folder = currentFolder, silent = false, signal = null) => {
    if (!activeAccount?.id || !activeAccount?.imap_host) return;
    if (isSyncingRef.current) return; // prevent concurrent syncs

    // Create dedicated abort controller for this sync
    syncAbortRef.current = new AbortController();
    const syncSignal = syncAbortRef.current.signal;
    // Also respect external signal (folder change abort)
    const checkAborted = () => syncSignal.aborted || signal?.aborted;

    isSyncingRef.current = true;
    if (!silent) setEmailSyncing(true);
    try {
      if (checkAborted()) return;

      // Step 1: Check how many emails need syncing (fast, no processing)
      let pending = 0;
      let totalOnServer = 0;
      try {
        const status = await api.syncStatus(folder, activeAccount.id);
        if (checkAborted()) return;
        if (status.success) {
          pending = status.pending || 0;
          totalOnServer = status.total_on_server || 0;
        } else if (status.error) {
          const msg = status.error.length > 120 ? status.error.substring(0, 120) + '...' : status.error;
          toast('Falha na sincronização: ' + msg, 'error');
          return;
        }
      } catch (e) {
        if (e.name === 'AbortError') return;
        const errMsg = e?.message || '';
        if (errMsg.includes('IMAP') || errMsg.includes('Authentication') || errMsg.includes('Connection')) {
          toast('Falha na sincronização: ' + (errMsg.length > 120 ? errMsg.substring(0, 120) + '...' : errMsg), 'error');
          return;
        }
      }

      // Step 2: If many pending, use batch mode with progress bar
      if (pending > SYNC_BATCH_THRESHOLD) {
        const totalNew = pending;
        let totalSynced = 0;
        setSyncProgress({ total: totalNew, synced: 0, percent: 0, folder });

        let batchNum = 0;
        while (totalSynced < totalNew) {
          if (checkAborted()) break;
          const batch = await api.syncEmails(folder, activeAccount.id, SYNC_BATCH_SIZE, syncSignal);
          if (!batch.success) break;

          const batchSynced = batch.synced_this_batch || 0;
          totalSynced += batchSynced;
          const percent = Math.min(99, Math.round((totalSynced / totalNew) * 100));
          setSyncProgress({ total: totalNew, synced: totalSynced, percent, folder });
          batchNum++;

          if (!checkAborted() && (batchNum % 2 === 0 || batch.is_complete)) {
            if (!listStateRef.current.searchActive && !listStateRef.current.filterTag) await loadEmails(folder, listStateRef.current.page, syncSignal, listStateRef.current.unreadOnly);
            refreshCounts();
          }

          if (batch.is_complete || batch.remaining <= 0 || batchSynced === 0) break;
        }

        if (!checkAborted()) {
          setSyncProgress({ total: totalNew, synced: totalSynced, percent: 100, folder });
          if (!listStateRef.current.searchActive && !listStateRef.current.filterTag) await loadEmails(folder, listStateRef.current.page, syncSignal, listStateRef.current.unreadOnly);
          refreshCounts();
          await new Promise(r => setTimeout(r, 500));
          setSyncProgress(null);
          if (!silent) toast(`${totalSynced} email(s) sincronizado(s)`, 'success');
        }

      } else {
        if (checkAborted()) return;
        const data = await api.syncEmails(folder, activeAccount.id, 0, syncSignal);
        if (checkAborted()) return;
        if (data.success && (data.new_emails > 0 || data.deleted_from_cache > 0 || data.flags_updated > 0)) {
          if (!listStateRef.current.searchActive && !listStateRef.current.filterTag) await loadEmails(folder, listStateRef.current.page, syncSignal, listStateRef.current.unreadOnly);
          refreshCounts();
          if (data.new_emails > 0 && !silent) {
            toast(`${data.new_emails} novo(s) email(s)`, 'success');
          }
          if (data.spam_detected > 0) {
            toast(`⚠️ ${data.spam_detected} email(s) identificado(s) como spam e movido(s) automaticamente`, 'info');
          }
        }
        // Handle storage exceeded from sync
        if (data.storage_exceeded) {
          setStorageExceeded(true);
          if (data.storage_used_bytes) setStorageUsedBytes(data.storage_used_bytes);
          if (data.storage_limit_mb) setStorageLimitMb(data.storage_limit_mb);
          if (!silent) toast('Limite de armazenamento atingido. Exclua emails para liberar espaço.', 'error');
        }
      }
    } catch (err) {
      if (err.name === 'AbortError') return;
      console.error('syncFolder error:', err);
      // Always show IMAP connection errors to user (not just in non-silent mode)
      const errMsg = err?.message || err?.toString() || '';
      if (errMsg.includes('IMAP') || errMsg.includes('Authentication') || errMsg.includes('Connection') || errMsg.includes('conexão') || errMsg.includes('Falha')) {
        toast('Erro de sincronização: ' + (errMsg.length > 120 ? errMsg.substring(0, 120) + '...' : errMsg), 'error');
      } else if (!silent) {
        toast('Erro ao sincronizar emails', 'error');
      }
    } finally {
      isSyncingRef.current = false;
      setEmailSyncing(false);
      setSyncProgress(null);
      syncAbortRef.current = null;
    }
  }, [activeAccount, currentFolder, emailPage, loadEmails, toast]);

  // Load counters from DB (no IMAP needed)
  const loadCounts = useCallback(async (signal = null) => {
    if (!activeAccount?.id) return;
    try {
      const data = await api.getEmailCounts(activeAccount.id, signal);
      if (data.success && data.counts) {
        setFolderCounts(data.counts);
      }
      // Update storage info from counts endpoint
      if (data.storage_used_bytes !== undefined) setStorageUsedBytes(data.storage_used_bytes);
      if (data.storage_limit_mb !== undefined) setStorageLimitMb(data.storage_limit_mb);
      if (data.storage_exceeded !== undefined) setStorageExceeded(data.storage_exceeded);
    } catch (err) {
      if (err.name === 'AbortError') return;
      // silent
    }
  }, [activeAccount?.id]); // eslint-disable-line

  // Independent counts refresh (never aborted) - use after actions
  const refreshCounts = useCallback(() => {
    loadCounts(null);
  }, [loadCounts]);

  // On folder change: abort pending ops, load cached emails instantly, then sync in background
  useEffect(() => {
    if (activeAccount?.id) {
      const signal = abortFolderOps(); // abort all pending requests for previous folder

      // Contadores sempre atualizam na troca de pasta — antes desta correção o
      // `return` do ramo IMAP tornava a chamada inalcançável no caminho comum.
      loadCounts(null);

      if (currentFolder === 'drafts') {
        loadDrafts(1);
        refreshDraftCount();
      } else {
        loadEmails(currentFolder, 1, signal);
        if (activeAccount?.imap_host) {
          const timer = setTimeout(() => syncFolder(currentFolder, true, signal), 300);
          return () => clearTimeout(timer);
        }
      }
    }
  }, [currentFolder, activeAccount?.id]); // eslint-disable-line

  // Ensure counts are loaded on account switch (independent of folder change)
  useEffect(() => {
    if (activeAccount?.id) {
      // Immediate load from cache
      loadCounts(null);
      // Also refresh draft count
      refreshDraftCount();
      // Retry after a short delay in case sync hasn't populated cache yet
      const retryTimer = setTimeout(() => loadCounts(null), 2000);
      return () => clearTimeout(retryTimer);
    }
  }, [activeAccount?.id]); // eslint-disable-line

  // Auto-sync interval
  useEffect(() => {
    if (syncIntervalRef.current) clearInterval(syncIntervalRef.current);
    const mins = settings.syncInterval || 5;
    if (mins > 0 && activeAccount?.id && activeAccount?.imap_host) {
      syncIntervalRef.current = setInterval(() => {
        syncFolder(currentFolder, true);
      }, mins * 60 * 1000);
    }
    return () => { if (syncIntervalRef.current) clearInterval(syncIntervalRef.current); };
  }, [settings.syncInterval, activeAccount?.id, currentFolder]); // eslint-disable-line

  // Server-side search with debounce
  const searchTimeoutRef = useRef(null);
  const executeSearch = useCallback(async (query, dateFrom, dateTo, page = 1) => {
    if (!activeAccount?.id) return;
    if (!query && !dateFrom && !dateTo) {
      setSearchActive(false);
      loadEmails(currentFolder, 1);
      return;
    }
    setSearchActive(true);
    setEmailsLoading(true);
    try {
      const data = await api.searchEmails(query, currentFolder, dateFrom, dateTo, page, emailsPerPage, activeAccount.id);
      if (data.success) {
        setEmails(data.emails || []);
        setEmailPagination(data.pagination || { total: 0, total_pages: 1 });
        setEmailPage(page);
      }
    } catch (err) {
      console.error('search error:', err);
    } finally {
      setEmailsLoading(false);
    }
  }, [activeAccount, currentFolder, emailsPerPage, loadEmails]);

  const handleSearchChange = (value) => {
    setSearchQuery(value);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      executeSearch(value, searchDateFrom, searchDateTo);
    }, 400);
  };

  const applyDateFilter = () => {
    setShowDateFilter(false);
    executeSearch(searchQuery, searchDateFrom, searchDateTo);
  };

  const clearSearch = () => {
    setSearchQuery('');
    setSearchDateFrom('');
    setSearchDateTo('');
    setSearchActive(false);
    setShowDateFilter(false);
    loadEmails(currentFolder, 1, null, filterUnreadOnly);
  };

  // Navegação de página unificada — antes cada botão chamava loadEmails direto
  // e descartava silenciosamente a busca ativa e o filtro por tag.
  const goToPage = (page) => {
    if (page < 1) return;
    if (currentFolder === 'drafts') { loadDrafts(page); return; }
    if (filterTag) { loadTagEmails(filterTag, page); return; }
    if (searchActive) { executeSearch(searchQuery, searchDateFrom, searchDateTo, page); return; }
    loadEmails(currentFolder, page, null, filterUnreadOnly);
  };

  // Emails to display (when filterUnreadOnly is on, server already returns only unread)
  const filteredEmails = emails;

  const unreadCount = folderCounts?.inbox?.unread || 0;
  const draftCount = folderCounts?.drafts?.total || 0;

  // Permission helpers
  const perms = authUser?.permissions || {};
  const hasPerm = (key) => authUser?.role === 'admin' || perms[key] !== false;
  const hasModelPerm = (modelId) => hasPerm('perm_ai') && (authUser?.role === 'admin' || perms[`perm_ai_${modelId}`] !== false);

  const isAiEnabled = hasPerm('perm_ai') && (authUser?.role === 'admin' || authUser?.ai_enabled !== false);
  const hasAiCredits = authUser?.role === 'admin' || aiCreditsLimit <= 0 || aiCreditsRemaining > 0;

  // Filtered AI models based on user permissions
  const availableAiModels = AI_MODELS.filter(m => hasModelPerm(m.id));

  // Auto-correct selected AI models if current selection is not available
  useEffect(() => {
    if (availableAiModels.length > 0) {
      const firstId = availableAiModels[0].id;
      if (!availableAiModels.find(m => m.id === aiModel)) setAiModel(firstId);
      if (!availableAiModels.find(m => m.id === composeAiModel)) setComposeAiModel(firstId);
      if (!availableAiModels.find(m => m.id === batchAiModel)) setBatchAiModel(firstId);
    }
  }, [availableAiModels.length]); // eslint-disable-line

  // Helper to update account unreads counter locally
  const adjustAccountUnreads = (accountId, delta) => {
    if (!accountId) return;
    setAccountUnreads(prev => {
      if (!(accountId in prev)) return prev;
      const newCount = Math.max(0, (prev[accountId] || 0) + delta);
      return { ...prev, [accountId]: newCount };
    });
  };

  // Account group helpers
  const loadAccountGroups = async () => {
    try {
      const data = await api.getAccountGroups();
      if (data.success) setAccountGroups(data.groups || []);
    } catch {}
  };
  const saveAccountGroupsToDb = async (groups) => {
    setAccountGroups(groups);
    try { await api.saveAccountGroups(groups); } catch {}
  };
  const addGroup = () => {
    if (!groupForm.name.trim()) return;
    const newGroup = { id: Date.now().toString(), name: groupForm.name.trim(), color: groupForm.color, accountIds: groupForm.accountIds };
    saveAccountGroupsToDb([...accountGroups, newGroup]);
    setGroupForm({ name: '', color: '#3B82F6', accountIds: [] });
    setShowGroupManager(false);
  };
  const updateGroup = () => {
    if (!editingGroup || !groupForm.name.trim()) return;
    saveAccountGroupsToDb(accountGroups.map(g => g.id === editingGroup.id ? { ...g, name: groupForm.name.trim(), color: groupForm.color, accountIds: groupForm.accountIds } : g));
    setEditingGroup(null);
    setGroupForm({ name: '', color: '#3B82F6', accountIds: [] });
    setShowGroupManager(false);
  };
  const deleteGroup = (groupId) => {
    saveAccountGroupsToDb(accountGroups.filter(g => g.id !== groupId));
    if (filterGroup === groupId) setFilterGroup(null);
  };
  const getAccountGroup = (accId) => accountGroups.find(g => g.accountIds?.includes(accId));
  const filteredAccountsByGroup = (accs) => {
    if (!filterGroup) return accs;
    if (filterGroup === 'ungrouped') return accs.filter(a => !getAccountGroup(a.id));
    const grp = accountGroups.find(g => g.id === filterGroup);
    return grp ? accs.filter(a => grp.accountIds?.includes(a.id)) : accs;
  };

  // Drag & drop account reorder
  const [dragIdx, setDragIdx] = useState(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);

  // Helper to refresh AI credits after usage
  const refreshAiCredits = async () => {
    try {
      const data = await api.getAiCredits();
      if (data.success) {
        setAiCreditsLimit(data.credits_limit);
        setAiCreditsUsed(data.credits_used);
        setAiCreditsRemaining(data.credits_remaining);
      }
    } catch {}
  };

  // Update browser tab title with unread count
  useEffect(() => {
    document.title = unreadCount > 0 ? `Coremail (${unreadCount})` : 'Coremail';
  }, [unreadCount]);

  // Save emailsPerPage preference and reload when it changes
  const changeEmailsPerPage = useCallback((newVal) => {
    setEmailsPerPage(newVal);
    localStorage.setItem('coremail-per-page', newVal);
    if (activeAccount?.id) {
      setEmailsLoading(true);
      const loader = currentFolder === 'drafts'
        ? api.getDrafts(1, newVal)
        : api.getEmails(currentFolder, 1, newVal, activeAccount.id);
      loader.then(data => {
        if (data.success) {
          setEmails(data.emails || []);
          setEmailPagination(data.pagination || { total: 0, total_pages: 1 });
          setEmailPage(1);
          setSelectedIds(new Set());
        }
      }).catch(() => {}).finally(() => setEmailsLoading(false));
    }
  }, [activeAccount, currentFolder]);
  useEffect(() => {
    localStorage.setItem('coremail-view-mode', viewMode);
  }, [viewMode]);
  useEffect(() => {
    localStorage.setItem('coremail-sidebar-collapsed', sidebarCollapsed);
  }, [sidebarCollapsed]);
  useEffect(() => {
    localStorage.setItem('coremail-list-width', listWidth);
  }, [listWidth]);

  // List panel resize handler
  const startListResize = useCallback((e) => {
    e.preventDefault();
    listResizing.current = true;
    const startX = e.clientX;
    const startWidth = listWidth;
    const onMove = (ev) => {
      if (!listResizing.current) return;
      const newWidth = Math.min(700, Math.max(280, startWidth + (ev.clientX - startX)));
      setListWidth(newWidth);
    };
    const onUp = () => {
      listResizing.current = false;
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, [listWidth]);

  // ===== EMAIL ACTIONS (API-connected) =====
  const getEmailById = (id) => emails.find(e => e.id === id);

  const toggleStar = async (id, e) => {
    e?.stopPropagation();
    const email = getEmailById(id);
    if (!email) return;
    const newStarred = !email.starred;
    // Optimistic update
    setEmails(prev => prev.map(em => em.id === id ? { ...em, starred: newStarred } : em));
    try {
      await api.flagEmail(email.uid, newStarred ? 'starred' : 'unstarred', email.folder, activeAccount?.id);
    } catch (err) {
      // Revert on error
      setEmails(prev => prev.map(em => em.id === id ? { ...em, starred: !newStarred } : em));
      toast('Erro ao atualizar favorito', 'error');
    }
  };

  const toggleRead = async (id) => {
    const email = getEmailById(id) || selectedEmail;
    if (!email) return;
    const newUnread = !email.unread;
    // Optimistic
    setEmails(prev => prev.map(em => em.id === id ? { ...em, unread: newUnread } : em));
    if (selectedEmail?.id === id) setSelectedEmail(prev => prev ? { ...prev, unread: newUnread } : prev);
    adjustAccountUnreads(activeAccount?.id, newUnread ? 1 : -1);
    try {
      await api.flagEmail(email.uid, newUnread ? 'unread' : 'read', email.folder, activeAccount?.id);
      toast(newUnread ? 'Marcado como não lido' : 'Marcado como lido', 'info');
      refreshCounts();
    } catch (err) {
      setEmails(prev => prev.map(em => em.id === id ? { ...em, unread: !newUnread } : em));
      adjustAccountUnreads(activeAccount?.id, newUnread ? -1 : 1);
      toast('Erro ao atualizar status', 'error');
    }
  };

  const deleteEmail = async (id) => {
    const email = getEmailById(id) || selectedEmail;
    if (!email) return;
    const permanent = email.folder === 'trash';

    // Com confirmação ativa, sempre abre (ou substitui) o diálogo para ESTE email.
    // Antes, um diálogo já aberto para outro email fazia a exclusão pular a confirmação.
    if (settings.confirmDelete && confirmDelete?.id !== id) {
      setConfirmDelete({
        id,
        permanent,
        label: permanent ? 'Excluir permanentemente' : 'Mover para lixeira',
        subject: email.subject || '(Sem assunto)',
      });
      return;
    }

    // Actually delete
    setConfirmDelete(null);
    setEmails(prev => prev.filter(em => em.id !== id));
    if (selectedEmail?.id === id) setSelectedEmail(null);
    try {
      await api.deleteEmail(email.uid, email.folder, permanent, activeAccount?.id);
      toast(permanent ? 'Email excluído permanentemente' : 'Email movido para lixeira', 'success');
      refreshCounts();
    } catch (err) {
      toast('Erro ao excluir email', 'error');
      loadEmails(currentFolder, emailPage);
    }
  };

  const confirmDeleteEmail = async () => {
    if (!confirmDelete) return;
    // Sem fallback para selectedEmail: se o email saiu da lista, não podemos
    // arriscar excluir outra mensagem.
    const email = getEmailById(confirmDelete.id) ||
      (selectedEmail?.id === confirmDelete.id ? selectedEmail : null);
    if (!email) {
      setConfirmDelete(null);
      toast('Email não encontrado — atualize a lista', 'error');
      return;
    }

    setEmails(prev => prev.filter(em => em.id !== confirmDelete.id));
    if (selectedEmail?.id === confirmDelete.id) setSelectedEmail(null);
    const permanent = confirmDelete.permanent;
    setConfirmDelete(null);
    try {
      await api.deleteEmail(email.uid, email.folder, permanent, activeAccount?.id);
      toast(permanent ? 'Email excluído permanentemente' : 'Email movido para lixeira', 'success');
      refreshCounts();
    } catch (err) {
      toast('Erro ao excluir email', 'error');
      loadEmails(currentFolder, emailPage);
    }
  };

  const archiveEmail = async (id) => {
    const email = getEmailById(id) || selectedEmail;
    if (!email) return;
    setEmails(prev => prev.filter(em => em.id !== id));
    if (selectedEmail?.id === id) setSelectedEmail(null);
    try {
      await api.moveEmail(email.uid, email.folder, 'archive', activeAccount?.id);
      toast('Email arquivado', 'success');
      refreshCounts();
    } catch (err) {
      toast('Erro ao arquivar email', 'error');
      loadEmails(currentFolder, emailPage);
    }
  };

  const markAsSpam = async (id) => {
    const email = getEmailById(id) || selectedEmail;
    if (!email) return;
    setEmails(prev => prev.filter(em => em.id !== id));
    if (selectedEmail?.id === id) setSelectedEmail(null);
    try {
      await api.moveEmail(email.uid, email.folder, 'spam', activeAccount?.id);
      toast('Email marcado como spam', 'success');
      refreshCounts();
    } catch (err) {
      toast('Erro ao marcar como spam', 'error');
      loadEmails(currentFolder, emailPage);
    }
  };

  const markAsNotSpam = async (id) => {
    const email = getEmailById(id) || selectedEmail;
    if (!email) return;
    const senderEmail = email.from?.email || '';
    const senderDomain = senderEmail.split('@')[1] || '';
    setEmails(prev => prev.filter(em => em.id !== id));
    if (selectedEmail?.id === id) setSelectedEmail(null);
    try {
      // Move to inbox
      await api.moveEmail(email.uid, email.folder, 'inbox', activeAccount?.id);
      // Add sender to whitelist so future emails aren't flagged
      if (senderEmail) {
        await api.addToWhitelist(senderEmail, '').catch(() => {});
      }
      toast(`"${email.from?.name || senderEmail}" movido para Entrada e adicionado à whitelist`, 'success');
      refreshCounts();
    } catch (err) {
      toast('Erro ao mover email', 'error');
      loadEmails(currentFolder, emailPage);
    }
  };

  const moveToFolder = async (id, targetFolder) => {
    const email = getEmailById(id) || selectedEmail;
    if (!email) return;
    setEmails(prev => prev.filter(em => em.id !== id));
    if (selectedEmail?.id === id) setSelectedEmail(null);
    try {
      await api.moveEmail(email.uid, email.folder, targetFolder, activeAccount?.id);
      toast(`Email movido para ${FOLDERS.find(f => f.id === targetFolder)?.name}`, 'success');
      refreshCounts();
    } catch (err) {
      toast('Erro ao mover email', 'error');
      loadEmails(currentFolder, emailPage);
    }
  };

  const toggleEmailTag = async (emailId, tagId) => {
    const email = emails.find(e => e.id === emailId) || selectedEmail;
    if (!email) return;
    const cacheId = email.cache_id || email.id;
    const has = (email.tags || []).includes(tagId);

    // Optimistic update
    const updateTags = (em) => {
      if (em.id !== emailId) return em;
      return { ...em, tags: has ? em.tags.filter(t => t !== tagId) : [...(em.tags || []), tagId] };
    };
    setEmails(prev => prev.map(updateTags));
    if (selectedEmail?.id === emailId) {
      setSelectedEmail(prev => prev ? updateTags(prev) : prev);
    }

    try {
      if (has) {
        await api.unassignTag(tagId, cacheId);
      } else {
        await api.assignTag(tagId, cacheId);
      }
    } catch (err) {
      // Revert
      const revert = (em) => {
        if (em.id !== emailId) return em;
        return { ...em, tags: has ? [...(em.tags || []), tagId] : em.tags.filter(t => t !== tagId) };
      };
      setEmails(prev => prev.map(revert));
      if (selectedEmail?.id === emailId) setSelectedEmail(prev => prev ? revert(prev) : prev);
      toast('Erro ao atualizar tag', 'error');
    }
  };

  // Select email and lazy-load full body
  const selectEmail = async (email) => {
    setSelectedEmail({ ...email, body: null });
    setShowAiPanel(false);
    resetAi();
    // Mark as read optimistically
    if (email.unread && settings.autoRead) {
      setEmails(prev => prev.map(em => em.id === email.id ? { ...em, unread: false } : em));
      adjustAccountUnreads(activeAccount?.id, -1);
      api.flagEmail(email.uid, 'read', email.folder, activeAccount?.id).then(() => refreshCounts()).catch(() => {});
    }
    // Load full body
    setEmailBodyLoading(true);
    try {
      const data = await api.getEmailBody(email.uid, email.folder, activeAccount?.id);
      if (data.success) {
        // Cor fixa: variáveis CSS do app não existem dentro do iframe do corpo
        const body = data.body && data.body.trim() ? data.body : '<p style="color:#9CA3AF;text-align:center;padding:40px 0;">(Este email não possui conteúdo de texto)</p>';
        setSelectedEmail(prev => prev?.id === email.id ? {
          ...prev,
          body,
          to: data.to?.length ? data.to : (prev.to || []),
          cc: data.cc?.length ? data.cc : (prev.cc || []),
          bcc: data.bcc?.length ? data.bcc : (prev.bcc || []),
          attachments: data.attachments?.length ? data.attachments : (prev.attachments || []),
        } : prev);
      } else {
        setSelectedEmail(prev => prev?.id === email.id ? {
          ...prev,
          body: '<p style="color:#9CA3AF;text-align:center;padding:40px 0;">Não foi possível carregar o conteúdo deste email.</p>',
        } : prev);
      }
    } catch (err) {
      console.error('loadEmailBody error:', err?.message || err);
      const errMsg = err?.message || 'Erro desconhecido';
      setSelectedEmail(prev => prev?.id === email.id ? {
        ...prev,
        body: `<div style="text-align:center;padding:40px 20px;">
          <p style="color:#EF4444;font-weight:600;margin-bottom:8px;">Erro ao carregar corpo do email</p>
          <p style="color:#9CA3AF;font-size:12px;word-break:break-all;">${errMsg.replace(/</g,'&lt;').replace(/>/g,'&gt;')}</p>
        </div>`,
      } : prev);
    } finally {
      setEmailBodyLoading(false);
    }
  };

  const toggleSelect = (id, e) => {
    e?.stopPropagation();
    // Qualquer ajuste manual invalida o modo "pasta inteira selecionada" —
    // sem isso, desmarcar um item ainda deixava o bulk agir sobre a pasta toda.
    setAllFolderSelected(false);
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // Select ALL emails in folder (not just visible page)
  const [allFolderSelected, setAllFolderSelected] = useState(false);

  const selectAllInFolder = async () => {
    setAllFolderSelected(true);
    setSelectedIds(new Set(filteredEmails.map(e => e.id)));
    toast(`Todos os ${emailPagination.total} emails da pasta serão afetados pelas ações`, 'info');
  };

  // Reset allFolderSelected when selection changes manually
  const selectAll = () => {
    const allSelected = selectedIds.size === filteredEmails.length && filteredEmails.length > 0;
    if (allSelected) {
      setSelectedIds(new Set());
      setAllFolderSelected(false);
    } else {
      setSelectedIds(new Set(filteredEmails.map(e => e.id)));
      setAllFolderSelected(false);
    }
  };

  // Generic bulk confirm handler
  const executeBulkAction = async () => {
    if (!bulkConfirm) return;
    const action = bulkConfirm.action;
    setBulkConfirm(null);

    const folder = currentFolder;
    const isAllFolder = allFolderSelected && emailPagination.total > filteredEmails.length;

    if (action === 'bulk_delete') {
      if (isAllFolder) {
        // Get ALL UIDs efficiently from backend
        setEmailsLoading(true);
        try {
          const data = await api.getBulkUids(folder, activeAccount?.id);
          if (data.success && data.uids?.length > 0) {
            await api.bulkDelete(data.uids, folder, folder === 'trash', activeAccount?.id);
            toast(`${data.uids.length} email(s) excluído(s)`, 'success');
          }
        } catch (err) {
          toast('Erro ao excluir em massa', 'error');
        }
        setSelectedIds(new Set());
        setAllFolderSelected(false);
        setSelectedEmail(null);
        await loadEmails(folder, 1);
        refreshCounts();
        setEmailsLoading(false);
      } else {
        const selected = emails.filter(e => selectedIds.has(e.id));
        const uids = selected.map(e => e.uid);
        setEmails(prev => prev.filter(em => !selectedIds.has(em.id)));
        setSelectedIds(new Set());
        setAllFolderSelected(false);
        if (selectedIds.has(selectedEmail?.id)) setSelectedEmail(null);
        try {
          await api.bulkDelete(uids, folder, folder === 'trash', activeAccount?.id);
          toast(`${uids.length} email(s) excluído(s)`, 'success');
        } catch (err) {
          toast('Erro ao excluir em massa', 'error');
        }
        refreshCounts();
      }
      return;
    }

    if (action === 'bulk_read' || action === 'bulk_unread') {
      const flag = action === 'bulk_read' ? 'read' : 'unread';
      const isUnread = flag === 'unread';

      if (isAllFolder) {
        setEmailsLoading(true);
        try {
          const data = await api.getBulkUids(folder, activeAccount?.id);
          if (data.success && data.uids?.length > 0) {
            await api.bulkFlag(data.uids, flag, folder, activeAccount?.id);
            toast(`${data.uids.length} email(s) marcado(s) como ${isUnread ? 'não lido' : 'lido'}`, 'info');
          }
        } catch (err) {
          toast('Erro ao atualizar em massa', 'error');
        }
        setSelectedIds(new Set());
        setAllFolderSelected(false);
        await loadEmails(folder, 1);
        refreshCounts();
        setEmailsLoading(false);
      } else {
        const selected = emails.filter(e => selectedIds.has(e.id));
        const uids = selected.map(e => e.uid);
        // Count how many actually change state
        const changing = selected.filter(e => e.unread !== isUnread).length;
        setEmails(prev => prev.map(em => selectedIds.has(em.id) ? { ...em, unread: isUnread } : em));
        adjustAccountUnreads(activeAccount?.id, isUnread ? changing : -changing);
        setSelectedIds(new Set());
        setAllFolderSelected(false);
        try {
          await api.bulkFlag(uids, flag, folder, activeAccount?.id);
          toast(`${uids.length} email(s) marcado(s) como ${isUnread ? 'não lido' : 'lido'}`, 'info');
        } catch (err) {
          toast('Erro ao atualizar em massa', 'error');
        }
        refreshCounts();
      }
      return;
    }
  };

  const bulkDelete = () => {
    const count = allFolderSelected ? emailPagination.total : selectedIds.size;
    const permanent = emails.find(e => selectedIds.has(e.id))?.folder === 'trash';
    setBulkConfirm({
      action: 'bulk_delete',
      count,
      label: permanent ? `Excluir ${count} email(s) permanentemente?` : `Mover ${count} email(s) para lixeira?`,
      description: permanent ? 'Esta ação não pode ser desfeita.' : 'Os emails serão movidos para a lixeira.',
    });
  };

  const bulkMarkRead = () => {
    const count = allFolderSelected ? emailPagination.total : selectedIds.size;
    setBulkConfirm({
      action: 'bulk_read',
      count,
      label: `Marcar ${count} email(s) como lido?`,
      description: allFolderSelected ? 'Todos os emails da pasta serão marcados como lidos.' : 'Os emails selecionados serão marcados como lidos.',
    });
  };

  const bulkMarkUnread = () => {
    const count = allFolderSelected ? emailPagination.total : selectedIds.size;
    setBulkConfirm({
      action: 'bulk_unread',
      count,
      label: `Marcar ${count} email(s) como não lido?`,
      description: allFolderSelected ? 'Todos os emails da pasta serão marcados como não lidos.' : 'Os emails selecionados serão marcados como não lidos.',
    });
  };

  // Compose
  // ===== COMPOSE HELPERS =====
  // ===== DRAG & DROP EMAIL REORDER =====
  const handleDragStart = (e, idx) => {
    setDragIdx(idx);
    e.dataTransfer.effectAllowed = 'move';
    e.currentTarget.style.opacity = '0.5';
  };
  const handleDragEnd = (e) => {
    e.currentTarget.style.opacity = '1';
    setDragIdx(null);
    setDragOverIdx(null);
  };
  const handleDragOver = (e, idx) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (idx !== dragOverIdx) setDragOverIdx(idx);
  };
  const handleDrop = (e, toId) => {
    e.preventDefault();
    if (dragIdx === null || dragIdx === toId) { setDragIdx(null); setDragOverIdx(null); return; }
    // dragIdx/toId são IDs de conta (não índices): com busca ou filtro de grupo
    // ativos, índices apontavam para contas erradas no array completo.
    let newOrder = null;
    setAccounts(prev => {
      const fromPos = prev.findIndex(a => a.id === dragIdx);
      const toPos = prev.findIndex(a => a.id === toId);
      if (fromPos === -1 || toPos === -1) return prev;
      const copy = [...prev];
      const [moved] = copy.splice(fromPos, 1);
      copy.splice(toPos, 0, moved);
      newOrder = copy.map(a => a.id);
      return copy;
    });
    // Persistência fora do updater (que deve ser puro)
    if (newOrder) localStorage.setItem('coremail-acc-order', JSON.stringify(newOrder));
    setDragIdx(null);
    setDragOverIdx(null);
  };

  const openCompose = (data = {}) => {
    const toArr = data.to ? (Array.isArray(data.to) ? data.to : data.to.split(',').map(e => e.trim()).filter(Boolean)) : [];
    const ccArr = data.cc ? (Array.isArray(data.cc) ? data.cc : data.cc.split(',').map(e => e.trim()).filter(Boolean)) : [];
    const bccArr = data.bcc ? (Array.isArray(data.bcc) ? data.bcc : data.bcc.split(',').map(e => e.trim()).filter(Boolean)) : [];
    setCompose({ to: toArr, cc: ccArr, bcc: bccArr, subject: data.subject || '', body: data.body || '' });
    setComposeToInput(''); setComposeCcInput(''); setComposeBccInput('');
    setShowCc(ccArr.length > 0);
    setShowBcc(bccArr.length > 0);
    setContactSuggestions([]); setContactsField(null); setHighlightedContact(-1);
    if (!data._keepDraftId) setEditingDraftId(data.draftId || null);
    setShowCompose(true);
    // Set editor content after DOM renders
    setTimeout(() => {
      if (editorRef.current) {
        editorRef.current.innerHTML = data.bodyHtml
          ? data.bodyHtml
          : (data.body ? data.body.replace(/\n/g, '<br>') : '');
      }
    }, 50);
  };

  // Helper: strip HTML tags to get plain text for quote
  const htmlToPlainText = (html) => {
    if (!html) return '';
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  // Build quote header (plain text for reply, HTML for forward)
  const buildQuoteHeader = (email, prefix = 'Mensagem Original') => {
    const ccLine = email.cc?.length ? `\nCc: ${email.cc.map(c => c.name ? `${c.name} <${c.email}>` : c.email).join(', ')}` : '';
    return `--- ${prefix} ---\nDe: ${email.from?.name || ''} <${email.from?.email || ''}>\nPara: ${(email.to || []).map(t => t.name ? `${t.name} <${t.email}>` : t.email).join(', ')}${ccLine}\nEm: ${formatFullDate(email.date)}\nAssunto: ${email.subject || '(sem assunto)'}`;
  };

  // Build HTML quote block preserving original email layout
  // O cabeçalho é escapado (nomes/assuntos podem conter HTML) e o corpo passa
  // pela mesma sanitização da visualização — sem isso, responder/encaminhar um
  // email malicioso injetava HTML ativo direto no editor (fora do iframe).
  const buildQuoteHtml = (email, prefix = 'Mensagem Original') => {
    const header = textToHtml(buildQuoteHeader(email, prefix));
    const originalBody = sanitizeEmailHtml(email.body || '') || '<p>(Sem conteúdo)</p>';
    return `<br><br><div style="padding:12px 0;border-top:1px solid #ccc;margin-top:12px;color:#666;font-size:13px;">${header}</div><blockquote style="margin:10px 0 0 0;padding:10px 15px;border-left:3px solid #ccc;">${originalBody}</blockquote>`;
  };

  // Build plain text quote (fallback)
  const buildQuoteBlock = (email, prefix = 'Mensagem Original') => {
    const plainBody = htmlToPlainText(email.body || '');
    return `\n\n${buildQuoteHeader(email, prefix)}\n\n${plainBody}`;
  };

  const openReply = (email) => openCompose({
    to: email.from?.email || '',
    subject: replySubject(email.subject),
    body: buildQuoteBlock(email),
    bodyHtml: buildQuoteHtml(email),
  });

  const openReplyAll = (email) => {
    const myEmail = activeAccount?.email?.toLowerCase();
    // Collect all unique emails: from + to + cc, excluding self
    const allRecipients = new Set();
    
    // Add original sender
    if (email.from?.email) allRecipients.add(email.from.email.toLowerCase());
    // Add all To recipients
    (email.to || []).forEach(t => { if (t.email) allRecipients.add(t.email.toLowerCase()); });
    
    // Remove self from To
    allRecipients.delete(myEmail);
    
    // CC recipients (excluding self and anyone already in To)
    const ccEmails = (email.cc || [])
      .map(c => c.email?.toLowerCase())
      .filter(e => e && e !== myEmail && !allRecipients.has(e));
    
    openCompose({
      to: Array.from(allRecipients),
      cc: ccEmails,
      subject: replySubject(email.subject),
      body: buildQuoteBlock(email),
      bodyHtml: buildQuoteHtml(email),
    });
  };

  const openForward = (email) => openCompose({
    subject: forwardSubject(email.subject),
    body: buildQuoteBlock(email, 'Mensagem Encaminhada'),
    bodyHtml: buildQuoteHtml(email, 'Mensagem Encaminhada'),
  });

  // ===== DRAFTS =====
  const getComposeContent = useCallback(() => {
    const htmlBody = editorRef.current?.innerHTML || '';
    const plainBody = editorRef.current?.innerText || '';
    return { htmlBody, plainBody };
  }, []);

  const isComposeEmpty = useCallback(() => {
    const { plainBody } = getComposeContent();
    return compose.to.length === 0 && compose.cc.length === 0 && compose.bcc.length === 0
      && !compose.subject.trim() && !plainBody.trim();
  }, [compose, getComposeContent]);

  const saveDraft = useCallback(async (silent = true) => {
    if (isComposeEmpty()) return null;
    const { htmlBody, plainBody } = getComposeContent();
    try {
      if (!silent) setDraftSaving(true);
      const data = await api.saveDraft({
        draft_id: editingDraftId || null,
        subject: compose.subject,
        to_address: compose.to.join(', '),
        cc_address: compose.cc.join(', '),
        bcc_address: compose.bcc.join(', '),
        body: plainBody,
        body_html: htmlBody,
      });
      if (data.success && data.draft_id) {
        setEditingDraftId(data.draft_id);
        refreshDraftCount();
        return data.draft_id;
      }
    } catch (err) {
      if (!silent) toast('Erro ao salvar rascunho', 'error');
    } finally {
      if (!silent) setDraftSaving(false);
    }
    return null;
  }, [compose, editingDraftId, getComposeContent, isComposeEmpty, toast]); // eslint-disable-line

  const closeCompose = useCallback(async () => {
    if (!isComposeEmpty()) {
      await saveDraft(true);
      toast('Rascunho salvo', 'info');
    }
    setShowCompose(false);
    setEditingDraftId(null);
    setCompose({ to: [], cc: [], bcc: [], subject: '', body: '' });
    if (editorRef.current) editorRef.current.innerHTML = '';
    // Refresh drafts list if viewing drafts
    if (currentFolder === 'drafts') loadDrafts();
  }, [isComposeEmpty, saveDraft, toast, currentFolder]); // eslint-disable-line

  const discardDraft = useCallback(async () => {
    if (editingDraftId) {
      try {
        await api.deleteDraft(editingDraftId);
        toast('Rascunho descartado', 'info');
        refreshDraftCount();
        if (currentFolder === 'drafts') loadDrafts();
      } catch (err) { /* silent */ }
    }
    setShowCompose(false);
    setEditingDraftId(null);
    setCompose({ to: [], cc: [], bcc: [], subject: '', body: '' });
    if (editorRef.current) editorRef.current.innerHTML = '';
  }, [editingDraftId, toast, currentFolder]); // eslint-disable-line

  const loadDrafts = useCallback(async (page = 1) => {
    setEmailsLoading(true);
    try {
      const data = await api.getDrafts(page, emailsPerPage);
      if (data.success) {
        setEmails(data.emails || []);
        setEmailPagination(data.pagination || { total: 0, total_pages: 1 });
        setEmailPage(page);
      }
    } catch (err) {
      console.error('loadDrafts error:', err);
    } finally {
      setEmailsLoading(false);
    }
  }, [emailsPerPage]);

  const refreshDraftCount = useCallback(async () => {
    try {
      const data = await api.getDraftCounts();
      if (data.success) {
        setFolderCounts(prev => ({
          ...prev,
          drafts: { total: data.total, unread: 0 },
        }));
      }
    } catch (err) { /* silent */ }
  }, []);

  const openDraft = useCallback(async (email) => {
    if (!email.draft_id) return;
    try {
      const data = await api.getDraft(email.draft_id);
      if (data.success && data.draft) {
        const d = data.draft;
        openCompose({
          to: d.to_address || '',
          cc: d.cc_address || '',
          bcc: d.bcc_address || '',
          subject: d.subject || '',
          body: d.body || '',
          bodyHtml: d.body_html || '',
          draftId: d.id,
        });
      }
    } catch (err) {
      toast('Erro ao abrir rascunho', 'error');
    }
  }, [toast]); // eslint-disable-line

  const deleteDraftFromList = useCallback(async (draftId, e) => {
    e?.stopPropagation();
    try {
      await api.deleteDraft(draftId);
      setEmails(prev => prev.filter(em => em.draft_id !== draftId));
      if (selectedEmail?.draft_id === draftId) setSelectedEmail(null);
      toast('Rascunho excluído', 'success');
      refreshDraftCount();
    } catch (err) {
      toast('Erro ao excluir rascunho', 'error');
    }
  }, [selectedEmail, toast, refreshDraftCount]);

  // Save draft on browser close / tab close
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (showCompose && !isComposeEmpty()) {
        // Sync save via sendBeacon (fire-and-forget)
        const htmlBody = editorRef.current?.innerHTML || '';
        const plainBody = editorRef.current?.innerText || '';
        const payload = JSON.stringify({
          draft_id: editingDraftId || null,
          subject: compose.subject,
          to_address: compose.to.join(', '),
          cc_address: compose.cc.join(', '),
          bcc_address: compose.bcc.join(', '),
          body: plainBody,
          body_html: htmlBody,
        });
        const token = api.getToken();
        const url = `${api.baseUrl}/index.php?route=drafts&action=save`;
        // fetch keepalive funciona em qualquer navegador moderno; a antiga
        // guarda em navigator.sendBeacon deixava o rascunho sem salvar quando
        // a API não existia, mesmo com fetch disponível.
        try {
          fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            body: payload,
            keepalive: true,
          }).catch(() => {});
        } catch {}
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [showCompose, compose, editingDraftId, isComposeEmpty]);

  // ===== CONTACTS AUTOCOMPLETE =====
  const searchContactsDebounce = useRef(null);

  const handleRecipientInput = (field, value) => {
    if (field === 'to') setComposeToInput(value);
    else if (field === 'cc') setComposeCcInput(value);
    else setComposeBccInput(value);

    setContactsField(field);
    setHighlightedContact(-1);

    if (searchContactsDebounce.current) clearTimeout(searchContactsDebounce.current);
    searchContactsDebounce.current = setTimeout(async () => {
      try {
        const data = await api.searchContacts(value, 8);
        if (data.success) {
          // Filter out already added emails
          const existing = [...compose.to, ...compose.cc, ...compose.bcc];
          setContactSuggestions(
            (data.contacts || []).filter(c => !existing.includes(c.email))
          );
        }
      } catch (err) {
        setContactSuggestions([]);
      }
    }, 200);
  };

  const addRecipient = (field, email) => {
    email = email.trim().toLowerCase();
    if (!email || !email.includes('@')) return;
    setCompose(prev => ({
      ...prev,
      [field]: prev[field].includes(email) ? prev[field] : [...prev[field], email],
    }));
    if (field === 'to') setComposeToInput('');
    else if (field === 'cc') setComposeCcInput('');
    else setComposeBccInput('');
    setContactSuggestions([]);
    setContactsField(null);
  };

  const removeRecipient = (field, email) => {
    setCompose(prev => ({ ...prev, [field]: prev[field].filter(e => e !== email) }));
  };

  const handleRecipientKeyDown = (field, e) => {
    const inputVal = field === 'to' ? composeToInput : field === 'cc' ? composeCcInput : composeBccInput;

    if (e.key === 'Enter' || e.key === 'Tab' || e.key === ',') {
      e.preventDefault();
      if (highlightedContact >= 0 && highlightedContact < contactSuggestions.length) {
        addRecipient(field, contactSuggestions[highlightedContact].email);
      } else if (inputVal.trim()) {
        addRecipient(field, inputVal);
      }
      return;
    }
    if (e.key === 'Backspace' && !inputVal && compose[field].length > 0) {
      removeRecipient(field, compose[field][compose[field].length - 1]);
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedContact(prev => Math.min(prev + 1, contactSuggestions.length - 1));
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedContact(prev => Math.max(prev - 1, 0));
    }
    if (e.key === 'Escape') {
      setContactSuggestions([]);
      setContactsField(null);
    }
  };

  const selectContact = (field, contact) => {
    addRecipient(field, contact.email);
  };

  // ===== RICH TEXT EDITOR =====
  // ===== RICH TEXT EDITOR =====
  const [formatState, setFormatState] = useState({});

  const updateFormatState = useCallback(() => {
    if (!editorRef.current) return;
    // Only update format state if the editor itself is focused
    // This prevents re-renders that steal focus from <select> dropdowns
    if (!editorRef.current.contains(document.activeElement) && document.activeElement !== editorRef.current) return;
    setFormatState({
      bold: document.queryCommandState('bold'),
      italic: document.queryCommandState('italic'),
      underline: document.queryCommandState('underline'),
      strikeThrough: document.queryCommandState('strikeThrough'),
      insertUnorderedList: document.queryCommandState('insertUnorderedList'),
      insertOrderedList: document.queryCommandState('insertOrderedList'),
      justifyLeft: document.queryCommandState('justifyLeft'),
      justifyCenter: document.queryCommandState('justifyCenter'),
      justifyRight: document.queryCommandState('justifyRight'),
    });
  }, []);

  useEffect(() => {
    // Só escuta seleção com o compose aberto — antes o listener global rodava
    // 9 queryCommandState a cada mudança de seleção em qualquer tela.
    if (!showCompose) return;
    const handler = () => updateFormatState();
    document.addEventListener('selectionchange', handler);
    return () => document.removeEventListener('selectionchange', handler);
  }, [updateFormatState, showCompose]);

  const execFormat = (command, value = null) => {
    document.execCommand(command, false, value);
    editorRef.current?.focus();
    setTimeout(updateFormatState, 10);
  };

  const handleInsertLink = () => {
    let url = (prompt('Insira a URL:') || '').trim();
    if (!url) return;
    // Só http(s)/mailto — bloqueia javascript: e afins
    if (!/^(https?:\/\/|mailto:)/i.test(url)) {
      if (/^[a-z0-9.-]+\.[a-z]{2,}/i.test(url)) url = `https://${url}`;
      else { toast('URL inválida', 'error'); return; }
    }
    const sel = window.getSelection();
    if (sel.rangeCount > 0 && sel.toString().length > 0) {
      execFormat('createLink', url);
    } else {
      const text = prompt('Texto do link:', url) || url;
      editorRef.current?.focus();
      document.execCommand('insertHTML', false, `<a href="${escapeHtml(url)}" target="_blank">${escapeHtml(text)}</a>`);
    }
  };

  const handleInsertImage = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = (e) => {
      const file = e.target.files[0];
      if (!file) return;
      if (file.size > 5 * 1024 * 1024) { toast('Imagem muito grande (máx 5MB)', 'error'); return; }
      const reader = new FileReader();
      reader.onload = () => {
        editorRef.current?.focus();
        document.execCommand('insertHTML', false, `<img src="${reader.result}" style="max-width:100%;border-radius:6px" />`);
      };
      reader.readAsDataURL(file);
    };
    input.click();
  };

  // ===== SEND EMAIL =====
  const [sendingEmail, setSendingEmail] = useState(false);

  const sendEmail = async () => {
    if (compose.to.length === 0) { toast('Informe o destinatário', 'error'); return; }
    if (!activeAccount?.id) { toast('Nenhuma conta de email ativa', 'error'); return; }
    if (!activeAccount?.smtp_host) { toast('Configure o servidor SMTP na conta para enviar emails', 'error'); return; }

    const htmlBody = editorRef.current?.innerHTML || '';
    const plainBody = editorRef.current?.innerText || '';

    setSendingEmail(true);
    try {
      const data = await api.sendEmail({
        account_id: activeAccount.id,
        to: compose.to.join(', '),
        cc: compose.cc.join(', '),
        bcc: compose.bcc.join(', '),
        subject: compose.subject.trim(),
        body: plainBody,
        body_html: htmlBody,
        signature: settings.useSignature ? signature : '',
      }, composeAttachments);

      if (data.success) {
        toast('Email enviado com sucesso!', 'success');
        // Delete draft if we were editing one
        if (editingDraftId) {
          api.deleteDraft(editingDraftId).catch(() => {});
          refreshDraftCount();
        }
        setShowCompose(false);
        setEditingDraftId(null);
        setCompose({ to: [], cc: [], bcc: [], subject: '', body: '' });
        setComposeAttachments([]);
        if (editorRef.current) editorRef.current.innerHTML = '';
        refreshCounts();
        if (currentFolder === 'sent') loadEmails('sent', 1);
        if (currentFolder === 'drafts') loadDrafts();
      } else {
        toast(data.error || 'Erro ao enviar email', 'error');
      }
    } catch (err) {
      toast(err.message || 'Erro ao enviar email', 'error');
    } finally {
      setSendingEmail(false);
    }
  };

  // Context menu
  const handleCtx = (e, email) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenu({ x: e.clientX, y: e.clientY, email });
  };

  // Tags CRUD
  const loadTags = useCallback(async () => {
    try {
      const data = await api.getTags(activeAccount?.id);
      if (data.success) setTags(data.tags || []);
    } catch (err) { /* silent */ }
  }, [activeAccount?.id]);

  const addTag = async () => {
    const name = newTagName.trim();
    if (!name) { toast('Digite um nome para a tag', 'error'); return; }
    try {
      const data = await api.createTag(name, newTagColor, activeAccount?.id);
      if (data.success && data.tag) {
        setTags(prev => [...prev, data.tag]);
        setNewTagName('');
        toast('Tag criada', 'success');
      } else if (data.success) {
        setNewTagName('');
        loadTags();
        toast('Tag criada', 'success');
      } else {
        toast(data.error || 'Erro ao criar tag', 'error');
      }
    } catch (err) {
      console.error('addTag error:', err);
      toast(err.message || 'Erro ao criar tag', 'error');
    }
  };

  const openEditTag = (tag) => {
    const hex = tag.color || TAG_COLOR_HEX[tag.colorId] || '#3B82F6';
    setEditingTag({ id: tag.id, name: tag.name, color: hex });
  };

  const saveEditTag = async () => {
    if (!editingTag) return;
    const name = editingTag.name.trim();
    if (!name) { toast('Nome da tag é obrigatório', 'error'); return; }
    try {
      const data = await api.updateTag(editingTag.id, name, editingTag.color);
      if (data.success) {
        setTags(prev => prev.map(t => t.id === editingTag.id
          ? { ...t, name, color: editingTag.color, colorId: '' }
          : t
        ));
        setEditingTag(null);
        toast('Tag atualizada', 'success');
      } else {
        toast(data.error || 'Erro ao atualizar', 'error');
      }
    } catch (err) {
      toast(err.message || 'Erro ao atualizar tag', 'error');
    }
  };

  const deleteTag = async (tagId) => {
    const tag = tags.find(t => t.id === tagId);
    // Ação destrutiva (remove a tag de todos os emails) — pede confirmação,
    // como o resto do app faz para exclusões.
    if (!window.confirm(`Excluir a tag "${tag?.name || ''}"? Ela será removida de todos os emails.`)) return;
    try {
      await api.deleteTag(tagId);
      setTags(prev => prev.filter(t => t.id !== tagId));
      // Remove tag from loaded emails
      setEmails(prev => prev.map(em => ({ ...em, tags: (em.tags || []).filter(t => t !== tagId) })));
      if (selectedEmail) {
        setSelectedEmail(prev => prev ? { ...prev, tags: (prev.tags || []).filter(t => t !== tagId) } : prev);
      }
      if (filterTag === tagId) setFilterTag(null);
      // Also remove rules associated with this tag
      setTagRules(prev => prev.filter(r => r.tag_id !== tagId));
      toast('Tag removida', 'success');
    } catch (err) {
      toast('Erro ao remover tag', 'error');
    }
  };

  // ===== TAG RULES (auto-tagging) =====
  const loadTagRules = useCallback(async () => {
    setTagRulesLoading(true);
    try {
      const data = await api.getTagRules(activeAccount?.id);
      if (data.success) setTagRules(data.rules || []);
    } catch (err) {
      console.log('Tag rules API error:', err);
    } finally {
      setTagRulesLoading(false);
    }
  }, [activeAccount?.id]); // eslint-disable-line

  const addTagRule = async () => {
    if (!newRule.tag_id) { toast('Selecione uma tag', 'error'); return; }
    if (!newRule.rule_value.trim()) { toast('Informe o valor da regra', 'error'); return; }
    try {
      const data = await api.createTagRule(newRule.tag_id, newRule.rule_type, newRule.rule_value.trim(), activeAccount?.id || 0);
      if (data.success) {
        toast('Regra criada com sucesso', 'success');
        setNewRule({ tag_id: 0, rule_type: 'domain', rule_value: '' });
        loadTagRules();
      } else {
        toast(data.error || 'Erro ao criar regra', 'error');
      }
    } catch (err) {
      toast(err.message || 'Erro ao criar regra', 'error');
    }
  };

  const deleteTagRule = async (ruleId) => {
    try {
      await api.deleteTagRule(ruleId);
      setTagRules(prev => prev.filter(r => r.id !== ruleId));
      toast('Regra removida', 'success');
    } catch (err) {
      toast('Erro ao remover regra', 'error');
    }
  };

  // ===== AI BATCH SUMMARY =====
  const runAiSummary = async (mode) => {
    if (!isAiEnabled) { toast('IA não habilitada para sua conta', 'error'); return; }
    if (!hasAiCredits) { toast('Sem créditos IA disponíveis. Entre em contato com o administrador.', 'error'); return; }
    if (emails.length === 0) { toast('Nenhum email carregado', 'error'); return; }

    setAiSummaryLoading(true);
    setAiSummary(null);

    try {
      // Pick emails based on mode
      let targetEmails;
      if (mode === 'unread') {
        targetEmails = emails.filter(e => e.unread).slice(0, 5);
        if (targetEmails.length === 0) { toast('Nenhum email não lido encontrado', 'info'); setAiSummaryLoading(false); return; }
      } else {
        targetEmails = emails.slice(0, 5);
      }

      // Fetch full body for each email
      const emailsWithBody = [];
      for (const em of targetEmails) {
        try {
          const data = await api.getEmailBody(em.uid, em.folder, activeAccount?.id);
          // htmlToText remove <style>/<script> e decodifica entidades — antes o
          // modelo recebia o CSS das newsletters no lugar do conteúdo real.
          const bodyText = htmlToText(data.body, 2000) || cleanPreview(em.preview);
          emailsWithBody.push({
            subject: em.subject || '(Sem assunto)',
            from: em.from?.name || em.from?.email || 'Desconhecido',
            date: em.date ? new Date(em.date).toLocaleString('pt-BR') : '',
            body: bodyText,
          });
        } catch (err) {
          emailsWithBody.push({
            subject: em.subject || '(Sem assunto)',
            from: em.from?.name || em.from?.email || 'Desconhecido',
            date: em.date ? new Date(em.date).toLocaleString('pt-BR') : '',
            body: cleanPreview(em.preview),
          });
        }
      }

      const data = await api.aiSummarizeBatch(emailsWithBody, mode, batchAiModel);
      if (data.success && data.result) {
        setAiSummary({ result: data.result, mode, count: data.count || emailsWithBody.length });
        refreshAiCredits();
      } else {
        if (data.no_credits) {
          toast('Sem créditos IA disponíveis.', 'error');
          refreshAiCredits();
        } else {
          toast(data.error || 'Erro ao gerar resumo', 'error');
        }
      }
    } catch (err) {
      if (err.data?.no_credits) {
        toast('Sem créditos IA disponíveis.', 'error');
        refreshAiCredits();
      } else {
        toast(err.message || 'Erro ao conectar com a IA', 'error');
      }
    } finally {
      setAiSummaryLoading(false);
    }
  };

  const loadTagEmails = useCallback(async (tagId, page = 1) => {
    setEmailsLoading(true);
    // Limpa seleção da lista anterior — IDs antigos não podem alimentar
    // ações em massa sobre a nova lista filtrada.
    setSelectedIds(new Set());
    setAllFolderSelected(false);
    try {
      const data = await api.getTagEmails(tagId, page);
      if (data.success) {
        setEmails(data.emails || []);
        setEmailPagination(data.pagination || { total: 0, total_pages: 1 });
        setEmailPage(page);
      }
    } catch (err) {
      console.error('loadTagEmails error:', err);
      toast('Erro ao carregar emails da tag', 'error');
    } finally {
      setEmailsLoading(false);
    }
  }, []);

  // ===== ACCOUNTS (CRUD com API) =====
  const emptyAccountForm = { name: '', email: '', imap_host: '', imap_port: '993', smtp_host: '', smtp_port: '587', password: '' };

  // Load accounts, settings and tags when user logs in
  useEffect(() => {
    if (authUser) {
      loadAccounts();
      loadSettings();
      loadTags();
      loadAccountGroups();
    }
  }, [authUser]); // eslint-disable-line

  // Reload theme settings and tags when active account changes (for per-account)
  useEffect(() => {
    if (authUser && activeAccount?.id) {
      loadSettings(activeAccount.id);
      loadTags();
    }
  }, [activeAccount?.id]); // eslint-disable-line

  const loadSettings = async (accountId = null) => {
    try {
      const data = await api.getSettings(accountId || activeAccount?.id);
      if (data.success && data.settings) {
        const s = data.settings;
        setSettings({
          notifications: s.notifications !== false,
          autoRead: s.auto_read !== false,
          confirmDelete: s.confirm_delete !== false,
          useSignature: s.use_signature !== false,
          syncInterval: s.sync_interval ?? 5,
          fontSize: s.font_size ? parseInt(s.font_size) : parseInt(localStorage.getItem('coremail-fontsize') || '14'),
        });
        // Signature scope
        if (s.signature_scope) {
          setSignatureScope(s.signature_scope);
        }
        // Signature: use account-specific if scope is 'account' and available
        if (s.signature !== undefined && s.signature !== null) {
          setSignature(s.signature);
        }
        if (s.theme) {
          setTheme(s.theme);
        }
        // Sem else ficava o tema customizado da conta anterior "vazando"
        // ao trocar para uma conta sem tema salvo.
        if (s.custom_theme) {
          try {
            const ct = typeof s.custom_theme === 'string' ? JSON.parse(s.custom_theme) : s.custom_theme;
            setCustomTheme(ct && typeof ct === 'object' && Object.keys(ct).length > 0 ? ct : {});
          } catch (e) { setCustomTheme({}); }
        } else {
          setCustomTheme({});
        }
        if (s.theme_scope) {
          setThemeScope(s.theme_scope);
          localStorage.setItem('coremail-theme-scope', s.theme_scope);
        }
      }
    } catch (err) {
      console.log('Settings API offline, usando configurações locais');
    }
  };

  const saveSettings = async () => {
    setSettingsLoading(true);
    try {
      const payload = {
        notifications: settings.notifications,
        auto_read: settings.autoRead,
        confirm_delete: settings.confirmDelete,
        use_signature: settings.useSignature,
        sync_interval: settings.syncInterval,
        font_size: settings.fontSize,
        signature: signature,
        signature_scope: signatureScope,
        theme: theme,
        custom_theme: JSON.stringify(customTheme || {}),
        theme_scope: themeScope,
      };
      // If theme scope is per-account, include account_id for theme
      if (themeScope === 'account' && activeAccount?.id) {
        payload.theme_account_id = activeAccount.id;
      }
      // If signature scope is per-account, include account_id for signature
      if (signatureScope === 'account' && activeAccount?.id) {
        payload.signature_account_id = activeAccount.id;
      }
      await api.saveSettings(payload);
      localStorage.setItem('coremail-theme-scope', themeScope);
      toast('Configurações salvas', 'success');
      setShowSettings(false);
    } catch (err) {
      // Mantém o modal aberto para o usuário tentar de novo — antes o modal
      // fechava e o toast dizia "salvo localmente" sem nada ter sido salvo.
      toast(err.message || 'Erro ao salvar as configurações no servidor', 'error');
    } finally {
      setSettingsLoading(false);
    }
  };

  const loadAccounts = async () => {
    setAccountsLoading(true);
    try {
      const data = await api.getAccounts();
      if (data.success) {
        if (data.accounts?.length > 0) {
          const mapped = data.accounts.map(a => ({
            ...a,
            id: Number(a.id),
            initials: getInitials(a.name),
          }));
          // Apply saved order (parse protegido: um valor corrompido no
          // localStorage não pode derrubar o carregamento das contas)
          let savedOrder = [];
          try {
            savedOrder = JSON.parse(localStorage.getItem('coremail-acc-order') || '[]');
            if (!Array.isArray(savedOrder)) savedOrder = [];
          } catch { savedOrder = []; }
          if (savedOrder.length > 0) {
            mapped.sort((a, b) => {
              const ai = savedOrder.indexOf(a.id);
              const bi = savedOrder.indexOf(b.id);
              return (ai === -1 ? Number.MAX_SAFE_INTEGER : ai) - (bi === -1 ? Number.MAX_SAFE_INTEGER : bi);
            });
          }
          setAccounts(mapped);
          const savedActiveId = localStorage.getItem('coremail-active-account');
          const found = mapped.find(a => a.id === Number(savedActiveId));
          setActiveAccount(found || mapped[0]);
        } else {
          // Sem contas - limpar tudo
          setAccounts([]);
          setActiveAccount(null);
        }
      }
    } catch (err) {
      console.error('Erro ao carregar contas:', err?.message || err);
      setAccounts([]);
      setActiveAccount(null);
      toast('Não foi possível carregar as contas — verifique a conexão', 'error');
    } finally {
      setAccountsLoading(false);
    }
  };

  // Fetch unread counts for all accounts
  const fetchAllUnreads = async () => {
    if (accounts.length === 0) return;
    setAccountUnreadsLoading(true);
    const unreads = {};
    try {
      const data = await api.quickUnreads();
      if (data.success && data.accounts) {
        data.accounts.forEach(a => {
          unreads[a.account_id] = a.unread || 0;
        });
        setAccountUnreads(unreads);
        // If current account got flag updates, refresh its counts
        // Number(): a API pode devolver account_id como string
        const currentAcc = data.accounts.find(a => Number(a.account_id) === activeAccount?.id);
        if (currentAcc) {
          loadCounts(null);
        }
        // Show summary toast
        const totalUnreads = Object.values(unreads).reduce((a, b) => a + b, 0);
        const newEmails = data.accounts.reduce((a, b) => a + (b.new_emails || 0), 0);
        let msg = `${totalUnreads} email(s) não lido(s) em ${data.accounts.length} conta(s)`;
        if (newEmails > 0) msg += ` · ${newEmails} novo(s) para sincronizar`;
        toast(msg, 'info');
      }
    } catch (err) {
      toast('Erro ao buscar não lidos', 'error');
      // Fallback: use cached counts
      try {
        await Promise.all(accounts.map(async (acc) => {
          try {
            const d = await api.getEmailCounts(acc.id);
            unreads[acc.id] = d.success && d.counts?.inbox ? (d.counts.inbox.unread || 0) : 0;
          } catch { unreads[acc.id] = 0; }
        }));
        setAccountUnreads(unreads);
      } catch {}
    } finally {
      setAccountUnreadsLoading(false);
    }
  };

  const openAddAccount = () => {
    setEditingAccount(null);
    setAccountForm({ ...emptyAccountForm });
    setAccountProvider('none');
    setShowAddAccount(true);
    setShowPassword(false);
  };

  const openEditAccount = (acc, e) => {
    e?.stopPropagation();
    setEditingAccount(acc);
    setAccountProvider('manual'); // skip provider selection when editing
    setAccountForm({
      name: acc.name || '',
      email: acc.email || '',
      imap_host: acc.imap_host || '',
      imap_port: acc.imap_port || '993',
      smtp_host: acc.smtp_host || '',
      smtp_port: acc.smtp_port || '587',
      password: '',
    });
    setShowAddAccount(true);
    setShowPassword(false);
  };

  const isValidEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleAccountEmailChange = (email) => {
    setAccountForm(p => {
      const updated = { ...p, email };
      if (accountProvider === 'hostgator' && email.includes('@')) {
        const domain = email.split('@')[1];
        if (domain && domain.includes('.')) {
          updated.imap_host = `mail.${domain}`;
          updated.imap_port = '993';
          updated.smtp_host = `mail.${domain}`;
          updated.smtp_port = '465';
        }
      }
      return updated;
    });
  };

  const saveAccount = async () => {
    if (!accountForm.name.trim()) {
      toast('Informe o nome', 'error');
      return;
    }
    if (!accountForm.email.trim()) {
      toast('Informe o email', 'error');
      return;
    }
    if (!isValidEmail(accountForm.email.trim())) {
      toast('Email inválido. Use o formato: nome@dominio.com', 'error');
      return;
    }

    setAccountLoading(true);
    try {
      if (editingAccount) {
        // UPDATE
        const payload = { ...accountForm };
        // Don't send empty password (means "keep current")
        if (!payload.password) delete payload.password;

        const data = await api.updateAccount(editingAccount.id, payload);
        if (data.success) {
          setAccounts(prev => prev.map(a => a.id === editingAccount.id
            ? { ...a, ...payload, password: undefined, initials: getInitials(payload.name) }
            : a
          ));
          if (activeAccount?.id === editingAccount.id) {
            setActiveAccount(prev => ({ ...prev, ...payload, password: undefined, initials: getInitials(payload.name) }));
          }
          toast('Conta atualizada com sucesso!', 'success');
        } else {
          // Erro de negócio: mantém o modal aberto com o formulário preenchido
          toast(data.error || 'Erro ao atualizar conta', 'error');
          return;
        }
      } else {
        // CREATE
        const data = await api.createAccount(accountForm);
        if (data.success) {
          const newAcc = {
            id: data.id || Date.now(),
            ...accountForm,
            password: undefined,
            initials: getInitials(accountForm.name),
          };
          setAccounts(prev => [...prev, newAcc]);
          if (accounts.length === 0) setActiveAccount(newAcc);
          toast('Conta criada com sucesso!', 'success');
        } else {
          toast(data.error || 'Erro ao criar conta', 'error');
          return;
        }
      }
      // Só fecha e limpa o formulário em caso de sucesso — antes o usuário
      // perdia tudo o que digitou (inclusive a senha) em qualquer erro.
      setShowAddAccount(false);
      setEditingAccount(null);
      setAccountForm({ ...emptyAccountForm });
    } catch (err) {
      toast(err.message || 'Erro ao salvar conta — verifique a conexão e tente novamente', 'error');
    } finally {
      setAccountLoading(false);
    }
  };

  const deleteAccount = async (accId, e) => {
    e?.stopPropagation();
    const acc = accounts.find(a => a.id === accId);
    if (!acc) return;
    if (!window.confirm(`Excluir a conta "${acc.email}" e TODOS os seus dados (emails, contatos, rascunhos)?\n\nEsta ação é irreversível.`)) return;
    try {
      const data = await api.deleteAccount(accId);
      if (data && !data.success) {
        toast(data.error || 'Erro ao deletar conta', 'error');
        return;
      }
    } catch (err) {
      toast('Erro ao deletar conta no servidor', 'error');
      return;
    }
    const remaining = accounts.filter(a => a.id !== accId);
    setAccounts(remaining);
    // Limpa referências à conta excluída (ordem salva, badge de não lidos)
    try {
      const order = JSON.parse(localStorage.getItem('coremail-acc-order') || '[]');
      if (Array.isArray(order)) {
        localStorage.setItem('coremail-acc-order', JSON.stringify(order.filter(id => id !== accId)));
      }
    } catch {}
    setAccountUnreads(prev => {
      const next = { ...prev };
      delete next[accId];
      return next;
    });
    if (activeAccount?.id === accId) {
      // Estado de emails pertence à conta excluída — zera sempre, não só
      // quando não resta nenhuma conta.
      setEmails([]);
      setSelectedEmail(null);
      setSelectedIds(new Set());
      setFolderCounts({});
      setFilterTag(null);
      setCurrentFolder('inbox');
      if (remaining[0]) {
        setActiveAccount(remaining[0]);
        localStorage.setItem('coremail-active-account', remaining[0].id);
      } else {
        setActiveAccount(null);
        localStorage.removeItem('coremail-active-account');
      }
    }
    toast('Conta removida com sucesso', 'success');
  };

  const testConnection = async () => {
    if (!accountForm.imap_host || !accountForm.email) {
      toast('Preencha host IMAP e email para testar', 'error');
      return;
    }
    // If editing and no password typed, we need to tell backend which account to fetch password from
    if (!accountForm.password && !editingAccount) {
      toast('Informe a senha para testar a conexão', 'error');
      return;
    }
    setAccountTesting(true);
    try {
      const payload = { ...accountForm };
      // Send account_id so backend can fetch stored password if none was typed
      if (editingAccount && !accountForm.password) {
        payload.account_id = editingAccount.id;
      }
      const data = await api.testAccount(payload);
      if (data.success) {
        const info = data.messages !== undefined
          ? `Conexão OK! ${data.messages} email(s), ${data.unread} não lido(s)`
          : 'Conexão estabelecida com sucesso!';
        toast(info, 'success');
      } else {
        toast(data.error || 'Falha na conexão', 'error');
      }
    } catch (err) {
      toast(err.message || 'Não foi possível testar a conexão', 'error');
    } finally {
      setAccountTesting(false);
    }
  };

  const setActiveAndSave = (acc) => {
    setActiveAccount(acc);
    localStorage.setItem('coremail-active-account', acc.id);
    toast(`Conta ${acc.email} ativada`, 'info');
    // Reset state and reload for new account
    setSelectedEmail(null);
    setSelectedIds(new Set());
    setEmails([]);
    setFolderCounts({});
    setCurrentFolder('inbox');
    setCurrentSection('email');
    setDashboardData(null);
    setDashTopData(null);
    setDashVolumeData(null);
    setCleanupData(null);
    setContactStats(null);
  };

  const getTagCss = (tagId) => {
    const tag = tags.find(t => t.id === tagId);
    if (!tag) return '';
    // If tag has a custom hex color (not a preset), return empty so inline style is used
    if (tag.color && tag.colorId !== tag.color) {
      const presetHex = TAG_COLOR_HEX[tag.colorId];
      if (!presetHex || presetHex.toLowerCase() !== tag.color.toLowerCase()) return '';
    }
    const tc = TAG_COLORS.find(c => c.id === tag.colorId);
    return tc?.css || '';
  };

  // ===== CONTACTS =====
  const loadContacts = useCallback(async (page = 1) => {
    setContactsLoading(true);
    try {
      const data = await api.getContacts(page, 50, contactsSearch, contactsSort, contactsSortOrder);
      if (data.success) {
        setContactsList(data.contacts || []);
        setContactsTotal(data.total || 0);
        setContactsTotalPages(data.total_pages || 1);
        setContactsPage(page);
      }
    } catch (err) { console.error(err); }
    finally { setContactsLoading(false); }
  }, [contactsSearch, contactsSort, contactsSortOrder]);

  const loadContactStats = useCallback(async () => {
    try {
      const data = await api.getContactStats(activeAccount?.id);
      if (data.success) setContactStats(data);
    } catch (err) { console.error(err); }
  }, [activeAccount?.id]);

  useEffect(() => {
    if (currentSection !== 'contacts') return;
    loadContactStats();
    // Debounce: loadContacts muda de identidade a cada tecla da busca
    // (deps em contactsSearch); sem isso era uma requisição por caractere.
    const timer = setTimeout(() => loadContacts(1), 300);
    return () => clearTimeout(timer);
  }, [currentSection, loadContacts, loadContactStats]); // eslint-disable-line

  const handleContactsSearch = useCallback(() => {
    loadContacts(1);
  }, [loadContacts]);

  const removeContact = async (id) => {
    try {
      await api.deleteContact(id);
      setContactsList(prev => prev.filter(c => c.id !== id));
      setContactsTotal(prev => prev - 1);
      toast('Contato removido', 'success');
    } catch (err) { toast('Erro ao remover contato', 'error'); }
  };

  // ===== DASHBOARD =====
  const loadDashboard = useCallback(async () => {
    setDashboardLoading(true);
    try {
      const data = await api.getDashboardStats();
      if (data.success) setDashboardData(data);
    } catch (err) { console.error(err); }
    finally { setDashboardLoading(false); }
  }, []);

  const loadDashTopByAccount = useCallback(async (accountId) => {
    setDashTopLoading(true);
    try {
      const data = await api.getDashboardTopByAccount(accountId === 'all' ? null : accountId);
      if (data.success) setDashTopData(data);
    } catch (err) { console.error(err); }
    finally { setDashTopLoading(false); }
  }, []);

  const loadDashVolumeByAccount = useCallback(async (accountId) => {
    setDashVolumeLoading(true);
    try {
      const data = await api.getDashboardVolumeByAccount(accountId === 'all' ? null : accountId);
      if (data.success) setDashVolumeData(data);
    } catch (err) { console.error(err); }
    finally { setDashVolumeLoading(false); }
  }, []);

  useEffect(() => {
    if (currentSection === 'dashboard') {
      loadDashboard();
      setDashTopAccountFilter('all');
      setDashTopData(null);
      setDashVolumeFilter('all');
      setDashVolumeData(null);
    }
  }, [currentSection, loadDashboard]);

  // Contact edit
  const openEditContact = (contact) => {
    setEditingContact(contact);
    setEditContactForm({ name: contact.name || '', email: contact.email || '' });
  };

  const saveEditContact = async () => {
    if (!editContactForm.email.trim()) { toast('Email obrigatório', 'error'); return; }
    setEditContactLoading(true);
    try {
      const data = await api.updateContact(editingContact.id, editContactForm);
      if (data.success) {
        toast('Contato atualizado', 'success');
        setEditingContact(null);
        loadContacts(1);
        loadContactStats();
      } else {
        toast(data.error || 'Erro', 'error');
      }
    } catch (err) { toast('Erro ao atualizar', 'error'); }
    finally { setEditContactLoading(false); }
  };

  // Admin: View user dashboard
  const openAdminUserDashboard = async (user) => {
    setAdminViewDashboard({ userId: user.id, userName: user.name });
    setAdminDashboardLoading(true);
    try {
      const data = await api.getUserDashboardStats(user.id);
      if (data.success) setAdminDashboardData(data);
      else { toast(data.error || 'Erro', 'error'); setAdminViewDashboard(null); }
    } catch (err) { toast('Erro ao carregar dashboard', 'error'); setAdminViewDashboard(null); }
    finally { setAdminDashboardLoading(false); }
  };

  // ===== SPAM RULES =====
  const loadSpamRules = async () => {
    setSpamRulesLoading(true);
    try {
      const data = await api.getSpamRules();
      if (data.success) setSpamRules(data.rules || []);
    } catch (err) { console.error(err); }
    finally { setSpamRulesLoading(false); }
  };

  const addSpamRule = async () => {
    if (!newSpamRule.pattern.trim()) { toast('Informe o padrão', 'error'); return; }
    try {
      const data = await api.addSpamRule(newSpamRule.type, newSpamRule.pattern.trim());
      if (data.success) {
        toast('Regra adicionada', 'success');
        setNewSpamRule({ type: 'domain', pattern: '' });
        loadSpamRules();
      } else { toast(data.error || 'Erro', 'error'); }
    } catch (err) { toast('Erro ao adicionar regra', 'error'); }
  };

  const removeSpamRule = async (id) => {
    try {
      await api.deleteSpamRule(id);
      setSpamRules(prev => prev.filter(r => r.id !== id));
      toast('Regra removida', 'success');
    } catch (err) { toast('Erro ao remover regra', 'error'); }
  };

  // ===== CLEANUP / EMAIL MANAGEMENT =====
  const loadCleanupStats = useCallback(async () => {
    if (!activeAccount?.id) return;
    setCleanupLoading(true);
    try {
      const data = await api.getCleanupStats(activeAccount.id);
      if (data.success) setCleanupData(data);
      else toast('Erro ao carregar dados de limpeza', 'error');
    } catch (err) {
      toast('Erro ao carregar dados de limpeza', 'error');
    } finally { setCleanupLoading(false); }
  }, [activeAccount?.id, toast]); // eslint-disable-line

  const executeCleanup = async (criteria, options = {}, actionKey = '', label = '') => {
    if (!activeAccount?.id) return;
    setCleanupActionLoading(actionKey);
    try {
      const data = await api.cleanupDelete(activeAccount.id, criteria, options);
      if (data.success) {
        const count = data.deleted || 0;
        toast(`${label || 'Limpeza'}: ${count} email(s) processado(s)`, 'success');
        loadCleanupStats(); // refresh stats
        refreshCounts(); // refresh folder counts
      } else {
        toast(data.error || 'Erro na limpeza', 'error');
      }
    } catch (err) {
      toast('Erro ao executar limpeza', 'error');
    } finally { setCleanupActionLoading(null); }
  };

  // Load cleanup on section change
  useEffect(() => {
    if (currentSection === 'cleanup') loadCleanupStats();
  }, [currentSection]); // eslint-disable-line

  // ===== ATTACHMENT HANDLERS =====
  const PREVIEWABLE_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp', 'image/svg+xml', 'application/pdf', 'text/plain', 'text/html'];

  const getAttachMime = (filename) => {
    const name = String(filename || '');
    const ext = name.includes('.') ? name.split('.').pop().toLowerCase() : '';
    const map = {
      pdf: 'application/pdf', png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg',
      gif: 'image/gif', webp: 'image/webp', svg: 'image/svg+xml', txt: 'text/plain',
      csv: 'text/plain', log: 'text/plain', html: 'text/html',
      doc: 'application/msword',
      docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      xls: 'application/vnd.ms-excel',
      xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      zip: 'application/zip', rar: 'application/x-rar-compressed',
    };
    return map[ext] || 'application/octet-stream';
  };

  const canPreview = (filename) => {
    const mime = getAttachMime(filename);
    return PREVIEWABLE_TYPES.includes(mime);
  };

  const downloadAttachment = async (att) => {
    if (!selectedEmail?.uid) return;
    const url = api.getAttachmentDownloadUrl(
      selectedEmail.uid, att.part, att.name, selectedEmail.folder, activeAccount?.id
    );
    try {
      const response = await fetch(url, {
        headers: api.getToken() ? { 'Authorization': `Bearer ${api.getToken()}` } : {},
      });
      if (!response.ok) throw new Error('Download failed');
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = att.name || 'attachment';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      // Revogar de imediato pode cancelar downloads grandes em alguns navegadores
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 2000);
    } catch (err) {
      toast('Erro ao baixar anexo', 'error');
    }
  };

  const attachPreviewReqRef = useRef(0);
  const previewAttachment = async (att) => {
    if (!selectedEmail?.uid) return;
    // Token de requisição: sem ele, abrir o anexo B antes de A responder
    // misturava o conteúdo de A com o cabeçalho de B.
    const reqId = ++attachPreviewReqRef.current;
    const mime = getAttachMime(att.name);
    setAttachPreview({ filename: att.name, mime, dataUrl: null, loading: true });
    try {
      const data = await api.getAttachmentPreview(
        selectedEmail.uid, att.part, att.name, selectedEmail.folder, activeAccount?.id
      );
      if (reqId !== attachPreviewReqRef.current) return; // resposta antiga
      if (data.success) {
        const dataUrl = `data:${data.mime};base64,${data.data}`;
        // Prioriza o MIME real do servidor sobre o adivinhado pela extensão
        setAttachPreview(prev => prev
          ? { ...prev, mime: data.mime || prev.mime, dataUrl, loading: false, size: data.size }
          : null);
      } else {
        toast(data.error || 'Erro ao carregar preview', 'error');
        setAttachPreview(null);
      }
    } catch (err) {
      if (reqId !== attachPreviewReqRef.current) return;
      toast('Erro ao carregar anexo', 'error');
      setAttachPreview(null);
    }
  };

  // ===== AI ACTIONS =====
  const resetAi = () => {
    setAiAction(null);
    setAiStyle('');
    setAiExtra('');
    setAiResult('');
    setAiLoading(false);
    // Mantém o modelo escolhido se ainda for permitido; senão usa o primeiro disponível
    setAiModel(prev => availableAiModels.some(m => m.id === prev)
      ? prev
      : (availableAiModels[0]?.id || DEFAULT_AI_MODEL));
  };

  const toggleAiPanel = () => {
    if (!isAiEnabled) { toast('IA não habilitada para sua conta', 'error'); return; }
    if (!hasAiCredits) { toast('Sem créditos IA disponíveis. Entre em contato com o administrador.', 'error'); return; }
    setShowAiPanel(v => !v);
    resetAi();
  };

  const executeAi = async () => {
    if (!aiAction || !selectedEmail) return;
    if (!hasAiCredits) { toast('Sem créditos IA disponíveis.', 'error'); return; }
    if ((aiAction === 'reply' || aiAction === 'thank') && !aiStyle) {
      toast('Selecione um estilo', 'error');
      return;
    }

    const emailText = htmlToText(selectedEmail.body, 6000) || cleanPreview(selectedEmail.preview);

    setAiLoading(true);
    setAiResult('');
    try {
      const data = await api.aiProcess({
        action_type: aiAction,
        email_body: emailText,
        email_subject: selectedEmail.subject || '',
        email_from: selectedEmail.from?.name || selectedEmail.from?.email || '',
        style: aiStyle,
        extra_info: aiExtra,
        user_name: activeAccount?.name || 'Usuário',
        model: aiModel,
      });
      if (data.success && data.result) {
        setAiResult(data.result);
        refreshAiCredits();
      } else {
        if (data.no_credits) { toast(data.error || 'Sem créditos IA disponíveis.', 'error'); refreshAiCredits(); }
        else toast(data.error || 'Erro na IA', 'error');
      }
    } catch (err) {
      if (err.data?.no_credits) { toast(err.message || 'Sem créditos IA disponíveis.', 'error'); refreshAiCredits(); }
      else toast(err.message || 'Erro ao processar IA', 'error');
    } finally {
      setAiLoading(false);
    }
  };

  const useAiResult = () => {
    if (!aiResult) return;
    if (aiAction === 'summarize') {
      if (navigator.clipboard?.writeText) {
        navigator.clipboard.writeText(aiResult)
          .then(() => toast('Resumo copiado!', 'success'))
          .catch(() => toast('Não foi possível copiar para a área de transferência', 'error'));
      } else {
        toast('Área de transferência indisponível neste navegador', 'error');
      }
    } else {
      // Abrir compose com a resposta (texto da IA escapado antes de virar HTML)
      openCompose({
        to: selectedEmail.from?.email || '',
        subject: replySubject(selectedEmail.subject),
        bodyHtml: textToHtml(aiResult),
        body: aiResult,
      });
      setShowAiPanel(false);
      resetAi();
    }
  };

  // ===== COMPOSE AI =====
  const resetComposeAi = () => {
    setComposeAiPrompt('');
    setComposeAiResult('');
    setComposeAiLoading(false);
    setComposeAiStyle('formal');
  };

  const executeComposeAi = async () => {
    if (!composeAiPrompt.trim()) { toast('Descreva o email que deseja gerar', 'error'); return; }
    if (!hasAiCredits) { toast('Sem créditos IA disponíveis.', 'error'); return; }
    setComposeAiLoading(true);
    setComposeAiResult('');
    try {
      const data = await api.aiProcess({
        action_type: 'generate',
        email_body: composeAiPrompt.trim(),
        email_subject: compose.subject || '',
        email_from: '',
        style: composeAiStyle,
        extra_info: compose.to.length > 0 ? `Destinatário(s): ${compose.to.join(', ')}` : '',
        user_name: activeAccount?.name || 'Usuário',
        model: composeAiModel,
      });
      if (data.success && data.result) {
        setComposeAiResult(data.result);
        refreshAiCredits();
      } else {
        if (data.no_credits) { toast(data.error || 'Sem créditos IA disponíveis.', 'error'); refreshAiCredits(); }
        else toast(data.error || 'Erro na IA', 'error');
      }
    } catch (err) {
      if (err.data?.no_credits) { toast(err.message || 'Sem créditos IA disponíveis.', 'error'); refreshAiCredits(); }
      else toast(err.message || 'Erro ao gerar email', 'error');
    } finally {
      setComposeAiLoading(false);
    }
  };

  const applyComposeAiResult = () => {
    if (!composeAiResult || !editorRef.current) return;
    // Try to parse [ASSUNTO]...[/ASSUNTO] and [CORPO]...[/CORPO]
    const subjectMatch = composeAiResult.match(/\[ASSUNTO\]([\s\S]*?)\[\/ASSUNTO\]/);
    const bodyMatch = composeAiResult.match(/\[CORPO\]([\s\S]*?)\[\/CORPO\]/);

    let appliedSubject = false;
    if (subjectMatch && bodyMatch) {
      const aiSubject = subjectMatch[1].trim();
      const aiBody = bodyMatch[1].trim();
      if (aiSubject) {
        setCompose(p => ({ ...p, subject: aiSubject }));
        appliedSubject = true;
      }
      editorRef.current.innerHTML = textToHtml(aiBody);
    } else {
      // Fallback: usa o texto inteiro como corpo (escapado)
      editorRef.current.innerHTML = textToHtml(composeAiResult);
    }
    setShowComposeAi(false);
    resetComposeAi();
    toast(appliedSubject ? 'Texto e assunto aplicados' : 'Texto aplicado', 'success');
  };

  // ===== AUTH LOADING =====
  if (authLoading) {
    return (
      <div className="app" style={{ display:'flex',alignItems:'center',justifyContent:'center',height:'100vh',background:'var(--bg-primary)' }}>
        <div style={{ textAlign:'center',color:'var(--text-tertiary)' }}>
          <Loader size={32} className="spin-icon" />
          <p style={{ marginTop:12,fontSize:14 }}>Carregando...</p>
        </div>
      </div>
    );
  }

  // ===== LANDING PAGE + LOGIN =====
  if (!authUser) {
    const plans = [
      {
        id: 'free',
        name: 'Free',
        price: 0,
        period: '',
        desc: 'Para experimentar a plataforma',
        color: '#6B7280',
        gradient: 'linear-gradient(135deg, #6B7280, #9CA3AF)',
        popular: false,
        features: [
          { text: '1 usuário', icon: <Users size={14} />, included: true },
          { text: '1 conta de email', icon: <Mail size={14} />, included: true },
          { text: '10 MB de armazenamento', icon: <Database size={14} />, included: true },
          { text: 'Temas personalizáveis', icon: <Monitor size={14} />, included: true },
          { text: 'Assistente IA', icon: <Sparkles size={14} />, included: false },
          { text: 'Tags', icon: <Tag size={14} />, included: false },
          { text: 'Dashboard', icon: <BarChart3 size={14} />, included: false },
          { text: 'Limpeza de emails', icon: <Trash2 size={14} />, included: false },
          { text: 'Regras anti-spam', icon: <ShieldAlert size={14} />, included: false },
        ],
      },
      {
        id: 'beginner',
        name: 'Beginner',
        price: 19.90,
        period: '/mês',
        desc: 'Para quem está começando',
        color: '#06B6D4',
        gradient: 'linear-gradient(135deg, #06B6D4, #67E8F9)',
        popular: false,
        features: [
          { text: '1 usuário', icon: <Users size={14} />, included: true },
          { text: '1 conta de email', icon: <Mail size={14} />, included: true },
          { text: '100 MB de armazenamento', icon: <Database size={14} />, included: true },
          { text: '10 créditos IA', icon: <Sparkles size={14} />, included: true },
          { text: 'Tags manuais', icon: <Tag size={14} />, included: true },
          { text: 'Temas personalizáveis', icon: <Monitor size={14} />, included: true },
          { text: 'Dashboard', icon: <BarChart3 size={14} />, included: false },
          { text: 'Limpeza de emails', icon: <Trash2 size={14} />, included: false },
        ],
      },
      {
        id: 'starter',
        name: 'Starter',
        price: 39.90,
        period: '/mês',
        desc: 'Para uso pessoal e freelancers',
        color: '#3B82F6',
        gradient: 'linear-gradient(135deg, #3B82F6, #60A5FA)',
        popular: false,
        features: [
          { text: '1 usuário · 3 contas', icon: <Layers size={14} />, included: true },
          { text: '150 MB de armazenamento', icon: <Database size={14} />, included: true },
          { text: '20 créditos IA', icon: <Sparkles size={14} />, included: true },
          { text: 'OpenAI + Gemini + Claude + Grok (10 modelos)', icon: <Cpu size={14} />, included: true },
          { text: 'Tags manuais', icon: <Tag size={14} />, included: true },
          { text: 'Temas personalizáveis', icon: <Monitor size={14} />, included: true },
          { text: 'Suporte por email', icon: <Headphones size={14} />, included: true },
          { text: 'Dashboard', icon: <BarChart3 size={14} />, included: false },
          { text: 'Limpeza de emails', icon: <Trash2 size={14} />, included: false },
        ],
      },
      {
        id: 'professional',
        name: 'Professional',
        price: 79.90,
        period: '/mês',
        desc: 'Para profissionais e pequenas equipes',
        color: '#8B5CF6',
        gradient: 'linear-gradient(135deg, #8B5CF6, #A78BFA)',
        popular: true,
        features: [
          { text: '2 usuários · 5 contas', icon: <Layers size={14} />, included: true },
          { text: '500 MB de armazenamento', icon: <Database size={14} />, included: true },
          { text: '80 créditos IA / usuário', icon: <Sparkles size={14} />, included: true },
          { text: 'OpenAI + Gemini + Claude + Grok (10 modelos)', icon: <Cpu size={14} />, included: true },
          { text: 'Dashboard completo', icon: <BarChart3 size={14} />, included: true },
          { text: 'Limpeza de emails', icon: <Trash2 size={14} />, included: true },
          { text: 'Tags manuais', icon: <Tag size={14} />, included: true },
          { text: 'Anti-spam avançado', icon: <ShieldAlert size={14} />, included: true },
          { text: 'Suporte prioritário', icon: <Headphones size={14} />, included: true },
        ],
      },
      {
        id: 'business',
        name: 'Business',
        price: 149.90,
        period: '/mês',
        desc: 'Para empresas e equipes maiores',
        color: '#F59E0B',
        gradient: 'linear-gradient(135deg, #F59E0B, #FBBF24)',
        popular: false,
        features: [
          { text: '5 usuários · 10 contas', icon: <Layers size={14} />, included: true },
          { text: '3 GB de armazenamento', icon: <Database size={14} />, included: true },
          { text: '150 créditos IA / usuário', icon: <Sparkles size={14} />, included: true },
          { text: 'OpenAI + Gemini + Claude + Grok (10 modelos)', icon: <Cpu size={14} />, included: true },
          { text: 'Dashboard completo', icon: <BarChart3 size={14} />, included: true },
          { text: 'Limpeza de emails', icon: <Trash2 size={14} />, included: true },
          { text: 'Tags automáticas', icon: <Zap size={14} />, included: true },
          { text: 'Anti-spam avançado', icon: <ShieldAlert size={14} />, included: true },
          { text: 'Suporte VIP dedicado', icon: <Crown size={14} />, included: true },
        ],
      },
      {
        id: 'ultra',
        name: 'Ultra',
        price: 500.00,
        period: '/mês',
        desc: 'Para operações de alta demanda',
        color: '#EF4444',
        gradient: 'linear-gradient(135deg, #EF4444, #F87171)',
        popular: false,
        features: [
          { text: '10 usuários · Contas ilimitadas', icon: <Layers size={14} />, included: true },
          { text: '15 GB de armazenamento', icon: <Database size={14} />, included: true },
          { text: '250 créditos IA / usuário', icon: <Sparkles size={14} />, included: true },
          { text: 'OpenAI + Gemini + Claude + Grok (10 modelos)', icon: <Cpu size={14} />, included: true },
          { text: 'Dashboard completo', icon: <BarChart3 size={14} />, included: true },
          { text: 'Limpeza de emails', icon: <Trash2 size={14} />, included: true },
          { text: 'Tags automáticas', icon: <Zap size={14} />, included: true },
          { text: 'Anti-spam avançado', icon: <ShieldAlert size={14} />, included: true },
          { text: 'Suporte VIP dedicado', icon: <Crown size={14} />, included: true },
        ],
      },
      {
        id: 'custom',
        name: 'A Consultar',
        price: null,
        period: '',
        desc: 'Sob medida para sua empresa',
        color: '#10B981',
        gradient: 'linear-gradient(135deg, #10B981, #34D399)',
        popular: false,
        features: [
          { text: 'Usuários ilimitados', icon: <Users size={14} />, included: true },
          { text: 'Contas de email ilimitadas', icon: <Layers size={14} />, included: true },
          { text: 'Armazenamento ilimitado', icon: <Database size={14} />, included: true },
          { text: 'Créditos IA ilimitados', icon: <Sparkles size={14} />, included: true },
          { text: 'Todos os modelos IA', icon: <Cpu size={14} />, included: true },
          { text: 'Todos os recursos incluídos', icon: <Check size={14} />, included: true },
          { text: 'Suporte dedicado + SLA', icon: <Crown size={14} />, included: true },
        ],
      },
    ];

    const aiModelsLanding = [
      { name: 'GPT-5 Mini', cost: '1 crédito', tag: 'Rápido', tagColor: '#10B981', desc: 'Velocidade + baixo custo, ideal para uso diário e tarefas simples.', provider: 'OpenAI' },
      { name: 'GPT-5.2', cost: '7 créditos', tag: 'Profissional', tagColor: '#3B82F6', desc: 'Padrão premium para trabalho sério, respostas equilibradas e precisas.', provider: 'OpenAI' },
      { name: 'GPT-5.2 Pro', cost: '70 créditos', tag: 'Máxima Precisão', tagColor: '#F59E0B', desc: 'Topo de linha, para quando precisa do melhor resultado possível.', provider: 'OpenAI' },
      { name: 'Gemini 2.5 Flash', cost: '1 crédito', tag: 'Econômico', tagColor: '#FBBC04', desc: 'Econômico e rápido do Google, ótimo para uso geral.', provider: 'Google' },
      { name: 'Gemini 3 Flash', cost: '2-4 créditos', tag: 'Raciocínio', tagColor: '#34A853', desc: 'Modelo Google com níveis de raciocínio Low (rápido) e High (profundo).', provider: 'Google' },
      { name: 'Claude Haiku 4.5', cost: '5 créditos', tag: 'Ágil', tagColor: '#06B6D4', desc: 'Mais rápido e ágil da Anthropic, ideal para tarefas leves.', provider: 'Anthropic' },
      { name: 'Claude Sonnet 4.6', cost: '30 créditos', tag: 'Inteligente', tagColor: '#7C3AED', desc: 'Inteligente e rápido, ótimo equilíbrio da Anthropic.', provider: 'Anthropic' },
      { name: 'Claude Opus 4.6', cost: '70 créditos', tag: 'Potente', tagColor: '#D97706', desc: 'O mais potente e poderoso da Anthropic, precisão máxima.', provider: 'Anthropic' },
      { name: 'Grok 4.1', cost: '2 créditos', tag: 'Raciocínio', tagColor: '#EF4444', desc: 'Modelo xAI com raciocínio rápido e eficiente.', provider: 'xAI' },
    ];

    const features = [
      { icon: <Lock size={24} />, title: 'Conexão Segura', desc: 'Criptografia TLS/SSL em todas as conexões IMAP e SMTP.' },
      { icon: <Zap size={24} />, title: 'Sincronização Rápida', desc: 'Sync inteligente que busca apenas emails novos, sem esperar.' },
      { icon: <Sparkles size={24} />, title: 'Múltiplas IAs', desc: 'OpenAI, Gemini, Claude e Grok — 4 provedores, 10 modelos.' },
      { icon: <Tag size={24} />, title: 'Regras Automáticas', desc: 'Tags aplicadas automaticamente por domínio ou assunto.' },
      { icon: <Shield size={24} />, title: 'Anti-Spam', desc: 'Detecção inteligente de spam com whitelist personalizável.' },
      { icon: <Monitor size={24} />, title: 'Multi-Tema', desc: 'Claro, escuro, midnight ou personalize cores por conta.' },
    ];

    const whatsappLink = 'https://wa.me/5521968819305?text=Olá! Gostaria de saber mais sobre os planos do Coremail.';

    // ===== LOGIN FORM VIEW =====
    if (showLoginForm) {
      return (
        <div style={{ minHeight:'100vh',background:'var(--bg-primary)',display:'flex',alignItems:'center',justifyContent:'center',padding:20 }}>
          <div style={{ width:'100%',maxWidth:420 }}>
            {/* Back to plans */}
            <button onClick={() => setShowLoginForm(false)}
              style={{
                display:'flex',alignItems:'center',gap:6,fontSize:13,fontWeight:500,color:'var(--text-tertiary)',
                background:'none',border:'none',cursor:'pointer',fontFamily:'var(--font-primary)',
                marginBottom:24,padding:0,transition:'color 0.2s',
              }}
              onMouseEnter={e => e.currentTarget.style.color='var(--accent)'}
              onMouseLeave={e => e.currentTarget.style.color='var(--text-tertiary)'}
            >
              <ArrowLeft size={16} /> Voltar para planos
            </button>

            <div style={{ textAlign:'center',marginBottom:32 }}>
              <img src="/favicon.ico" alt="Coremail" style={{ width:56,height:56,borderRadius:16,marginBottom:12 }} />
              <h1 style={{ fontSize:24,fontWeight:700,color:'var(--text-primary)',margin:'0 0 6px' }}>Coremail</h1>
              <p style={{ fontSize:13,color:'var(--text-tertiary)',margin:0,lineHeight:1.5,maxWidth:300,marginLeft:'auto',marginRight:'auto' }}>
                Gerencie todas as suas contas de email em um só lugar.
              </p>
            </div>

            <div style={{ background:'var(--bg-secondary)',borderRadius:'var(--radius-lg)',border:'1px solid var(--border-color)',padding:28,boxShadow:'var(--shadow-lg)' }}>
              <h2 style={{ fontSize:16,fontWeight:600,marginBottom:20,textAlign:'center' }}>Entrar na sua conta</h2>

              <form onSubmit={handleLogin}>
                <div style={{ marginBottom:14 }}>
                  <label style={{ display:'block',fontSize:12,fontWeight:600,color:'var(--text-secondary)',marginBottom:4 }}>Email</label>
                  <input type="email" className="compose-input"
                    style={{ width:'100%',padding:'10px 12px',fontSize:14 }}
                    placeholder="seu@email.com"
                    value={loginForm.email}
                    onChange={e => setLoginForm(p => ({...p, email: e.target.value}))}
                    autoFocus required />
                </div>

                <div style={{ marginBottom:18 }}>
                  <label style={{ display:'block',fontSize:12,fontWeight:600,color:'var(--text-secondary)',marginBottom:4 }}>Senha</label>
                  <div style={{ position:'relative' }}>
                    <input type={showLoginPassword ? 'text' : 'password'} className="compose-input"
                      style={{ width:'100%',padding:'10px 40px 10px 12px',fontSize:14 }}
                      placeholder="••••••••"
                      value={loginForm.password}
                      onChange={e => setLoginForm(p => ({...p, password: e.target.value}))}
                      required />
                    <button type="button" className="icon-btn"
                      style={{ position:'absolute',right:4,top:'50%',transform:'translateY(-50%)',width:32,height:32 }}
                      onClick={() => setShowLoginPassword(!showLoginPassword)}>
                      {showLoginPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>

                {loginError && (
                  <div style={{ background:'var(--danger-light)',color:'var(--danger)',padding:'8px 12px',borderRadius:8,fontSize:13,marginBottom:14,display:'flex',alignItems:'center',gap:6 }}>
                    <AlertCircle size={14} /> {loginError}
                  </div>
                )}

                <button type="submit"
                  disabled={loginLoading}
                  style={{
                    width:'100%',padding:'11px',fontSize:14,fontWeight:600,
                    display:'flex',alignItems:'center',justifyContent:'center',gap:8,
                    background:'linear-gradient(135deg, #3B82F6, #2563EB)',color:'#fff',
                    border:'none',borderRadius:'var(--radius-sm)',cursor:'pointer',
                    fontFamily:'var(--font-primary)',
                    boxShadow:'0 4px 12px rgba(37,99,235,0.3)',
                    transition:'all 0.2s',opacity:loginLoading?0.7:1,
                  }}>
                  {loginLoading ? <Loader size={16} className="spin-icon" /> : <ShieldCheck size={16} />}
                  {loginLoading ? 'Entrando...' : 'Entrar'}
                </button>
              </form>
            </div>

            <div style={{ display:'flex',alignItems:'center',justifyContent:'center',gap:12,marginTop:20 }}>
              <span style={{ fontSize:11,color:'var(--text-tertiary)' }}>Desenvolvido por Ramcore Tecnologia</span>
              <button className="icon-btn" onClick={() => setTheme(t => t === 'light' ? 'dark' : t === 'dark' ? 'midnight' : 'light')}
                style={{ width:32,height:32 }} title="Alternar tema">
                {theme === 'light' ? <Sun size={14} /> : theme === 'dark' ? <Moon size={14} /> : <EyeOff size={14} />}
              </button>
            </div>
          </div>
        </div>
      );
    }

    // ===== LANDING PAGE VIEW =====
    return (
      <div style={{ minHeight:'100vh',background:'var(--bg-primary)',overflowY:'auto',overflowX:'hidden' }}>

        {/* ===== NAVBAR ===== */}
        <nav style={{
          position:'sticky',top:0,zIndex:50,
          background:'rgba(var(--bg-primary-rgb, 248,249,252),0.85)',
          backdropFilter:'blur(20px)',WebkitBackdropFilter:'blur(20px)',
          borderBottom:'1px solid var(--border-color)',
        }}>
          <div style={{
            maxWidth:1200,margin:'0 auto',padding:'0 24px',height:64,
            display:'flex',alignItems:'center',justifyContent:'space-between',
          }}>
            <div style={{ display:'flex',alignItems:'center',gap:10 }}>
              <img src="/favicon.ico" alt="Coremail" style={{ width:36,height:36,borderRadius:10 }} />
              <span style={{ fontSize:18,fontWeight:700,color:'var(--text-primary)',letterSpacing:'-0.3px' }}>Coremail</span>
            </div>
            <div style={{ display:'flex',alignItems:'center',gap:10 }}>
              <button className="icon-btn" onClick={() => setTheme(t => t === 'light' ? 'dark' : t === 'dark' ? 'midnight' : 'light')}
                style={{ width:36,height:36 }} title="Alternar tema">
                {theme === 'light' ? <Sun size={16} /> : theme === 'dark' ? <Moon size={16} /> : <EyeOff size={16} />}
              </button>
              <button onClick={() => setShowLoginForm(true)}
                style={{
                  padding:'9px 20px',fontSize:13,fontWeight:600,
                  background:'linear-gradient(135deg, #3B82F6, #2563EB)',color:'#fff',
                  border:'none',borderRadius:8,cursor:'pointer',
                  fontFamily:'var(--font-primary)',
                  boxShadow:'0 4px 12px rgba(37,99,235,0.3)',
                  transition:'all 0.2s',display:'flex',alignItems:'center',gap:6,
                }}
                onMouseEnter={e => { e.currentTarget.style.transform='translateY(-1px)'; e.currentTarget.style.boxShadow='0 6px 20px rgba(37,99,235,0.4)'; }}
                onMouseLeave={e => { e.currentTarget.style.transform='none'; e.currentTarget.style.boxShadow='0 4px 12px rgba(37,99,235,0.3)'; }}
              >
                <ShieldCheck size={15} /> Já sou cliente
              </button>
            </div>
          </div>
        </nav>

        {/* ===== HERO ===== */}
        <section style={{ padding:'80px 24px 60px',textAlign:'center',position:'relative',overflow:'hidden' }}>
          {/* Decorative gradient blobs */}
          <div style={{ position:'absolute',top:'-30%',left:'-10%',width:500,height:500,borderRadius:'50%',background:'radial-gradient(circle, rgba(59,130,246,0.08) 0%, transparent 70%)',pointerEvents:'none' }} />
          <div style={{ position:'absolute',bottom:'-20%',right:'-10%',width:400,height:400,borderRadius:'50%',background:'radial-gradient(circle, rgba(139,92,246,0.08) 0%, transparent 70%)',pointerEvents:'none' }} />

          <div style={{ maxWidth:720,margin:'0 auto',position:'relative' }}>
            <div style={{
              display:'inline-flex',alignItems:'center',gap:8,
              padding:'6px 16px',borderRadius:20,
              background:'var(--accent-light)',color:'var(--accent)',
              fontSize:12,fontWeight:600,marginBottom:20,
            }}>
              <Rocket size={14} /> Plataforma profissional de email
            </div>

            <h1 style={{
              fontSize:'clamp(32px, 5vw, 52px)',fontWeight:800,
              color:'var(--text-primary)',lineHeight:1.15,
              letterSpacing:'-1px',margin:'0 0 18px',
            }}>
              Todos os seus emails.{' '}
              <span style={{ background:'linear-gradient(135deg, #3B82F6, #8B5CF6)',WebkitBackgroundClip:'text',WebkitTextFillColor:'transparent' }}>
                Um só lugar.
              </span>
            </h1>

            <p style={{
              fontSize:'clamp(15px, 2vw, 18px)',color:'var(--text-secondary)',
              lineHeight:1.65,maxWidth:560,margin:'0 auto 32px',
            }}>
              Gerencie múltiplas contas de email, organize com tags inteligentes,
              detecte spam automaticamente e use IA para economizar tempo.
            </p>

            <div style={{ display:'flex',alignItems:'center',justifyContent:'center',gap:14,flexWrap:'wrap' }}>
              <a href="#planos"
                style={{
                  padding:'14px 32px',fontSize:15,fontWeight:700,
                  background:'linear-gradient(135deg, #3B82F6, #2563EB)',color:'#fff',
                  border:'none',borderRadius:10,cursor:'pointer',textDecoration:'none',
                  fontFamily:'var(--font-primary)',
                  boxShadow:'0 8px 32px rgba(37,99,235,0.3)',
                  transition:'all 0.25s',display:'inline-flex',alignItems:'center',gap:8,
                }}
                onMouseEnter={e => { e.currentTarget.style.transform='translateY(-2px)'; e.currentTarget.style.boxShadow='0 12px 40px rgba(37,99,235,0.4)'; }}
                onMouseLeave={e => { e.currentTarget.style.transform='none'; e.currentTarget.style.boxShadow='0 8px 32px rgba(37,99,235,0.3)'; }}
              >
                Ver planos <ArrowRight size={16} />
              </a>
              <button onClick={() => setShowLoginForm(true)}
                style={{
                  padding:'14px 28px',fontSize:15,fontWeight:600,
                  background:'var(--bg-secondary)',color:'var(--text-primary)',
                  border:'1px solid var(--border-color)',borderRadius:10,cursor:'pointer',
                  fontFamily:'var(--font-primary)',
                  boxShadow:'var(--shadow-sm)',
                  transition:'all 0.25s',display:'inline-flex',alignItems:'center',gap:8,
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor='var(--accent)'; e.currentTarget.style.transform='translateY(-2px)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor='var(--border-color)'; e.currentTarget.style.transform='none'; }}
              >
                <ShieldCheck size={16} /> Entrar
              </button>
            </div>
          </div>

          {/* Stats */}
          <div style={{
            display:'flex',justifyContent:'center',gap:'clamp(24px, 5vw, 60px)',
            marginTop:56,flexWrap:'wrap',
          }}>
            {[
              { value: '99.9%', label: 'Uptime' },
              { value: 'TLS/SSL', label: 'Criptografia' },
              { value: 'IMAP/SMTP', label: 'Protocolos' },
              { value: '< 2s', label: 'Sync Médio' },
            ].map((s, i) => (
              <div key={i} style={{ textAlign:'center' }}>
                <div style={{ fontSize:24,fontWeight:800,color:'var(--text-primary)',letterSpacing:'-0.5px' }}>{s.value}</div>
                <div style={{ fontSize:12,color:'var(--text-tertiary)',marginTop:4,fontWeight:500 }}>{s.label}</div>
              </div>
            ))}
          </div>
        </section>

        {/* ===== FEATURES GRID ===== */}
        <section style={{ padding:'40px 24px 60px' }}>
          <div style={{ maxWidth:1000,margin:'0 auto' }}>
            <div style={{ textAlign:'center',marginBottom:40 }}>
              <h2 style={{ fontSize:28,fontWeight:800,color:'var(--text-primary)',letterSpacing:'-0.5px',margin:'0 0 10px' }}>
                Por que escolher o Coremail?
              </h2>
              <p style={{ fontSize:15,color:'var(--text-secondary)',margin:0 }}>
                Tudo que você precisa para dominar seus emails, em uma única plataforma.
              </p>
            </div>
            <div style={{
              display:'grid',
              gridTemplateColumns:'repeat(auto-fit, minmax(280px, 1fr))',
              gap:16,
            }}>
              {features.map((f, i) => (
                <div key={i} style={{
                  padding:24,borderRadius:14,
                  background:'var(--bg-secondary)',border:'1px solid var(--border-color)',
                  transition:'all 0.25s',cursor:'default',
                }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor='var(--accent)'; e.currentTarget.style.transform='translateY(-3px)'; e.currentTarget.style.boxShadow='var(--shadow-md)'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor='var(--border-color)'; e.currentTarget.style.transform='none'; e.currentTarget.style.boxShadow='none'; }}
                >
                  <div style={{
                    width:44,height:44,borderRadius:12,marginBottom:14,
                    background:'var(--accent-light)',color:'var(--accent)',
                    display:'flex',alignItems:'center',justifyContent:'center',
                  }}>{f.icon}</div>
                  <h3 style={{ fontSize:15,fontWeight:700,color:'var(--text-primary)',margin:'0 0 6px' }}>{f.title}</h3>
                  <p style={{ fontSize:13,color:'var(--text-secondary)',lineHeight:1.55,margin:0 }}>{f.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ===== AI MODELS SECTION ===== */}
        <section style={{ padding:'60px 24px',background:'var(--bg-tertiary)' }}>
          <div style={{ maxWidth:900,margin:'0 auto' }}>
            <div style={{ textAlign:'center',marginBottom:40 }}>
              <div style={{
                display:'inline-flex',alignItems:'center',gap:6,
                padding:'5px 14px',borderRadius:20,
                background:'rgba(139,92,246,0.1)',color:'#8B5CF6',
                fontSize:12,fontWeight:600,marginBottom:14,
              }}>
                <Sparkles size={13} /> Inteligência Artificial
              </div>
              <h2 style={{ fontSize:28,fontWeight:800,color:'var(--text-primary)',letterSpacing:'-0.5px',margin:'0 0 10px' }}>
                OpenAI + Gemini + Claude + Grok à sua escolha
              </h2>
              <p style={{ fontSize:15,color:'var(--text-secondary)',margin:0,maxWidth:600,marginLeft:'auto',marginRight:'auto' }}>
                4 provedores de IA, 10 modelos de última geração. Escolha o ideal para cada tarefa e pague por créditos.
              </p>
            </div>

            <div style={{ display:'grid',gridTemplateColumns:'repeat(auto-fit, minmax(240px, 1fr))',gap:14 }}>
              {aiModelsLanding.map((m, i) => (
                <div key={i} style={{
                  padding:24,borderRadius:16,
                  background:'var(--bg-secondary)',border:'1px solid var(--border-color)',
                  transition:'all 0.25s',position:'relative',overflow:'hidden',
                }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = m.tagColor; e.currentTarget.style.transform='translateY(-4px)'; e.currentTarget.style.boxShadow=`0 12px 32px ${m.tagColor}15`; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor='var(--border-color)'; e.currentTarget.style.transform='none'; e.currentTarget.style.boxShadow='none'; }}
                >
                  {/* Glow accent */}
                  <div style={{
                    position:'absolute',top:-30,right:-30,width:80,height:80,borderRadius:'50%',
                    background:m.tagColor,opacity:0.06,filter:'blur(20px)',
                  }} />

                  <div style={{ display:'flex',alignItems:'center',gap:6,marginBottom:12 }}>
                    <div style={{
                      display:'inline-flex',alignItems:'center',gap:4,
                      padding:'3px 8px',borderRadius:6,
                      background:`${m.tagColor}12`,color:m.tagColor,
                      fontSize:10,fontWeight:700,
                    }}>
                      <Sparkles size={10} /> {m.tag}
                    </div>
                    <span style={{
                      fontSize:9,fontWeight:700,padding:'2px 6px',borderRadius:4,
                      background: m.provider === 'Google' ? 'rgba(66,133,244,0.1)' : 'rgba(16,185,129,0.1)',
                      color: m.provider === 'Google' ? '#4285F4' : '#10B981',
                      textTransform:'uppercase',letterSpacing:0.3,
                    }}>{m.provider}</span>
                  </div>

                  <h3 style={{ fontSize:18,fontWeight:800,color:'var(--text-primary)',margin:'0 0 6px' }}>{m.name}</h3>
                  <p style={{ fontSize:12,color:'var(--text-secondary)',lineHeight:1.5,margin:'0 0 14px',minHeight:36 }}>{m.desc}</p>

                  <div style={{
                    display:'flex',alignItems:'center',gap:6,
                    padding:'8px 12px',borderRadius:8,
                    background:`${m.tagColor}08`,border:`1px solid ${m.tagColor}20`,
                  }}>
                    <Cpu size={14} color={m.tagColor} />
                    <span style={{ fontSize:13,fontWeight:700,color:m.tagColor }}>{m.cost}</span>
                    <span style={{ fontSize:11,color:'var(--text-tertiary)' }}>por uso</span>
                  </div>
                </div>
              ))}
            </div>

            <div style={{ textAlign:'center',marginTop:24 }}>
              <p style={{ fontSize:12,color:'var(--text-tertiary)',margin:0 }}>
                💡 Resumir emails, gerar respostas, criar emails do zero — cada ação consome créditos do modelo escolhido.
              </p>
            </div>
          </div>
        </section>

        {/* ===== PRICING ===== */}
        <section id="planos" style={{ padding:'60px 24px 80px',background:'var(--bg-tertiary)' }}>
          <div style={{ maxWidth:1400,margin:'0 auto' }}>
            <div style={{ textAlign:'center',marginBottom:48 }}>
              <div style={{
                display:'inline-flex',alignItems:'center',gap:6,
                padding:'5px 14px',borderRadius:20,
                background:'var(--accent-light)',color:'var(--accent)',
                fontSize:12,fontWeight:600,marginBottom:14,
              }}>
                <Crown size={13} /> Planos e Preços
              </div>
              <h2 style={{ fontSize:32,fontWeight:800,color:'var(--text-primary)',letterSpacing:'-0.5px',margin:'0 0 10px' }}>
                Escolha o plano ideal para você
              </h2>
              <p style={{ fontSize:15,color:'var(--text-secondary)',margin:0,maxWidth:500,marginLeft:'auto',marginRight:'auto' }}>
                Sem fidelidade, cancele quando quiser. Todos os planos incluem atualizações gratuitas.
              </p>
            </div>

            <div style={{
              display:'grid',
              gridTemplateColumns:'repeat(auto-fit, minmax(260px, 1fr))',
              gap:16,alignItems:'start',
            }}>
              {plans.map(plan => (
                <div key={plan.id} style={{
                  borderRadius:18,overflow:'hidden',
                  background:'var(--bg-secondary)',
                  border: plan.popular ? `2px solid ${plan.color}` : '1px solid var(--border-color)',
                  boxShadow: plan.popular ? `0 8px 40px ${plan.color}20` : 'var(--shadow-sm)',
                  transition:'all 0.3s',position:'relative',
                  transform: plan.popular ? 'scale(1.03)' : 'none',
                }}
                  onMouseEnter={e => { if (!plan.popular) { e.currentTarget.style.transform='translateY(-4px)'; e.currentTarget.style.boxShadow='var(--shadow-lg)'; } }}
                  onMouseLeave={e => { if (!plan.popular) { e.currentTarget.style.transform='none'; e.currentTarget.style.boxShadow='var(--shadow-sm)'; } }}
                >
                  {/* Popular badge */}
                  {plan.popular && (
                    <div style={{
                      background: plan.gradient,color:'#fff',
                      textAlign:'center',padding:'6px 0',fontSize:11,fontWeight:700,
                      textTransform:'uppercase',letterSpacing:1,
                    }}>
                      ⭐ Mais popular
                    </div>
                  )}

                  <div style={{ padding:'24px 20px 20px' }}>
                    {/* Plan header */}
                    <div style={{ marginBottom:16 }}>
                      <div style={{
                        width:40,height:40,borderRadius:11,marginBottom:10,
                        background: plan.gradient,
                        display:'flex',alignItems:'center',justifyContent:'center',
                        boxShadow: `0 4px 12px ${plan.color}30`,
                      }}>
                        {plan.id === 'free' && <Gift size={18} color="#fff" />}
                        {plan.id === 'beginner' && <Mail size={18} color="#fff" />}
                        {plan.id === 'starter' && <Zap size={18} color="#fff" />}
                        {plan.id === 'professional' && <Rocket size={18} color="#fff" />}
                        {plan.id === 'business' && <Crown size={18} color="#fff" />}
                        {plan.id === 'ultra' && <Cpu size={18} color="#fff" />}
                        {plan.id === 'custom' && <Server size={18} color="#fff" />}
                      </div>
                      <h3 style={{ fontSize:18,fontWeight:700,color:'var(--text-primary)',margin:'0 0 3px' }}>{plan.name}</h3>
                      <p style={{ fontSize:12,color:'var(--text-secondary)',margin:0 }}>{plan.desc}</p>
                    </div>

                    {/* Price */}
                    <div style={{ marginBottom:20,paddingBottom:16,borderBottom:'1px solid var(--border-color)' }}>
                      {plan.price === 0 ? (
                        <div>
                          <div style={{ fontSize:34,fontWeight:800,color:plan.color,lineHeight:1 }}>
                            Grátis
                          </div>
                          <div style={{ fontSize:10,color:'var(--text-tertiary)',marginTop:5 }}>
                            Para sempre, sem cartão de crédito
                          </div>
                        </div>
                      ) : plan.price !== null ? (
                        <>
                          <div style={{ display:'flex',alignItems:'baseline',gap:3 }}>
                            <span style={{ fontSize:11,color:'var(--text-tertiary)',fontWeight:500 }}>R$</span>
                            <span style={{ fontSize:34,fontWeight:800,color:'var(--text-primary)',letterSpacing:'-1px',lineHeight:1 }}>
                              {plan.price.toFixed(2).replace('.', ',')}
                            </span>
                            <span style={{ fontSize:13,color:'var(--text-tertiary)',fontWeight:500 }}>{plan.period}</span>
                          </div>
                          <div style={{ fontSize:10,color:'var(--text-tertiary)',marginTop:5 }}>
                            Equivale a R$ {(plan.price / 30).toFixed(2).replace('.', ',')} por dia
                          </div>
                        </>
                      ) : (
                        <div>
                          <div style={{ fontSize:28,fontWeight:800,color:plan.color,lineHeight:1 }}>
                            Sob consulta
                          </div>
                          <div style={{ fontSize:10,color:'var(--text-tertiary)',marginTop:5 }}>
                            Personalizado para sua operação
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Features */}
                    <div style={{ display:'flex',flexDirection:'column',gap:8,marginBottom:20 }}>
                      {plan.features.map((f, i) => (
                        <div key={i} style={{ display:'flex',alignItems:'center',gap:8,opacity:f.included?1:0.4 }}>
                          <div style={{
                            width:20,height:20,borderRadius:5,flexShrink:0,
                            background: f.included ? `${plan.color}15` : 'var(--bg-tertiary)',
                            color: f.included ? plan.color : 'var(--text-tertiary)',
                            display:'flex',alignItems:'center',justifyContent:'center',
                          }}>
                            {f.included ? <Check size={11} strokeWidth={3} /> : <X size={11} />}
                          </div>
                          <div style={{ display:'flex',alignItems:'center',gap:5 }}>
                            <span style={{ color: f.included ? plan.color : 'var(--text-tertiary)' }}>{f.icon}</span>
                            <span style={{ fontSize:12,fontWeight:f.included?500:400,color:f.included?'var(--text-primary)':'var(--text-tertiary)' }}>
                              {f.text}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* CTA */}
                    <a href={whatsappLink} target="_blank" rel="noopener noreferrer"
                      style={{
                        display:'flex',alignItems:'center',justifyContent:'center',gap:8,
                        width:'100%',padding:'12px 0',borderRadius:10,
                        fontSize:13,fontWeight:700,textDecoration:'none',
                        fontFamily:'var(--font-primary)',
                        cursor:'pointer',transition:'all 0.25s',
                        background: plan.popular ? plan.gradient : 'transparent',
                        color: plan.popular ? '#fff' : plan.color,
                        border: plan.popular ? 'none' : `2px solid ${plan.color}`,
                        boxShadow: plan.popular ? `0 4px 16px ${plan.color}30` : 'none',
                      }}
                      onMouseEnter={e => {
                        if (!plan.popular) {
                          e.currentTarget.style.background = plan.color;
                          e.currentTarget.style.color = '#fff';
                        }
                        e.currentTarget.style.transform='translateY(-1px)';
                      }}
                      onMouseLeave={e => {
                        if (!plan.popular) {
                          e.currentTarget.style.background = 'transparent';
                          e.currentTarget.style.color = plan.color;
                        }
                        e.currentTarget.style.transform='none';
                      }}
                    >
                      <MessageSquare size={15} /> Consultar
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ===== COMPARISON TABLE ===== */}
        <section style={{ padding:'60px 24px',overflow:'auto' }}>
          <div style={{ maxWidth:1400,margin:'0 auto' }}>
            <h2 style={{ fontSize:24,fontWeight:800,color:'var(--text-primary)',textAlign:'center',margin:'0 0 32px',letterSpacing:'-0.3px' }}>
              Comparativo dos Planos
            </h2>
            <div style={{ borderRadius:14,overflow:'hidden',border:'1px solid var(--border-color)',background:'var(--bg-secondary)',overflowX:'auto' }}>
              <table style={{ width:'100%',borderCollapse:'collapse',fontSize:11.5,minWidth:900 }}>
                <thead>
                  <tr style={{ background:'var(--bg-tertiary)' }}>
                    <th style={{ textAlign:'left',padding:'10px 14px',fontWeight:600,color:'var(--text-secondary)',borderBottom:'1px solid var(--border-color)',minWidth:120 }}>Recurso</th>
                    {plans.map(p => (
                      <th key={p.id} style={{
                        textAlign:'center',padding:'10px 8px',fontWeight:700,
                        borderBottom:'1px solid var(--border-color)',
                        color: p.color,fontSize:11,
                      }}>{p.name}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {[
                    { label: 'Preço', values: ['Grátis', 'R$ 19,90', 'R$ 39,90', 'R$ 79,90', 'R$ 149,90', 'R$ 500', 'Sob consulta'] },
                    { label: 'Usuários', values: ['1', '1', '1', '2', '5', '10', 'Ilimitados'] },
                    { label: 'Contas de email', values: ['1', '1', '3', '5', '10', 'Ilimitadas', 'Ilimitadas'] },
                    { label: 'Armazenamento', values: ['10 MB', '100 MB', '150 MB', '500 MB', '3 GB', '15 GB', 'Ilimitado'] },
                    { label: 'Créditos IA', values: [false, '10', '20', '80/usr', '150/usr', '250/usr', 'Ilimitados'] },
                    { label: 'Modelos IA', values: [false, true, true, true, true, true, true] },
                    { label: 'Dashboard', values: [false, false, false, true, true, true, true] },
                    { label: 'Limpeza de emails', values: [false, false, false, true, true, true, true] },
                    { label: 'Tags manuais', values: [false, true, true, true, true, true, true] },
                    { label: 'Tags automáticas', values: [false, false, false, false, true, true, true] },
                    { label: 'Anti-spam', values: [false, 'Básico', 'Básico', 'Avançado', 'Avançado', 'Avançado', 'Avançado'] },
                    { label: 'Suporte', values: ['—', 'Email', 'Email', 'Prioritário', 'VIP', 'VIP', 'SLA'] },
                  ].map((row, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid var(--border-light)' }}>
                      <td style={{ padding:'8px 14px',fontWeight:500,color:'var(--text-primary)' }}>{row.label}</td>
                      {row.values.map((v, j) => (
                        <td key={j} style={{ padding:'8px 8px',textAlign:'center' }}>
                          {v === true ? <Check size={14} color={plans[j]?.color} strokeWidth={3} /> :
                           v === false ? <X size={14} color="var(--text-tertiary)" style={{ opacity:0.3 }} /> :
                           <span style={{ fontWeight:500,color:'var(--text-secondary)',fontSize:11 }}>{v}</span>}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* ===== FOOTER ===== */}
        <footer style={{
          padding:'40px 24px',background:'var(--bg-sidebar)',color:'rgba(255,255,255,0.5)',
        }}>
          <div style={{ maxWidth:1000,margin:'0 auto',display:'flex',flexWrap:'wrap',justifyContent:'space-between',alignItems:'center',gap:20 }}>
            <div>
              <div style={{ display:'flex',alignItems:'center',gap:10,marginBottom:10 }}>
                <img src="/favicon.ico" alt="Coremail" style={{ width:32,height:32,borderRadius:8 }} />
                <span style={{ fontSize:16,fontWeight:700,color:'#fff' }}>Coremail</span>
              </div>
              <p style={{ fontSize:12,margin:0,lineHeight:1.6 }}>
                Desenvolvido por Ramcore Tecnologia<br />
                Plataforma profissional de email corporativo.
              </p>
            </div>
            <div style={{ display:'flex',gap:20,alignItems:'center' }}>
              <a href={whatsappLink} target="_blank" rel="noopener noreferrer"
                style={{ fontSize:12,color:'rgba(255,255,255,0.6)',textDecoration:'none',transition:'color 0.2s' }}
                onMouseEnter={e => e.currentTarget.style.color='#fff'}
                onMouseLeave={e => e.currentTarget.style.color='rgba(255,255,255,0.6)'}
              >Contato</a>
              <button onClick={() => setShowLoginForm(true)}
                style={{
                  padding:'8px 20px',fontSize:12,fontWeight:600,
                  background:'rgba(255,255,255,0.1)',color:'#fff',
                  border:'1px solid rgba(255,255,255,0.15)',borderRadius:8,cursor:'pointer',
                  fontFamily:'var(--font-primary)',transition:'all 0.2s',
                }}
                onMouseEnter={e => { e.currentTarget.style.background='rgba(255,255,255,0.15)'; e.currentTarget.style.borderColor='rgba(255,255,255,0.3)'; }}
                onMouseLeave={e => { e.currentTarget.style.background='rgba(255,255,255,0.1)'; e.currentTarget.style.borderColor='rgba(255,255,255,0.15)'; }}
              >
                Área do Cliente
              </button>
            </div>
          </div>
          <div style={{ maxWidth:1000,margin:'20px auto 0',paddingTop:16,borderTop:'1px solid rgba(255,255,255,0.06)',textAlign:'center',fontSize:11 }}>
            © {new Date().getFullYear()} Ramcore Tecnologia. Todos os direitos reservados.
          </div>
        </footer>
      </div>
    );
  }

  // ===== Compute account limit for current user =====
  const userMaxAccounts = authUser?.max_accounts || 0; // 0 = unlimited
  const accountLimitReached = userMaxAccounts > 0 && accounts.length >= userMaxAccounts;
  // Só mostra o onboarding depois que as contas carregaram — antes, todo login
  // piscava a tela de boas-vindas por uma fração de segundo.
  const showWelcome = authUser && !accountsLoading && accounts.length === 0;

  // ===== MAIN APP (authenticated) =====
  return (
    <div className="app" style={showWelcome ? { display:'flex',alignItems:'center',justifyContent:'center' } : undefined}>
      {showWelcome ? (
        /* ===== WELCOME SCREEN ===== */
        <div style={{ width:'100%',maxWidth:560,textAlign:'center',padding:20 }}>
          <div style={{ marginBottom:32 }}>
            <div style={{ width:72,height:72,borderRadius:20,background:'linear-gradient(135deg, #3B82F6, #8B5CF6)',display:'inline-flex',alignItems:'center',justifyContent:'center',marginBottom:16,boxShadow:'0 8px 32px rgba(59,130,246,0.3)' }}>
              <Mail size={36} color="#fff" />
            </div>
            <h1 style={{ fontSize:28,fontWeight:800,color:'var(--text-primary)',margin:'0 0 6px',letterSpacing:'-0.5px' }}>Bem-vindo ao Coremail!</h1>
            <p style={{ fontSize:14,color:'var(--text-tertiary)',margin:0 }}>Olá, <strong style={{ color:'var(--text-secondary)' }}>{authUser.name}</strong></p>
          </div>

          <div style={{ background:'var(--bg-secondary)',borderRadius:16,border:'1px solid var(--border-color)',padding:32,boxShadow:'var(--shadow-lg)',textAlign:'left',marginBottom:24 }}>
            <h2 style={{ fontSize:18,fontWeight:700,marginBottom:16,textAlign:'center' }}>Seu email profissional em um só lugar</h2>
            
            <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:14,marginBottom:24 }}>
              {[
                { icon: <Inbox size={20} />, title: 'Gerenciar Emails', desc: 'Envie, receba e organize seus emails com interface moderna' },
                { icon: <ShieldCheck size={20} />, title: 'Segurança', desc: 'Conexão criptografada via IMAP/SMTP' },
                { icon: <Tag size={20} />, title: 'Tags e Filtros', desc: 'Organize com tags coloridas e filtros inteligentes' },
                { icon: <Sparkles size={20} />, title: 'Assistente IA', desc: 'Resumos e análise de emails com inteligência artificial' },
              ].map((item, i) => (
                <div key={i} style={{ display:'flex',gap:10,padding:12,borderRadius:10,background:'var(--bg-tertiary)',border:'1px solid var(--border-light)' }}>
                  <div style={{ color:'var(--accent)',flexShrink:0,marginTop:2 }}>{item.icon}</div>
                  <div>
                    <div style={{ fontSize:13,fontWeight:600,marginBottom:2 }}>{item.title}</div>
                    <div style={{ fontSize:11.5,color:'var(--text-tertiary)',lineHeight:1.4 }}>{item.desc}</div>
                  </div>
                </div>
              ))}
            </div>

            <div style={{ background:'var(--accent-light)',border:'1px solid var(--accent)',borderRadius:10,padding:'14px 16px',marginBottom:20,display:'flex',alignItems:'center',gap:10 }}>
              <Info size={18} color="var(--accent)" style={{ flexShrink:0 }} />
              <p style={{ fontSize:13,color:'var(--accent)',margin:0,lineHeight:1.5 }}>
                Para começar, configure sua primeira conta de email. Você precisará dos dados do servidor IMAP e SMTP do seu provedor.
              </p>
            </div>

            <button className="btn-primary" onClick={() => { setShowAccountModal(true); openAddAccount(); }}
              style={{ width:'100%',padding:'12px',fontSize:15,fontWeight:600,display:'flex',alignItems:'center',justifyContent:'center',gap:8,borderRadius:10,background:'linear-gradient(135deg, #3B82F6, #2563EB)',boxShadow:'0 4px 16px rgba(37,99,235,0.3)' }}>
              <Plus size={18} /> Configurar minha conta de email
            </button>

            {userMaxAccounts > 0 && (
              <p style={{ fontSize:11,color:'var(--text-tertiary)',textAlign:'center',margin:'10px 0 0' }}>
                Seu plano permite {userMaxAccounts} conta(s) de email
              </p>
            )}
          </div>

          <div style={{ display:'flex',alignItems:'center',justifyContent:'center',gap:16 }}>
            {hasPerm('perm_theme') && (
            <button className="icon-btn" onClick={() => setTheme(t => t === 'light' ? 'dark' : 'light')} style={{ width:36,height:36 }} title="Alternar tema">
              {theme === 'light' ? <Moon size={16} /> : <Sun size={16} />}
            </button>
            )}
            <button className="icon-btn" onClick={handleLogout} style={{ width:36,height:36 }} title="Sair">
              <ArrowLeft size={16} />
            </button>
            <span style={{ fontSize:11,color:'var(--text-tertiary)' }}>Desenvolvido por Ramcore Tecnologia</span>
          </div>
        </div>
      ) : (
      <>
      {/* ===== SIDEBAR ===== */}
      <aside className={`sidebar ${sidebarOpen ? 'open' : ''}`}
        style={sidebarCollapsed ? { width:64,minWidth:64 } : undefined}>
        <div className="sidebar-brand" style={sidebarCollapsed ? { padding:'20px 14px 16px',justifyContent:'center' } : undefined}>
          <img src="/favicon.ico" alt="Coremail" style={{ width:34,height:34,borderRadius:8,flexShrink:0 }} />
          {!sidebarCollapsed && <span className="brand-name">Coremail</span>}
        </div>

        <button className="compose-btn" onClick={() => openCompose()}
          style={sidebarCollapsed ? { margin:'4px 8px 16px',padding:'11px 0',justifyContent:'center' } : undefined}>
          <Pen size={16} /> {!sidebarCollapsed && 'Novo Email'}
        </button>

        <nav className="sidebar-nav">
          {!sidebarCollapsed && <div className="nav-section-title">Pastas</div>}
          {FOLDERS.map(folder => {
            const Icon = FOLDER_ICONS[folder.icon];
            return (
              <div
                key={folder.id}
                className={`nav-item ${currentFolder === folder.id ? 'active' : ''}`}
                onClick={() => { setCurrentSection('email'); setCurrentFolder(folder.id); setSelectedEmail(null); setSelectedIds(new Set()); setAllFolderSelected(false); setFilterTag(null); setFilterUnreadOnly(false); setSidebarOpen(false); setAiSummary(null); }}
                title={sidebarCollapsed ? folder.name : undefined}
                style={sidebarCollapsed ? { justifyContent:'center',padding:'9px 0' } : undefined}
              >
                {Icon && <Icon size={18} />}
                {!sidebarCollapsed && folder.name}
                {!sidebarCollapsed && (() => {
                  const fc = folderCounts?.[folder.id];
                  if (!fc) return null;
                  // inbox: show unread count with accent
                  if (folder.id === 'inbox') {
                    return fc.unread > 0 ? <span className="nav-badge">{fc.unread}</span> : null;
                  }
                  // drafts: show total with yellow
                  if (folder.id === 'drafts') {
                    return fc.total > 0 ? <span className="nav-badge draft">{fc.total}</span> : null;
                  }
                  // spam: show only unread count in danger color
                  if (folder.id === 'spam') {
                    return fc.unread > 0 ? <span className="nav-badge" style={{ background:'var(--danger)' }}>{fc.unread}</span> : null;
                  }
                  // starred: show only unread count (not total favorites)
                  if (folder.id === 'starred') {
                    return fc.unread > 0 ? <span className="nav-badge" style={{ background:'var(--star-color, #F59E0B)' }}>{fc.unread}</span> : null;
                  }
                  // others (trash, archive): show total with subtle — skip sent
                  if (folder.id === 'sent') return null;
                  return fc.total > 0 ? <span className="nav-badge" style={{ background:'rgba(255,255,255,0.15)' }}>{fc.total}</span> : null;
                })()}
                {sidebarCollapsed && (() => {
                  const fc = folderCounts?.[folder.id];
                  const count = folder.id === 'inbox' ? fc?.unread : folder.id === 'starred' ? (fc?.unread || 0) : folder.id === 'sent' ? 0 : folder.id === 'spam' ? (fc?.unread || 0) : fc?.total;
                  if (!count || count <= 0) return null;
                  const bg = folder.id === 'inbox' ? 'var(--accent)' : folder.id === 'spam' ? 'var(--danger)' : folder.id === 'drafts' ? 'var(--warning)' : 'var(--text-tertiary)';
                  return (
                    <span style={{ position:'absolute',top:2,right:4,fontSize:9,fontWeight:700,background:bg,color:'#fff',borderRadius:8,padding:'0 4px',minWidth:14,textAlign:'center' }}>
                      {count}
                    </span>
                  );
                })()}
              </div>
            );
          })}

          {/* ─── FERRAMENTAS ─── */}
          {!sidebarCollapsed && <div className="nav-section-title" style={{ marginTop: 8 }}>Ferramentas</div>}
          {!sidebarCollapsed && tags.map(tag => (
            <div key={tag.id}
              className={`nav-item ${filterTag === tag.id ? 'active' : ''}`}
              onClick={() => {
                setCurrentSection('email');
                if (filterTag === tag.id) {
                  setFilterTag(null);
                  if (currentFolder === 'drafts') loadDrafts();
                  else loadEmails(currentFolder, 1);
                } else {
                  setFilterTag(tag.id);
                  setSelectedEmail(null);
                  loadTagEmails(tag.id);
                }
                setSidebarOpen(false);
              }}>
              <span style={{ width: 10, height: 10, borderRadius: '50%', background: tag.color || TAG_COLOR_HEX[tag.colorId] || '#3B82F6', flexShrink: 0 }} />
              {tag.name}
              {tag.count > 0 && <span style={{ marginLeft:'auto',fontSize:11,color:'var(--text-sidebar)',opacity:0.5 }}>{tag.count}</span>}
            </div>
          ))}
          {!sidebarCollapsed && hasPerm('perm_tags') && (
            <div className="nav-item" onClick={() => { setShowTagManager(true); loadTagRules(); }} style={{ color: 'var(--accent)' }}>
              <Plus size={16} /> Gerenciar Tags
            </div>
          )}
          {hasPerm('perm_cleanup') && (
          <div
            className={`nav-item ${currentSection === 'cleanup' ? 'active' : ''}`}
            onClick={() => { setCurrentSection(currentSection === 'cleanup' ? 'email' : 'cleanup'); setSidebarOpen(false); }}
            title={sidebarCollapsed ? 'Limpeza' : undefined}
            style={sidebarCollapsed ? { justifyContent:'center',padding:'9px 0' } : undefined}
          >
            <Sparkles size={18} />
            {!sidebarCollapsed && 'Limpeza de Emails'}
          </div>
          )}
          {hasPerm('perm_contacts') && (
          <div
            className={`nav-item ${currentSection === 'contacts' ? 'active' : ''}`}
            onClick={() => { setCurrentSection(currentSection === 'contacts' ? 'email' : 'contacts'); setSidebarOpen(false); }}
            title={sidebarCollapsed ? 'Contatos' : undefined}
            style={sidebarCollapsed ? { justifyContent:'center',padding:'9px 0' } : undefined}
          >
            <Users size={18} />
            {!sidebarCollapsed && 'Contatos'}
          </div>
          )}
          {hasPerm('perm_dashboard') && (
          <div
            className={`nav-item ${currentSection === 'dashboard' ? 'active' : ''}`}
            onClick={() => { setCurrentSection(currentSection === 'dashboard' ? 'email' : 'dashboard'); setSidebarOpen(false); }}
            title={sidebarCollapsed ? 'Dashboard' : undefined}
            style={sidebarCollapsed ? { justifyContent:'center',padding:'9px 0' } : undefined}
          >
            <BarChart3 size={18} />
            {!sidebarCollapsed && 'Dashboard'}
          </div>
          )}
          {hasPerm('perm_spam_rules') && (
          <div
            className={`nav-item`}
            onClick={() => { setShowSpamRules(true); loadSpamRules(); setSidebarOpen(false); }}
            title={sidebarCollapsed ? 'Regras de Spam' : undefined}
            style={sidebarCollapsed ? { justifyContent:'center',padding:'9px 0' } : undefined}
          >
            <ShieldAlert size={18} />
            {!sidebarCollapsed && 'Regras de Spam'}
          </div>
          )}
        </nav>

        {!sidebarCollapsed && <div className="powered-by">Desenvolvido por Ramcore Tecnologia</div>}

        {/* Collapse toggle */}
        <button onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          style={{
            display:'flex',alignItems:'center',justifyContent:'center',
            width:'100%',padding:'8px 0',background:'transparent',border:'none',
            borderTop:'1px solid rgba(255,255,255,0.06)',cursor:'pointer',
            color:'rgba(255,255,255,0.3)',transition:'all 0.2s',
          }}>
          {sidebarCollapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
          {!sidebarCollapsed && <span style={{ fontSize:11,marginLeft:6 }}>Minimizar</span>}
        </button>

        {!sidebarCollapsed && (
        <div className="sidebar-footer">
          {/* ─── SESSÃO DO SISTEMA ─── */}
          <div style={{ padding:'8px 10px' }}>
            <div style={{ display:'flex',alignItems:'center',gap:6,padding:'4px 2px' }}>
              <ShieldCheck size={13} color="rgba(255,255,255,0.2)" />
              <div style={{ flex:1,minWidth:0 }}>
                <div style={{ fontSize:10.5,fontWeight:500,color:'rgba(255,255,255,0.55)',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis' }}>
                  {authUser?.name || 'Usuário'} <span style={{ color:'rgba(255,255,255,0.25)' }}>·</span> <span style={{ fontWeight:400,color:'rgba(255,255,255,0.3)' }}>{authUser?.email}</span>
                </div>
              </div>
              {authUser?.role === 'admin' && (
                <button className="icon-btn" onClick={openAdminPanel} title="Gerenciar Usuários"
                  style={{ width:24,height:24,color:'rgba(255,255,255,0.3)' }}>
                  <Settings size={12} />
                </button>
              )}
              <button className="icon-btn" onClick={handleLogout} title="Sair do sistema"
                style={{ width:24,height:24,color:'rgba(255,255,255,0.3)' }}>
                <ArrowLeft size={12} />
              </button>
            </div>
          </div>
        </div>
        )}
      </aside>

      {/* Sidebar backdrop for mobile */}
      <div className={`sidebar-backdrop ${sidebarOpen ? 'visible' : ''}`} onClick={() => setSidebarOpen(false)} />

      {/* ===== MAIN ===== */}
      <main className="main-content">
        <header className="topbar">
          <button className="icon-btn mobile-menu-btn" onClick={() => setSidebarOpen(!sidebarOpen)} style={{ display: 'none' }}>
            <Menu size={20} />
          </button>
          {currentSection === 'email' ? (
          <div className="search-wrapper">
            <Search size={16} className="search-icon" />
            <input
              className="search-input"
              placeholder="Pesquisar em todos os emails..."
              value={searchQuery}
              onChange={(e) => handleSearchChange(e.target.value)}
              style={{ paddingRight: searchActive || searchQuery ? 70 : 14 }}
            />
            <div style={{ position:'absolute',right:8,top:'50%',transform:'translateY(-50%)',display:'flex',gap:2 }}>
              <button className="icon-btn" onClick={() => setShowDateFilter(!showDateFilter)} title="Filtrar por data"
                style={{ width:28,height:28,color: (searchDateFrom || searchDateTo) ? 'var(--accent)' : undefined }}>
                <Calendar size={14} />
              </button>
              {(searchActive || searchQuery) && (
                <button className="icon-btn" onClick={clearSearch} title="Limpar busca" style={{ width:28,height:28 }}>
                  <X size={14} />
                </button>
              )}
            </div>
            {/* Date filter dropdown */}
            {showDateFilter && (
              <div style={{
                position:'absolute',top:'calc(100% + 6px)',left:0,right:0,
                background:'var(--bg-secondary)',border:'1px solid var(--border-color)',
                borderRadius:'var(--radius)',boxShadow:'var(--shadow-lg)',padding:12,zIndex:50,
              }} onClick={e => e.stopPropagation()}>
                <div style={{ fontSize:12,fontWeight:600,marginBottom:8,color:'var(--text-secondary)' }}>Filtrar por período</div>
                {/* Quick options */}
                <div style={{ display:'flex',gap:6,marginBottom:10,flexWrap:'wrap' }}>
                  {(() => {
                    const localDate = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
                    const today = localDate(new Date());
                    const daysAgo = (n) => { const d = new Date(); d.setDate(d.getDate()-n); return localDate(d); };
                    const monthStart = () => { const d = new Date(); return localDate(new Date(d.getFullYear(),d.getMonth(),1)); };
                    return [
                      { label:'Hoje', from: today, to: today },
                      { label:'Últimos 7 dias', from: daysAgo(7), to: today },
                      { label:'Últimos 30 dias', from: daysAgo(30), to: today },
                      { label:'Este mês', from: monthStart(), to: today },
                    ].map(opt => {
                      const isActive = searchDateFrom === opt.from && searchDateTo === opt.to;
                      return (
                        <button key={opt.label} className="toolbar-btn" onClick={() => {
                          setSearchDateFrom(opt.from); setSearchDateTo(opt.to);
                          setShowDateFilter(false);
                          executeSearch(searchQuery, opt.from, opt.to);
                        }}
                          style={{
                            padding:'4px 10px',fontSize:11,borderRadius:20,
                            border: isActive ? '1px solid var(--accent)' : '1px solid var(--border-color)',
                            background: isActive ? 'var(--accent-light)' : 'var(--bg-tertiary)',
                            color: isActive ? 'var(--accent)' : undefined,
                            fontWeight: isActive ? 600 : undefined,
                          }}>
                          {opt.label}
                        </button>
                      );
                    });
                  })()}
                </div>
                {/* Custom range */}
                <div style={{ fontSize:11,color:'var(--text-tertiary)',marginBottom:6 }}>Personalizado:</div>
                <div style={{ display:'flex',gap:8,alignItems:'center' }}>
                  <div style={{ flex:1 }}>
                    <label style={{ fontSize:11,color:'var(--text-tertiary)',display:'block',marginBottom:2 }}>De</label>
                    <input type="date" className="form-input" style={{ fontSize:12,padding:'6px 8px' }}
                      value={searchDateFrom} onChange={e => setSearchDateFrom(e.target.value)} />
                  </div>
                  <div style={{ flex:1 }}>
                    <label style={{ fontSize:11,color:'var(--text-tertiary)',display:'block',marginBottom:2 }}>Até</label>
                    <input type="date" className="form-input" style={{ fontSize:12,padding:'6px 8px' }}
                      value={searchDateTo} onChange={e => setSearchDateTo(e.target.value)} />
                  </div>
                </div>
                <div style={{ display:'flex',gap:8,marginTop:10,justifyContent:'space-between' }}>
                  {(searchDateFrom || searchDateTo) && (
                    <button onClick={() => { setSearchDateFrom(''); setSearchDateTo(''); setShowDateFilter(false); executeSearch(searchQuery, '', ''); }}
                      style={{ fontSize:11,color:'var(--danger)',background:'none',border:'none',cursor:'pointer',fontFamily:'var(--font-primary)' }}>
                      Limpar datas
                    </button>
                  )}
                  <button className="btn-primary" onClick={applyDateFilter} style={{ padding:'6px 14px',fontSize:12,marginLeft:'auto' }}>
                    Aplicar
                  </button>
                </div>
              </div>
            )}
          </div>
          ) : (
            <div style={{ flex:1,display:'flex',alignItems:'center',gap:8 }}>
              {currentSection === 'contacts' && <><Users size={18} color="var(--accent)" /><span style={{ fontSize:15,fontWeight:600 }}>Contatos</span></>}
              {currentSection === 'dashboard' && <><BarChart3 size={18} color="var(--accent)" /><span style={{ fontSize:15,fontWeight:600 }}>Dashboard</span></>}
              {currentSection === 'cleanup' && <><Sparkles size={18} color="var(--accent)" /><span style={{ fontSize:15,fontWeight:600 }}>Limpeza de Emails</span></>}
            </div>
          )}
          <div className="topbar-actions">
            {/* AI Credits Panel */}
            {isAiEnabled && (
              <div style={{ position:'relative' }}>
                <button
                  onClick={(e) => { e.stopPropagation(); setShowAiCreditsPanel(!showAiCreditsPanel); }}
                  title="Créditos IA"
                  style={{
                    display:'flex',alignItems:'center',gap:5,
                    padding:'5px 10px',borderRadius:8,cursor:'pointer',
                    background: !hasAiCredits ? 'var(--danger-light)' : 'var(--accent-light)',
                    border:'1px solid ' + (!hasAiCredits ? 'var(--danger)' : 'transparent'),
                    color: !hasAiCredits ? 'var(--danger)' : 'var(--accent)',
                    fontFamily:'var(--font-primary)',fontSize:12,fontWeight:600,
                    transition:'all 0.15s',
                  }}
                >
                  <Sparkles size={14} />
                  {aiCreditsLimit > 0 ? (
                    <span>{Math.max(0, aiCreditsRemaining)}/{aiCreditsLimit}</span>
                  ) : (
                    <span>∞</span>
                  )}
                </button>
                {showAiCreditsPanel && (
                  <div onClick={e => e.stopPropagation()} style={{
                    position:'absolute',top:'calc(100% + 8px)',right:0,
                    width:260,background:'var(--bg-secondary)',
                    border:'1px solid var(--border-color)',borderRadius:12,
                    boxShadow:'var(--shadow-xl)',zIndex:100,overflow:'hidden',
                  }}>
                    <div style={{ padding:'14px 16px 10px',borderBottom:'1px solid var(--border-color)' }}>
                      <div style={{ display:'flex',alignItems:'center',gap:6,marginBottom:8 }}>
                        <Sparkles size={16} color="var(--accent)" />
                        <span style={{ fontSize:13,fontWeight:700 }}>Créditos IA</span>
                      </div>
                      {aiCreditsLimit > 0 ? (
                        <>
                          <div style={{ display:'flex',justifyContent:'space-between',fontSize:12,color:'var(--text-secondary)',marginBottom:6 }}>
                            <span>Usados: {aiCreditsUsed}</span>
                            <span>Limite: {aiCreditsLimit}</span>
                          </div>
                          <div style={{ height:6,borderRadius:3,background:'var(--bg-tertiary)',overflow:'hidden' }}>
                            <div style={{
                              height:'100%',borderRadius:3,transition:'width 0.3s',
                              width: `${Math.min(100, (aiCreditsUsed / aiCreditsLimit) * 100)}%`,
                              background: aiCreditsRemaining <= 0
                                ? 'var(--danger)'
                                : aiCreditsRemaining <= Math.ceil(aiCreditsLimit * 0.2)
                                  ? 'var(--warning)'
                                  : 'var(--accent)',
                            }} />
                          </div>
                          <div style={{ fontSize:11,color:'var(--text-tertiary)',marginTop:6,textAlign:'center' }}>
                            {aiCreditsRemaining > 0
                              ? `${aiCreditsRemaining} crédito(s) restante(s)`
                              : <span style={{ color:'var(--danger)',fontWeight:600 }}>Sem créditos disponíveis</span>}
                          </div>
                        </>
                      ) : (
                        <div style={{ fontSize:12,color:'var(--text-secondary)',textAlign:'center',padding:'8px 0' }}>
                          <span style={{ fontSize:20 }}>∞</span>
                          <div style={{ marginTop:2,fontSize:11,color:'var(--text-tertiary)' }}>Créditos ilimitados</div>
                        </div>
                      )}
                    </div>
                    <div style={{ padding:'10px 16px',fontSize:11,color:'var(--text-tertiary)',lineHeight:1.6 }}>
                      <div style={{ marginBottom:6 }}>Custo por modelo:</div>
                      {[
                        { label: 'OpenAI', models: availableAiModels.filter(m => m.provider === 'openai') },
                        { label: 'Gemini', models: availableAiModels.filter(m => m.provider === 'gemini') },
                        { label: 'Anthropic', models: availableAiModels.filter(m => m.provider === 'anthropic') },
                        { label: 'xAI Grok', models: availableAiModels.filter(m => m.provider === 'grok') },
                      ].filter(g => g.models.length > 0).map(g => (
                        <div key={g.label} style={{ marginBottom:6 }}>
                          <div style={{ fontSize:9,fontWeight:700,color:'var(--text-tertiary)',textTransform:'uppercase',letterSpacing:0.5,marginBottom:2 }}>{g.label}</div>
                          {g.models.map(m => (
                            <div key={m.id} style={{ display:'flex',alignItems:'center',gap:5,marginBottom:2 }}>
                              <span style={{
                                fontSize:9,fontWeight:700,padding:'1px 5px',borderRadius:4,minWidth:18,textAlign:'center',
                                background:`${m.tagColor}15`,color:m.tagColor,
                              }}>{m.credits}</span>
                              <span style={{ fontWeight:500,color:'var(--text-secondary)',fontSize:10.5 }}>{m.label}</span>
                              {m.thinking && <span style={{ fontSize:9,color:m.tagColor,fontWeight:600 }}>[{m.tag}]</span>}
                            </div>
                          ))}
                        </div>
                      ))}
                      {aiCreditsLimit > 0 && aiCreditsRemaining <= 0 && (
                        <div style={{ marginTop:6,color:'var(--danger)',fontWeight:500 }}>
                          Entre em contato com o administrador para obter mais créditos.
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
            <button className={`icon-btn ${emailSyncing ? 'syncing' : ''}`} onClick={() => syncFolder(currentFolder)} title="Sincronizar">
              <RefreshCw size={18} className={emailSyncing ? 'spin-icon' : ''} />
            </button>
            <button className="icon-btn" onClick={() => setShowSettings(true)} title="Configurações">
              <Settings size={18} />
            </button>
            {hasPerm('perm_theme') && (
            <button className="theme-btn" onClick={() => setTheme(t => t === 'light' ? 'dark' : t === 'dark' ? 'midnight' : 'light')}
              title={`Tema: ${theme === 'light' ? 'Claro' : theme === 'dark' ? 'Escuro' : 'Noturno'}`}>
              {theme === 'light' ? <Sun size={18} /> : theme === 'dark' ? <Moon size={18} /> : <EyeOff size={18} />}
            </button>
            )}

            {/* Active account email indicator */}
            {activeAccount && (
              <div
                onClick={() => setShowAccountModal(true)}
                style={{
                  display:'flex',alignItems:'center',gap:6,
                  padding:'4px 10px 4px 5px',borderRadius:20,cursor:'pointer',
                  background:'var(--bg-tertiary)',border:'1px solid var(--border-color)',
                  transition:'all 0.15s',maxWidth:280,marginLeft:4,
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor='var(--accent)'; e.currentTarget.style.background='var(--accent-light)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor='var(--border-color)'; e.currentTarget.style.background='var(--bg-tertiary)'; }}
                title={`${activeAccount.name} — ${activeAccount.email}\nClique para trocar de conta`}
              >
                <div style={{
                  width:24,height:24,borderRadius:'50%',flexShrink:0,
                  background:'linear-gradient(135deg, var(--accent), #8B5CF6)',
                  display:'flex',alignItems:'center',justifyContent:'center',
                  fontSize:9,fontWeight:700,color:'#fff',
                }}>{activeAccount.initials}</div>
                <span style={{ fontSize:12,fontWeight:600,color:'var(--text-primary)',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap' }}>
                  {activeAccount.email}
                </span>
                <ChevronDown size={12} color="var(--text-tertiary)" style={{ flexShrink:0 }} />
              </div>
            )}
          </div>
        </header>

        {/* Storage exceeded warning banner */}
        {storageExceeded && storageLimitMb > 0 && (
          <div style={{
            display:'flex',alignItems:'center',gap:10,padding:'10px 20px',
            background:'var(--danger-light)',borderBottom:'1px solid var(--danger)',
            color:'var(--danger)',fontSize:13,fontWeight:500,flexShrink:0,
          }}>
            <AlertCircle size={18} style={{ flexShrink:0 }} />
            <span style={{ flex:1 }}>
              Limite de armazenamento atingido ({formatBytes(storageUsedBytes)} de {storageLimitMb >= 1024 ? `${(storageLimitMb/1024).toFixed(1)} GB` : `${storageLimitMb} MB`}).
              Exclua emails para liberar espaço e voltar a receber novos emails.
            </span>
            <button className="toolbar-btn" onClick={() => { setCurrentSection('email'); setCurrentFolder('trash'); setSelectedEmail(null); }}
              style={{ background:'var(--danger)',color:'#fff',borderRadius:6,padding:'4px 12px',fontSize:12,fontWeight:600 }}>
              Ir para Lixeira
            </button>
          </div>
        )}

        {currentSection === 'email' && (
        <div className="email-layout">
          {/* ===== EMAIL LIST ===== */}
          <div className="email-list-panel" style={{ width:listWidth,minWidth:280,maxWidth:700,flexShrink:0,position:'relative' }}>
            {/* Resize handle */}
            <div onMouseDown={startListResize} style={{
              position:'absolute',right:-3,top:0,bottom:0,width:6,cursor:'col-resize',zIndex:5,
              display:'flex',alignItems:'center',justifyContent:'center',
            }}>
              <div style={{ width:2,height:32,borderRadius:1,background:'var(--border-color)',opacity:0.5,transition:'opacity 0.2s' }} />
            </div>
            {/* Sync Progress Banner */}
            {(syncProgress || (emailSyncing && !syncProgress)) && (
              <div style={{
                padding: syncProgress ? '12px 16px' : '8px 16px',
                background: 'linear-gradient(135deg, rgba(59,130,246,0.08), rgba(139,92,246,0.08))',
                borderBottom: '1px solid var(--border-color)',
                flexShrink: 0,
              }}>
                <div style={{ display:'flex',alignItems:'center',gap:8,marginBottom: syncProgress ? 8 : 0 }}>
                  <Loader size={14} className="spin-icon" style={{ color:'var(--accent)' }} />
                  <span style={{ fontSize:12.5,fontWeight:600,color:'var(--text-primary)',flex:1 }}>
                    {syncProgress
                      ? `Sincronizando emails... ${syncProgress.synced} de ${syncProgress.total}`
                      : 'Sincronizando...'
                    }
                  </span>
                  {syncProgress && (
                    <span style={{ fontSize:11,fontWeight:700,color:'var(--accent)' }}>
                      {syncProgress.percent}%
                    </span>
                  )}
                  <button
                    onClick={cancelSync}
                    style={{
                      display:'flex',alignItems:'center',gap:4,
                      padding:'4px 10px',fontSize:11,fontWeight:600,
                      background:'var(--danger-light)',color:'var(--danger)',
                      border:'1px solid var(--danger)',borderRadius:6,
                      cursor:'pointer',fontFamily:'var(--font-primary)',
                      transition:'all var(--transition)',whiteSpace:'nowrap',
                    }}
                    title="Cancelar sincronização"
                  >
                    <X size={12} /> Cancelar
                  </button>
                </div>
                {syncProgress && (
                  <>
                    <div style={{
                      width:'100%',height:6,borderRadius:3,
                      background:'var(--border-color)',overflow:'hidden',
                    }}>
                      <div style={{
                        width:`${syncProgress.percent}%`,height:'100%',borderRadius:3,
                        background:'linear-gradient(90deg, var(--btn-gradient-from, #3B82F6), var(--purple))',
                        transition:'width 0.4s ease',
                      }} />
                    </div>
                    <div style={{ fontSize:11,color:'var(--text-tertiary)',marginTop:6 }}>
                      {syncProgress.percent < 100
                        ? 'Você já pode navegar normalmente enquanto sincroniza.'
                        : 'Finalizando sincronização...'
                      }
                    </div>
                  </>
                )}
              </div>
            )}

            <div className="email-list-toolbar">
              <div
                className={`checkbox-wrap ${selectedIds.size === filteredEmails.length && filteredEmails.length > 0 ? 'checked' : ''}`}
                onClick={selectAll}
              >
                {selectedIds.size === filteredEmails.length && filteredEmails.length > 0 && <Check size={12} color="#fff" strokeWidth={3} />}
              </div>
              {selectedIds.size > 0 ? (
                <>
                  <span style={{ fontSize: 11, color: 'var(--text-secondary)', fontWeight: 600 }}>
                    {allFolderSelected ? `Todos ${emailPagination.total}` : selectedIds.size}
                  </span>
                  {!allFolderSelected && emailPagination.total > filteredEmails.length && selectedIds.size === filteredEmails.length && (
                    <button className="toolbar-btn" onClick={selectAllInFolder}
                      style={{ color:'var(--accent)',fontWeight:600,fontSize:10,padding:'2px 6px' }}>
                      Todos ({emailPagination.total})
                    </button>
                  )}
                  <div className="toolbar-sep" />
                  <button className="toolbar-btn" onClick={bulkMarkRead} style={{ padding:'4px 6px',fontSize:11 }}><MailOpen size={13} /> Lido</button>
                  <button className="toolbar-btn" onClick={bulkMarkUnread} style={{ padding:'4px 6px',fontSize:11 }}><Mail size={13} /> Não lido</button>
                  <button className="toolbar-btn" onClick={bulkDelete} style={{ padding:'4px 6px',fontSize:11,color:'var(--danger)' }}><Trash2 size={13} /> Excluir</button>
                </>
              ) : (
                <>
                  {filterTag && (() => {
                    const ft = tags.find(t => t.id === filterTag);
                    return ft ? (
                      <div style={{ display:'flex',alignItems:'center',gap:6,padding:'2px 8px',background:'var(--accent-light)',borderRadius:20,fontSize:12,fontWeight:600,color:'var(--accent)' }}>
                        <Tag size={12} />
                        {ft.name}
                        <button onClick={() => {
                          setFilterTag(null);
                          if (currentFolder === 'drafts') loadDrafts(1);
                          else loadEmails(currentFolder, 1, null, filterUnreadOnly);
                        }}
                          style={{ background:'none',border:'none',cursor:'pointer',color:'var(--accent)',display:'flex',padding:0 }}
                          title="Remover filtro de tag">
                          <X size={12} />
                        </button>
                      </div>
                    ) : null;
                  })()}
                  {searchActive && (
                    <div style={{ display:'flex',alignItems:'center',gap:4,padding:'2px 8px',background:'var(--warning-light)',borderRadius:20,fontSize:11,fontWeight:600,color:'var(--warning)' }}>
                      <Search size={11} /> Busca ativa
                    </div>
                  )}
                  <div className="toolbar-spacer" />
                  {/* Unread filter toggle */}
                  <button className="toolbar-btn" onClick={() => {
                      const newVal = !filterUnreadOnly;
                      setFilterUnreadOnly(newVal);
                      // Sem sync antes: syncFolder dispara seu próprio loadEmails sem
                      // o filtro e as duas requisições disputavam a lista.
                      loadEmails(currentFolder, 1, null, newVal);
                    }}
                    title={filterUnreadOnly ? 'Mostrar todos' : 'Apenas não lidos'}
                    style={filterUnreadOnly ? { background:'var(--accent-light)',color:'var(--accent)',fontWeight:600 } : undefined}>
                    <Mail size={14} /> {filterUnreadOnly ? 'Mostrar todos' : 'Não lidos'}
                  </button>
                  {/* View mode toggle */}
                  <button className="toolbar-btn" onClick={() => setViewMode(v => v === 'comfortable' ? 'compact' : 'comfortable')}
                    title={viewMode === 'comfortable' ? 'Modo compacto' : 'Modo confortável'}>
                    {viewMode === 'comfortable' ? <LayoutList size={14} /> : <LayoutGrid size={14} />}
                  </button>
                  {/* Per-page selector */}
                  <select value={emailsPerPage} onChange={e => changeEmailsPerPage(Number(e.target.value))}
                    style={{
                      fontSize:11,padding:'3px 4px',border:'1px solid var(--border-color)',borderRadius:4,
                      background:'var(--bg-tertiary)',color:'var(--text-secondary)',fontFamily:'var(--font-primary)',
                      cursor:'pointer',outline:'none',
                    }}>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                    <option value={300}>300</option>
                  </select>
                  <span className="email-count">
                    {filteredEmails.length} de {emailPagination.total}
                  </span>
                </>
              )}
            </div>

            <div className="email-list">
              {emailsLoading ? (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, padding: 40, color: 'var(--text-tertiary)' }}>
                  <Loader size={20} className="spin-icon" />
                  <span style={{ fontSize: 14 }}>Carregando emails...</span>
                </div>
              ) : filteredEmails.length === 0 ? (
                <div className="empty-state">
                  <Mail size={40} />
                  <p style={{ fontSize: 14 }}>
                    {!activeAccount
                      ? 'Nenhuma conta de email configurada'
                      : !activeAccount.imap_host
                        ? 'Configure o servidor IMAP na conta para receber emails'
                        : 'Nenhum email encontrado'}
                  </p>
                  {!activeAccount && (
                    <button className="btn-primary" onClick={() => { setShowAccountModal(true); setTimeout(() => setShowAddAccount(true), 100); }}
                      style={{ marginTop:8,padding:'8px 16px',fontSize:13,display:'inline-flex',alignItems:'center',gap:6 }}>
                      <Plus size={14} /> Adicionar conta de email
                    </button>
                  )}
                </div>
              ) : (
                <>
                  {filteredEmails.map((email, emailIdx) => (
                    <div
                      key={email.id}
                      className={`email-item ${email.unread ? 'unread' : ''} ${selectedEmail?.id === email.id ? 'active' : ''} ${selectedIds.has(email.id) ? 'selected' : ''}`}
                      onClick={() => email.is_draft ? openDraft(email) : selectEmail(email)}
                      onContextMenu={(e) => handleCtx(e, email)}
                      style={{
                        ...(viewMode === 'compact' ? { padding:'6px 16px',gap:8 } : {}),
                      }}
                    >
                      <div
                        className={`e-checkbox ${selectedIds.has(email.id) ? 'checked' : ''}`}
                        onClick={(e) => toggleSelect(email.id, e)}
                        style={viewMode === 'compact' ? { width:14,height:14,marginTop:1 } : undefined}
                      >
                        {selectedIds.has(email.id) && <Check size={viewMode === 'compact' ? 8 : 10} color="#fff" strokeWidth={3} />}
                      </div>
                      <div
                        className={`e-star ${email.starred ? 'starred' : ''}`}
                        onClick={(e) => toggleStar(email.id, e)}
                        style={viewMode === 'compact' ? { marginTop:1 } : undefined}
                      >
                        <Star size={viewMode === 'compact' ? 13 : 15} fill={email.starred ? 'currentColor' : 'none'} />
                      </div>
                      {email.unread && <div className="unread-dot" style={viewMode === 'compact' ? { width:6,height:6,marginTop:4 } : undefined} />}
                      <div className="e-content">
                        {viewMode === 'compact' ? (
                          /* Compact: single line */
                          <div style={{ display:'flex',alignItems:'center',gap:8 }}>
                            <span className="e-sender" style={{ fontSize:12,minWidth:120,maxWidth:160,flexShrink:0 }}>{email.from?.name || email.from?.email || '(desconhecido)'}</span>
                            <span className="e-subject" style={{ fontSize:12,flex:1,marginBottom:0 }}>{email.subject || '(Sem assunto)'}</span>
                            <span style={{ fontSize:11,color:'var(--text-tertiary)',whiteSpace:'nowrap',flexShrink:0 }}>{formatDate(email.date)}</span>
                          </div>
                        ) : (
                          /* Comfortable: multi-line */
                          <>
                            <div className="e-top">
                              <span className="e-sender">{email.from?.name || email.from?.email || '(desconhecido)'}</span>
                              <span className="e-date">{formatDate(email.date)}</span>
                            </div>
                            <div className="e-subject">{email.subject || '(Sem assunto)'}</div>
                            <div className="e-preview">{cleanPreview(email.preview) || ' '}</div>
                            {email.tags?.length > 0 && (
                              <div className="e-tags">
                                {email.tags.map(tagId => {
                                  const tag = tags.find(t => t.id === tagId);
                                  if (!tag) return null;
                                  const css = getTagCss(tagId);
                                  const hex = tag.color || TAG_COLOR_HEX[tag.colorId] || '#3B82F6';
                                  return css
                                    ? <span key={tagId} className={`e-tag ${css}`}>{tag.name}</span>
                                    : <span key={tagId} className="e-tag" style={{ background:`${hex}20`, color:hex }}>{tag.name}</span>;
                                })}
                              </div>
                            )}
                          </>
                        )}
                      </div>
                      {email.hasAttachment && (
                        <div className="e-attach"><Paperclip size={viewMode === 'compact' ? 11 : 13} /></div>
                      )}
                      {email.is_draft && (
                        <button className="icon-btn" style={{ width:28,height:28,flexShrink:0,marginTop:1 }}
                          title="Excluir rascunho"
                          onClick={(e) => { e.stopPropagation(); deleteDraftFromList(email.draft_id, e); }}>
                          <Trash2 size={13} color="var(--danger)" />
                        </button>
                      )}
                    </div>
                  ))}
                  {/* Pagination */}
                  {emailPagination.total_pages > 1 && (
                    <div style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      gap: 8, padding: '12px 16px', borderTop: '1px solid var(--border-color)',
                    }}>
                      <button className="btn-secondary" disabled={emailPage <= 1}
                        onClick={() => goToPage(emailPage - 1)}
                        style={{ padding: '6px 12px', fontSize: 12 }}>
                        Anterior
                      </button>
                      <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>
                        {emailPage} de {emailPagination.total_pages} ({emailPagination.total} {currentFolder === 'drafts' ? 'rascunho(s)' : 'emails'})
                      </span>
                      <button className="btn-secondary" disabled={emailPage >= emailPagination.total_pages}
                        onClick={() => goToPage(emailPage + 1)}
                        style={{ padding: '6px 12px', fontSize: 12 }}>
                        Próxima
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* ===== EMAIL DETAIL ===== */}
          <div className={`email-detail ${selectedEmail ? 'active' : ''}`}>
            {!selectedEmail ? (
              <div className="detail-empty" style={{ gap:0,padding:32 }}>
                {aiSummary ? (
                  /* AI Summary Result */
                  <div style={{ width:'100%',maxWidth:640,textAlign:'left' }}>
                    <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:16 }}>
                      <div style={{ display:'flex',alignItems:'center',gap:8 }}>
                        <Sparkles size={18} color="var(--accent)" />
                        <span style={{ fontSize:15,fontWeight:700,color:'var(--text-primary)' }}>
                          Resumo IA — {aiSummary.count} email(s) {aiSummary.mode === 'unread' ? 'não lidos' : 'recentes'}
                        </span>
                      </div>
                      <button className="icon-btn" onClick={() => setAiSummary(null)} title="Fechar"
                        style={{ width:30,height:30 }}>
                        <X size={16} />
                      </button>
                    </div>
                    <div style={{
                      background:'var(--bg-secondary)',border:'1px solid var(--border-color)',
                      borderRadius:12,padding:20,fontSize:14,lineHeight:1.7,
                      color:'var(--text-primary)',whiteSpace:'pre-wrap',maxHeight:'60vh',overflowY:'auto',
                    }}
                      dangerouslySetInnerHTML={{
                        // Escapa o texto do modelo ANTES do markdown-lite: o resumo
                        // reflete conteúdo de emails de terceiros e podia injetar HTML.
                        __html: escapeHtml(aiSummary.result)
                          .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                          .replace(/\r\n|\r|\n/g, '<br/>')
                      }}
                    />
                    <div style={{ display:'flex',gap:8,marginTop:14,justifyContent:'center' }}>
                      <button className="btn-secondary" onClick={() => setAiSummary(null)} style={{ display:'flex',alignItems:'center',gap:5,fontSize:12 }}>
                        <ArrowLeft size={13} /> Voltar
                      </button>
                      <button className="btn-secondary" onClick={() => {
                        if (navigator.clipboard?.writeText) {
                          navigator.clipboard.writeText(aiSummary.result)
                            .then(() => toast('Resumo copiado!', 'success'))
                            .catch(() => toast('Não foi possível copiar', 'error'));
                        } else {
                          toast('Área de transferência indisponível', 'error');
                        }
                      }} style={{ display:'flex',alignItems:'center',gap:5,fontSize:12 }}>
                        <ClipboardCopy size={13} /> Copiar
                      </button>
                    </div>
                  </div>
                ) : aiSummaryLoading ? (
                  /* Loading state */
                  <div style={{ textAlign:'center' }}>
                    <div style={{
                      width:64,height:64,borderRadius:20,
                      background:'linear-gradient(135deg, var(--accent-light), rgba(139,92,246,0.1))',
                      display:'flex',alignItems:'center',justifyContent:'center',
                      margin:'0 auto 16px',
                    }}>
                      <Loader size={28} className="spin-icon" style={{ color:'var(--accent)' }} />
                    </div>
                    <p style={{ fontSize:15,fontWeight:600,color:'var(--text-primary)',marginBottom:6 }}>
                      Analisando emails com IA...
                    </p>
                    <p style={{ fontSize:12,color:'var(--text-tertiary)' }}>
                      Carregando conteúdo e gerando resumo
                    </p>
                  </div>
                ) : (
                  /* Default empty state with AI actions */
                  <>
                    <Mail size={48} style={{ opacity:0.15,marginBottom:12 }} />
                    <p style={{ fontSize:14,marginBottom:4,color:'var(--text-secondary)' }}>Selecione um email para visualizar</p>
                    <p style={{ fontSize:12,color:'var(--text-tertiary)',marginBottom:24 }}>Coremail</p>

                    {isAiEnabled && emails.length > 0 && (
                      <div style={{ width:'100%',maxWidth:400 }}>
                        <div style={{
                          display:'flex',alignItems:'center',gap:6,justifyContent:'center',
                          marginBottom:14,
                        }}>
                          <Sparkles size={14} color="var(--accent)" />
                          <span style={{ fontSize:12,fontWeight:600,color:'var(--accent)',textTransform:'uppercase',letterSpacing:0.5 }}>
                            Ações da IA
                          </span>
                        </div>
                        <div style={{ display:'flex',flexDirection:'column',gap:8 }}>
                          {/* Model selector for batch */}
                          <div style={{ display:'flex',alignItems:'center',gap:6,padding:'0 2px',marginBottom:2 }}>
                            <span style={{ fontSize:11,fontWeight:600,color:'var(--text-tertiary)' }}>Modelo:</span>
                            <select value={batchAiModel} onChange={e => setBatchAiModel(e.target.value)}
                              style={{
                                padding:'4px 8px',borderRadius:6,fontSize:11,fontWeight:600,
                                border:'1px solid var(--border-color)',background:'var(--bg-tertiary)',
                                color:'var(--text-primary)',cursor:'pointer',fontFamily:'var(--font-primary)',outline:'none',flex:1,
                              }}>
                              {[
                                { label: 'OpenAI', provider: 'openai' },
                                { label: 'Google Gemini', provider: 'gemini' },
                                { label: 'Anthropic Claude', provider: 'anthropic' },
                                { label: 'xAI Grok', provider: 'grok' },
                              ].map(g => {
                                const models = availableAiModels.filter(m => m.provider === g.provider);
                                return models.length > 0 ? (
                                  <optgroup key={g.provider} label={g.label}>
                                    {models.map(m => (
                                      <option key={m.id} value={m.id}>{m.label}{m.thinking ? ` [${m.tag}]` : ''} ({m.credits} créd.)</option>
                                    ))}
                                  </optgroup>
                                ) : null;
                              })}
                            </select>
                          </div>

                          {/* Summarize recent 5 */}
                          <button
                            onClick={() => runAiSummary('recent')}
                            style={{
                              display:'flex',alignItems:'center',gap:12,padding:'14px 16px',
                              background:'var(--bg-secondary)',border:'1px solid var(--border-color)',
                              borderRadius:10,cursor:'pointer',transition:'all 0.2s',
                              textAlign:'left',fontFamily:'var(--font-primary)',width:'100%',
                            }}
                            onMouseEnter={e => { e.currentTarget.style.borderColor='var(--accent)'; e.currentTarget.style.background='var(--accent-light)'; }}
                            onMouseLeave={e => { e.currentTarget.style.borderColor='var(--border-color)'; e.currentTarget.style.background='var(--bg-secondary)'; }}
                          >
                            <div style={{
                              width:38,height:38,borderRadius:10,flexShrink:0,
                              background:'linear-gradient(135deg, var(--accent), #8B5CF6)',
                              display:'flex',alignItems:'center',justifyContent:'center',
                            }}>
                              <Wand2 size={18} color="#fff" />
                            </div>
                            <div>
                              <div style={{ fontSize:13,fontWeight:600,color:'var(--text-primary)' }}>
                                Resumir últimos 5 emails
                              </div>
                              <div style={{ fontSize:11,color:'var(--text-tertiary)',marginTop:2 }}>
                                Gera um resumo executivo dos emails mais recentes
                              </div>
                            </div>
                          </button>

                          {/* Summarize unread */}
                          <button
                            onClick={() => runAiSummary('unread')}
                            style={{
                              display:'flex',alignItems:'center',gap:12,padding:'14px 16px',
                              background:'var(--bg-secondary)',border:'1px solid var(--border-color)',
                              borderRadius:10,cursor:'pointer',transition:'all 0.2s',
                              textAlign:'left',fontFamily:'var(--font-primary)',width:'100%',
                            }}
                            onMouseEnter={e => { e.currentTarget.style.borderColor='var(--accent)'; e.currentTarget.style.background='var(--accent-light)'; }}
                            onMouseLeave={e => { e.currentTarget.style.borderColor='var(--border-color)'; e.currentTarget.style.background='var(--bg-secondary)'; }}
                          >
                            <div style={{
                              width:38,height:38,borderRadius:10,flexShrink:0,
                              background:'linear-gradient(135deg, #F59E0B, #EF4444)',
                              display:'flex',alignItems:'center',justifyContent:'center',
                            }}>
                              <MailOpen size={18} color="#fff" />
                            </div>
                            <div>
                              <div style={{ fontSize:13,fontWeight:600,color:'var(--text-primary)' }}>
                                Resumir não lidos
                                {(() => { const c = emails.filter(e => e.unread).length; return c > 0 ? ` (${Math.min(c, 5)})` : ''; })()}
                              </div>
                              <div style={{ fontSize:11,color:'var(--text-tertiary)',marginTop:2 }}>
                                Resumo dos emails não lidos mais recentes (limite 5)
                              </div>
                            </div>
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            ) : (
              <>
                <div className="detail-header">
                  {/* Mobile back button */}
                  <button className="mobile-back-btn action-btn" onClick={() => setSelectedEmail(null)}
                    style={{ display:'none',marginBottom:10,gap:6 }}>
                    <ArrowLeft size={16} /> Voltar
                  </button>
                  <div className="detail-subject">{selectedEmail.subject || '(Sem assunto)'}</div>
                  <div className="detail-meta">
                    {/* Guardas: emails automáticos costumam vir sem from.name — sem
                        elas, getInitials/`.to.map` derrubavam a tela inteira */}
                    <div className="sender-avatar">{getInitials(selectedEmail.from?.name || selectedEmail.from?.email)}</div>
                    <div className="sender-info">
                      <div className="sender-row">
                        <span className="sender-name">{selectedEmail.from?.name || selectedEmail.from?.email || '(desconhecido)'}</span>
                        {selectedEmail.from?.email && (
                          <span className="sender-email">&lt;{selectedEmail.from.email}&gt;</span>
                        )}
                      </div>
                      <div className="recipients">
                        Para: {(selectedEmail.to || []).map(t => t.name ? `${t.name} <${t.email}>` : t.email).join(', ') || '—'}
                      </div>
                      {selectedEmail.cc && selectedEmail.cc.length > 0 && (
                        <div className="recipients" style={{ marginTop:1 }}>
                          Cc: {selectedEmail.cc.map(c => c.name ? `${c.name} <${c.email}>` : c.email).join(', ')}
                        </div>
                      )}
                      {selectedEmail.bcc && selectedEmail.bcc.length > 0 && (
                        <div className="recipients" style={{ marginTop:1 }}>
                          Cco: {selectedEmail.bcc.map(c => c.name ? `${c.name} <${c.email}>` : c.email).join(', ')}
                        </div>
                      )}
                    </div>
                    <div className="detail-date">{formatFullDate(selectedEmail.date)}</div>
                  </div>

                  <div className="detail-actions">
                    <button className="action-btn" onClick={() => openReply(selectedEmail)}>
                      <Reply size={14} /> Responder
                    </button>
                    <button className="action-btn" onClick={() => openReplyAll(selectedEmail)}>
                      <ReplyAll size={14} /> Responder a todos
                    </button>
                    <button className="action-btn" onClick={() => openForward(selectedEmail)}>
                      <Forward size={14} /> Encaminhar
                    </button>
                    <button className="action-btn" onClick={() => toggleRead(selectedEmail.id)}>
                      {selectedEmail.unread ?
                        <><MailOpen size={14} /> Marcar como lido</> :
                        <><Mail size={14} /> Marcar como não lido</>
                      }
                    </button>

                    {/* Tag dropdown */}
                    <div style={{ position: 'relative' }}>
                      <button className="action-btn" onClick={(e) => {
                        e.stopPropagation();
                        setTagDropdown(tagDropdown === selectedEmail.id ? null : selectedEmail.id);
                      }}>
                        <Tag size={14} /> Tags
                      </button>
                      {tagDropdown === selectedEmail.id && (
                        <div className="tag-dropdown" onClick={e => e.stopPropagation()}>
                          {tags.map(tag => {
                            const has = (selectedEmail.tags || []).includes(tag.id);
                            return (
                              <div key={tag.id} className="ctx-item" onClick={() => toggleEmailTag(selectedEmail.id, tag.id)}>
                                <span style={{ width: 10, height: 10, borderRadius: '50%', background: tag.color || TAG_COLOR_HEX[tag.colorId] || '#3B82F6' }} />
                                <span style={{ flex: 1 }}>{tag.name}</span>
                                {has && <Check size={14} color="var(--success)" />}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    {selectedEmail.folder === 'archive' ? (
                      <button className="action-btn" onClick={() => moveToFolder(selectedEmail.id, 'inbox')}>
                        <Inbox size={14} /> Mover para Entrada
                      </button>
                    ) : (
                      <button className="action-btn" onClick={() => archiveEmail(selectedEmail.id)}>
                        <Archive size={14} /> Arquivar
                      </button>
                    )}
                    {selectedEmail.folder === 'spam' && (
                      <button className="action-btn" onClick={() => markAsNotSpam(selectedEmail.id)}
                        style={{ background:'rgba(16,185,129,0.1)',color:'var(--success)',border:'1px solid rgba(16,185,129,0.2)' }}>
                        <ShieldCheck size={14} /> Não é spam
                      </button>
                    )}
                    <button className="action-btn danger" onClick={() => deleteEmail(selectedEmail.id)}>
                      <Trash2 size={14} /> Excluir
                    </button>
                    <div style={{ flex:1 }} />
                    {isAiEnabled && (
                      <button
                        className={`action-btn ${showAiPanel ? 'ai-active' : ''}`}
                        onClick={toggleAiPanel}
                        style={showAiPanel ? { background:'linear-gradient(135deg,#8B5CF6,#6366F1)', color:'#fff', borderColor:'transparent' } : {}}
                      >
                        <Sparkles size={14} /> {showAiPanel ? 'Fechar IA' : 'Ações IA'}
                      </button>
                    )}
                  </div>
                </div>

                <div className="detail-body">
                  {/* AI Panel */}
                  {showAiPanel && (
                    <div style={{
                      margin:'0 0 16px',padding:20,
                      background:'var(--bg-secondary)',borderRadius:'var(--radius-lg)',
                      border:'1px solid var(--border-color)',
                      boxShadow:'0 2px 12px rgba(139,92,246,0.08)',
                    }}>
                      {/* Header */}
                      <div style={{ display:'flex',alignItems:'center',gap:8,marginBottom:16 }}>
                        <div style={{
                          width:32,height:32,borderRadius:8,
                          background:'linear-gradient(135deg,#8B5CF6,#6366F1)',
                          display:'flex',alignItems:'center',justifyContent:'center',
                        }}>
                          <Sparkles size={16} color="#fff" />
                        </div>
                        <div>
                          <div style={{ fontSize:14,fontWeight:700,color:'var(--text-primary)' }}>Assistente IA</div>
                          <div style={{ fontSize:11,color:'var(--text-tertiary)' }}>{getModelInfo(aiModel).label}</div>
                        </div>
                      </div>

                      {/* Action Selection */}
                      {!aiResult && (
                        <>
                          <div style={{ fontSize:12,fontWeight:600,color:'var(--text-secondary)',marginBottom:8,textTransform:'uppercase',letterSpacing:0.5 }}>
                            Escolha uma ação
                          </div>
                          <div style={{ display:'flex',gap:8,marginBottom:16,flexWrap:'wrap' }}>
                            {[
                              { id:'summarize', icon: FileText, label:'Resumir email', desc:'Entenda o email rapidamente' },
                              { id:'reply', icon: MessageSquare, label:'Responder email', desc:'Gere uma resposta inteligente' },
                              { id:'thank', icon: ThumbsUp, label:'Agradecer email', desc:'Envie um agradecimento' },
                            ].map(act => {
                              const Icon = act.icon;
                              return (
                                <button key={act.id} onClick={() => { setAiAction(act.id); setAiStyle(''); setAiResult(''); }}
                                  style={{
                                    flex:'1 1 140px',padding:'12px 14px',borderRadius:'var(--radius)',cursor:'pointer',
                                    border: aiAction === act.id ? '2px solid #8B5CF6' : '1px solid var(--border-color)',
                                    background: aiAction === act.id ? 'rgba(139,92,246,0.08)' : 'var(--bg-tertiary)',
                                    textAlign:'left',fontFamily:'var(--font-primary)',transition:'all 0.15s ease',
                                  }}>
                                  <div style={{ display:'flex',alignItems:'center',gap:6,marginBottom:4 }}>
                                    <Icon size={14} color={aiAction === act.id ? '#8B5CF6' : 'var(--text-secondary)'} />
                                    <span style={{ fontSize:13,fontWeight:600,color: aiAction === act.id ? '#8B5CF6' : 'var(--text-primary)' }}>
                                      {act.label}
                                    </span>
                                  </div>
                                  <div style={{ fontSize:11,color:'var(--text-tertiary)' }}>{act.desc}</div>
                                </button>
                              );
                            })}
                          </div>

                          {/* Style Options for Reply */}
                          {aiAction === 'reply' && (
                            <div style={{ marginBottom:14 }}>
                              <div style={{ fontSize:12,fontWeight:600,color:'var(--text-secondary)',marginBottom:6 }}>Tom da resposta</div>
                              <div style={{ display:'flex',gap:6,flexWrap:'wrap' }}>
                                {[
                                  { id:'formal', label:'Formal', emoji:'👔' },
                                  { id:'informal', label:'Informal', emoji:'😊' },
                                  { id:'casual', label:'Casual', emoji:'✌️' },
                                  { id:'simpatico', label:'Simpático', emoji:'🤗' },
                                  { id:'agressivo', label:'Agressivo', emoji:'🔥' },
                                  { id:'autoritario', label:'Autoritário', emoji:'👊' },
                                  { id:'compreensivo', label:'Compreensivo', emoji:'💙' },
                                ].map(s => (
                                  <button key={s.id} onClick={() => setAiStyle(s.id)}
                                    style={{
                                      padding:'8px 16px',borderRadius:20,cursor:'pointer',
                                      border: aiStyle === s.id ? '2px solid #8B5CF6' : '1px solid var(--border-color)',
                                      background: aiStyle === s.id ? 'rgba(139,92,246,0.08)' : 'var(--bg-tertiary)',
                                      fontSize:13,fontWeight:500,fontFamily:'var(--font-primary)',
                                      color: aiStyle === s.id ? '#8B5CF6' : 'var(--text-primary)',
                                      transition:'all 0.15s ease',
                                    }}>
                                    {s.emoji} {s.label}
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Style Options for Thank */}
                          {aiAction === 'thank' && (
                            <div style={{ marginBottom:14 }}>
                              <div style={{ fontSize:12,fontWeight:600,color:'var(--text-secondary)',marginBottom:6 }}>Tamanho do agradecimento</div>
                              <div style={{ display:'flex',gap:6 }}>
                                {[
                                  { id:'short', label:'Curto' },
                                  { id:'medium', label:'Médio' },
                                  { id:'long', label:'Longo' },
                                ].map(s => (
                                  <button key={s.id} onClick={() => setAiStyle(s.id)}
                                    style={{
                                      padding:'8px 16px',borderRadius:20,cursor:'pointer',
                                      border: aiStyle === s.id ? '2px solid #8B5CF6' : '1px solid var(--border-color)',
                                      background: aiStyle === s.id ? 'rgba(139,92,246,0.08)' : 'var(--bg-tertiary)',
                                      fontSize:13,fontWeight:500,fontFamily:'var(--font-primary)',
                                      color: aiStyle === s.id ? '#8B5CF6' : 'var(--text-primary)',
                                      transition:'all 0.15s ease',
                                    }}>
                                    {s.label}
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Extra Info for Reply */}
                          {aiAction === 'reply' && (
                            <div style={{ marginBottom:14 }}>
                              <div style={{ fontSize:12,fontWeight:600,color:'var(--text-secondary)',marginBottom:6 }}>
                                Informações extras <span style={{ fontWeight:400,color:'var(--text-tertiary)' }}>(opcional)</span>
                              </div>
                              <textarea
                                value={aiExtra}
                                onChange={e => setAiExtra(e.target.value)}
                                placeholder="Ex: Diga que estarei disponível na quinta-feira às 15h..."
                                style={{
                                  width:'100%',padding:'10px 12px',borderRadius:'var(--radius-sm)',
                                  border:'1px solid var(--border-color)',background:'var(--bg-tertiary)',
                                  fontFamily:'var(--font-primary)',fontSize:13,color:'var(--text-primary)',
                                  outline:'none',resize:'vertical',minHeight:60,lineHeight:1.5,
                                }}
                              />
                            </div>
                          )}

                          {/* AI Model Selector */}
                          {aiAction && (
                            <div style={{ marginBottom:14 }}>
                              {[
                                { provider: 'openai', label: 'OpenAI', icon: '🤖', models: availableAiModels.filter(m => m.provider === 'openai') },
                                { provider: 'gemini', label: 'Google Gemini', icon: '✨', models: availableAiModels.filter(m => m.provider === 'gemini') },
                                { provider: 'anthropic', label: 'Anthropic Claude', icon: '🧠', models: availableAiModels.filter(m => m.provider === 'anthropic') },
                                { provider: 'grok', label: 'xAI Grok', icon: '⚡', models: availableAiModels.filter(m => m.provider === 'grok') },
                              ].filter(group => group.models.length > 0).map(group => (
                                <div key={group.provider} style={{ marginBottom:10 }}>
                                  <div style={{ fontSize:10,fontWeight:700,color:'var(--text-tertiary)',marginBottom:6,textTransform:'uppercase',letterSpacing:0.5,display:'flex',alignItems:'center',gap:4 }}>
                                    {group.icon} {group.label}
                                  </div>
                                  <div style={{ display:'flex',gap:5,flexWrap:'wrap' }}>
                                    {group.models.map(m => {
                                      const sel = aiModel === m.id;
                                      const canAfford = aiCreditsLimit <= 0 || aiCreditsRemaining >= m.credits || authUser?.role === 'admin';
                                      return (
                                        <button key={m.id} onClick={() => canAfford && setAiModel(m.id)}
                                          title={!canAfford ? `Créditos insuficientes (precisa de ${m.credits})` : m.desc}
                                          style={{
                                            padding:'8px 10px',borderRadius:8,cursor: canAfford ? 'pointer' : 'not-allowed',
                                            border: sel ? `2px solid ${m.tagColor}` : '1px solid var(--border-color)',
                                            background: sel ? `${m.tagColor}10` : 'var(--bg-tertiary)',
                                            textAlign:'center',fontFamily:'var(--font-primary)',transition:'all 0.15s ease',
                                            opacity: canAfford ? 1 : 0.4,minWidth:0,flex:'1 1 auto',
                                          }}>
                                          <div style={{ fontSize:11,fontWeight:700,color: sel ? m.tagColor : 'var(--text-primary)',marginBottom:2,whiteSpace:'nowrap' }}>
                                            {m.label}
                                          </div>
                                          <div style={{
                                            display:'inline-flex',alignItems:'center',gap:3,
                                            fontSize:9,fontWeight:700,padding:'1px 6px',borderRadius:6,
                                            background: `${m.tagColor}18`,color: m.tagColor,
                                          }}>
                                            {m.credits} créd.
                                          </div>
                                          {m.thinking && <div style={{ fontSize:8,color:'var(--text-tertiary)',marginTop:2,fontWeight:600 }}>{m.tag}</div>}
                                        </button>
                                      );
                                    })}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}

                          {/* Execute Button */}
                          {aiAction && (
                            <button onClick={executeAi} disabled={aiLoading}
                              style={{
                                display:'flex',alignItems:'center',gap:8,padding:'10px 20px',
                                background:'linear-gradient(135deg,#8B5CF6,#6366F1)',color:'#fff',
                                border:'none',borderRadius:'var(--radius)',cursor:'pointer',
                                fontFamily:'var(--font-primary)',fontSize:13.5,fontWeight:600,
                                boxShadow:'0 4px 12px rgba(139,92,246,0.3)',
                                opacity:aiLoading?0.7:1,transition:'all 0.2s ease',
                              }}>
                              {aiLoading ? <Loader size={15} className="spin-icon" /> : <Wand2 size={15} />}
                              {aiLoading ? 'Processando...' : `Executar IA (${getModelInfo(aiModel).credits} créd.)`}
                            </button>
                          )}
                        </>
                      )}

                      {/* AI Result */}
                      {aiResult && (
                        <div>
                          <div style={{ fontSize:12,fontWeight:600,color:'var(--text-secondary)',marginBottom:8,textTransform:'uppercase',letterSpacing:0.5 }}>
                            {aiAction === 'summarize' ? 'Resumo' : aiAction === 'reply' ? 'Resposta sugerida' : 'Agradecimento sugerido'}
                          </div>
                          <div style={{
                            padding:16,borderRadius:'var(--radius)',
                            background:'var(--bg-tertiary)',border:'1px solid var(--border-color)',
                            fontSize:14,lineHeight:1.7,color:'var(--text-primary)',
                            whiteSpace:'pre-wrap',marginBottom:14,
                          }}>
                            {aiResult}
                          </div>
                          <div style={{ display:'flex',gap:8,flexWrap:'wrap' }}>
                            <button onClick={useAiResult}
                              style={{
                                display:'flex',alignItems:'center',gap:6,padding:'9px 18px',
                                background:'linear-gradient(135deg,#8B5CF6,#6366F1)',color:'#fff',
                                border:'none',borderRadius:'var(--radius-sm)',cursor:'pointer',
                                fontFamily:'var(--font-primary)',fontSize:13,fontWeight:600,
                                boxShadow:'0 2px 8px rgba(139,92,246,0.3)',
                              }}>
                              {aiAction === 'summarize' ? <ClipboardCopy size={14} /> : <Send size={14} />}
                              {aiAction === 'summarize' ? 'Copiar resumo' : 'Usar esta resposta'}
                            </button>
                            <button onClick={() => { setAiResult(''); executeAi(); }}
                              className="btn-secondary" style={{ display:'flex',alignItems:'center',gap:6 }}>
                              <RefreshCw size={14} /> Gerar novamente
                            </button>
                            <button onClick={() => { resetAi(); }}
                              className="btn-secondary" style={{ display:'flex',alignItems:'center',gap:6 }}>
                              <X size={14} /> Descartar
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Loading animation */}
                      {aiLoading && (
                        <div style={{
                          display:'flex',alignItems:'center',gap:10,marginTop:16,
                          padding:14,borderRadius:'var(--radius)',background:'var(--bg-tertiary)',
                          color:'var(--text-secondary)',fontSize:13,
                        }}>
                          <Loader size={16} className="spin-icon" style={{ color:'#8B5CF6' }} />
                          <span>Analisando email e gerando resposta...</span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Email Body Theme Toggle */}
                  <div style={{ display:'flex',alignItems:'center',gap:4,marginBottom:12 }}>
                    <span style={{ fontSize:11,color:'var(--text-tertiary)',marginRight:4 }}>Fundo:</span>
                    {[
                      { id:'auto', label:'Auto', icon:<Monitor size={12} /> },
                      { id:'light', label:'Claro', icon:<Sun size={12} /> },
                      { id:'dark', label:'Escuro', icon:<Moon size={12} /> },
                      { id:'midnight', label:'Noturno', icon:<EyeOff size={12} /> },
                    ].map(opt => (
                      <button key={opt.id} onClick={() => { setEmailBodyTheme(opt.id); localStorage.setItem('coremail-body-theme', opt.id); }}
                        title={opt.label}
                        style={{
                          display:'flex',alignItems:'center',gap:4,
                          padding:'3px 8px',borderRadius:5,border:'none',cursor:'pointer',
                          fontSize:11,fontWeight:500,fontFamily:'var(--font-primary)',
                          background: emailBodyTheme === opt.id ? 'var(--accent)' : 'var(--bg-tertiary)',
                          color: emailBodyTheme === opt.id ? '#fff' : 'var(--text-tertiary)',
                          transition:'all 0.15s',
                        }}>
                        {opt.icon} {opt.label}
                      </button>
                    ))}
                  </div>

                  {emailBodyLoading ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: 20, color: 'var(--text-tertiary)' }}>
                      <Loader size={18} className="spin-icon" /> Carregando email...
                    </div>
                  ) : (
                    <EmailBodyFrame
                      html={selectedEmail.body || ''}
                      theme={emailBodyTheme === 'auto' ? theme : emailBodyTheme}
                    />
                  )}

                  {selectedEmail.attachments && selectedEmail.attachments.length > 0 && (
                    <div className="attachments-section">
                      <div className="attachments-title">
                        <Paperclip size={13} /> {selectedEmail.attachments.length} Anexo(s)
                      </div>
                      <div className="attach-list">
                        {selectedEmail.attachments.map((att, i) => {
                          // Ícone/label pela extensão do arquivo (att.type pode vir
                          // como MIME completo e virava classe CSS inválida)
                          const name = String(att.name || '');
                          const ext = name.includes('.') ? name.split('.').pop().toLowerCase() : '';
                          const iconClass = ({
                            pdf:'pdf', doc:'doc', docx:'doc', xls:'xls', xlsx:'xls',
                            zip:'zip', rar:'zip', png:'img', jpg:'img', jpeg:'img', gif:'img', webp:'img',
                          })[ext] || 'default';
                          const sizeLabel = /^\d+$/.test(String(att.size ?? ''))
                            ? formatBytes(Number(att.size))
                            : (att.size || '');
                          return (
                          <div key={`${name}-${i}`} className="attach-item" style={{ cursor: 'default' }}>
                            <div className={`attach-icon ${iconClass}`}>
                              {(ext || 'file').toUpperCase().substring(0, 3)}
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div className="attach-name">{name || 'anexo'}</div>
                              {sizeLabel && <div className="attach-size">{sizeLabel}</div>}
                            </div>
                            <div style={{ display: 'flex', gap: 2 }}>
                              {canPreview(att.name) && (
                                <button className="icon-btn" style={{ width: 28, height: 28 }}
                                  onClick={() => previewAttachment(att)} title="Visualizar">
                                  <Eye size={14} />
                                </button>
                              )}
                              <button className="icon-btn" style={{ width: 28, height: 28 }}
                                onClick={() => downloadAttachment(att)} title="Baixar">
                                <Download size={14} />
                              </button>
                            </div>
                          </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
        )}

        {/* ===== CONTACTS SECTION ===== */}
        {currentSection === 'contacts' && (
          <div style={{ flex:1,overflow:'auto',padding:24 }}>
            <div style={{ maxWidth:1100,margin:'0 auto' }}>
              <h2 style={{ fontSize:20,fontWeight:700,marginBottom:4,display:'flex',alignItems:'center',gap:10 }}>
                <Users size={22} /> Contatos
              </h2>
              <p style={{ fontSize:13,color:'var(--text-tertiary)',marginBottom:20,display:'flex',alignItems:'center',gap:6 }}>
                <Mail size={13} /> Conta: <strong>{activeAccount?.email}</strong>
              </p>

              {/* Stats Cards */}
              {contactStats && (
                <div style={{ display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(200px,1fr))',gap:12,marginBottom:24 }}>
                  <div style={{ background:'var(--bg-secondary)',border:'1px solid var(--border-color)',borderRadius:'var(--radius)',padding:16 }}>
                    <div style={{ fontSize:11,fontWeight:600,color:'var(--text-tertiary)',textTransform:'uppercase',letterSpacing:0.5,marginBottom:6 }}>Total de Contatos</div>
                    <div style={{ fontSize:28,fontWeight:700,color:'var(--accent)' }}>{contactStats.total_contacts}</div>
                  </div>
                  <div style={{ background:'var(--bg-secondary)',border:'1px solid var(--border-color)',borderRadius:'var(--radius)',padding:16 }}>
                    <div style={{ fontSize:11,fontWeight:600,color:'var(--text-tertiary)',textTransform:'uppercase',letterSpacing:0.5,marginBottom:6 }}>Mensagens Enviadas</div>
                    <div style={{ fontSize:28,fontWeight:700,color:'var(--success)' }}>{contactStats.total_messages_sent}</div>
                  </div>
                  <div style={{ background:'var(--bg-secondary)',border:'1px solid var(--border-color)',borderRadius:'var(--radius)',padding:16 }}>
                    <div style={{ fontSize:11,fontWeight:600,color:'var(--text-tertiary)',textTransform:'uppercase',letterSpacing:0.5,marginBottom:6 }}>Domínios</div>
                    <div style={{ fontSize:28,fontWeight:700,color:'var(--purple)' }}>{contactStats.domain_distribution?.length || 0}</div>
                  </div>
                </div>
              )}

              {/* Most contacted + Most received */}
              {contactStats && (
                <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:16,marginBottom:24 }}>
                  <div style={{ background:'var(--bg-secondary)',border:'1px solid var(--border-color)',borderRadius:'var(--radius)',padding:16 }}>
                    <h3 style={{ fontSize:13,fontWeight:700,marginBottom:12,color:'var(--text-secondary)',textTransform:'uppercase',letterSpacing:0.5,display:'flex',alignItems:'center',gap:6 }}>
                      <TrendingUp size={14} /> Mais Contatados (Enviados)
                    </h3>
                    {(contactStats.most_contacted || []).map((c, i) => (
                      <div key={i} style={{ display:'flex',alignItems:'center',gap:10,padding:'6px 0',borderBottom:'1px solid var(--border-light)' }}>
                        <span style={{ fontSize:12,fontWeight:700,color:'var(--text-tertiary)',width:20 }}>#{i+1}</span>
                        <div style={{ flex:1,minWidth:0 }}>
                          <div style={{ fontSize:13,fontWeight:500,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap' }}>{c.name || c.email}</div>
                          <div style={{ fontSize:11,color:'var(--text-tertiary)' }}>{c.email}</div>
                        </div>
                        <span style={{ fontSize:12,fontWeight:600,color:'var(--accent)',background:'var(--accent-light)',padding:'2px 8px',borderRadius:12 }}>{c.times_contacted}x</span>
                      </div>
                    ))}
                  </div>
                  <div style={{ background:'var(--bg-secondary)',border:'1px solid var(--border-color)',borderRadius:'var(--radius)',padding:16 }}>
                    <h3 style={{ fontSize:13,fontWeight:700,marginBottom:12,color:'var(--text-secondary)',textTransform:'uppercase',letterSpacing:0.5,display:'flex',alignItems:'center',gap:6 }}>
                      <Mail size={14} /> Mais Emails Recebidos De
                    </h3>
                    {(contactStats.most_received_from || []).map((c, i) => (
                      <div key={i} style={{ display:'flex',alignItems:'center',gap:10,padding:'6px 0',borderBottom:'1px solid var(--border-light)' }}>
                        <span style={{ fontSize:12,fontWeight:700,color:'var(--text-tertiary)',width:20 }}>#{i+1}</span>
                        <div style={{ flex:1,minWidth:0 }}>
                          <div style={{ fontSize:13,fontWeight:500,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap' }}>{c.from_name || c.from_email}</div>
                          <div style={{ fontSize:11,color:'var(--text-tertiary)' }}>{c.from_email}</div>
                        </div>
                        <span style={{ fontSize:12,fontWeight:600,color:'var(--success)',background:'var(--success-light)',padding:'2px 8px',borderRadius:12 }}>{c.received_count}x</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Domain distribution */}
              {contactStats?.domain_distribution?.length > 0 && (
                <div style={{ background:'var(--bg-secondary)',border:'1px solid var(--border-color)',borderRadius:'var(--radius)',padding:16,marginBottom:24 }}>
                  <h3 style={{ fontSize:13,fontWeight:700,marginBottom:12,color:'var(--text-secondary)',textTransform:'uppercase',letterSpacing:0.5,display:'flex',alignItems:'center',gap:6 }}>
                    <Globe size={14} /> Distribuição por Domínio
                  </h3>
                  <div style={{ display:'flex',flexWrap:'wrap',gap:8 }}>
                    {contactStats.domain_distribution.map((d, i) => (
                      <div key={i} style={{ display:'flex',alignItems:'center',gap:6,padding:'6px 12px',background:'var(--bg-tertiary)',borderRadius:20,fontSize:12 }}>
                        <AtSign size={12} color="var(--text-tertiary)" />
                        <span style={{ fontWeight:600 }}>{d.domain}</span>
                        <span style={{ color:'var(--text-tertiary)' }}>({d.count})</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Contacts table */}
              <div style={{ background:'var(--bg-secondary)',border:'1px solid var(--border-color)',borderRadius:'var(--radius)',overflow:'hidden' }}>
                <div style={{ display:'flex',alignItems:'center',gap:8,padding:'12px 16px',borderBottom:'1px solid var(--border-color)',flexWrap:'wrap' }}>
                  <div style={{ flex:1,position:'relative',minWidth:200 }}>
                    <Search size={14} style={{ position:'absolute',left:10,top:'50%',transform:'translateY(-50%)',color:'var(--text-tertiary)' }} />
                    <input className="form-input" placeholder="Buscar contatos..."
                      style={{ paddingLeft:32,fontSize:13 }}
                      value={contactsSearch}
                      onChange={e => setContactsSearch(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && handleContactsSearch()} />
                  </div>
                  <select value={contactsSort} onChange={e => setContactsSort(e.target.value)}
                    style={{ fontSize:12,padding:'6px 8px',border:'1px solid var(--border-color)',borderRadius:6,background:'var(--bg-tertiary)',color:'var(--text-primary)',fontFamily:'var(--font-primary)' }}>
                    <option value="times_contacted">Mais contatados</option>
                    <option value="last_contacted">Último contato</option>
                    <option value="name">Nome</option>
                    <option value="email">Email</option>
                    <option value="created_at">Data de criação</option>
                  </select>
                  <button className="toolbar-btn" onClick={() => setContactsSortOrder(o => o === 'DESC' ? 'ASC' : 'DESC')}
                    style={{ padding:'6px 8px',fontSize:11,border:'1px solid var(--border-color)',borderRadius:6 }}>
                    {contactsSortOrder === 'DESC' ? '↓' : '↑'}
                  </button>
                  <span style={{ fontSize:12,color:'var(--text-tertiary)' }}>
                    {contactsTotal} contato(s)
                    {activeAccount?.email && <span style={{ marginLeft:4,fontSize:10,opacity:0.7 }}>· {activeAccount.email}</span>}
                  </span>
                </div>
                {contactsLoading ? (
                  <div style={{ display:'flex',alignItems:'center',justifyContent:'center',gap:10,padding:40,color:'var(--text-tertiary)' }}>
                    <Loader size={20} className="spin-icon" /> Carregando contatos...
                  </div>
                ) : contactsList.length === 0 ? (
                  <div style={{ textAlign:'center',padding:40,color:'var(--text-tertiary)' }}>
                    <Users size={40} style={{ opacity:0.3,marginBottom:8 }} />
                    <p style={{ fontSize:14 }}>Nenhum contato encontrado</p>
                  </div>
                ) : (
                  <>
                    {contactsList.map(c => (
                      <div key={c.id} style={{ display:'flex',alignItems:'center',gap:12,padding:'10px 16px',borderBottom:'1px solid var(--border-light)',transition:'background 0.15s' }}>
                        <div style={{ width:36,height:36,borderRadius:'50%',background:'linear-gradient(135deg,var(--accent),var(--purple))',display:'flex',alignItems:'center',justifyContent:'center',fontSize:13,fontWeight:700,color:'#fff',flexShrink:0 }}>
                          {(c.name || c.email || '?').substring(0,1).toUpperCase()}
                        </div>
                        <div style={{ flex:1,minWidth:0 }}>
                          <div style={{ fontSize:13,fontWeight:600,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap' }}>{c.name || '(sem nome)'}</div>
                          <div style={{ fontSize:12,color:'var(--text-tertiary)' }}>{c.email}</div>
                        </div>
                        <div style={{ textAlign:'right',flexShrink:0 }}>
                          <div style={{ fontSize:12,fontWeight:600 }}>{c.times_contacted}x contatado</div>
                          <div style={{ fontSize:11,color:'var(--text-tertiary)' }}>
                            {c.last_contacted ? `Último: ${new Date(c.last_contacted).toLocaleDateString('pt-BR')}` : 'Nunca'}
                          </div>
                        </div>
                        <button className="icon-btn" style={{ width:30,height:30 }} onClick={() => openEditContact(c)} title="Editar">
                          <Edit3 size={14} />
                        </button>
                        <button className="icon-btn" style={{ width:30,height:30 }} onClick={() => removeContact(c.id)} title="Remover">
                          <Trash2 size={14} color="var(--danger)" />
                        </button>
                      </div>
                    ))}
                    {contactsTotalPages > 1 && (
                      <div style={{ display:'flex',alignItems:'center',justifyContent:'center',gap:8,padding:12 }}>
                        <button className="btn-secondary" disabled={contactsPage<=1} onClick={() => loadContacts(contactsPage-1)} style={{ padding:'6px 12px',fontSize:12 }}>Anterior</button>
                        <span style={{ fontSize:12,color:'var(--text-tertiary)' }}>{contactsPage} de {contactsTotalPages}</span>
                        <button className="btn-secondary" disabled={contactsPage>=contactsTotalPages} onClick={() => loadContacts(contactsPage+1)} style={{ padding:'6px 12px',fontSize:12 }}>Próxima</button>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ===== DASHBOARD SECTION ===== */}
        {currentSection === 'dashboard' && (
          <div style={{ flex:1,overflow:'auto',padding:24 }}>
            <div style={{ maxWidth:1100,margin:'0 auto' }}>
              <h2 style={{ fontSize:20,fontWeight:700,marginBottom:20,display:'flex',alignItems:'center',gap:10 }}>
                <BarChart3 size={22} /> Dashboard
                <button className="toolbar-btn" onClick={loadDashboard} style={{ marginLeft:8 }}>
                  <RefreshCw size={14} className={dashboardLoading ? 'spin-icon' : ''} />
                </button>
              </h2>

              {dashboardLoading && !dashboardData ? (
                <div style={{ display:'flex',alignItems:'center',justifyContent:'center',gap:10,padding:60,color:'var(--text-tertiary)' }}>
                  <Loader size={24} className="spin-icon" /> Carregando dashboard...
                </div>
              ) : dashboardData ? (
                <>
                  {/* Summary Cards */}
                  <div style={{ display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(160px,1fr))',gap:12,marginBottom:24 }}>
                    {[
                      { label:'Contas', value:dashboardData.summary.total_accounts, icon:<UserCheck size={18} />, color:'var(--accent)' },
                      { label:'Total Emails', value:dashboardData.summary.total_emails.toLocaleString(), icon:<Mail size={18} />, color:'var(--text-primary)' },
                      { label:'Não Lidos', value:dashboardData.summary.total_unread, icon:<MailOpen size={18} />, color:'var(--warning)' },
                      { label:'Favoritos', value:dashboardData.summary.total_starred, icon:<Star size={18} />, color:'var(--star-color)' },
                      { label:'Enviados', value:dashboardData.summary.total_sent, icon:<Send size={18} />, color:'var(--success)' },
                      { label:'Spam', value:dashboardData.summary.total_spam, icon:<ShieldAlert size={18} />, color:'var(--danger)' },
                      { label:'Lixeira', value:dashboardData.summary.total_trash, icon:<Trash2 size={18} />, color:'var(--text-tertiary)' },
                      { label:'Contatos', value:dashboardData.summary.total_contacts, icon:<Users size={18} />, color:'var(--purple)' },
                      { label:'Armazenamento', value:dashboardData.summary.total_size_formatted, icon:<HardDrive size={18} />, color:'var(--accent)' },
                      { label:'Spam (7 dias)', value:dashboardData.summary.recent_spam_7d, icon:<AlertTriangle size={18} />, color:'var(--danger)' },
                    ].map((card, i) => (
                      <div key={i} style={{ background:'var(--bg-secondary)',border:'1px solid var(--border-color)',borderRadius:'var(--radius)',padding:16,display:'flex',flexDirection:'column',gap:8 }}>
                        <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between' }}>
                          <span style={{ fontSize:10,fontWeight:700,color:'var(--text-tertiary)',textTransform:'uppercase',letterSpacing:0.5 }}>{card.label}</span>
                          <span style={{ color:card.color,opacity:0.6 }}>{card.icon}</span>
                        </div>
                        <div style={{ fontSize:24,fontWeight:700,color:card.color }}>{card.value}</div>
                      </div>
                    ))}
                  </div>

                  {/* Accounts breakdown */}
                  <div style={{ background:'var(--bg-secondary)',border:'1px solid var(--border-color)',borderRadius:'var(--radius)',padding:16,marginBottom:24 }}>
                    <h3 style={{ fontSize:13,fontWeight:700,marginBottom:16,color:'var(--text-secondary)',textTransform:'uppercase',letterSpacing:0.5 }}>
                      Emails por Conta
                    </h3>
                    {dashboardData.accounts.map((acc, i) => (
                      <div key={i} style={{ display:'flex',alignItems:'center',gap:16,padding:'12px 0',borderBottom:i < dashboardData.accounts.length-1 ? '1px solid var(--border-light)' : 'none' }}>
                        <div style={{ width:40,height:40,borderRadius:'50%',background:'linear-gradient(135deg,var(--accent),var(--purple))',display:'flex',alignItems:'center',justifyContent:'center',fontSize:14,fontWeight:700,color:'#fff',flexShrink:0 }}>
                          {(acc.name || '?').substring(0,1).toUpperCase()}
                        </div>
                        <div style={{ flex:1,minWidth:0 }}>
                          <div style={{ fontSize:14,fontWeight:600 }}>{acc.name}</div>
                          <div style={{ fontSize:12,color:'var(--text-tertiary)' }}>{acc.email}</div>
                          {acc.imap_host && <div style={{ fontSize:11,color:'var(--text-tertiary)' }}>IMAP: {acc.imap_host}</div>}
                        </div>
                        <div style={{ display:'flex',gap:16,alignItems:'center',flexWrap:'wrap' }}>
                          <div style={{ textAlign:'center' }}>
                            <div style={{ fontSize:18,fontWeight:700,color:'var(--text-primary)' }}>{acc.total_emails.toLocaleString()}</div>
                            <div style={{ fontSize:10,color:'var(--text-tertiary)',textTransform:'uppercase' }}>Emails</div>
                          </div>
                          <div style={{ textAlign:'center' }}>
                            <div style={{ fontSize:18,fontWeight:700,color:'var(--warning)' }}>{acc.unread}</div>
                            <div style={{ fontSize:10,color:'var(--text-tertiary)',textTransform:'uppercase' }}>Não lidos</div>
                          </div>
                          <div style={{ textAlign:'center' }}>
                            <div style={{ fontSize:18,fontWeight:700,color:'var(--success)' }}>{acc.sent}</div>
                            <div style={{ fontSize:10,color:'var(--text-tertiary)',textTransform:'uppercase' }}>Enviados</div>
                          </div>
                          <div style={{ textAlign:'center' }}>
                            <div style={{ fontSize:14,fontWeight:600,color:'var(--accent)' }}>{acc.estimated_size_formatted}</div>
                            <div style={{ fontSize:10,color:'var(--text-tertiary)',textTransform:'uppercase' }}>Tamanho</div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Top senders + domains with account filter */}
                  <div style={{ background:'var(--bg-secondary)',border:'1px solid var(--border-color)',borderRadius:'var(--radius)',padding:16,marginBottom:24 }}>
                    <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:14 }}>
                      <h3 style={{ fontSize:13,fontWeight:700,color:'var(--text-secondary)',textTransform:'uppercase',letterSpacing:0.5,margin:0 }}>
                        Top Remetentes & Domínios
                      </h3>
                      <div style={{ display:'flex',alignItems:'center',gap:8 }}>
                        {dashTopLoading && <Loader size={13} className="spin-icon" color="var(--text-tertiary)" />}
                        <select value={dashTopAccountFilter}
                          onChange={e => { setDashTopAccountFilter(e.target.value); loadDashTopByAccount(e.target.value); }}
                          style={{ padding:'5px 10px',fontSize:11,border:'1px solid var(--border-color)',borderRadius:6,background:'var(--bg-tertiary)',color:'var(--text-primary)',fontFamily:'var(--font-primary)',cursor:'pointer' }}>
                          <option value="all">Todas as contas</option>
                          {accounts.map(acc => <option key={acc.id} value={acc.id}>{acc.email}</option>)}
                        </select>
                      </div>
                    </div>
                    {(() => {
                      const senders = dashTopData?.top_senders || dashboardData.top_senders || [];
                      const domains = dashTopData?.top_domains || dashboardData.top_domains || [];
                      return (
                        <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:16 }}>
                          <div>
                            <div style={{ fontSize:11,fontWeight:600,color:'var(--text-tertiary)',marginBottom:8 }}>REMETENTES</div>
                            {senders.map((s, i) => (
                              <div key={i} style={{ display:'flex',alignItems:'center',gap:8,padding:'5px 0',borderBottom:'1px solid var(--border-light)' }}>
                                <span style={{ fontSize:11,fontWeight:700,color:'var(--text-tertiary)',width:18 }}>#{i+1}</span>
                                <div style={{ flex:1,minWidth:0,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',fontSize:12.5 }}>{s.from_name || s.from_email}</div>
                                <span style={{ fontSize:12,fontWeight:600,color:'var(--accent)' }}>{s.count}</span>
                              </div>
                            ))}
                            {senders.length === 0 && <div style={{ fontSize:12,color:'var(--text-tertiary)',padding:8 }}>Nenhum dado</div>}
                          </div>
                          <div>
                            <div style={{ fontSize:11,fontWeight:600,color:'var(--text-tertiary)',marginBottom:8 }}>DOMÍNIOS</div>
                            {domains.map((d, i) => (
                              <div key={i} style={{ display:'flex',alignItems:'center',gap:8,padding:'5px 0',borderBottom:'1px solid var(--border-light)' }}>
                                <span style={{ fontSize:11,fontWeight:700,color:'var(--text-tertiary)',width:18 }}>#{i+1}</span>
                                <Globe size={12} color="var(--text-tertiary)" />
                                <div style={{ flex:1,fontSize:12.5 }}>{d.domain}</div>
                                <span style={{ fontSize:12,fontWeight:600,color:'var(--purple)' }}>{d.count}</span>
                              </div>
                            ))}
                            {domains.length === 0 && <div style={{ fontSize:12,color:'var(--text-tertiary)',padding:8 }}>Nenhum dado</div>}
                          </div>
                        </div>
                      );
                    })()}
                  </div>

                  {/* Volume chart with hover tooltip + account filter */}
                  {(dashVolumeData?.emails_by_day?.length > 0 || dashboardData.emails_by_day?.length > 0) && (() => {
                    const volumeData = dashVolumeData?.emails_by_day || dashboardData.emails_by_day || [];
                    const totalVol = volumeData.reduce((s,d) => s + parseInt(d.count), 0);
                    return (
                    <div style={{ background:'var(--bg-secondary)',border:'1px solid var(--border-color)',borderRadius:'var(--radius)',padding:16,marginBottom:24 }}>
                      <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:12,flexWrap:'wrap',gap:8 }}>
                        <h3 style={{ fontSize:13,fontWeight:700,color:'var(--text-secondary)',textTransform:'uppercase',letterSpacing:0.5,margin:0 }}>
                          Volume de Emails (30 dias)
                        </h3>
                        <div style={{ display:'flex',alignItems:'center',gap:10 }}>
                          <div style={{ fontSize:12,color:'var(--text-tertiary)' }}>
                            Total: <strong style={{ color:'var(--text-primary)' }}>{totalVol}</strong>
                            {' · '}Média: <strong style={{ color:'var(--text-primary)' }}>{volumeData.length ? Math.round(totalVol / volumeData.length) : 0}/dia</strong>
                          </div>
                          {dashVolumeLoading && <Loader size={13} className="spin-icon" color="var(--text-tertiary)" />}
                          <select value={dashVolumeFilter}
                            onChange={e => { setDashVolumeFilter(e.target.value); loadDashVolumeByAccount(e.target.value); }}
                            style={{ padding:'4px 8px',fontSize:11,border:'1px solid var(--border-color)',borderRadius:6,background:'var(--bg-tertiary)',color:'var(--text-primary)',fontFamily:'var(--font-primary)',cursor:'pointer' }}>
                            <option value="all">Todas as contas</option>
                            {accounts.map(acc => <option key={acc.id} value={acc.id}>{acc.email}</option>)}
                          </select>
                        </div>
                      </div>
                      {volumeData.length > 0 ? (
                      <>
                      <div style={{ display:'flex',alignItems:'flex-end',gap:2,height:120,position:'relative' }}
                        onMouseLeave={() => setChartHoverIdx(null)}>
                        {(() => {
                          const max = Math.max(1, ...volumeData.map(d => parseInt(d.count)));
                          return volumeData.map((d, i) => {
                            const isHovered = chartHoverIdx === i;
                            return (
                              <div key={i} style={{ flex:1,display:'flex',flexDirection:'column',alignItems:'center',position:'relative',cursor:'pointer' }}
                                onMouseEnter={() => setChartHoverIdx(i)}>
                                {isHovered && (
                                  <div style={{
                                    position:'absolute',bottom:'100%',marginBottom:6,
                                    background:'var(--bg-sidebar)',color:'#fff',padding:'4px 8px',
                                    borderRadius:6,fontSize:11,fontWeight:600,whiteSpace:'nowrap',
                                    zIndex:10,boxShadow:'var(--shadow-md)',
                                    left:'50%',transform:'translateX(-50%)',
                                  }}>
                                    <div>{new Date(d.day + 'T12:00:00').toLocaleDateString('pt-BR', { day:'2-digit',month:'short' })}</div>
                                    <div style={{ textAlign:'center',color:'var(--accent)',fontSize:13 }}>{d.count}</div>
                                  </div>
                                )}
                                <div style={{
                                  width:'100%',maxWidth:20,borderRadius:'3px 3px 0 0',
                                  background: isHovered ? 'var(--accent)' : 'linear-gradient(to top, var(--accent), var(--purple))',
                                  height: `${Math.max(4, (parseInt(d.count) / max) * 100)}px`,
                                  transition:'height 0.3s, opacity 0.15s',
                                  opacity: isHovered ? 1 : (0.6 + (parseInt(d.count) / max) * 0.4),
                                }} />
                              </div>
                            );
                          });
                        })()}
                      </div>
                      <div style={{ display:'flex',justifyContent:'space-between',marginTop:6,fontSize:10,color:'var(--text-tertiary)' }}>
                        <span>{new Date(volumeData[0]?.day + 'T12:00:00').toLocaleDateString('pt-BR', { day:'2-digit',month:'short' })}</span>
                        <span>{new Date(volumeData[volumeData.length-1]?.day + 'T12:00:00').toLocaleDateString('pt-BR', { day:'2-digit',month:'short' })}</span>
                      </div>
                      </>
                      ) : (
                        <div style={{ padding:20,textAlign:'center',color:'var(--text-tertiary)',fontSize:13 }}>Sem dados para esta conta</div>
                      )}
                    </div>
                    );
                  })()}

                  {/* Tags usage */}
                  {dashboardData.tags_usage?.length > 0 && (
                    <div style={{ background:'var(--bg-secondary)',border:'1px solid var(--border-color)',borderRadius:'var(--radius)',padding:16 }}>
                      <h3 style={{ fontSize:13,fontWeight:700,marginBottom:12,color:'var(--text-secondary)',textTransform:'uppercase',letterSpacing:0.5 }}>
                        Uso de Tags
                      </h3>
                      <div style={{ display:'flex',flexWrap:'wrap',gap:8 }}>
                        {dashboardData.tags_usage.map((t, i) => (
                          <div key={i} style={{ display:'flex',alignItems:'center',gap:6,padding:'6px 12px',background:'var(--bg-tertiary)',borderRadius:20,fontSize:12 }}>
                            <span style={{ width:8,height:8,borderRadius:'50%',background:t.color || 'var(--accent)' }} />
                            <span style={{ fontWeight:600 }}>{t.name}</span>
                            <span style={{ color:'var(--text-tertiary)' }}>{t.usage_count}x</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div style={{ textAlign:'center',padding:60,color:'var(--text-tertiary)' }}>
                  <BarChart3 size={48} style={{ opacity:0.3,marginBottom:12 }} />
                  <p style={{ fontSize:14 }}>Não foi possível carregar o dashboard</p>
                  <button className="btn-primary" onClick={loadDashboard} style={{ marginTop:12 }}>Tentar novamente</button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ===== CLEANUP / LIMPEZA DE EMAILS ===== */}
        {currentSection === 'cleanup' && hasPerm('perm_cleanup') && (
          <div style={{ flex:1,overflow:'auto',padding:24 }}>
            <div style={{ maxWidth:1100,margin:'0 auto' }}>
              <h2 style={{ fontSize:20,fontWeight:700,marginBottom:4,display:'flex',alignItems:'center',gap:10 }}>
                <Sparkles size={22} color="var(--accent)" /> Limpeza de Emails
                <button className="toolbar-btn" onClick={loadCleanupStats} style={{ marginLeft:8 }}>
                  <RefreshCw size={14} className={cleanupLoading ? 'spin-icon' : ''} />
                </button>
              </h2>
              <p style={{ fontSize:13,color:'var(--text-tertiary)',marginBottom:20 }}>
                Analise e limpe sua caixa de entrada. Remova emails antigos, newsletters, spam suspeito e mais.
              </p>

              {cleanupLoading && !cleanupData ? (
                <div style={{ display:'flex',alignItems:'center',justifyContent:'center',gap:10,padding:60,color:'var(--text-tertiary)' }}>
                  <Loader size={24} className="spin-icon" /> Analisando caixa de entrada...
                </div>
              ) : cleanupData ? (
                <>
                  {/* ── Summary cards ── */}
                  <div style={{ display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(180px,1fr))',gap:12,marginBottom:24 }}>
                    <div style={{ background:'var(--bg-secondary)',border:'1px solid var(--border-color)',borderRadius:10,padding:16 }}>
                      <div style={{ fontSize:10,fontWeight:700,color:'var(--text-tertiary)',textTransform:'uppercase',letterSpacing:0.5,marginBottom:6 }}>Caixa de Entrada</div>
                      <div style={{ fontSize:28,fontWeight:800,color:'var(--accent)' }}>{cleanupData.total_inbox?.toLocaleString()}</div>
                      <div style={{ fontSize:11,color:'var(--text-tertiary)',marginTop:2 }}>emails</div>
                    </div>
                    <div style={{ background:'var(--bg-secondary)',border:'1px solid var(--border-color)',borderRadius:10,padding:16 }}>
                      <div style={{ fontSize:10,fontWeight:700,color:'var(--text-tertiary)',textTransform:'uppercase',letterSpacing:0.5,marginBottom:6 }}>Spam Suspeito</div>
                      <div style={{ fontSize:28,fontWeight:800,color:'var(--danger)' }}>{cleanupData.suspected_spam_count || 0}</div>
                      <div style={{ fontSize:11,color:'var(--text-tertiary)',marginTop:2 }}>detectados na inbox</div>
                    </div>
                    <div style={{ background:'var(--bg-secondary)',border:'1px solid var(--border-color)',borderRadius:10,padding:16 }}>
                      <div style={{ fontSize:10,fontWeight:700,color:'var(--text-tertiary)',textTransform:'uppercase',letterSpacing:0.5,marginBottom:6 }}>Newsletters</div>
                      <div style={{ fontSize:28,fontWeight:800,color:'var(--warning)' }}>{cleanupData.newsletters?.reduce((s,n) => s + parseInt(n.count), 0) || 0}</div>
                      <div style={{ fontSize:11,color:'var(--text-tertiary)',marginTop:2 }}>emails de newsletters</div>
                    </div>
                    <div style={{ background:'var(--bg-secondary)',border:'1px solid var(--border-color)',borderRadius:10,padding:16 }}>
                      <div style={{ fontSize:10,fontWeight:700,color:'var(--text-tertiary)',textTransform:'uppercase',letterSpacing:0.5,marginBottom:6 }}>Lidos +30 dias</div>
                      <div style={{ fontSize:28,fontWeight:800,color:'var(--purple)' }}>{cleanupData.read_old_30d || 0}</div>
                      <div style={{ fontSize:11,color:'var(--text-tertiary)',marginTop:2 }}>candidatos a arquivo</div>
                    </div>
                  </div>

                  {/* ── Spam Suspeito ── */}
                  {(cleanupData.suspected_spam?.length > 0) && (
                    <div style={{ background:'var(--bg-secondary)',border:'1px solid var(--border-color)',borderRadius:10,padding:16,marginBottom:16 }}>
                      <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:12 }}>
                        <div style={{ display:'flex',alignItems:'center',gap:8 }}>
                          <ShieldAlert size={18} color="var(--danger)" />
                          <span style={{ fontSize:14,fontWeight:700 }}>Spam Suspeito na Caixa de Entrada</span>
                          <span style={{ fontSize:11,background:'var(--danger-light)',color:'var(--danger)',padding:'2px 8px',borderRadius:10,fontWeight:600 }}>{cleanupData.suspected_spam.length}</span>
                        </div>
                        <button className="btn-primary" disabled={cleanupActionLoading === 'spam_all'}
                          onClick={() => {
                            if (!window.confirm(`Mover ${cleanupData.suspected_spam.length} email(s) suspeito(s) para Spam?`)) return;
                            executeCleanup('suspected_spam', { uids: cleanupData.suspected_spam.map(s => s.uid), action: 'spam', folder: 'inbox' }, 'spam_all', 'Spam suspeito');
                          }}
                          style={{ padding:'6px 14px',fontSize:12,display:'flex',alignItems:'center',gap:5,background:'var(--danger)' }}>
                          {cleanupActionLoading === 'spam_all' ? <Loader size={13} className="spin-icon" /> : <Ban size={13} />}
                          Mover todos para Spam
                        </button>
                      </div>
                      <div style={{ maxHeight:220,overflow:'auto' }}>
                        {cleanupData.suspected_spam.slice(0, 20).map((spam, i) => (
                          <div key={i} style={{ display:'flex',alignItems:'center',gap:8,padding:'6px 0',borderBottom:'1px solid var(--border-light)',fontSize:12 }}>
                            <div style={{ flex:1,minWidth:0 }}>
                              <div style={{ fontWeight:600,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap' }}>{spam.from_name || spam.from_email}</div>
                              <div style={{ color:'var(--text-tertiary)',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap' }}>{spam.subject}</div>
                            </div>
                            <div style={{ display:'flex',gap:3,flexWrap:'wrap',flexShrink:0,maxWidth:180 }}>
                              {spam.reasons?.slice(0, 2).map((r, j) => (
                                <span key={j} style={{ fontSize:9,background:'var(--danger-light)',color:'var(--danger)',padding:'1px 6px',borderRadius:3,whiteSpace:'nowrap' }}>{r}</span>
                              ))}
                            </div>
                            <button className="btn-secondary" disabled={!!cleanupActionLoading}
                              onClick={async () => {
                                setCleanupActionLoading(`spam_ind_${i}`);
                                try {
                                  await api.cleanupDelete(activeAccount?.id, 'by_uids', { uids: [spam.uid], action: 'spam', folder: 'inbox' });
                                  toast(`Email movido para spam`, 'success');
                                  setCleanupData(prev => prev ? {
                                    ...prev,
                                    suspected_spam: prev.suspected_spam.filter((_, idx) => idx !== i),
                                    suspected_spam_count: (prev.suspected_spam_count || 1) - 1,
                                  } : prev);
                                  refreshCounts();
                                } catch (err) { toast('Erro ao mover', 'error'); }
                                finally { setCleanupActionLoading(null); }
                              }}
                              style={{ padding:'3px 8px',fontSize:10,whiteSpace:'nowrap',display:'flex',alignItems:'center',gap:3,flexShrink:0,color:'var(--danger)',borderColor:'var(--danger)' }}>
                              {cleanupActionLoading === `spam_ind_${i}` ? <Loader size={10} className="spin-icon" /> : <Ban size={10} />}
                              Spam
                            </button>
                            <button className="btn-secondary" disabled={!!cleanupActionLoading}
                              onClick={async () => {
                                setCleanupActionLoading(`safe_${i}`);
                                try {
                                  const domain = spam.from_email.includes('@') ? spam.from_email.split('@')[1] : '';
                                  await api.addToWhitelist(spam.from_email, domain);
                                  toast(`${spam.from_email} adicionado à lista segura`, 'success');
                                  setCleanupData(prev => prev ? {
                                    ...prev,
                                    suspected_spam: prev.suspected_spam.filter((_, idx) => idx !== i),
                                    suspected_spam_count: (prev.suspected_spam_count || 1) - 1,
                                  } : prev);
                                } catch (err) { toast('Erro ao salvar', 'error'); }
                                finally { setCleanupActionLoading(null); }
                              }}
                              style={{ padding:'3px 8px',fontSize:10,whiteSpace:'nowrap',display:'flex',alignItems:'center',gap:3,flexShrink:0 }}>
                              {cleanupActionLoading === `safe_${i}` ? <Loader size={10} className="spin-icon" /> : <Check size={10} />}
                              Seguro
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* ── Newsletters / Automáticos ── */}
                  {(cleanupData.newsletters?.length > 0) && (
                    <div style={{ background:'var(--bg-secondary)',border:'1px solid var(--border-color)',borderRadius:10,padding:16,marginBottom:16 }}>
                      <div style={{ display:'flex',alignItems:'center',gap:8,marginBottom:12 }}>
                        <Mail size={18} color="var(--warning)" />
                        <span style={{ fontSize:14,fontWeight:700 }}>Newsletters e Automáticos</span>
                        <span style={{ fontSize:11,background:'var(--warning-light)',color:'var(--warning)',padding:'2px 8px',borderRadius:10,fontWeight:600 }}>
                          {cleanupData.newsletters.reduce((s,n) => s + parseInt(n.count), 0)}
                        </span>
                      </div>
                      <div style={{ maxHeight:260,overflow:'auto' }}>
                        {cleanupData.newsletters.map((nl, i) => (
                          <div key={i} style={{ display:'flex',alignItems:'center',gap:10,padding:'8px 0',borderBottom:'1px solid var(--border-light)' }}>
                            <div style={{ width:32,height:32,borderRadius:'50%',background:'var(--warning-light)',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0 }}>
                              <Mail size={14} color="var(--warning)" />
                            </div>
                            <div style={{ flex:1,minWidth:0 }}>
                              <div style={{ fontSize:13,fontWeight:600,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap' }}>{nl.from_name || nl.from_email}</div>
                              <div style={{ fontSize:11,color:'var(--text-tertiary)' }}>{nl.from_email}</div>
                            </div>
                            <span style={{ fontSize:12,fontWeight:700,color:'var(--text-secondary)',marginRight:8 }}>{nl.count}</span>
                            <button className="btn-secondary" disabled={cleanupActionLoading === `nl_${i}`}
                              onClick={() => {
                                if (!window.confirm(`Excluir ${nl.count} email(s) de ${nl.from_email}?`)) return;
                                executeCleanup('newsletters', { sender: nl.from_email, action: 'trash', folder: 'inbox' }, `nl_${i}`, nl.from_email);
                              }}
                              style={{ padding:'4px 10px',fontSize:11,display:'flex',alignItems:'center',gap:4,whiteSpace:'nowrap' }}>
                              {cleanupActionLoading === `nl_${i}` ? <Loader size={12} className="spin-icon" /> : <Trash2 size={12} />}
                              Excluir
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* ── Emails por Idade ── */}
                  <div style={{ background:'var(--bg-secondary)',border:'1px solid var(--border-color)',borderRadius:10,padding:16,marginBottom:16 }}>
                    <div style={{ display:'flex',alignItems:'center',gap:8,marginBottom:12 }}>
                      <Calendar size={18} color="var(--purple)" />
                      <span style={{ fontSize:14,fontWeight:700 }}>Emails Antigos</span>
                    </div>
                    <div style={{ display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(220px,1fr))',gap:10 }}>
                      {(cleanupData.age_breakdown || []).map((age, i) => (
                        <div key={i} style={{ display:'flex',alignItems:'center',justifyContent:'space-between',padding:12,background:'var(--bg-tertiary)',borderRadius:8,border:'1px solid var(--border-light)' }}>
                          <div>
                            <div style={{ fontSize:13,fontWeight:600 }}>+{age.days} dias</div>
                            <div style={{ fontSize:22,fontWeight:800,color:'var(--purple)' }}>{age.count}</div>
                          </div>
                          {age.count > 0 && (
                            <button className="btn-secondary" disabled={cleanupActionLoading === `age_${age.days}`}
                              onClick={() => {
                                if (!window.confirm(`Mover ${age.count} email(s) com mais de ${age.days} dias para a lixeira?`)) return;
                                executeCleanup('by_age', { days: age.days, action: 'trash', folder: 'inbox' }, `age_${age.days}`, `+${age.days} dias`);
                              }}
                              style={{ padding:'5px 10px',fontSize:11,display:'flex',alignItems:'center',gap:4 }}>
                              {cleanupActionLoading === `age_${age.days}` ? <Loader size={12} className="spin-icon" /> : <Trash2 size={12} />}
                              Limpar
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* ── Top Remetentes ── */}
                  <div style={{ background:'var(--bg-secondary)',border:'1px solid var(--border-color)',borderRadius:10,padding:16,marginBottom:16 }}>
                    <div style={{ display:'flex',alignItems:'center',gap:8,marginBottom:12 }}>
                      <Users size={18} color="var(--accent)" />
                      <span style={{ fontSize:14,fontWeight:700 }}>Remetentes com Mais Emails</span>
                    </div>
                    <div style={{ maxHeight:400,overflow:'auto' }}>
                      {(cleanupData.top_senders || []).map((sender, i) => {
                        const isExpanded = cleanupExpandedSender === sender.from_email;
                        return (
                          <div key={i} style={{ borderBottom:'1px solid var(--border-light)' }}>
                            <div style={{ display:'flex',alignItems:'center',gap:10,padding:'8px 0',cursor:'pointer' }}
                              onClick={() => setCleanupExpandedSender(isExpanded ? null : sender.from_email)}>
                              <div style={{ width:8,height:8,borderRadius:'50%',background: i < 3 ? 'var(--danger)' : i < 7 ? 'var(--warning)' : 'var(--text-tertiary)',flexShrink:0 }} />
                              <div style={{ flex:1,minWidth:0 }}>
                                <div style={{ fontSize:13,fontWeight:600,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap' }}>{sender.from_name || sender.from_email}</div>
                                <div style={{ fontSize:11,color:'var(--text-tertiary)' }}>{sender.from_email}</div>
                              </div>
                              <div style={{ textAlign:'right',flexShrink:0,marginRight:8 }}>
                                <div style={{ fontSize:16,fontWeight:800 }}>{sender.count}</div>
                                <div style={{ fontSize:10,color:'var(--text-tertiary)' }}>{sender.unread > 0 ? `${sender.unread} não lido(s)` : 'todos lidos'}</div>
                              </div>
                              <ChevronDown size={14} style={{ color:'var(--text-tertiary)',transition:'transform 0.2s',transform:isExpanded?'rotate(180deg)':'none' }} />
                            </div>
                            {isExpanded && (
                              <div style={{ padding:'4px 0 10px 18px',display:'flex',gap:6,flexWrap:'wrap' }}>
                                <button className="btn-secondary" disabled={!!cleanupActionLoading}
                                  onClick={() => {
                                    if (!window.confirm(`Mover ${sender.count} email(s) de "${sender.from_name || sender.from_email}" para a lixeira?`)) return;
                                    executeCleanup('by_sender', { sender: sender.from_email, action: 'trash', folder: 'inbox' }, `sender_${i}`, sender.from_email);
                                  }}
                                  style={{ padding:'5px 10px',fontSize:11,display:'flex',alignItems:'center',gap:4 }}>
                                  {cleanupActionLoading === `sender_${i}` ? <Loader size={12} className="spin-icon" /> : <Trash2 size={12} />}
                                  Excluir todos ({sender.count})
                                </button>
                                <button className="btn-secondary" disabled={!!cleanupActionLoading}
                                  onClick={() => {
                                    if (!window.confirm(`Mover ${sender.count} email(s) de "${sender.from_name || sender.from_email}" para Spam?`)) return;
                                    executeCleanup('by_sender', { sender: sender.from_email, action: 'spam', folder: 'inbox' }, `sender_spam_${i}`, sender.from_email);
                                  }}
                                  style={{ padding:'5px 10px',fontSize:11,display:'flex',alignItems:'center',gap:4 }}>
                                  {cleanupActionLoading === `sender_spam_${i}` ? <Loader size={12} className="spin-icon" /> : <Ban size={12} />}
                                  Marcar como Spam
                                </button>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* ── Esvaziar Lixeira ── */}
                  {cleanupData.total_trash > 0 && (
                    <div style={{ background:'var(--danger-light)',border:'1px solid var(--danger)',borderRadius:10,padding:16 }}>
                      <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between' }}>
                        <div style={{ display:'flex',alignItems:'center',gap:10 }}>
                          <Trash2 size={20} color="var(--danger)" />
                          <div>
                            <div style={{ fontSize:14,fontWeight:700,color:'var(--danger)' }}>Esvaziar Lixeira</div>
                            <div style={{ fontSize:12,color:'var(--text-secondary)' }}>{cleanupData.total_trash} email(s) na lixeira</div>
                          </div>
                        </div>
                        <button className="btn-primary" disabled={cleanupActionLoading === 'empty_trash'}
                          onClick={() => {
                            if (!window.confirm(`Excluir permanentemente ${cleanupData.total_trash} email(s) da lixeira? Esta ação é irreversível.`)) return;
                            executeCleanup('by_age', { days: 0, action: 'delete', folder: 'trash' }, 'empty_trash', 'Esvaziar lixeira');
                          }}
                          style={{ padding:'7px 16px',fontSize:12,display:'flex',alignItems:'center',gap:6,background:'var(--danger)' }}>
                          {cleanupActionLoading === 'empty_trash' ? <Loader size={14} className="spin-icon" /> : <Trash2 size={14} />}
                          Esvaziar Lixeira
                        </button>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div style={{ textAlign:'center',padding:60,color:'var(--text-tertiary)' }}>
                  <Sparkles size={48} style={{ opacity:0.2,marginBottom:12 }} />
                  <p style={{ fontSize:14 }}>Não foi possível carregar os dados de limpeza</p>
                  <button className="btn-primary" onClick={loadCleanupStats} style={{ marginTop:12 }}>Tentar novamente</button>
                </div>
              )}
            </div>
          </div>
        )}
      </main>
      </>
      )}

      {/* ===== COMPOSE FULL PAGE ===== */}
      <div className={`compose-overlay ${showCompose ? 'active' : ''}`}>
        {/* Header */}
        <div className="compose-header">
          <div className="compose-header-left">
            <button className="compose-close" onClick={closeCompose}>
              <ArrowLeft size={18} />
            </button>
            <span className="compose-title">{editingDraftId ? 'Editar Rascunho' : 'Nova Mensagem'}</span>
            {activeAccount && (
              <span style={{ fontSize:12,color:'var(--text-tertiary)',fontWeight:400 }}>
                — {activeAccount.email}
              </span>
            )}
          </div>
          <div style={{ display:'flex',gap:8,alignItems:'center' }}>
            <button className="send-btn" onClick={sendEmail} disabled={sendingEmail}
              style={{ opacity:sendingEmail?0.7:1 }}>
              {sendingEmail ? <Loader size={15} className="spin-icon" /> : <Send size={15} />}
              {sendingEmail ? 'Enviando...' : 'Enviar'}
            </button>
          </div>
        </div>

        {/* Compose body */}
        <div className="compose-main">
          {/* To field */}
          <div className="compose-field" onClick={() => document.getElementById('to-input')?.focus()}>
            <span className="compose-label">Para</span>
            <div style={{ flex:1,display:'flex',flexWrap:'wrap',gap:4,alignItems:'center',minHeight:20 }}>
              {compose.to.map(email => (
                <span key={email} className="email-chip">
                  {email}
                  <button className="email-chip-remove" onClick={(e) => { e.stopPropagation(); removeRecipient('to', email); }}>
                    <X size={10} />
                  </button>
                </span>
              ))}
              <input id="to-input" className="compose-input" placeholder={compose.to.length === 0 ? 'destinatario@email.com' : ''}
                style={{ minWidth:150,flex:'1 1 150px' }}
                value={composeToInput}
                onChange={e => handleRecipientInput('to', e.target.value)}
                onKeyDown={e => handleRecipientKeyDown('to', e)}
                onFocus={() => handleRecipientInput('to', composeToInput)}
                onBlur={() => { setTimeout(() => { if (contactsField === 'to') { setContactSuggestions([]); setContactsField(null); } }, 200); }}
              />
            </div>
            <div style={{ display:'flex',gap:4 }}>
              {!showCc && <button className="cc-bcc-toggle" onClick={() => setShowCc(true)}>Cc</button>}
              {!showBcc && <button className="cc-bcc-toggle" onClick={() => setShowBcc(true)}>Cco</button>}
            </div>
            {/* Contacts dropdown for To */}
            {contactsField === 'to' && contactSuggestions.length > 0 && (
              <div className="contacts-dropdown" onMouseDown={e => e.preventDefault()}>
                {contactSuggestions.map((c, i) => (
                  <div key={c.id || c.email} className={`contact-item ${highlightedContact === i ? 'highlighted' : ''}`}
                    onClick={() => selectContact('to', c)}
                    onMouseEnter={() => setHighlightedContact(i)}>
                    <div className="contact-avatar-sm">
                      {(c.name || c.email || '?')[0].toUpperCase()}
                    </div>
                    <div className="contact-info">
                      {c.name && <div className="contact-name">{c.name}</div>}
                      <div className="contact-email-text">{c.email}</div>
                    </div>
                    {c.times_contacted > 1 && (
                      <span className="contact-freq">{c.times_contacted}x</span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Cc field */}
          {showCc && (
            <div className="compose-field" onClick={() => document.getElementById('cc-input')?.focus()}>
              <span className="compose-label">Cc</span>
              <div style={{ flex:1,display:'flex',flexWrap:'wrap',gap:4,alignItems:'center',minHeight:20 }}>
                {compose.cc.map(email => (
                  <span key={email} className="email-chip">
                    {email}
                    <button className="email-chip-remove" onClick={(e) => { e.stopPropagation(); removeRecipient('cc', email); }}>
                      <X size={10} />
                    </button>
                  </span>
                ))}
                <input id="cc-input" className="compose-input" placeholder={compose.cc.length === 0 ? 'cc@email.com' : ''}
                  style={{ minWidth:150,flex:'1 1 150px' }}
                  value={composeCcInput}
                  onChange={e => handleRecipientInput('cc', e.target.value)}
                  onKeyDown={e => handleRecipientKeyDown('cc', e)}
                  onFocus={() => handleRecipientInput('cc', composeCcInput)}
                  onBlur={() => { setTimeout(() => { if (contactsField === 'cc') { setContactSuggestions([]); setContactsField(null); } }, 200); }}
                />
              </div>
              <button className="compose-close" onClick={() => { setShowCc(false); setCompose(p => ({ ...p, cc: [] })); }} style={{ width:24,height:24 }}>
                <X size={14} />
              </button>
              {contactsField === 'cc' && contactSuggestions.length > 0 && (
                <div className="contacts-dropdown" onMouseDown={e => e.preventDefault()}>
                  {contactSuggestions.map((c, i) => (
                    <div key={c.id || c.email} className={`contact-item ${highlightedContact === i ? 'highlighted' : ''}`}
                      onClick={() => selectContact('cc', c)}
                      onMouseEnter={() => setHighlightedContact(i)}>
                      <div className="contact-avatar-sm">{(c.name || c.email || '?')[0].toUpperCase()}</div>
                      <div className="contact-info">
                        {c.name && <div className="contact-name">{c.name}</div>}
                        <div className="contact-email-text">{c.email}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Bcc field */}
          {showBcc && (
            <div className="compose-field" onClick={() => document.getElementById('bcc-input')?.focus()}>
              <span className="compose-label">Cco</span>
              <div style={{ flex:1,display:'flex',flexWrap:'wrap',gap:4,alignItems:'center',minHeight:20 }}>
                {compose.bcc.map(email => (
                  <span key={email} className="email-chip">
                    {email}
                    <button className="email-chip-remove" onClick={(e) => { e.stopPropagation(); removeRecipient('bcc', email); }}>
                      <X size={10} />
                    </button>
                  </span>
                ))}
                <input id="bcc-input" className="compose-input" placeholder={compose.bcc.length === 0 ? 'cco@email.com' : ''}
                  style={{ minWidth:150,flex:'1 1 150px' }}
                  value={composeBccInput}
                  onChange={e => handleRecipientInput('bcc', e.target.value)}
                  onKeyDown={e => handleRecipientKeyDown('bcc', e)}
                  onFocus={() => handleRecipientInput('bcc', composeBccInput)}
                  onBlur={() => { setTimeout(() => { if (contactsField === 'bcc') { setContactSuggestions([]); setContactsField(null); } }, 200); }}
                />
              </div>
              <button className="compose-close" onClick={() => { setShowBcc(false); setCompose(p => ({ ...p, bcc: [] })); }} style={{ width:24,height:24 }}>
                <X size={14} />
              </button>
              {contactsField === 'bcc' && contactSuggestions.length > 0 && (
                <div className="contacts-dropdown" onMouseDown={e => e.preventDefault()}>
                  {contactSuggestions.map((c, i) => (
                    <div key={c.id || c.email} className={`contact-item ${highlightedContact === i ? 'highlighted' : ''}`}
                      onClick={() => selectContact('bcc', c)}
                      onMouseEnter={() => setHighlightedContact(i)}>
                      <div className="contact-avatar-sm">{(c.name || c.email || '?')[0].toUpperCase()}</div>
                      <div className="contact-info">
                        {c.name && <div className="contact-name">{c.name}</div>}
                        <div className="contact-email-text">{c.email}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Subject */}
          <div className="compose-field">
            <span className="compose-label">Assunto</span>
            <input className="compose-input" placeholder="Assunto do email"
              style={{ fontSize:15,fontWeight:500 }}
              value={compose.subject} onChange={e => setCompose(p => ({ ...p, subject: e.target.value }))} />
          </div>

          {/* Rich editor area */}
          <div className="compose-editor-area">
            {/* Format toolbar */}
            <div className="compose-format-bar">
              <button className="fmt-btn" title="Desfazer" onClick={() => execFormat('undo')}><Undo size={15} /></button>
              <button className="fmt-btn" title="Refazer" onClick={() => execFormat('redo')}><Redo size={15} /></button>
              <div className="fmt-sep" />
              <button className={`fmt-btn ${formatState.bold ? 'active' : ''}`} title="Negrito (Ctrl+B)" onClick={() => execFormat('bold')}><Bold size={15} /></button>
              <button className={`fmt-btn ${formatState.italic ? 'active' : ''}`} title="Itálico (Ctrl+I)" onClick={() => execFormat('italic')}><Italic size={15} /></button>
              <button className={`fmt-btn ${formatState.underline ? 'active' : ''}`} title="Sublinhado (Ctrl+U)" onClick={() => execFormat('underline')}><Underline size={15} /></button>
              <button className={`fmt-btn ${formatState.strikeThrough ? 'active' : ''}`} title="Tachado" onClick={() => execFormat('strikeThrough')}><Strikethrough size={15} /></button>
              <div className="fmt-sep" />
              <select
                onChange={e => { if (e.target.value) execFormat('formatBlock', e.target.value); e.target.value = ''; }}
                defaultValue=""
                style={{
                  padding:'4px 8px',border:'1px solid var(--border-color)',borderRadius:6,
                  background:'var(--bg-tertiary)',color:'var(--text-secondary)',fontSize:12,
                  cursor:'pointer',fontFamily:'var(--font-primary)',outline:'none',
                }}>
                <option value="" disabled>Formato</option>
                <option value="p">Normal</option>
                <option value="h1">Título 1</option>
                <option value="h2">Título 2</option>
                <option value="h3">Título 3</option>
                <option value="blockquote">Citação</option>
              </select>
              <div className="fmt-sep" />
              <button className={`fmt-btn ${formatState.justifyLeft ? 'active' : ''}`} title="Alinhar à esquerda" onClick={() => execFormat('justifyLeft')}><AlignLeft size={15} /></button>
              <button className={`fmt-btn ${formatState.justifyCenter ? 'active' : ''}`} title="Centralizar" onClick={() => execFormat('justifyCenter')}><AlignCenter size={15} /></button>
              <button className={`fmt-btn ${formatState.justifyRight ? 'active' : ''}`} title="Alinhar à direita" onClick={() => execFormat('justifyRight')}><AlignRight size={15} /></button>
              <div className="fmt-sep" />
              <button className={`fmt-btn ${formatState.insertUnorderedList ? 'active' : ''}`} title="Lista com marcadores" onClick={() => execFormat('insertUnorderedList')}><List size={15} /></button>
              <button className={`fmt-btn ${formatState.insertOrderedList ? 'active' : ''}`} title="Lista numerada" onClick={() => execFormat('insertOrderedList')}>
                <span style={{ fontSize:13,fontWeight:700,fontFamily:'var(--font-mono)' }}>1.</span>
              </button>
              <div className="fmt-sep" />
              <button className="fmt-btn" title="Inserir link" onClick={handleInsertLink}><Link size={15} /></button>
              <button className="fmt-btn" title="Inserir imagem" onClick={handleInsertImage}><Image size={15} /></button>
              {isAiEnabled && (
                <>
                <div className="fmt-sep" />
                <button
                  title="Gerar email com IA"
                  onClick={() => { setShowComposeAi(!showComposeAi); if (showComposeAi) resetComposeAi(); }}
                  style={{
                    display:'flex',alignItems:'center',gap:5,padding:'5px 12px',
                    borderRadius:6,cursor:'pointer',fontSize:12.5,fontWeight:600,
                    fontFamily:'var(--font-primary)',border:'none',transition:'all 0.15s ease',
                    background: showComposeAi ? 'linear-gradient(135deg,#8B5CF6,#6366F1)' : 'linear-gradient(135deg,rgba(139,92,246,0.1),rgba(99,102,241,0.1))',
                    color: showComposeAi ? '#fff' : '#8B5CF6',
                  }}
                >
                  <Sparkles size={14} /> IA
                </button>
                </>
              )}
            </div>

            {/* Compose AI Panel */}
            {showComposeAi && (
              <div style={{
                padding:18,margin:'0 0 0',
                background:'var(--bg-secondary)',
                borderBottom:'1px solid var(--border-color)',
                flexShrink:0,overflow:'visible',position:'relative',zIndex:5,
              }}
              onMouseDown={e => e.stopPropagation()}
              onClick={e => e.stopPropagation()}
              >
                {/* Header */}
                <div style={{ display:'flex',alignItems:'center',gap:8,marginBottom:14 }}>
                  <div style={{
                    width:28,height:28,borderRadius:7,
                    background:'linear-gradient(135deg,#8B5CF6,#6366F1)',
                    display:'flex',alignItems:'center',justifyContent:'center',
                  }}>
                    <Sparkles size={14} color="#fff" />
                  </div>
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:13,fontWeight:700,color:'var(--text-primary)' }}>Gerar email com IA</div>
                    <div style={{ fontSize:11,color:'var(--text-tertiary)' }}>Descreva o email e a IA escreve para você</div>
                  </div>
                  <button className="icon-btn" style={{ width:28,height:28 }}
                    onClick={() => { setShowComposeAi(false); resetComposeAi(); }}>
                    <X size={14} />
                  </button>
                </div>

                {!composeAiResult ? (
                  <>
                    {/* Style selection */}
                    <div style={{ display:'flex',gap:6,marginBottom:12,flexWrap:'wrap' }}>
                      {[
                        { id:'formal', label:'Formal', emoji:'👔' },
                        { id:'informal', label:'Informal', emoji:'😊' },
                        { id:'casual', label:'Casual', emoji:'✌️' },
                        { id:'simpatico', label:'Simpático', emoji:'🤗' },
                        { id:'agressivo', label:'Agressivo', emoji:'🔥' },
                        { id:'autoritario', label:'Autoritário', emoji:'👊' },
                        { id:'compreensivo', label:'Compreensivo', emoji:'💙' },
                      ].map(s => (
                        <button key={s.id} onClick={() => setComposeAiStyle(s.id)}
                          style={{
                            padding:'6px 14px',borderRadius:20,cursor:'pointer',
                            border: composeAiStyle === s.id ? '2px solid #8B5CF6' : '1px solid var(--border-color)',
                            background: composeAiStyle === s.id ? 'rgba(139,92,246,0.08)' : 'var(--bg-tertiary)',
                            fontSize:12.5,fontWeight:500,fontFamily:'var(--font-primary)',
                            color: composeAiStyle === s.id ? '#8B5CF6' : 'var(--text-primary)',
                            transition:'all 0.15s ease',
                          }}>
                          {s.emoji} {s.label}
                        </button>
                      ))}
                    </div>

                    {/* Instructions */}
                    <textarea
                      value={composeAiPrompt}
                      onChange={e => setComposeAiPrompt(e.target.value)}
                      placeholder="Ex: Escreva um email para o cliente informando que o projeto será entregue na sexta-feira com todas as funcionalidades solicitadas..."
                      onKeyDown={e => { if (e.key === 'Enter' && e.ctrlKey) executeComposeAi(); }}
                      style={{
                        width:'100%',padding:'10px 12px',borderRadius:'var(--radius-sm)',
                        border:'1px solid var(--border-color)',background:'var(--bg-tertiary)',
                        fontFamily:'var(--font-primary)',fontSize:13,color:'var(--text-primary)',
                        outline:'none',resize:'vertical',minHeight:80,lineHeight:1.5,
                      }}
                    />
                    <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between',marginTop:10,gap:8,flexWrap:'wrap' }}>
                      {/* Compact model selector dropdown */}
                      <div style={{ display:'flex',alignItems:'center',gap:6 }}>
                        <select value={composeAiModel} onChange={e => { e.stopPropagation(); setComposeAiModel(e.target.value); }}
                          onMouseDown={e => e.stopPropagation()}
                          style={{
                            padding:'5px 8px',borderRadius:6,fontSize:11,fontWeight:600,
                            border:'1px solid var(--border-color)',background:'var(--bg-tertiary)',
                            color:'var(--text-primary)',cursor:'pointer',fontFamily:'var(--font-primary)',outline:'none',
                            position:'relative',zIndex:10,
                          }}>
                          {[
                            { label: 'OpenAI', provider: 'openai' },
                            { label: 'Google Gemini', provider: 'gemini' },
                            { label: 'Anthropic Claude', provider: 'anthropic' },
                            { label: 'xAI Grok', provider: 'grok' },
                          ].map(g => {
                            const models = availableAiModels.filter(m => m.provider === g.provider);
                            return models.length > 0 ? (
                              <optgroup key={g.provider} label={g.label}>
                                {models.map(m => (
                                  <option key={m.id} value={m.id}>{m.label}{m.thinking ? ` [${m.tag}]` : ''} ({m.credits} créd.)</option>
                                ))}
                              </optgroup>
                            ) : null;
                          })}
                        </select>
                        <span style={{ fontSize:10,color:'var(--text-tertiary)',fontWeight:500 }}>
                          {getModelInfo(composeAiModel).credits} créd. • {({openai:'OpenAI',gemini:'Google',anthropic:'Anthropic',grok:'xAI'})[getModelInfo(composeAiModel).provider] || 'IA'}
                        </span>
                      </div>
                      <button onClick={executeComposeAi} disabled={composeAiLoading}
                        style={{
                          display:'flex',alignItems:'center',gap:6,padding:'8px 18px',
                          background:'linear-gradient(135deg,#8B5CF6,#6366F1)',color:'#fff',
                          border:'none',borderRadius:'var(--radius-sm)',cursor:'pointer',
                          fontFamily:'var(--font-primary)',fontSize:13,fontWeight:600,
                          boxShadow:'0 3px 10px rgba(139,92,246,0.3)',
                          opacity:composeAiLoading?0.7:1,transition:'all 0.2s ease',
                        }}>
                        {composeAiLoading ? <Loader size={14} className="spin-icon" /> : <Wand2 size={14} />}
                        {composeAiLoading ? 'Gerando...' : `Gerar (${getModelInfo(composeAiModel).credits} créd.)`}
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    {/* Result */}
                    {(() => {
                      const sm = composeAiResult.match(/\[ASSUNTO\]([\s\S]*?)\[\/ASSUNTO\]/);
                      const bm = composeAiResult.match(/\[CORPO\]([\s\S]*?)\[\/CORPO\]/);
                      const parsedSubject = sm ? sm[1].trim() : null;
                      const parsedBody = bm ? bm[1].trim() : composeAiResult;
                      return (
                        <>
                          {parsedSubject && (
                            <div style={{
                              padding:'8px 14px',borderRadius:8,marginBottom:8,
                              background:'rgba(139,92,246,0.08)',border:'1px solid rgba(139,92,246,0.2)',
                              display:'flex',alignItems:'center',gap:8,
                            }}>
                              <span style={{ fontSize:11,fontWeight:600,color:'#8B5CF6',textTransform:'uppercase',letterSpacing:0.5 }}>Assunto:</span>
                              <span style={{ fontSize:13,fontWeight:600,color:'var(--text-primary)' }}>{parsedSubject}</span>
                            </div>
                          )}
                          <div style={{
                            padding:14,borderRadius:'var(--radius)',
                            background:'var(--bg-tertiary)',border:'1px solid var(--border-color)',
                            fontSize:13.5,lineHeight:1.7,color:'var(--text-primary)',
                            whiteSpace:'pre-wrap',marginBottom:12,maxHeight:250,overflowY:'auto',
                          }}>
                            {parsedBody}
                          </div>
                        </>
                      );
                    })()}
                    <div style={{ display:'flex',gap:8,flexWrap:'wrap' }}>
                      <button onClick={applyComposeAiResult}
                        style={{
                          display:'flex',alignItems:'center',gap:6,padding:'8px 16px',
                          background:'linear-gradient(135deg,#8B5CF6,#6366F1)',color:'#fff',
                          border:'none',borderRadius:'var(--radius-sm)',cursor:'pointer',
                          fontFamily:'var(--font-primary)',fontSize:12.5,fontWeight:600,
                          boxShadow:'0 2px 8px rgba(139,92,246,0.3)',
                        }}>
                        <Check size={14} /> Usar este texto
                      </button>
                      <button onClick={() => setComposeAiResult('')}
                        className="btn-secondary" style={{ display:'flex',alignItems:'center',gap:5,fontSize:12.5 }}>
                        <RefreshCw size={13} /> Gerar novamente
                      </button>
                      <button onClick={() => { setShowComposeAi(false); resetComposeAi(); }}
                        className="btn-secondary" style={{ display:'flex',alignItems:'center',gap:5,fontSize:12.5 }}>
                        <X size={13} /> Descartar
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* contentEditable editor */}
            <div
              ref={editorRef}
              className="compose-editor"
              contentEditable
              data-placeholder="Escreva sua mensagem..."
              suppressContentEditableWarning
            />

            {/* Signature */}
            {settings.useSignature && (
              <div className="compose-signature">
                <div style={{ fontWeight:600,color:'var(--text-secondary)' }}>{signature.split('\n')[0]}</div>
                {signature.split('\n').slice(1).map((l, i) => <div key={i}>{l}</div>)}
              </div>
            )}

            {/* Attachments area */}
            {composeAttachments.length > 0 && (
              <div style={{
                padding:'10px 18px',borderTop:'1px solid var(--border-color)',
                display:'flex',gap:8,flexWrap:'wrap',alignItems:'center',
              }}>
                <Paperclip size={14} style={{ color:'var(--text-tertiary)',flexShrink:0 }} />
                {composeAttachments.map((file, i) => (
                  <div key={i} style={{
                    display:'flex',alignItems:'center',gap:6,
                    padding:'5px 10px',borderRadius:8,
                    background:'var(--bg-tertiary)',border:'1px solid var(--border-color)',
                    fontSize:12,color:'var(--text-primary)',
                  }}>
                    <span style={{ maxWidth:140,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',fontWeight:500 }}>
                      {file.name}
                    </span>
                    <span style={{ fontSize:10,color:'var(--text-tertiary)',flexShrink:0 }}>
                      {file.size >= 1048576 ? (file.size/1048576).toFixed(1)+' MB' : Math.round(file.size/1024)+' KB'}
                    </span>
                    <button onClick={() => setComposeAttachments(prev => prev.filter((_,j) => j !== i))}
                      style={{ background:'none',border:'none',cursor:'pointer',padding:2,display:'flex',color:'var(--text-tertiary)' }}
                      title="Remover">
                      <X size={12} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Hidden file input */}
            <input
              ref={composeFileRef}
              type="file"
              multiple
              style={{ display:'none' }}
              onChange={(e) => {
                if (e.target.files?.length) {
                  setComposeAttachments(prev => [...prev, ...Array.from(e.target.files)]);
                  e.target.value = '';
                }
              }}
            />
          </div>

          {/* Bottom toolbar */}
          <div className="compose-toolbar">
            <button className="send-btn" onClick={sendEmail} disabled={sendingEmail}
              style={{ opacity:sendingEmail?0.7:1 }}>
              {sendingEmail ? <Loader size={15} className="spin-icon" /> : <Send size={15} />}
              {sendingEmail ? 'Enviando...' : composeAttachments.length > 0 ? `Enviar (${composeAttachments.length} anexo${composeAttachments.length>1?'s':''})` : 'Enviar'}
            </button>
            <button className="btn-secondary" onClick={() => saveDraft(false)} disabled={draftSaving}
              style={{ display:'flex',alignItems:'center',gap:5,fontSize:12.5,padding:'7px 14px',opacity:draftSaving?0.6:1 }}>
              {draftSaving ? <Loader size={13} className="spin-icon" /> : <Save size={13} />}
              {draftSaving ? 'Salvando...' : 'Salvar rascunho'}
            </button>
            {editingDraftId && (
              <span style={{ fontSize:11,color:'var(--text-tertiary)',padding:'0 4px' }}>
                Rascunho #{editingDraftId}
              </span>
            )}
            <div style={{ flex:1 }} />
            <span style={{ fontSize:12,color:'var(--text-tertiary)' }}>
              {compose.to.length > 0 ? `Para: ${compose.to.length} destinatário(s)` : ''}
            </span>
            <div style={{ flex:1 }} />
            <button className="btn-secondary" onClick={() => composeFileRef.current?.click()}
              style={{ display:'flex',alignItems:'center',gap:5,fontSize:12,padding:'6px 12px' }}
              title="Anexar arquivos">
              <Paperclip size={14} /> Anexar
            </button>
            <button className="fmt-btn" title="Descartar rascunho" onClick={discardDraft}
              style={{ color:'var(--danger)' }}>
              <Trash2 size={16} />
            </button>
          </div>
        </div>
      </div>


      {/* ===== CONTEXT MENU ===== */}
      {contextMenu && (
        <div
          className="ctx-menu active"
          ref={ctxRef}
          style={{
            left: Math.max(4, Math.min(contextMenu.x, window.innerWidth - 220)),
            top: Math.max(4, Math.min(contextMenu.y, window.innerHeight - 340)),
          }}
          onClick={() => setContextMenu(null)}
        >
          <div className="ctx-item" onClick={() => openReply(contextMenu.email)}>
            <Reply size={15} /> Responder
          </div>
          <div className="ctx-item" onClick={() => openReplyAll(contextMenu.email)}>
            <ReplyAll size={15} /> Responder a todos
          </div>
          <div className="ctx-item" onClick={() => openForward(contextMenu.email)}>
            <Forward size={15} /> Encaminhar
          </div>
          <div className="ctx-divider" />
          <div className="ctx-item" onClick={() => toggleRead(contextMenu.email.id)}>
            {contextMenu.email.unread ? <MailOpen size={15} /> : <Mail size={15} />}
            {contextMenu.email.unread ? 'Marcar como lido' : 'Marcar como não lido'}
          </div>
          <div className="ctx-item" onClick={() => toggleStar(contextMenu.email.id)}>
            <Star size={15} /> {contextMenu.email.starred ? 'Remover favorito' : 'Adicionar favorito'}
          </div>
          <div className="ctx-divider" />
          {contextMenu.email.folder === 'archive' ? (
            <div className="ctx-item" onClick={() => moveToFolder(contextMenu.email.id, 'inbox')}>
              <Inbox size={15} /> Desarquivar
            </div>
          ) : (
            <div className="ctx-item" onClick={() => archiveEmail(contextMenu.email.id)}>
              <Archive size={15} /> Arquivar
            </div>
          )}
          {contextMenu.email.folder === 'spam' ? (
            <div className="ctx-item" onClick={() => markAsNotSpam(contextMenu.email.id)} style={{ color:'var(--success)' }}>
              <ShieldCheck size={15} /> Não é spam
            </div>
          ) : (
            <div className="ctx-item" onClick={() => markAsSpam(contextMenu.email.id)}>
              <Ban size={15} /> Marcar como spam
            </div>
          )}
          {/* Em 'archive' a opção "Desarquivar" acima já faz o mesmo movimento */}
          {contextMenu.email.folder !== 'inbox' && contextMenu.email.folder !== 'archive' && (
            <div className="ctx-item" onClick={() => moveToFolder(contextMenu.email.id, 'inbox')}>
              <FolderInput size={15} /> Mover para Entrada
            </div>
          )}
          <div className="ctx-divider" />
          <div className="ctx-item danger" onClick={() => deleteEmail(contextMenu.email.id)}>
            <Trash2 size={15} /> Excluir
          </div>
        </div>
      )}

      {/* ===== TAG MANAGER MODAL ===== */}
      <div className={`modal-overlay ${showTagManager ? 'active' : ''}`} onClick={() => { setShowTagManager(false); setEditingTag(null); }}>
        <div className="modal" onClick={e => e.stopPropagation()} style={{ width: 520 }}>
          <div className="modal-header">
            <div style={{ display:'flex',alignItems:'center',gap:8 }}>
              <span className="modal-title">{editingTag ? 'Editar Tag' : 'Gerenciar Tags'}</span>
              {!editingTag && activeAccount && (
                <span style={{ fontSize:11,color:'var(--text-tertiary)',fontWeight:400 }}>
                  — {activeAccount.email}
                </span>
              )}
            </div>
            <button className="modal-close-btn" onClick={() => {
              if (editingTag) { setEditingTag(null); } else { setShowTagManager(false); }
            }}>
              {editingTag ? <ArrowLeft size={18} /> : <X size={18} />}
            </button>
          </div>
          <div className="modal-body" style={{ padding:0 }}>
            {editingTag ? (
              /* ===== EDIT TAG VIEW ===== */
              <div style={{ padding:20 }}>
                <div style={{ marginBottom:16 }}>
                  <label style={{ display:'block',fontSize:12,fontWeight:600,color:'var(--text-secondary)',marginBottom:6 }}>Nome da tag</label>
                  <input className="form-input" value={editingTag.name}
                    onChange={e => setEditingTag(p => ({ ...p, name: e.target.value }))}
                    onKeyDown={e => e.key === 'Enter' && saveEditTag()}
                    autoFocus />
                </div>
                <div style={{ marginBottom:20 }}>
                  <label style={{ display:'block',fontSize:12,fontWeight:600,color:'var(--text-secondary)',marginBottom:6 }}>Cor</label>
                  <div style={{ display:'flex',alignItems:'center',gap:12 }}>
                    <div style={{ position:'relative' }}>
                      <input type="color" value={editingTag.color}
                        onChange={e => setEditingTag(p => ({ ...p, color: e.target.value }))}
                        style={{ width:44,height:44,border:'none',borderRadius:10,cursor:'pointer',padding:0,background:'transparent' }} />
                    </div>
                    <div style={{ display:'flex',gap:6,flexWrap:'wrap',flex:1 }}>
                      {TAG_COLORS.map(c => (
                        <button key={c.id} onClick={() => setEditingTag(p => ({ ...p, color: TAG_COLOR_HEX[c.id] }))}
                          title={c.label}
                          style={{
                            width:28,height:28,borderRadius:7,cursor:'pointer',transition:'all 0.15s',
                            background:TAG_COLOR_HEX[c.id],
                            border: editingTag.color === TAG_COLOR_HEX[c.id] ? '2px solid var(--text-primary)' : '2px solid transparent',
                            transform: editingTag.color === TAG_COLOR_HEX[c.id] ? 'scale(1.15)' : 'scale(1)',
                          }} />
                      ))}
                    </div>
                  </div>
                </div>
                {/* Preview */}
                <div style={{ marginBottom:20,padding:14,borderRadius:10,background:'var(--bg-tertiary)',border:'1px solid var(--border-color)' }}>
                  <span style={{ fontSize:10,color:'var(--text-tertiary)',textTransform:'uppercase',letterSpacing:0.5 }}>Preview</span>
                  <div style={{ marginTop:8,display:'flex',alignItems:'center',gap:10 }}>
                    <div style={{ width:14,height:14,borderRadius:5,background:editingTag.color }} />
                    <span style={{ fontSize:13,fontWeight:600,padding:'3px 12px',borderRadius:6,background:`${editingTag.color}18`,color:editingTag.color }}>
                      {editingTag.name || 'Nome da tag'}
                    </span>
                  </div>
                </div>
                <div style={{ display:'flex',gap:8,justifyContent:'flex-end' }}>
                  <button className="btn-secondary" onClick={() => setEditingTag(null)}>Cancelar</button>
                  <button className="btn-primary" onClick={saveEditTag} style={{ display:'flex',alignItems:'center',gap:5 }}>
                    <Check size={14} /> Salvar
                  </button>
                </div>
              </div>
            ) : (
              <>
                {/* Create tag row */}
                <div style={{ display:'flex',gap:8,alignItems:'center',padding:'14px 20px',borderBottom:'1px solid var(--border-color)',background:'var(--bg-tertiary)' }}>
                  <input className="tag-input" placeholder="Nome da nova tag..."
                    style={{ flex:1,margin:0 }}
                    value={newTagName} onChange={e => setNewTagName(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && addTag()} />
                  {/* Color picker: native input */}
                  <div style={{ position:'relative' }}>
                    <input type="color" value={newTagColor}
                      onChange={e => setNewTagColor(e.target.value)}
                      title="Escolher cor"
                      style={{ width:36,height:36,border:'2px solid var(--border-color)',borderRadius:8,cursor:'pointer',padding:0,background:'transparent' }} />
                  </div>
                  <button type="button" className="btn-primary" onClick={addTag} style={{ whiteSpace:'nowrap',padding:'8px 14px',display:'flex',alignItems:'center',gap:5 }}>
                    <Plus size={14} /> Criar
                  </button>
                </div>

                {/* View mode toggle + count */}
                <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between',padding:'10px 20px',borderBottom:'1px solid var(--border-color)' }}>
                  <span style={{ fontSize:12,color:'var(--text-tertiary)' }}>{tags.length} tag(s)</span>
                  <div style={{ display:'flex',borderRadius:8,overflow:'hidden',border:'1px solid var(--border-color)' }}>
                    {[
                      { id:'list', icon: <LayoutList size={13} />, label:'Lista' },
                      { id:'grid', icon: <LayoutGrid size={13} />, label:'Grade' },
                      { id:'compact', icon: <AlignLeft size={13} />, label:'Compacto' },
                    ].map(v => (
                      <button key={v.id} onClick={() => { setTagViewMode(v.id); localStorage.setItem('coremail-tag-view', v.id); }}
                        title={v.label}
                        style={{
                          display:'flex',alignItems:'center',gap:4,padding:'5px 10px',border:'none',cursor:'pointer',
                          fontSize:11,fontWeight: tagViewMode===v.id ? 600 : 400,
                          color: tagViewMode===v.id ? 'var(--accent)' : 'var(--text-tertiary)',
                          background: tagViewMode===v.id ? 'var(--accent-light)' : 'var(--bg-secondary)',
                          transition:'all 0.15s',fontFamily:'var(--font-primary)',
                        }}>
                        {v.icon}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Tags display */}
                <div style={{ padding: tagViewMode === 'grid' ? '12px 16px' : '4px 0', maxHeight:400, overflowY:'auto' }}>
                  {tags.length === 0 ? (
                    <div style={{ padding:32,textAlign:'center',color:'var(--text-tertiary)' }}>
                      <Tag size={28} style={{ opacity:0.2,marginBottom:8 }} />
                      <p style={{ fontSize:13 }}>Nenhuma tag criada</p>
                      <p style={{ fontSize:11,marginTop:4 }}>Crie sua primeira tag acima</p>
                    </div>
                  ) : tagViewMode === 'list' ? (
                    tags.map(tag => {
                      const hex = tag.color || TAG_COLOR_HEX[tag.colorId] || '#3B82F6';
                      return (
                        <div key={tag.id} style={{
                          display:'flex',alignItems:'center',gap:12,padding:'10px 20px',
                          borderBottom:'1px solid var(--border-light)',transition:'background 0.15s',
                        }}
                          onMouseEnter={e => e.currentTarget.style.background='var(--bg-hover)'}
                          onMouseLeave={e => e.currentTarget.style.background='transparent'}
                        >
                          <div style={{ width:12,height:12,borderRadius:4,background:hex,flexShrink:0 }} />
                          <span style={{ fontSize:13,fontWeight:600,padding:'2px 10px',borderRadius:6,background:`${hex}15`,color:hex }}>
                            {tag.name}
                          </span>
                          <span style={{ flex:1 }} />
                          {tag.count > 0 && (
                            <span style={{ fontSize:11,color:'var(--text-tertiary)',background:'var(--bg-tertiary)',padding:'2px 8px',borderRadius:10 }}>
                              {tag.count}
                            </span>
                          )}
                          <button className="icon-btn" style={{ width:26,height:26 }} onClick={() => openEditTag(tag)} title="Editar">
                            <Edit3 size={13} />
                          </button>
                          <button className="icon-btn" style={{ width:26,height:26,color:'var(--danger)' }} onClick={() => deleteTag(tag.id)} title="Excluir">
                            <Trash2 size={13} />
                          </button>
                        </div>
                      );
                    })
                  ) : tagViewMode === 'grid' ? (
                    <div style={{ display:'grid',gridTemplateColumns:'repeat(3, 1fr)',gap:8 }}>
                      {tags.map(tag => {
                        const hex = tag.color || TAG_COLOR_HEX[tag.colorId] || '#3B82F6';
                        return (
                          <div key={tag.id} style={{
                            padding:12,borderRadius:10,border:'1px solid var(--border-color)',
                            background:'var(--bg-secondary)',display:'flex',flexDirection:'column',
                            alignItems:'center',gap:8,position:'relative',transition:'all 0.15s',cursor:'pointer',
                          }}
                            onClick={() => openEditTag(tag)}
                            onMouseEnter={e => { e.currentTarget.style.borderColor=hex; e.currentTarget.style.transform='translateY(-1px)'; }}
                            onMouseLeave={e => { e.currentTarget.style.borderColor='var(--border-color)'; e.currentTarget.style.transform='none'; }}
                          >
                            <div style={{ width:24,height:24,borderRadius:8,background:hex }} />
                            <span style={{ fontSize:12,fontWeight:600,color:'var(--text-primary)',textAlign:'center',wordBreak:'break-word' }}>
                              {tag.name}
                            </span>
                            {tag.count > 0 && (
                              <span style={{ fontSize:10,color:'var(--text-tertiary)' }}>{tag.count}</span>
                            )}
                            <button onClick={e => { e.stopPropagation(); deleteTag(tag.id); }} title="Excluir"
                              style={{
                                position:'absolute',top:6,right:6,width:20,height:20,borderRadius:4,
                                border:'none',background:'transparent',cursor:'pointer',color:'var(--text-tertiary)',
                                display:'flex',alignItems:'center',justifyContent:'center',transition:'all 0.15s',
                              }}
                              onMouseEnter={e => { e.currentTarget.style.background='var(--danger-light)'; e.currentTarget.style.color='var(--danger)'; }}
                              onMouseLeave={e => { e.currentTarget.style.background='transparent'; e.currentTarget.style.color='var(--text-tertiary)'; }}
                            >
                              <X size={12} />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    tags.map(tag => {
                      const hex = tag.color || TAG_COLOR_HEX[tag.colorId] || '#3B82F6';
                      return (
                        <div key={tag.id} style={{
                          display:'flex',alignItems:'center',gap:8,padding:'6px 20px',
                          transition:'background 0.15s',cursor:'pointer',
                        }}
                          onMouseEnter={e => e.currentTarget.style.background='var(--bg-hover)'}
                          onMouseLeave={e => e.currentTarget.style.background='transparent'}
                        >
                          <div style={{ width:8,height:8,borderRadius:'50%',background:hex,flexShrink:0 }} />
                          <span onClick={() => openEditTag(tag)} style={{ fontSize:12,fontWeight:500,color:'var(--text-primary)',flex:1,cursor:'pointer' }}>
                            {tag.name}
                          </span>
                          {tag.count > 0 && (
                            <span style={{ fontSize:10,color:'var(--text-tertiary)' }}>{tag.count}</span>
                          )}
                          <button onClick={() => openEditTag(tag)} title="Editar"
                            style={{ width:22,height:22,border:'none',background:'transparent',borderRadius:4,cursor:'pointer',color:'var(--text-tertiary)',display:'flex',alignItems:'center',justifyContent:'center',transition:'all 0.15s' }}
                            onMouseEnter={e => { e.currentTarget.style.color='var(--accent)'; }}
                            onMouseLeave={e => { e.currentTarget.style.color='var(--text-tertiary)'; }}
                          >
                            <Edit3 size={11} />
                          </button>
                          <button onClick={() => deleteTag(tag.id)} title="Excluir"
                            style={{ width:22,height:22,border:'none',background:'transparent',borderRadius:4,cursor:'pointer',color:'var(--text-tertiary)',display:'flex',alignItems:'center',justifyContent:'center',transition:'all 0.15s' }}
                            onMouseEnter={e => { e.currentTarget.style.color='var(--danger)'; }}
                            onMouseLeave={e => { e.currentTarget.style.color='var(--text-tertiary)'; }}
                          >
                            <X size={12} />
                          </button>
                        </div>
                      );
                    })
                  )}
                </div>

                {/* ===== TAG RULES (auto-tagging) ===== */}
                {hasPerm('perm_auto_tag_rules') && (
                <div style={{ borderTop:'1px solid var(--border-color)' }}>
                  <button onClick={() => setShowTagRules(!showTagRules)}
                    style={{
                      display:'flex',alignItems:'center',gap:8,width:'100%',padding:'12px 20px',
                      border:'none',background:showTagRules ? 'var(--accent-light)' : 'transparent',
                      cursor:'pointer',fontSize:13,fontWeight:600,fontFamily:'var(--font-primary)',
                      color:showTagRules ? 'var(--accent)' : 'var(--text-secondary)',transition:'all 0.15s',
                    }}>
                    <Filter size={14} />
                    Regras automáticas
                    {tagRules.length > 0 && (
                      <span style={{
                        fontSize:10,fontWeight:700,padding:'1px 7px',borderRadius:10,marginLeft:4,
                        background:showTagRules ? 'var(--accent)' : 'var(--bg-tertiary)',
                        color:showTagRules ? '#fff' : 'var(--text-tertiary)',
                      }}>{tagRules.length}</span>
                    )}
                    <ChevronDown size={14} style={{ marginLeft:'auto',transform:showTagRules?'rotate(180deg)':'none',transition:'transform 0.2s' }} />
                  </button>

                  {showTagRules && (
                    <div style={{ padding:'0 0 12px',borderTop:'1px solid var(--border-color)' }}>
                      {/* Add rule form */}
                      <div style={{ padding:'14px 20px',background:'var(--bg-tertiary)',borderBottom:'1px solid var(--border-color)' }}>
                        <div style={{ fontSize:11,fontWeight:700,color:'var(--text-tertiary)',textTransform:'uppercase',letterSpacing:0.5,marginBottom:10 }}>
                          Nova regra
                        </div>

                        {/* Rule type selector */}
                        <div style={{ display:'flex',gap:6,marginBottom:10 }}>
                          {[
                            { id:'domain', label:'Domínio / Email', desc:'Ex: gmail.com ou nome@email.com' },
                            { id:'subject', label:'Palavra no assunto', desc:'Ex: fatura, urgente, projeto' },
                          ].map(t => (
                            <button key={t.id}
                              onClick={() => setNewRule(p => ({ ...p, rule_type: t.id }))}
                              style={{
                                flex:1,padding:'8px 10px',borderRadius:8,cursor:'pointer',
                                border: newRule.rule_type === t.id ? '2px solid var(--accent)' : '1px solid var(--border-color)',
                                background: newRule.rule_type === t.id ? 'var(--accent-light)' : 'var(--bg-secondary)',
                                color: newRule.rule_type === t.id ? 'var(--accent)' : 'var(--text-secondary)',
                                fontSize:12,fontWeight:600,fontFamily:'var(--font-primary)',transition:'all 0.15s',
                                textAlign:'left',
                              }}>
                              <div>{t.label}</div>
                              <div style={{ fontSize:10,fontWeight:400,marginTop:2,opacity:0.7 }}>{t.desc}</div>
                            </button>
                          ))}
                        </div>

                        {/* Value + Tag selector + Add button */}
                        <div style={{ display:'flex',gap:8,alignItems:'flex-end' }}>
                          <div style={{ flex:1 }}>
                            <label style={{ display:'block',fontSize:11,fontWeight:600,color:'var(--text-secondary)',marginBottom:3 }}>
                              {newRule.rule_type === 'domain' ? 'Domínio ou email' : 'Palavra-chave'}
                            </label>
                            <input className="form-input" style={{ fontSize:13 }}
                              placeholder={newRule.rule_type === 'domain' ? 'ex: empresa.com ou joao@empresa.com' : 'ex: fatura'}
                              value={newRule.rule_value}
                              onChange={e => setNewRule(p => ({ ...p, rule_value: e.target.value }))}
                              onKeyDown={e => e.key === 'Enter' && addTagRule()} />
                          </div>
                          <div>
                            <label style={{ display:'block',fontSize:11,fontWeight:600,color:'var(--text-secondary)',marginBottom:3 }}>
                              Aplicar tag
                            </label>
                            <select value={newRule.tag_id}
                              onChange={e => setNewRule(p => ({ ...p, tag_id: parseInt(e.target.value) || 0 }))}
                              style={{
                                padding:'8px 10px',fontSize:12,borderRadius:6,border:'1px solid var(--border-color)',
                                background:'var(--bg-secondary)',color:'var(--text-primary)',fontFamily:'var(--font-primary)',
                                cursor:'pointer',minWidth:130,
                              }}>
                              <option value={0}>Selecionar tag...</option>
                              {tags.map(tag => (
                                <option key={tag.id} value={tag.id}>{tag.name}</option>
                              ))}
                            </select>
                          </div>
                          <button className="btn-primary" onClick={addTagRule}
                            style={{ padding:'8px 14px',whiteSpace:'nowrap',display:'flex',alignItems:'center',gap:5 }}>
                            <Plus size={14} /> Criar
                          </button>
                        </div>

                        {/* Description of how it works */}
                        <div style={{ marginTop:10,padding:'8px 10px',borderRadius:6,background:'var(--bg-secondary)',border:'1px solid var(--border-color)' }}>
                          <p style={{ fontSize:11,color:'var(--text-tertiary)',lineHeight:1.5,margin:0 }}>
                            {newRule.rule_type === 'domain'
                              ? '📧 Quando um email for recebido deste domínio ou endereço, a tag selecionada será aplicada automaticamente.'
                              : '🔍 Quando o assunto de um email recebido contiver esta palavra-chave, a tag selecionada será aplicada automaticamente.'
                            }
                          </p>
                        </div>
                      </div>

                      {/* Rules list */}
                      <div style={{ maxHeight:220,overflowY:'auto' }}>
                        {tagRulesLoading ? (
                          <div style={{ display:'flex',alignItems:'center',justifyContent:'center',gap:8,padding:20,color:'var(--text-tertiary)' }}>
                            <Loader size={16} className="spin-icon" /> Carregando...
                          </div>
                        ) : tagRules.length === 0 ? (
                          <div style={{ padding:20,textAlign:'center',color:'var(--text-tertiary)' }}>
                            <Filter size={24} style={{ opacity:0.2,marginBottom:6 }} />
                            <p style={{ fontSize:12 }}>Nenhuma regra criada</p>
                            <p style={{ fontSize:11,marginTop:2,opacity:0.7 }}>Crie regras para organizar seus emails automaticamente</p>
                          </div>
                        ) : (
                          tagRules.map(rule => {
                            const tagHex = rule.tag_color || '#3B82F6';
                            return (
                              <div key={rule.id} style={{
                                display:'flex',alignItems:'center',gap:10,padding:'10px 20px',
                                borderBottom:'1px solid var(--border-light)',transition:'background 0.15s',
                              }}
                                onMouseEnter={e => e.currentTarget.style.background='var(--bg-hover)'}
                                onMouseLeave={e => e.currentTarget.style.background='transparent'}
                              >
                                {/* Type icon */}
                                <div style={{
                                  width:28,height:28,borderRadius:7,flexShrink:0,
                                  background: rule.rule_type === 'domain' ? 'var(--accent-light)' : 'var(--warning-light)',
                                  display:'flex',alignItems:'center',justifyContent:'center',
                                }}>
                                  {rule.rule_type === 'domain'
                                    ? <Mail size={13} color="var(--accent)" />
                                    : <Search size={13} color="var(--warning)" />
                                  }
                                </div>
                                {/* Rule info */}
                                <div style={{ flex:1,minWidth:0 }}>
                                  <div style={{ fontSize:12,fontWeight:600,color:'var(--text-primary)',display:'flex',alignItems:'center',gap:6 }}>
                                    <span style={{
                                      fontSize:10,padding:'1px 6px',borderRadius:4,fontWeight:600,textTransform:'uppercase',
                                      background: rule.rule_type === 'domain' ? 'var(--accent-light)' : 'var(--warning-light)',
                                      color: rule.rule_type === 'domain' ? 'var(--accent)' : 'var(--warning)',
                                    }}>
                                      {rule.rule_type === 'domain' ? 'Domínio' : 'Assunto'}
                                    </span>
                                    <span style={{ overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap' }}>
                                      {rule.rule_value}
                                    </span>
                                  </div>
                                  <div style={{ fontSize:11,color:'var(--text-tertiary)',marginTop:2 }}>
                                    → Aplica tag{' '}
                                    <span style={{ fontWeight:600,padding:'0 6px',borderRadius:4,background:`${tagHex}18`,color:tagHex }}>
                                      {rule.tag_name}
                                    </span>
                                  </div>
                                </div>
                                {/* Delete button */}
                                <button className="icon-btn" style={{ width:26,height:26,color:'var(--text-tertiary)' }}
                                  onClick={() => deleteTagRule(rule.id)} title="Remover regra"
                                  onMouseEnter={e => { e.currentTarget.style.color='var(--danger)'; e.currentTarget.style.background='var(--danger-light)'; }}
                                  onMouseLeave={e => { e.currentTarget.style.color='var(--text-tertiary)'; e.currentTarget.style.background='transparent'; }}
                                >
                                  <Trash2 size={13} />
                                </button>
                              </div>
                            );
                          })
                        )}
                      </div>
                    </div>
                  )}
                </div>
                )}
              </>
            )}
          </div>
          {!editingTag && (
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowTagManager(false)}>Fechar</button>
            </div>
          )}
        </div>
      </div>

      {/* ===== SETTINGS MODAL ===== */}
      <div className={`modal-overlay ${showSettings ? 'active' : ''}`} onClick={() => setShowSettings(false)}>
        <div className="modal" onClick={e => e.stopPropagation()} style={{ width: 580, maxHeight: '90vh' }}>
          <div className="modal-header">
            <span className="modal-title">Configurações</span>
            <button className="modal-close-btn" onClick={() => setShowSettings(false)}><X size={18} /></button>
          </div>
          <div className="modal-body">
            <div className="settings-section">
              <div className="settings-section-title">Geral</div>
              <div className="settings-row">
                <div>
                  <div className="settings-label">Marcar como lido automaticamente</div>
                  <div className="settings-desc">Ao abrir o email, marcar como lido</div>
                </div>
                <button className={`toggle ${settings.autoRead ? 'on' : ''}`}
                  onClick={() => setSettings(p => ({ ...p, autoRead: !p.autoRead }))} />
              </div>
              <div className="settings-row">
                <div>
                  <div className="settings-label">Confirmar exclusão</div>
                  <div className="settings-desc">Pedir confirmação antes de excluir</div>
                </div>
                <button className={`toggle ${settings.confirmDelete ? 'on' : ''}`}
                  onClick={() => setSettings(p => ({ ...p, confirmDelete: !p.confirmDelete }))} />
              </div>
              <div className="settings-row">
                <div>
                  <div className="settings-label">Intervalo de sincronização</div>
                  <div className="settings-desc">De quanto em quanto tempo buscar novos emails</div>
                </div>
                <select value={settings.syncInterval} onChange={e => setSettings(p => ({ ...p, syncInterval: Number(e.target.value) }))}
                  style={{
                    fontSize:13,padding:'6px 10px',border:'1px solid var(--border-color)',borderRadius:6,
                    background:'var(--bg-tertiary)',color:'var(--text-primary)',fontFamily:'var(--font-primary)',
                    cursor:'pointer',outline:'none',
                  }}>
                  <option value={1}>1 minuto</option>
                  <option value={2}>2 minutos</option>
                  <option value={5}>5 minutos</option>
                  <option value={10}>10 minutos</option>
                  <option value={15}>15 minutos</option>
                  <option value={30}>30 minutos</option>
                  <option value={0}>Desativado</option>
                </select>
              </div>
            </div>

            <div className="settings-section">
              <div className="settings-section-title">Assinatura</div>
              <div className="settings-row">
                <div>
                  <div className="settings-label">Usar assinatura</div>
                  <div className="settings-desc">Incluir assinatura em novos emails</div>
                </div>
                <button className={`toggle ${settings.useSignature ? 'on' : ''}`}
                  onClick={() => setSettings(p => ({ ...p, useSignature: !p.useSignature }))} />
              </div>

              {settings.useSignature && (<>
                {/* Scope selector */}
                <div style={{
                  padding:12,border:'1px solid var(--border-color)',borderRadius:8,
                  background:'var(--bg-tertiary)',marginTop:10,marginBottom:10,
                }}>
                  <div style={{ fontSize:12,fontWeight:600,marginBottom:8 }}>Aplicar assinatura para:</div>
                  <div style={{ display:'flex',gap:8 }}>
                    <button onClick={() => setSignatureScope('user')}
                      style={{
                        flex:1,padding:'8px 12px',border: signatureScope==='user' ? '2px solid var(--accent)' : '1px solid var(--border-color)',
                        borderRadius:6,cursor:'pointer',fontSize:12,fontWeight:500,fontFamily:'var(--font-primary)',
                        background: signatureScope==='user' ? 'var(--accent-light)' : 'transparent',
                        color: signatureScope==='user' ? 'var(--accent)' : 'var(--text-secondary)',transition:'all 0.15s',
                      }}>
                      <div style={{ fontWeight:600 }}>Todas as Contas</div>
                      <div style={{ fontSize:10,color:'var(--text-tertiary)',marginTop:2 }}>Mesma assinatura para todas as contas</div>
                    </button>
                    <button onClick={() => setSignatureScope('account')}
                      style={{
                        flex:1,padding:'8px 12px',border: signatureScope==='account' ? '2px solid var(--accent)' : '1px solid var(--border-color)',
                        borderRadius:6,cursor:'pointer',fontSize:12,fontWeight:500,fontFamily:'var(--font-primary)',
                        background: signatureScope==='account' ? 'var(--accent-light)' : 'transparent',
                        color: signatureScope==='account' ? 'var(--accent)' : 'var(--text-secondary)',transition:'all 0.15s',
                      }}>
                      <div style={{ fontWeight:600 }}>Conta Atual</div>
                      <div style={{ fontSize:10,color:'var(--text-tertiary)',marginTop:2 }}>Assinatura só para: {activeAccount?.email}</div>
                    </button>
                  </div>
                </div>

                {signatureScope === 'account' && (
                  <div style={{
                    fontSize:12,color:'var(--accent)',background:'var(--accent-light)',
                    padding:'6px 10px',borderRadius:6,marginBottom:8,display:'flex',alignItems:'center',gap:6,
                  }}>
                    <Mail size={13} /> Editando assinatura de: <strong>{activeAccount?.email}</strong>
                  </div>
                )}

                <textarea className="sig-editor" value={signature}
                  onChange={e => setSignature(e.target.value)}
                  placeholder="Sua assinatura de email..." />
              </>)}
            </div>

            {hasPerm('perm_theme') && (
            <div className="settings-section">
              <div className="settings-section-title">Tema</div>
              <div style={{ display:'flex',gap:8,marginBottom:12 }}>
                {[
                  { id:'light', label:'Claro', icon:<Sun size={14} />, desc:'Fundo branco, texto escuro' },
                  { id:'dark', label:'Escuro', icon:<Moon size={14} />, desc:'Fundo escuro, texto claro' },
                  { id:'midnight', label:'Noturno', icon:<EyeOff size={14} />, desc:'AMOLED, tudo preto' },
                ].map(t => (
                  <button key={t.id} onClick={() => setTheme(t.id)}
                    style={{
                      flex:1,padding:'12px 8px',border: theme===t.id ? '2px solid var(--accent)' : '1px solid var(--border-color)',
                      borderRadius:10,cursor:'pointer',background: theme===t.id ? 'var(--accent-light)' : 'var(--bg-tertiary)',
                      display:'flex',flexDirection:'column',alignItems:'center',gap:6,fontFamily:'var(--font-primary)',
                      color: theme===t.id ? 'var(--accent)' : 'var(--text-secondary)',transition:'all 0.2s',
                    }}>
                    {t.icon}
                    <span style={{ fontSize:12,fontWeight:600 }}>{t.label}</span>
                    <span style={{ fontSize:10,color:'var(--text-tertiary)' }}>{t.desc}</span>
                  </button>
                ))}
              </div>
            </div>
            )}

            {hasPerm('perm_theme') ? (<>
            <div className="settings-section">
              <div className="settings-section-title">Tamanho da Fonte</div>
              <div className="settings-desc" style={{ marginBottom:12 }}>Ajuste o tamanho base da fonte de todo o sistema.</div>
              <div style={{ display:'flex',alignItems:'center',gap:14 }}>
                <button className="btn-secondary" onClick={() => setSettings(p => ({ ...p, fontSize: Math.max(10, p.fontSize - 1) }))}
                  style={{ width:36,height:36,padding:0,display:'flex',alignItems:'center',justifyContent:'center',fontSize:18,fontWeight:700 }}>
                  −
                </button>
                <div style={{ flex:1,position:'relative' }}>
                  <input type="range" min={10} max={22} step={1} value={settings.fontSize}
                    onChange={e => setSettings(p => ({ ...p, fontSize: parseInt(e.target.value) }))}
                    style={{ width:'100%',accentColor:'var(--accent)',cursor:'pointer' }}
                  />
                  <div style={{ display:'flex',justifyContent:'space-between',marginTop:4 }}>
                    <span style={{ fontSize:10,color:'var(--text-tertiary)' }}>10px</span>
                    <span style={{ fontSize:10,color:'var(--text-tertiary)' }}>22px</span>
                  </div>
                </div>
                <button className="btn-secondary" onClick={() => setSettings(p => ({ ...p, fontSize: Math.min(22, p.fontSize + 1) }))}
                  style={{ width:36,height:36,padding:0,display:'flex',alignItems:'center',justifyContent:'center',fontSize:18,fontWeight:700 }}>
                  +
                </button>
              </div>
              <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between',marginTop:10 }}>
                <div style={{ display:'flex',alignItems:'baseline',gap:6 }}>
                  <span style={{ fontSize:settings.fontSize,fontWeight:600,color:'var(--text-primary)',transition:'font-size 0.2s' }}>
                    {settings.fontSize}px
                  </span>
                  <span style={{ fontSize:12,color:'var(--text-tertiary)' }}>
                    {settings.fontSize < 13 ? 'Pequeno' : settings.fontSize <= 15 ? 'Normal' : settings.fontSize <= 18 ? 'Grande' : 'Extra grande'}
                  </span>
                </div>
                {settings.fontSize !== 14 && (
                  <button className="btn-secondary" onClick={() => setSettings(p => ({ ...p, fontSize: 14 }))}
                    style={{ fontSize:11,padding:'4px 10px' }}>
                    Restaurar padrão
                  </button>
                )}
              </div>
            </div>

            <div className="settings-section">
              <div className="settings-section-title">Personalização de Cores</div>
              <div className="settings-desc" style={{ marginBottom:14 }}>Personalize as cores do sistema. Mudanças são aplicadas em tempo real.</div>

              {/* Color pickers grid */}
              <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:14 }}>
                {[
                  { key:'accent', label:'Cor de Destaque', desc:'Links, seleção, badges', def:'#3B82F6' },
                  { key:'sidebar', label:'Barra Lateral (fundo)', desc:'Fundo do menu lateral', def: theme==='light'?'#1B1F2E': theme==='dark'?'#0B0D14':'#000000' },
                  { key:'sidebarIcons', label:'Ícones da Barra Lateral', desc:'Cor dos ícones e texto do menu', def:'#94A3B8' },
                  { key:'buttons', label:'Botões de Ação', desc:'Novo Email, Enviar, Salvar', def:'#2563EB' },
                  { key:'fontPrimary', label:'Fonte Principal', desc:'Títulos, nomes, assuntos', def: theme==='light'?'#111827':'#F1F5F9' },
                  { key:'fontSecondary', label:'Fonte Secundária', desc:'Previews, descrições, labels', def: theme==='light'?'#4B5563':'#94A3B8' },
                ].map(item => (
                  <div key={item.key} style={{
                    padding:10,border:'1px solid var(--border-color)',borderRadius:8,
                    background:'var(--bg-tertiary)',display:'flex',alignItems:'center',gap:10,
                  }}>
                    <input type="color" value={customTheme?.[item.key] || item.def}
                      onChange={e => setCustomTheme(prev => ({ ...(prev || {}), [item.key]: e.target.value }))}
                      style={{ width:36,height:36,border:'2px solid var(--border-color)',borderRadius:8,cursor:'pointer',padding:0,background:'none',flexShrink:0 }} />
                    <div style={{ minWidth:0 }}>
                      <div style={{ fontSize:12,fontWeight:600,marginBottom:1 }}>{item.label}</div>
                      <div style={{ fontSize:10,color:'var(--text-tertiary)' }}>{item.desc}</div>
                      <div style={{ fontSize:10,fontFamily:'var(--font-mono)',color:'var(--text-tertiary)',marginTop:2 }}>
                        {customTheme?.[item.key] || 'padrão'}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Presets */}
              <div style={{ fontSize:11,fontWeight:600,color:'var(--text-tertiary)',marginBottom:6,textTransform:'uppercase',letterSpacing:0.5 }}>Presets</div>
              <div style={{ display:'flex',gap:6,flexWrap:'wrap',marginBottom:14 }}>
                {[
                  { label:'Azul',accent:'#3B82F6',sidebar:'#1B1F2E',buttons:'#2563EB',sidebarIcons:'#60A5FA',fontPrimary:null,fontSecondary:null },
                  { label:'Verde',accent:'#10B981',sidebar:'#0F1F1B',buttons:'#059669',sidebarIcons:'#6EE7B7',fontPrimary:null,fontSecondary:null },
                  { label:'Roxo',accent:'#8B5CF6',sidebar:'#1A1530',buttons:'#7C3AED',sidebarIcons:'#C4B5FD',fontPrimary:null,fontSecondary:null },
                  { label:'Vermelho',accent:'#EF4444',sidebar:'#1F1315',buttons:'#DC2626',sidebarIcons:'#FCA5A5',fontPrimary:null,fontSecondary:null },
                  { label:'Laranja',accent:'#F97316',sidebar:'#1F1710',buttons:'#EA580C',sidebarIcons:'#FDBA74',fontPrimary:null,fontSecondary:null },
                  { label:'Rosa',accent:'#EC4899',sidebar:'#1F1020',buttons:'#DB2777',sidebarIcons:'#F9A8D4',fontPrimary:null,fontSecondary:null },
                  { label:'Dourado',accent:'#D97706',sidebar:'#1A1508',buttons:'#B45309',sidebarIcons:'#FDE68A',fontPrimary:null,fontSecondary:null },
                  { label:'Ciano',accent:'#06B6D4',sidebar:'#0A1A1E',buttons:'#0891B2',sidebarIcons:'#67E8F9',fontPrimary:null,fontSecondary:null },
                ].map(preset => (
                  <button key={preset.label} onClick={() => {
                    const ct = {};
                    Object.entries(preset).forEach(([k,v]) => { if (k !== 'label' && v) ct[k] = v; });
                    setCustomTheme(ct);
                  }}
                    style={{
                      padding:'5px 12px',fontSize:11,fontWeight:600,border:'1px solid var(--border-color)',borderRadius:6,cursor:'pointer',
                      background:preset.accent + '15',color:preset.accent,fontFamily:'var(--font-primary)',transition:'all 0.15s',
                    }}>
                    <span style={{ display:'inline-block',width:8,height:8,borderRadius:'50%',background:preset.accent,marginRight:5,verticalAlign:'middle' }} />
                    {preset.label}
                  </button>
                ))}
                <button onClick={() => setCustomTheme({})}
                  style={{
                    padding:'5px 12px',fontSize:11,fontWeight:600,border:'1px solid var(--border-color)',borderRadius:6,cursor:'pointer',
                    background:'var(--bg-tertiary)',color:'var(--text-secondary)',fontFamily:'var(--font-primary)',
                  }}>
                  ↺ Resetar Tudo
                </button>
              </div>

              {/* Scope */}
              <div style={{
                padding:12,border:'1px solid var(--border-color)',borderRadius:8,
                background:'var(--bg-tertiary)',
              }}>
                <div style={{ fontSize:12,fontWeight:600,marginBottom:8 }}>Aplicar tema para:</div>
                <div style={{ display:'flex',gap:8 }}>
                  <button onClick={() => setThemeScope('user')}
                    style={{
                      flex:1,padding:'8px 12px',border: themeScope==='user' ? '2px solid var(--accent)' : '1px solid var(--border-color)',
                      borderRadius:6,cursor:'pointer',fontSize:12,fontWeight:500,fontFamily:'var(--font-primary)',
                      background: themeScope==='user' ? 'var(--accent-light)' : 'transparent',
                      color: themeScope==='user' ? 'var(--accent)' : 'var(--text-secondary)',transition:'all 0.15s',
                    }}>
                    <div style={{ fontWeight:600 }}>Minha Conta</div>
                    <div style={{ fontSize:10,color:'var(--text-tertiary)',marginTop:2 }}>Tema aplicado em todas as contas de email</div>
                  </button>
                  <button onClick={() => setThemeScope('account')}
                    style={{
                      flex:1,padding:'8px 12px',border: themeScope==='account' ? '2px solid var(--accent)' : '1px solid var(--border-color)',
                      borderRadius:6,cursor:'pointer',fontSize:12,fontWeight:500,fontFamily:'var(--font-primary)',
                      background: themeScope==='account' ? 'var(--accent-light)' : 'transparent',
                      color: themeScope==='account' ? 'var(--accent)' : 'var(--text-secondary)',transition:'all 0.15s',
                    }}>
                    <div style={{ fontWeight:600 }}>Conta Atual</div>
                    <div style={{ fontSize:10,color:'var(--text-tertiary)',marginTop:2 }}>Tema só para: {activeAccount?.email}</div>
                  </button>
                </div>
              </div>
            </div>
            </>) : (
              <div className="settings-section" style={{ textAlign:'center',padding:'30px 20px' }}>
                <Lock size={28} style={{ color:'var(--text-tertiary)',opacity:0.3,marginBottom:10 }} />
                <div style={{ fontSize:14,fontWeight:600,color:'var(--text-secondary)',marginBottom:4 }}>Personalização bloqueada</div>
                <div style={{ fontSize:12,color:'var(--text-tertiary)' }}>Tema, fontes e cores estão desativados para sua conta. Fale com o administrador.</div>
              </div>
            )}
          </div>
          <div className="modal-footer">
            {/* "Fechar", não "Cancelar": tema/fonte/cores são aplicados na hora
                e fechar o modal não os reverte */}
            <button className="btn-secondary" onClick={() => setShowSettings(false)}>Fechar</button>
            <button className="btn-primary" onClick={saveSettings} disabled={settingsLoading}
              style={{ display: 'flex', alignItems: 'center', gap: 6, opacity: settingsLoading ? 0.6 : 1 }}>
              {settingsLoading && <Loader size={14} className="spin-icon" />}
              Salvar
            </button>
          </div>
        </div>
      </div>

      {/* ===== ACCOUNTS MODAL ===== */}
      <div className={`modal-overlay ${showAccountModal ? 'active' : ''}`} onClick={() => { setShowAccountModal(false); setShowAddAccount(false); setEditingAccount(null); setAccountSearch(''); }}>
        <div className="modal" onClick={e => e.stopPropagation()} style={{ width: 520 }}>
          <div className="modal-header">
            <span className="modal-title">
              {showAddAccount
                ? (editingAccount ? 'Editar Conta'
                  : accountProvider === 'none' ? 'Nova Conta — Provedor'
                  : accountProvider === 'gmail_setup' ? 'Nova Conta — Gmail'
                  : 'Nova Conta')
                : 'Contas de Email'
              }
            </span>
            <button className="modal-close-btn" onClick={() => {
              if (showAddAccount) {
                if (!editingAccount && (accountProvider === 'hostgator' || accountProvider === 'gmail' || accountProvider === 'manual')) {
                  setAccountProvider('none'); setAccountForm({ ...emptyAccountForm });
                } else if (accountProvider === 'gmail_setup') {
                  setAccountProvider('none');
                } else {
                  setShowAddAccount(false); setEditingAccount(null); setAccountProvider('none');
                }
              }
              else { setShowAccountModal(false); }
            }}>
              {showAddAccount ? <ArrowLeft size={18} /> : <X size={18} />}
            </button>
          </div>
          <div className="modal-body">
            {!showAddAccount ? (
              <>
                {/* Account limit info */}
                <div style={{
                  display:'flex',alignItems:'center',justifyContent:'space-between',
                  padding:'8px 12px',marginBottom:12,borderRadius:8,fontSize:12,
                  background: accountLimitReached ? 'var(--warning-light)' : 'var(--accent-light)',
                  border: `1px solid ${accountLimitReached ? 'var(--warning)' : 'var(--accent)'}`,
                  color: accountLimitReached ? 'var(--warning)' : 'var(--accent)',
                }}>
                  <span style={{ display:'flex',alignItems:'center',gap:6 }}>
                    <Mail size={14} />
                    {accounts.length} conta(s) configurada(s)
                    {userMaxAccounts > 0
                      ? ` — Limite: ${userMaxAccounts}`
                      : ' — Ilimitado'
                    }
                  </span>
                  {accountLimitReached && (
                    <span style={{ fontWeight:600 }}>Limite atingido</span>
                  )}
                </div>

                {/* Storage info */}
                <div style={{
                  display:'flex',alignItems:'center',justifyContent:'space-between',
                  padding:'8px 12px',marginBottom:12,borderRadius:8,fontSize:12,
                  background: storageExceeded ? 'var(--danger-light)' : 'var(--bg-tertiary)',
                  border: `1px solid ${storageExceeded ? 'var(--danger)' : 'var(--border-color)'}`,
                  color: storageExceeded ? 'var(--danger)' : 'var(--text-secondary)',
                }}>
                  <span style={{ display:'flex',alignItems:'center',gap:6 }}>
                    <Archive size={14} />
                    Armazenamento: {formatBytes(storageUsedBytes)}
                    {storageLimitMb > 0
                      ? ` de ${storageLimitMb >= 1024 ? `${(storageLimitMb/1024).toFixed(1)} GB` : `${storageLimitMb} MB`}`
                      : ' — Ilimitado'
                    }
                  </span>
                  {storageExceeded && (
                    <span style={{ fontWeight:600 }}>Cheio</span>
                  )}
                  {storageLimitMb > 0 && !storageExceeded && (
                    <span style={{ color:'var(--text-tertiary)' }}>
                      {Math.round((storageUsedBytes / (storageLimitMb * 1024 * 1024)) * 100)}%
                    </span>
                  )}
                </div>

                {/* Search + Unread + View toggle */}
                <div style={{ display:'flex',gap:8,marginBottom:12,alignItems:'center' }}>
                  <div style={{ flex:1,position:'relative' }}>
                    <Search size={14} style={{ position:'absolute',left:10,top:'50%',transform:'translateY(-50%)',color:'var(--text-tertiary)' }} />
                    <input
                      className="form-input"
                      placeholder="Pesquisar contas..."
                      value={accountSearch}
                      onChange={e => setAccountSearch(e.target.value)}
                      style={{ paddingLeft:32,fontSize:13 }}
                    />
                  </div>
                  <button
                    className="btn-secondary"
                    onClick={fetchAllUnreads}
                    disabled={accountUnreadsLoading}
                    title="Conecta no servidor IMAP de cada conta para buscar emails não lidos em tempo real"
                    style={{ display:'flex',alignItems:'center',gap:6,whiteSpace:'nowrap',fontSize:12,padding:'7px 10px',opacity:accountUnreadsLoading?0.6:1 }}
                  >
                    {accountUnreadsLoading ? <Loader size={14} className="spin-icon" /> : <RefreshCw size={14} />}
                    Buscar não lidos
                  </button>
                </div>

                {/* View mode toggle */}
                <div style={{ display:'flex',gap:2,marginBottom:12,background:'var(--bg-tertiary)',borderRadius:8,padding:3 }}>
                  {[
                    { id:'list', icon: <LayoutList size={14} />, label:'Lista' },
                    { id:'grid', icon: <LayoutGrid size={14} />, label:'Grade' },
                    { id:'compact', icon: <AlignLeft size={14} />, label:'Compacto' },
                  ].map(v => (
                    <button key={v.id}
                      onClick={() => { setAccountViewMode(v.id); localStorage.setItem('coremail-acc-view', v.id); }}
                      style={{
                        flex:1,display:'flex',alignItems:'center',justifyContent:'center',gap:5,
                        padding:'6px 8px',border:'none',borderRadius:6,cursor:'pointer',
                        fontSize:12,fontWeight:accountViewMode===v.id?600:500,
                        fontFamily:'var(--font-primary)',
                        background:accountViewMode===v.id?'var(--bg-secondary)':'transparent',
                        color:accountViewMode===v.id?'var(--text-primary)':'var(--text-tertiary)',
                        boxShadow:accountViewMode===v.id?'var(--shadow-sm)':'none',
                        transition:'all 0.15s',
                      }}>
                      {v.icon} {v.label}
                    </button>
                  ))}
                </div>

                {/* Account Groups */}
                {accountGroups.length > 0 && (
                  <div style={{ display:'flex',gap:4,marginBottom:10,flexWrap:'wrap',alignItems:'center' }}>
                    <button onClick={() => setFilterGroup(null)}
                      style={{
                        padding:'4px 10px',borderRadius:20,fontSize:11,fontWeight:filterGroup===null?700:500,
                        border: filterGroup===null ? '2px solid var(--accent)' : '1px solid var(--border-color)',
                        background: filterGroup===null ? 'var(--accent-light)' : 'transparent',
                        color: filterGroup===null ? 'var(--accent)' : 'var(--text-tertiary)',
                        cursor:'pointer',fontFamily:'var(--font-primary)',
                      }}>Todas</button>
                    {accountGroups.map(g => (
                      <button key={g.id} onClick={() => setFilterGroup(filterGroup === g.id ? null : g.id)}
                        style={{
                          padding:'4px 10px',borderRadius:20,fontSize:11,fontWeight:filterGroup===g.id?700:500,
                          border: filterGroup===g.id ? `2px solid ${g.color}` : '1px solid var(--border-color)',
                          background: filterGroup===g.id ? `${g.color}15` : 'transparent',
                          color: filterGroup===g.id ? g.color : 'var(--text-secondary)',
                          cursor:'pointer',fontFamily:'var(--font-primary)',display:'flex',alignItems:'center',gap:4,
                        }}>
                        <span style={{ width:8,height:8,borderRadius:'50%',background:g.color,flexShrink:0 }}></span>
                        {g.name} ({g.accountIds?.length || 0})
                      </button>
                    ))}
                    <button onClick={() => setFilterGroup(filterGroup === 'ungrouped' ? null : 'ungrouped')}
                      style={{
                        padding:'4px 10px',borderRadius:20,fontSize:11,fontWeight:filterGroup==='ungrouped'?700:500,
                        border: filterGroup==='ungrouped' ? '2px solid var(--text-tertiary)' : '1px solid var(--border-color)',
                        background: filterGroup==='ungrouped' ? 'var(--bg-hover)' : 'transparent',
                        color:'var(--text-tertiary)',cursor:'pointer',fontFamily:'var(--font-primary)',
                      }}>Sem grupo</button>
                  </div>
                )}

                {/* Group manager button */}
                <div style={{ display:'flex',gap:6,marginBottom:12 }}>
                  <button className="btn-secondary" onClick={() => { setShowGroupManager(true); setEditingGroup(null); setGroupForm({ name:'', color:'#3B82F6', accountIds:[] }); }}
                    style={{ fontSize:11,padding:'5px 10px',display:'flex',alignItems:'center',gap:4 }}>
                    <Layers size={12} /> Gerenciar Grupos
                  </button>
                </div>

                {/* Group Manager Panel */}
                {showGroupManager && (
                  <div style={{ padding:14,background:'var(--bg-tertiary)',borderRadius:10,marginBottom:12,border:'1px solid var(--border-color)' }}>
                    <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:12 }}>
                      <span style={{ fontSize:13,fontWeight:700 }}>{editingGroup ? 'Editar Grupo' : 'Novo Grupo'}</span>
                      <button className="icon-btn" style={{ width:26,height:26 }} onClick={() => { setShowGroupManager(false); setEditingGroup(null); }}><X size={14} /></button>
                    </div>
                    <div style={{ display:'flex',gap:8,marginBottom:10 }}>
                      <input className="form-input" placeholder="Nome do grupo" value={groupForm.name}
                        onChange={e => setGroupForm(p => ({ ...p, name: e.target.value }))}
                        style={{ flex:1,fontSize:12,padding:'6px 10px' }} />
                      <input type="color" value={groupForm.color}
                        onChange={e => setGroupForm(p => ({ ...p, color: e.target.value }))}
                        style={{ width:36,height:34,border:'1px solid var(--border-color)',borderRadius:6,cursor:'pointer',padding:2 }} />
                    </div>
                    <div style={{ fontSize:11,fontWeight:600,color:'var(--text-tertiary)',marginBottom:6 }}>Selecione as contas:</div>
                    <div style={{ display:'flex',flexDirection:'column',gap:4,maxHeight:160,overflow:'auto',marginBottom:10 }}>
                      {accounts.map(acc => (
                        <label key={acc.id} style={{ display:'flex',alignItems:'center',gap:8,fontSize:12,cursor:'pointer',padding:'4px 6px',borderRadius:6,
                          background: groupForm.accountIds.includes(acc.id) ? 'var(--accent-light)' : 'transparent' }}>
                          <input type="checkbox" checked={groupForm.accountIds.includes(acc.id)}
                            onChange={e => {
                              setGroupForm(p => ({
                                ...p,
                                accountIds: e.target.checked
                                  ? [...p.accountIds, acc.id]
                                  : p.accountIds.filter(id => id !== acc.id)
                              }));
                            }} />
                          <span style={{ fontWeight:600 }}>{acc.name}</span>
                          <span style={{ color:'var(--text-tertiary)' }}>{acc.email}</span>
                        </label>
                      ))}
                    </div>
                    <div style={{ display:'flex',gap:6 }}>
                      <button className="btn-primary" style={{ fontSize:11,padding:'6px 14px' }}
                        onClick={editingGroup ? updateGroup : addGroup}>
                        {editingGroup ? 'Salvar' : 'Criar Grupo'}
                      </button>
                      {editingGroup && (
                        <button className="btn-secondary" style={{ fontSize:11,padding:'6px 14px',color:'var(--danger)' }}
                          onClick={() => { deleteGroup(editingGroup.id); setEditingGroup(null); setShowGroupManager(false); }}>
                          Excluir
                        </button>
                      )}
                    </div>
                    {/* Existing groups */}
                    {accountGroups.length > 0 && (
                      <div style={{ marginTop:12,borderTop:'1px solid var(--border-color)',paddingTop:10 }}>
                        <div style={{ fontSize:11,fontWeight:600,color:'var(--text-tertiary)',marginBottom:6 }}>Grupos existentes:</div>
                        {accountGroups.map(g => (
                          <div key={g.id} style={{ display:'flex',alignItems:'center',justifyContent:'space-between',padding:'5px 0',borderBottom:'1px solid var(--border-light)' }}>
                            <div style={{ display:'flex',alignItems:'center',gap:6 }}>
                              <span style={{ width:10,height:10,borderRadius:'50%',background:g.color }}></span>
                              <span style={{ fontSize:12,fontWeight:600 }}>{g.name}</span>
                              <span style={{ fontSize:10,color:'var(--text-tertiary)' }}>({g.accountIds?.length || 0} contas)</span>
                            </div>
                            <button className="icon-btn" style={{ width:24,height:24 }}
                              onClick={() => { setEditingGroup(g); setGroupForm({ name: g.name, color: g.color, accountIds: g.accountIds || [] }); }}>
                              <Edit3 size={11} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Account list container */}
                <div style={
                  accountViewMode === 'grid'
                    ? { display:'grid',gridTemplateColumns:'1fr 1fr',gap:8 }
                    : accountViewMode === 'compact'
                    ? { display:'flex',flexDirection:'column',gap:2 }
                    : { display:'flex',flexDirection:'column',gap:8 }
                }>
                  {filteredAccountsByGroup(accounts
                    .filter(acc => {
                      if (!accountSearch.trim()) return true;
                      const q = accountSearch.toLowerCase();
                      return acc.name?.toLowerCase().includes(q) || acc.email?.toLowerCase().includes(q);
                    }))
                    .map((acc, accIdx) => {
                      const isActive = activeAccount?.id === acc.id;
                      const unread = accountUnreads[acc.id];
                      const hasUnreadData = acc.id in accountUnreads;

                      {/* === GRID VIEW === */}
                      if (accountViewMode === 'grid') return (
                        <div key={acc.id}
                          draggable
                          onDragStart={(e) => handleDragStart(e, acc.id)}
                          onDragEnd={handleDragEnd}
                          onDragOver={(e) => handleDragOver(e, acc.id)}
                          onDrop={(e) => handleDrop(e, acc.id)}
                          onClick={() => setActiveAndSave(acc)}
                          style={{
                            display:'flex',flexDirection:'column',alignItems:'center',gap:8,
                            padding:'16px 12px',border:`1px solid ${isActive?'var(--accent)':'var(--border-color)'}`,
                            borderRadius:'var(--radius)',cursor:'pointer',
                            background:isActive?'var(--accent-light)':'var(--bg-tertiary)',
                            transition:'all 0.15s',position:'relative',textAlign:'center',
                            ...(dragOverIdx === acc.id && dragIdx !== acc.id ? { borderTop:'3px solid var(--accent)' } : {}),
                          }}>
                          <div style={{ position:'absolute',top:6,left:6,cursor:'grab',color:'var(--text-tertiary)',opacity:0.3 }}
                            onMouseDown={e => e.stopPropagation()}>
                            <GripVertical size={12} />
                          </div>
                          <div style={{
                            width:48,height:48,borderRadius:'50%',
                            background:'linear-gradient(135deg, var(--accent), var(--purple))',
                            display:'flex',alignItems:'center',justifyContent:'center',
                            fontSize:17,fontWeight:700,color:'#fff',
                          }}>{acc.initials}</div>
                          <div style={{ width:'100%',minWidth:0 }}>
                            <div style={{ fontSize:13,fontWeight:700,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap' }}>{acc.name}</div>
                            <div style={{ fontSize:11,fontWeight:600,color:'var(--text-secondary)',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',marginTop:1 }}>{acc.email}</div>
                          </div>
                          {/* Badges */}
                          <div style={{ display:'flex',gap:4,alignItems:'center',flexWrap:'wrap',justifyContent:'center' }}>
                            {unread > 0 && (
                              <span style={{ fontSize:10,fontWeight:700,color:'#fff',background:'var(--accent)',padding:'1px 7px',borderRadius:10 }}>{unread} não lido(s)</span>
                            )}
                            {unread === 0 && hasUnreadData && (
                              <span style={{ fontSize:10,color:'var(--success)',display:'flex',alignItems:'center',gap:2 }}><Check size={12} /> Em dia</span>
                            )}
                            {isActive && (
                              <span style={{ fontSize:10,fontWeight:600,color:'var(--accent)',display:'flex',alignItems:'center',gap:2 }}><Check size={12} /> Ativa</span>
                            )}
                          </div>
                          {/* Actions */}
                          <div style={{ display:'flex',gap:2,marginTop:2 }}>
                            <button className="icon-btn" style={{ width:28,height:28 }} onClick={(e) => { e.stopPropagation(); openEditAccount(acc, e); }} title="Editar">
                              <Edit3 size={13} />
                            </button>
                            <button className="icon-btn" style={{ width:28,height:28 }} onClick={(e) => { e.stopPropagation(); deleteAccount(acc.id, e); }} title="Remover">
                              <Trash2 size={13} color="var(--danger)" />
                            </button>
                          </div>
                        </div>
                      );

                      {/* === COMPACT VIEW === */}
                      if (accountViewMode === 'compact') return (
                        <div key={acc.id}
                          draggable
                          onDragStart={(e) => handleDragStart(e, acc.id)}
                          onDragEnd={handleDragEnd}
                          onDragOver={(e) => handleDragOver(e, acc.id)}
                          onDrop={(e) => handleDrop(e, acc.id)}
                          onClick={() => setActiveAndSave(acc)}
                          style={{
                            display:'flex',alignItems:'center',gap:10,
                            padding:'7px 10px',borderRadius:6,cursor:'pointer',
                            background:isActive?'var(--accent-light)':'transparent',
                            transition:'all 0.15s',
                            ...(dragOverIdx === acc.id && dragIdx !== acc.id ? { borderTop:'3px solid var(--accent)' } : {}),
                          }}
                          onMouseEnter={e => { if(!isActive) e.currentTarget.style.background='var(--bg-hover)'; }}
                          onMouseLeave={e => { if(!isActive) e.currentTarget.style.background='transparent'; }}
                        >
                          <div style={{ cursor:'grab',display:'flex',alignItems:'center',color:'var(--text-tertiary)',flexShrink:0,opacity:0.3 }}
                            onMouseDown={e => e.stopPropagation()}>
                            <GripVertical size={12} />
                          </div>
                          <div style={{
                            width:28,height:28,borderRadius:'50%',flexShrink:0,
                            background:'linear-gradient(135deg, var(--accent), var(--purple))',
                            display:'flex',alignItems:'center',justifyContent:'center',
                            fontSize:11,fontWeight:700,color:'#fff',
                          }}>{acc.initials}</div>
                          <div style={{ flex:1,minWidth:0,display:'flex',alignItems:'center',gap:8 }}>
                            <span style={{ fontSize:13,fontWeight:600,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis' }}>{acc.name}</span>
                            <span style={{ fontSize:12,fontWeight:600,color:'var(--text-secondary)',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis' }}>{acc.email}</span>
                          </div>
                          <div style={{ display:'flex',gap:4,alignItems:'center',flexShrink:0 }}>
                            {unread > 0 && (
                              <span style={{ fontSize:10,fontWeight:700,color:'#fff',background:'var(--accent)',padding:'1px 6px',borderRadius:10 }}>{unread}</span>
                            )}
                            {unread === 0 && hasUnreadData && <Check size={13} color="var(--success)" />}
                            {isActive && <Check size={14} color="var(--accent)" />}
                            <button className="icon-btn" style={{ width:26,height:26 }} onClick={(e) => { e.stopPropagation(); openEditAccount(acc, e); }} title="Editar">
                              <Edit3 size={12} />
                            </button>
                            <button className="icon-btn" style={{ width:26,height:26 }} onClick={(e) => { e.stopPropagation(); deleteAccount(acc.id, e); }} title="Remover">
                              <Trash2 size={12} color="var(--danger)" />
                            </button>
                          </div>
                        </div>
                      );

                      {/* === LIST VIEW (default) === */}
                      return (
                        <div key={acc.id} className={`acc-item ${isActive ? 'active' : ''}`}
                          draggable
                          onDragStart={(e) => handleDragStart(e, acc.id)}
                          onDragEnd={handleDragEnd}
                          onDragOver={(e) => handleDragOver(e, acc.id)}
                          onDrop={(e) => handleDrop(e, acc.id)}
                          style={dragOverIdx === acc.id && dragIdx !== acc.id ? { borderTop:'3px solid var(--accent)' } : {}}
                        >
                          <div style={{ cursor:'grab',display:'flex',alignItems:'center',color:'var(--text-tertiary)',flexShrink:0,opacity:0.4 }}
                            onMouseDown={e => e.stopPropagation()}>
                            <GripVertical size={14} />
                          </div>
                          <div className="acc-item-avatar" onClick={() => setActiveAndSave(acc)}>{acc.initials}</div>
                          <div className="acc-item-info" onClick={() => setActiveAndSave(acc)} style={{ cursor: 'pointer' }}>
                            <div className="acc-item-name">
                              {acc.name}
                              {(() => { const g = getAccountGroup(acc.id); return g ? (
                                <span style={{ fontSize:9,fontWeight:600,padding:'1px 6px',borderRadius:8,marginLeft:6,
                                  background:`${g.color}18`,color:g.color,verticalAlign:'middle' }}>{g.name}</span>
                              ) : null; })()}
                            </div>
                            <div className="acc-item-email" style={{ fontWeight:600,color:'var(--text-secondary)' }}>{acc.email}</div>
                            {acc.imap_host && <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 2 }}>IMAP: {acc.imap_host}:{acc.imap_port || 993}</div>}
                          </div>
                          <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                            {unread > 0 && (
                              <span style={{
                                fontSize:11,fontWeight:700,color:'#fff',
                                background:'var(--accent)',padding:'2px 8px',
                                borderRadius:10,minWidth:22,textAlign:'center',
                              }}>
                                {unread}
                              </span>
                            )}
                            {unread === 0 && hasUnreadData && (
                              <span style={{ color:'var(--success)',display:'flex' }}>
                                <Check size={14} />
                              </span>
                            )}
                            {isActive && <Check size={16} color="var(--accent)" />}
                            <button className="icon-btn" style={{ width: 30, height: 30 }} onClick={(e) => openEditAccount(acc, e)} title="Editar conta">
                              <Edit3 size={14} />
                            </button>
                            <button className="icon-btn" style={{ width: 30, height: 30 }} onClick={(e) => deleteAccount(acc.id, e)} title="Remover conta">
                              <Trash2 size={14} color="var(--danger)" />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                </div>

                {accounts.filter(acc => {
                  if (!accountSearch.trim()) return true;
                  const q = accountSearch.toLowerCase();
                  return acc.name?.toLowerCase().includes(q) || acc.email?.toLowerCase().includes(q);
                }).length === 0 && (
                  <div style={{ textAlign:'center',padding:'20px 0',color:'var(--text-tertiary)',fontSize:13 }}>
                    Nenhuma conta encontrada para "{accountSearch}"
                  </div>
                )}

                <button className="add-acc-btn" onClick={openAddAccount}
                  disabled={accountLimitReached}
                  style={{
                    marginTop:12,
                    ...(accountLimitReached ? { opacity:0.5,cursor:'not-allowed',borderColor:'var(--border-color)' } : {}),
                  }}>
                  <Plus size={18} />
                  {accountLimitReached
                    ? `Limite de ${userMaxAccounts} conta(s) atingido`
                    : 'Adicionar conta'
                  }
                </button>
              </>
            ) : (
              /* ===== ADD / EDIT ACCOUNT FORM ===== */
              <div>
                {/* === STEP 1: Provider Selection (only for new accounts) === */}
                {!editingAccount && accountProvider === 'none' && (
                  <div>
                    <div style={{ textAlign:'center',marginBottom:20 }}>
                      <div style={{ fontSize:14,fontWeight:600,marginBottom:4 }}>Selecione o provedor de email</div>
                      <div style={{ fontSize:12,color:'var(--text-tertiary)' }}>Vamos pré-configurar os servidores para você</div>
                    </div>
                    <div style={{ display:'flex',flexDirection:'column',gap:10 }}>
                      {/* Hostgator */}
                      <button onClick={() => setAccountProvider('hostgator')}
                        style={{
                          display:'flex',alignItems:'center',gap:14,padding:'16px 18px',
                          border:'1px solid var(--border-color)',borderRadius:10,cursor:'pointer',
                          background:'var(--bg-tertiary)',transition:'all 0.15s',
                          fontFamily:'var(--font-primary)',textAlign:'left',
                        }}
                        onMouseEnter={e => { e.currentTarget.style.borderColor='#F97316'; e.currentTarget.style.background='rgba(249,115,22,0.06)'; }}
                        onMouseLeave={e => { e.currentTarget.style.borderColor='var(--border-color)'; e.currentTarget.style.background='var(--bg-tertiary)'; }}
                      >
                        <div style={{
                          width:44,height:44,borderRadius:10,background:'#F97316',
                          display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0,
                          fontSize:18,fontWeight:800,color:'#fff',
                        }}>H</div>
                        <div>
                          <div style={{ fontSize:14,fontWeight:600,color:'var(--text-primary)' }}>HostGator</div>
                          <div style={{ fontSize:12,color:'var(--text-tertiary)',marginTop:2 }}>Configuração automática IMAP/SMTP para domínios HostGator</div>
                        </div>
                        <ChevronRight size={18} style={{ marginLeft:'auto',color:'var(--text-tertiary)' }} />
                      </button>

                      {/* Gmail */}
                      <button onClick={() => setAccountProvider('gmail_setup')}
                        style={{
                          display:'flex',alignItems:'center',gap:14,padding:'16px 18px',
                          border:'1px solid var(--border-color)',borderRadius:10,cursor:'pointer',
                          background:'var(--bg-tertiary)',transition:'all 0.15s',
                          fontFamily:'var(--font-primary)',textAlign:'left',
                        }}
                        onMouseEnter={e => { e.currentTarget.style.borderColor='#EF4444'; e.currentTarget.style.background='rgba(239,68,68,0.06)'; }}
                        onMouseLeave={e => { e.currentTarget.style.borderColor='var(--border-color)'; e.currentTarget.style.background='var(--bg-tertiary)'; }}
                      >
                        <div style={{
                          width:44,height:44,borderRadius:10,
                          background:'linear-gradient(135deg,#EA4335,#FBBC04,#34A853,#4285F4)',
                          display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0,
                          fontSize:18,fontWeight:800,color:'#fff',
                        }}>G</div>
                        <div>
                          <div style={{ fontSize:14,fontWeight:600,color:'var(--text-primary)' }}>Gmail</div>
                          <div style={{ fontSize:12,color:'var(--text-tertiary)',marginTop:2 }}>Conta Google com senha de app (requer configuração prévia)</div>
                        </div>
                        <ChevronRight size={18} style={{ marginLeft:'auto',color:'var(--text-tertiary)' }} />
                      </button>

                      {/* Manual */}
                      <button onClick={() => setAccountProvider('manual')}
                        style={{
                          display:'flex',alignItems:'center',gap:14,padding:'16px 18px',
                          border:'1px solid var(--border-color)',borderRadius:10,cursor:'pointer',
                          background:'var(--bg-tertiary)',transition:'all 0.15s',
                          fontFamily:'var(--font-primary)',textAlign:'left',
                        }}
                        onMouseEnter={e => { e.currentTarget.style.borderColor='var(--accent)'; e.currentTarget.style.background='var(--accent-subtle)'; }}
                        onMouseLeave={e => { e.currentTarget.style.borderColor='var(--border-color)'; e.currentTarget.style.background='var(--bg-tertiary)'; }}
                      >
                        <div style={{
                          width:44,height:44,borderRadius:10,background:'var(--accent)',
                          display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0,
                        }}>
                          <Settings size={20} color="#fff" />
                        </div>
                        <div>
                          <div style={{ fontSize:14,fontWeight:600,color:'var(--text-primary)' }}>Configuração Manual</div>
                          <div style={{ fontSize:12,color:'var(--text-tertiary)',marginTop:2 }}>Preencher servidores IMAP e SMTP manualmente</div>
                        </div>
                        <ChevronRight size={18} style={{ marginLeft:'auto',color:'var(--text-tertiary)' }} />
                      </button>
                    </div>
                  </div>
                )}

                {/* === STEP: Gmail Setup Instructions === */}
                {accountProvider === 'gmail_setup' && (
                  <div>
                    <div style={{
                      padding:20,borderRadius:10,
                      background:'linear-gradient(135deg,rgba(234,67,53,0.08),rgba(66,133,244,0.08))',
                      border:'1px solid rgba(66,133,244,0.2)',marginBottom:16,
                    }}>
                      <div style={{ display:'flex',alignItems:'center',gap:10,marginBottom:14 }}>
                        <div style={{
                          width:36,height:36,borderRadius:8,
                          background:'linear-gradient(135deg,#EA4335,#FBBC04,#34A853,#4285F4)',
                          display:'flex',alignItems:'center',justifyContent:'center',
                          fontSize:16,fontWeight:800,color:'#fff',flexShrink:0,
                        }}>G</div>
                        <div>
                          <div style={{ fontSize:15,fontWeight:700 }}>Configurar Gmail</div>
                          <div style={{ fontSize:12,color:'var(--text-tertiary)' }}>É necessário criar uma senha de app no Google</div>
                        </div>
                      </div>

                      <div style={{ fontSize:13,lineHeight:1.7,color:'var(--text-secondary)' }}>
                        <p style={{ marginBottom:10 }}>Para conectar sua conta Gmail ao Coremail, você precisa gerar uma <strong>senha de app</strong> no Google. Siga os passos:</p>
                        <div style={{
                          background:'var(--bg-secondary)',borderRadius:8,padding:14,marginBottom:12,
                          border:'1px solid var(--border-color)',
                        }}>
                          <div style={{ display:'flex',alignItems:'flex-start',gap:10,marginBottom:10 }}>
                            <span style={{ width:22,height:22,borderRadius:'50%',background:'var(--accent)',color:'#fff',display:'flex',alignItems:'center',justifyContent:'center',fontSize:11,fontWeight:700,flexShrink:0 }}>1</span>
                            <span>Acesse o link abaixo para abrir as Senhas de App do Google:</span>
                          </div>
                          <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noopener noreferrer"
                            style={{
                              display:'inline-flex',alignItems:'center',gap:6,
                              padding:'8px 14px',background:'var(--accent)',color:'#fff',
                              borderRadius:6,fontSize:12.5,fontWeight:600,textDecoration:'none',
                              marginLeft:32,marginBottom:10,
                            }}>
                            <Globe size={14} /> Abrir Google App Passwords
                          </a>
                          <div style={{ display:'flex',alignItems:'flex-start',gap:10,marginBottom:10 }}>
                            <span style={{ width:22,height:22,borderRadius:'50%',background:'var(--accent)',color:'#fff',display:'flex',alignItems:'center',justifyContent:'center',fontSize:11,fontWeight:700,flexShrink:0 }}>2</span>
                            <span>No campo de nome, digite <strong style={{ color:'var(--accent)' }}>Coremail</strong> e clique em <strong>Criar</strong></span>
                          </div>
                          <div style={{ display:'flex',alignItems:'flex-start',gap:10 }}>
                            <span style={{ width:22,height:22,borderRadius:'50%',background:'var(--accent)',color:'#fff',display:'flex',alignItems:'center',justifyContent:'center',fontSize:11,fontWeight:700,flexShrink:0 }}>3</span>
                            <span>O Google vai gerar uma senha de 16 caracteres. <strong>Copie essa senha</strong> — ela será usada como senha da conta no Coremail</span>
                          </div>
                        </div>
                        <div style={{
                          padding:'8px 12px',borderRadius:6,fontSize:12,
                          background:'var(--warning-light)',color:'var(--warning)',
                          display:'flex',alignItems:'center',gap:6,
                        }}>
                          <AlertTriangle size={14} />
                          A verificação em duas etapas precisa estar ativada na sua conta Google.
                        </div>
                      </div>
                    </div>

                    <div style={{ display:'flex',gap:8,justifyContent:'flex-end' }}>
                      <button className="btn-secondary" onClick={() => setAccountProvider('none')}>
                        <ArrowLeft size={14} style={{ marginRight:4,verticalAlign:'middle' }} /> Cancelar
                      </button>
                      <button className="btn-primary" onClick={() => {
                        setAccountProvider('gmail');
                        setAccountForm(p => ({
                          ...p,
                          imap_host: 'imap.gmail.com',
                          imap_port: '993',
                          smtp_host: 'smtp.gmail.com',
                          smtp_port: '587',
                        }));
                      }}
                        style={{ display:'flex',alignItems:'center',gap:6 }}>
                        <Check size={14} /> Já fiz e possuo a senha
                      </button>
                    </div>
                  </div>
                )}

                {/* === STEP 2: Account Form (shown for hostgator, gmail, manual, and editing) === */}
                {(accountProvider === 'hostgator' || accountProvider === 'gmail' || accountProvider === 'manual' || editingAccount) && (
                  <>
                    {editingAccount && (
                      <div style={{
                        padding: '8px 12px', marginBottom: 16, borderRadius: 'var(--radius-sm)',
                        background: 'var(--accent-light)', border: '1px solid var(--accent)',
                        fontSize: 12.5, color: 'var(--accent)', display: 'flex', alignItems: 'center', gap: 6,
                      }}>
                        <Edit3 size={14} />
                        Editando: {editingAccount.email}
                      </div>
                    )}

                    {/* Provider badge */}
                    {!editingAccount && (
                      <div style={{
                        display:'flex',alignItems:'center',gap:8,marginBottom:14,
                        padding:'8px 12px',borderRadius:8,fontSize:12,
                        background: accountProvider === 'hostgator' ? 'rgba(249,115,22,0.08)' : accountProvider === 'gmail' ? 'rgba(234,67,53,0.08)' : 'var(--accent-subtle)',
                        border: `1px solid ${accountProvider === 'hostgator' ? 'rgba(249,115,22,0.2)' : accountProvider === 'gmail' ? 'rgba(234,67,53,0.2)' : 'var(--accent)'}`,
                        color: accountProvider === 'hostgator' ? '#EA580C' : accountProvider === 'gmail' ? '#DC2626' : 'var(--accent)',
                      }}>
                        <strong>{accountProvider === 'hostgator' ? 'HostGator' : accountProvider === 'gmail' ? 'Gmail' : 'Manual'}</strong>
                        <span style={{ color:'var(--text-tertiary)' }}>
                          {accountProvider === 'hostgator' && '— Servidores serão preenchidos automaticamente'}
                          {accountProvider === 'gmail' && '— Use a senha de app gerada no Google'}
                          {accountProvider === 'manual' && '— Preencha os servidores manualmente'}
                        </span>
                        <button onClick={() => { setAccountProvider('none'); setAccountForm({ ...emptyAccountForm }); }}
                          style={{ marginLeft:'auto',background:'none',border:'none',cursor:'pointer',color:'var(--text-tertiary)',padding:2,display:'flex' }}>
                          <X size={14} />
                        </button>
                      </div>
                    )}

                    <div style={{ display: 'grid', gap: 12 }}>
                      <div className="form-row">
                        <div className="form-group">
                          <label className="form-label">Nome *</label>
                          <input className="form-input" placeholder="Seu nome completo"
                            value={accountForm.name}
                            onChange={e => setAccountForm(p => ({ ...p, name: e.target.value }))} />
                        </div>
                        <div className="form-group">
                          <label className="form-label">Email *</label>
                          <input className="form-input"
                            placeholder={accountProvider === 'gmail' ? 'seuemail@gmail.com' : 'email@seudominio.com'}
                            value={accountForm.email}
                            onChange={e => handleAccountEmailChange(e.target.value)} />
                        </div>
                      </div>

                      <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: 0.5, marginTop: 4 }}>
                        Servidor de Entrada (IMAP)
                      </div>

                      <div className="form-row">
                        <div className="form-group">
                          <label className="form-label">Servidor IMAP</label>
                          <input className="form-input"
                            placeholder={accountProvider === 'hostgator' ? 'mail.seudominio.com (automático)' : accountProvider === 'gmail' ? 'imap.gmail.com' : 'mail.seudominio.com'}
                            value={accountForm.imap_host}
                            readOnly={accountProvider === 'gmail'}
                            onChange={e => setAccountForm(p => ({ ...p, imap_host: e.target.value }))}
                            style={accountProvider === 'gmail' ? { opacity:0.7, cursor:'default' } : {}} />
                        </div>
                        <div className="form-group" style={{ maxWidth: 90 }}>
                          <label className="form-label">Porta</label>
                          <input className="form-input" placeholder="993"
                            value={accountForm.imap_port}
                            readOnly={accountProvider === 'gmail'}
                            onChange={e => setAccountForm(p => ({ ...p, imap_port: e.target.value }))}
                            style={accountProvider === 'gmail' ? { opacity:0.7, cursor:'default' } : {}} />
                        </div>
                      </div>

                      <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        Servidor de Saída (SMTP)
                      </div>

                      <div className="form-row">
                        <div className="form-group">
                          <label className="form-label">Servidor SMTP</label>
                          <input className="form-input"
                            placeholder={accountProvider === 'hostgator' ? 'mail.seudominio.com (automático)' : accountProvider === 'gmail' ? 'smtp.gmail.com' : 'mail.seudominio.com'}
                            value={accountForm.smtp_host}
                            readOnly={accountProvider === 'gmail'}
                            onChange={e => setAccountForm(p => ({ ...p, smtp_host: e.target.value }))}
                            style={accountProvider === 'gmail' ? { opacity:0.7, cursor:'default' } : {}} />
                        </div>
                        <div className="form-group" style={{ maxWidth: 90 }}>
                          <label className="form-label">Porta</label>
                          <input className="form-input"
                            placeholder={accountProvider === 'hostgator' ? '465' : '587'}
                            value={accountForm.smtp_port}
                            readOnly={accountProvider === 'gmail'}
                            onChange={e => setAccountForm(p => ({ ...p, smtp_port: e.target.value }))}
                            style={accountProvider === 'gmail' ? { opacity:0.7, cursor:'default' } : {}} />
                        </div>
                      </div>

                      <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        Autenticação
                      </div>

                      <div className="form-group">
                        <label className="form-label">
                          {accountProvider === 'gmail' && !editingAccount
                            ? <span>Senha de App <span style={{ fontWeight:400,color:'var(--text-tertiary)' }}>(gerada no Google App Passwords)</span></span>
                            : editingAccount
                              ? <span>Senha <span style={{ fontWeight:400,color:'var(--text-tertiary)' }}>(deixe vazio para manter a atual)</span></span>
                              : 'Senha'
                          }
                        </label>
                        <div style={{ position: 'relative' }}>
                          <input
                            className="form-input"
                            type={showPassword ? 'text' : 'password'}
                            placeholder={editingAccount ? '••••••••' : accountProvider === 'gmail' ? 'Cole a senha de 16 caracteres aqui' : 'Senha do email'}
                            value={accountForm.password}
                            onChange={e => setAccountForm(p => ({ ...p, password: e.target.value }))}
                            style={{ paddingRight: 38 }}
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            style={{
                              position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
                              background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-tertiary)',
                              padding: 4, display: 'flex',
                            }}
                          >
                            {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                          </button>
                        </div>
                      </div>
                    </div>

                    <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between', marginTop: 16, alignItems: 'center' }}>
                      <button
                        className="btn-secondary"
                        onClick={testConnection}
                        disabled={accountTesting}
                        style={{ display: 'flex', alignItems: 'center', gap: 6, opacity: accountTesting ? 0.6 : 1 }}
                      >
                        {accountTesting ? <Loader size={14} className="spin-icon" /> : <ShieldCheck size={14} />}
                        {accountTesting ? 'Testando...' : 'Testar Conexão'}
                      </button>

                      <div style={{ display: 'flex', gap: 8 }}>
                        <button className="btn-secondary" onClick={() => {
                          if (editingAccount) { setShowAddAccount(false); setEditingAccount(null); }
                          else { setAccountProvider('none'); setAccountForm({ ...emptyAccountForm }); }
                        }}>
                          {editingAccount ? 'Cancelar' : 'Voltar'}
                        </button>
                        <button
                          className="btn-primary"
                          onClick={saveAccount}
                          disabled={accountLoading}
                          style={{ display: 'flex', alignItems: 'center', gap: 6, opacity: accountLoading ? 0.6 : 1 }}
                        >
                          {accountLoading ? <Loader size={14} className="spin-icon" /> : <Save size={14} />}
                          {editingAccount ? 'Salvar Alterações' : 'Adicionar Conta'}
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ===== ADMIN: USER MANAGEMENT PANEL ===== */}
      <div className={`modal-overlay ${showAdminPanel ? 'active' : ''}`} onClick={() => { setShowAdminPanel(false); setShowAddUser(false); setShowAddCompany(false); setAdminSearch(''); setAdminFilter('all'); }}>
        {showAdminPanel && (
          <div className="modal" onClick={e => e.stopPropagation()} style={{ width:720,maxHeight:'90vh' }}>
            <div className="modal-header" style={{ padding:'14px 20px',background:'var(--bg-sidebar)',borderBottom:'none',borderRadius:'var(--radius-lg) var(--radius-lg) 0 0' }}>
              <div>
                <span style={{ fontSize:16,fontWeight:700,color:'#fff',display:'flex',alignItems:'center',gap:8 }}>
                  <Settings size={18} /> Painel Administrativo
                </span>
              </div>
              <button className="compose-close" onClick={() => { setShowAdminPanel(false); setShowAddUser(false); setShowAddCompany(false); }}><X size={16} /></button>
            </div>

            {/* Tabs */}
            {!showAddUser && !showAddCompany && (
              <div style={{ display:'flex',borderBottom:'1px solid var(--border-color)',padding:'0 20px',background:'var(--bg-secondary)' }}>
                {[
                  { id:'users', label:'Usuários', icon:<Users size={14} />, count:adminUsers.length },
                  { id:'companies', label:'Empresas', icon:<Building2 size={14} />, count:companies.length },
                ].map(tab => (
                  <button key={tab.id} onClick={() => setAdminTab(tab.id)}
                    style={{
                      display:'flex',alignItems:'center',gap:6,padding:'10px 16px',
                      border:'none',background:'none',cursor:'pointer',
                      fontFamily:'var(--font-primary)',fontSize:13,fontWeight:600,
                      color: adminTab===tab.id ? 'var(--accent)' : 'var(--text-tertiary)',
                      borderBottom: adminTab===tab.id ? '2px solid var(--accent)' : '2px solid transparent',
                      transition:'all 0.15s ease',
                    }}>
                    {tab.icon} {tab.label}
                    <span style={{
                      fontSize:10,padding:'1px 6px',borderRadius:10,fontWeight:700,
                      background: adminTab===tab.id ? 'var(--accent)' : 'var(--bg-tertiary)',
                      color: adminTab===tab.id ? '#fff' : 'var(--text-tertiary)',
                    }}>{tab.count}</span>
                  </button>
                ))}
              </div>
            )}

            <div className="modal-body" style={{ padding:0,overflow:'auto',maxHeight:'calc(90vh - 60px)' }}>
              {showAddUser ? (
                /* ═══ ADD/EDIT USER FORM ═══ */
                <div style={{ padding:24 }}>
                  <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:20 }}>
                    <div style={{ display:'flex',alignItems:'center',gap:10 }}>
                      <button className="icon-btn" onClick={() => setShowAddUser(false)} style={{ width:32,height:32 }}>
                        <ArrowLeft size={16} />
                      </button>
                      <h3 style={{ fontSize:16,fontWeight:700,margin:0 }}>{editingUser ? `Editar: ${editingUser.name}` : 'Novo Usuário'}</h3>
                    </div>
                    {editingUser && (
                      <span style={{
                        fontSize:10,fontWeight:700,padding:'3px 10px',borderRadius:20,
                        background: editingUser.role==='admin' ? 'var(--accent)' : 'var(--bg-tertiary)',
                        color: editingUser.role==='admin' ? '#fff' : 'var(--text-secondary)',
                      }}>
                        {editingUser.role === 'admin' ? 'ADMINISTRADOR' : 'USUÁRIO'}
                      </span>
                    )}
                  </div>

                  <div style={{ display:'flex',flexDirection:'column',gap:14 }}>
                    <div className="form-row">
                      <div className="form-group">
                        <label className="form-label">Nome completo *</label>
                        <input className="form-input" placeholder="Nome completo"
                          value={userForm.name} onChange={e => setUserForm(p=>({...p,name:e.target.value}))} />
                      </div>
                      <div className="form-group">
                        <label className="form-label">Email *</label>
                        <input className="form-input" type="email" placeholder="email@exemplo.com"
                          value={userForm.email} onChange={e => setUserForm(p=>({...p,email:e.target.value}))} />
                      </div>
                    </div>
                    <div>
                      <label className="form-label">
                        Senha {editingUser ? <span style={{ fontWeight:400,color:'var(--text-tertiary)' }}>(deixe vazio para manter a atual)</span> : '*'}
                      </label>
                      {/* Senha atual nunca é exibida — senhas não devem aparecer em
                          texto claro na interface, mesmo para administradores. */}
                      <div style={{ position:'relative' }}>
                        <input className="form-input" type={showLoginPassword ? 'text' : 'password'}
                          style={{ paddingRight:38 }}
                          placeholder={editingUser ? '••••••••' : 'Mínimo 6 caracteres'}
                          value={userForm.password} onChange={e => setUserForm(p=>({...p,password:e.target.value}))} />
                        <button type="button" className="icon-btn"
                          style={{ position:'absolute',right:4,top:'50%',transform:'translateY(-50%)',width:28,height:28 }}
                          onClick={() => setShowLoginPassword(!showLoginPassword)}>
                          {showLoginPassword ? <EyeOff size={14} /> : <Eye size={14} />}
                        </button>
                      </div>
                    </div>
                    <div className="form-row">
                      <div className="form-group">
                        <label className="form-label">Limite de contas de email</label>
                        <div style={{ display:'flex',alignItems:'center',gap:8 }}>
                          <input className="form-input" type="number" min="0" style={{ width:100 }}
                            value={userForm.max_accounts}
                            onChange={e => setUserForm(p=>({...p, max_accounts: Math.max(0, parseInt(e.target.value) || 0)}))} />
                          <span style={{ fontSize:12,color:'var(--text-tertiary)' }}>
                            {userForm.max_accounts === 0 ? 'Ilimitado' : `Máximo ${userForm.max_accounts} conta(s)`}
                          </span>
                        </div>
                      </div>
                      <div className="form-group">
                        <label className="form-label">Limite de armazenamento</label>
                        <div style={{ display:'flex',alignItems:'center',gap:6 }}>
                          <input className="form-input" type="number" min="0" style={{ width:100 }}
                            value={userFormStorageUnit === 'GB'
                              ? (userForm.storage_limit_mb >= 1024 ? Math.round(userForm.storage_limit_mb / 1024 * 10) / 10 : userForm.storage_limit_mb / 1024 || 0)
                              : userForm.storage_limit_mb
                            }
                            onChange={e => {
                              const val = Math.max(0, parseFloat(e.target.value) || 0);
                              const mb = userFormStorageUnit === 'GB' ? Math.round(val * 1024) : Math.round(val);
                              setUserForm(p=>({...p, storage_limit_mb: mb}));
                            }} />
                          <select value={userFormStorageUnit} onChange={e => setUserFormStorageUnit(e.target.value)}
                            style={{ fontSize:12,padding:'6px 8px',border:'1px solid var(--border-color)',borderRadius:6,background:'var(--bg-tertiary)',color:'var(--text-primary)',fontFamily:'var(--font-primary)',cursor:'pointer' }}>
                            <option value="MB">MB</option>
                            <option value="GB">GB</option>
                          </select>
                          <span style={{ fontSize:12,color:'var(--text-tertiary)',whiteSpace:'nowrap' }}>
                            {userForm.storage_limit_mb === 0 ? 'Ilimitado'
                              : userForm.storage_limit_mb >= 1024 ? `${(userForm.storage_limit_mb/1024).toFixed(1)} GB`
                              : `${userForm.storage_limit_mb} MB`
                            }
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Company selector */}
                    <div>
                      <label className="form-label">Empresa (opcional)</label>
                      <select value={userForm.company_id || ''} onChange={e => setUserForm(p=>({...p, company_id: e.target.value ? Number(e.target.value) : ''}))}
                        style={{ width:'100%',padding:'8px 12px',border:'1px solid var(--border-color)',borderRadius:6,background:'var(--bg-tertiary)',color:'var(--text-primary)',fontFamily:'var(--font-primary)',fontSize:13,cursor:'pointer' }}>
                        <option value="">— Sem empresa —</option>
                        {companies.map(c => (
                          <option key={c.id} value={c.id}>{c.name} ({c.users_count || 0} usuários)</option>
                        ))}
                      </select>
                      {userForm.company_id && (() => {
                        const co = companies.find(c => c.id === Number(userForm.company_id));
                        if (!co) return null;
                        return (
                          <div style={{ fontSize:11,color:'var(--text-tertiary)',marginTop:4,display:'flex',gap:12,flexWrap:'wrap' }}>
                            <span>👥 Máx {co.max_users || '∞'} usuários</span>
                            <span>📧 {co.max_accounts_per_user || '∞'} contas/usr</span>
                            <span>💾 {co.storage_limit_mb ? (co.storage_limit_mb >= 1024 ? `${(co.storage_limit_mb/1024).toFixed(1)}GB` : `${co.storage_limit_mb}MB`) : '∞'}</span>
                            <span>🤖 {co.ai_credits_per_user || '∞'} créd IA/usr</span>
                            <span>{co.ai_enabled ? '✅ IA ativa' : '❌ IA desativada'}</span>
                          </div>
                        );
                      })()}
                    </div>

                    {editingUser && (
                      <div style={{ fontSize:12,color:'var(--text-tertiary)',padding:'4px 0',display:'flex',alignItems:'center',gap:6 }}>
                        <Info size={13} />
                        Para alterar o papel para administrador, modifique diretamente no banco de dados.
                      </div>
                    )}
                    <div style={{ display:'flex',gap:8,justifyContent:'flex-end',marginTop:4 }}>
                      <button className="btn-secondary" onClick={() => setShowAddUser(false)}>Cancelar</button>
                      <button className="btn-primary" onClick={saveUser} disabled={userFormLoading}
                        style={{ display:'flex',alignItems:'center',gap:6,opacity:userFormLoading?0.7:1 }}>
                        {userFormLoading ? <Loader size={14} className="spin-icon" /> : <Save size={14} />}
                        {editingUser ? 'Salvar Alterações' : 'Criar Usuário'}
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  {/* ═══ USERS TAB ═══ */}
                  {adminTab === 'users' && (
                  <>
                  {/* ═══ STATS CARDS ═══ */}
                  <div style={{ padding:'16px 20px 8px',display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:8 }}>
                    {[
                      { label:'Total', value:adminStats.total, color:'var(--accent)', filter:'all' },
                      { label:'Admins', value:adminStats.admins, color:'var(--purple)', filter:'admin' },
                      { label:'Usuários', value:adminStats.users, color:'var(--success)', filter:'user' },
                      { label:'Contas Email', value:adminStats.totalAccounts, color:'var(--warning)', filter:null },
                    ].map((stat,i) => (
                      <div key={i} onClick={() => stat.filter && setAdminFilter(f => f === stat.filter ? 'all' : stat.filter)}
                        style={{
                          padding:'10px 12px',borderRadius:10,cursor:stat.filter?'pointer':'default',
                          background: adminFilter === stat.filter ? `${stat.color}15` : 'var(--bg-tertiary)',
                          border: `1px solid ${adminFilter === stat.filter ? stat.color : 'var(--border-color)'}`,
                          transition:'all 0.15s ease',
                        }}>
                        <div style={{ fontSize:20,fontWeight:800,color:stat.color,lineHeight:1 }}>{stat.value}</div>
                        <div style={{ fontSize:10,fontWeight:600,color:'var(--text-tertiary)',marginTop:3,textTransform:'uppercase',letterSpacing:'0.5px' }}>{stat.label}</div>
                      </div>
                    ))}
                  </div>

                  {/* ═══ TOOLBAR: Search + Filter + Sort + View + Add ═══ */}
                  <div style={{ padding:'8px 20px 12px',display:'flex',flexDirection:'column',gap:8 }}>
                    {/* Search bar */}
                    <div style={{ position:'relative' }}>
                      <Search size={15} style={{ position:'absolute',left:10,top:'50%',transform:'translateY(-50%)',color:'var(--text-tertiary)' }} />
                      <input className="search-input" placeholder="Pesquisar por nome ou email..."
                        style={{ paddingLeft:34,fontSize:13,height:38 }}
                        value={adminSearch} onChange={e => setAdminSearch(e.target.value)} />
                      {adminSearch && (
                        <button className="icon-btn" style={{ position:'absolute',right:4,top:'50%',transform:'translateY(-50%)',width:28,height:28 }}
                          onClick={() => setAdminSearch('')}>
                          <X size={14} />
                        </button>
                      )}
                    </div>

                    {/* Controls row */}
                    <div style={{ display:'flex',alignItems:'center',gap:6,flexWrap:'wrap' }}>
                      {/* Filter pills */}
                      {[
                        { id:'all', label:'Todos' },
                        { id:'admin', label:'Admins' },
                        { id:'user', label:'Usuários' },
                        { id:'active', label:'Ativos' },
                        { id:'inactive', label:'Inativos' },
                      ].map(f => (
                        <button key={f.id} onClick={() => setAdminFilter(f.id)}
                          style={{
                            padding:'4px 10px',borderRadius:20,fontSize:11,fontWeight:600,
                            border:'1px solid ' + (adminFilter === f.id ? 'var(--accent)' : 'var(--border-color)'),
                            background: adminFilter === f.id ? 'var(--accent)' : 'transparent',
                            color: adminFilter === f.id ? '#fff' : 'var(--text-secondary)',
                            cursor:'pointer',transition:'all 0.15s ease',fontFamily:'var(--font-primary)',
                          }}>
                          {f.label}
                        </button>
                      ))}

                      <div style={{ flex:1 }} />

                      {/* Sort */}
                      <select value={adminSort} onChange={e => setAdminSort(e.target.value)}
                        style={{
                          padding:'4px 8px',borderRadius:6,fontSize:11,fontWeight:500,
                          border:'1px solid var(--border-color)',background:'var(--bg-tertiary)',
                          color:'var(--text-secondary)',cursor:'pointer',fontFamily:'var(--font-primary)',outline:'none',
                        }}>
                        <option value="name">Nome A-Z</option>
                        <option value="email">Email A-Z</option>
                        <option value="recent">Último acesso</option>
                        <option value="created">Mais recente</option>
                      </select>

                      {/* View toggle */}
                      <div style={{ display:'flex',borderRadius:6,border:'1px solid var(--border-color)',overflow:'hidden' }}>
                        <button onClick={() => setAdminView('list')} style={{
                          padding:'4px 8px',border:'none',cursor:'pointer',
                          background: adminView==='list' ? 'var(--accent)' : 'var(--bg-tertiary)',
                          color: adminView==='list' ? '#fff' : 'var(--text-tertiary)',display:'flex',alignItems:'center',
                        }}><Menu size={13} /></button>
                        <button onClick={() => setAdminView('grid')} style={{
                          padding:'4px 8px',border:'none',borderLeft:'1px solid var(--border-color)',cursor:'pointer',
                          background: adminView==='grid' ? 'var(--accent)' : 'var(--bg-tertiary)',
                          color: adminView==='grid' ? '#fff' : 'var(--text-tertiary)',display:'flex',alignItems:'center',
                        }}><LayoutGrid size={13} /></button>
                      </div>

                      {/* Add button */}
                      <button className="btn-primary" onClick={openAddUser}
                        style={{ padding:'5px 12px',fontSize:11,display:'flex',alignItems:'center',gap:4,borderRadius:8 }}>
                        <Plus size={13} /> Novo
                      </button>
                    </div>

                    {/* Results count */}
                    {(adminSearch || adminFilter !== 'all') && (
                      <div style={{ fontSize:11,color:'var(--text-tertiary)',display:'flex',alignItems:'center',gap:4 }}>
                        {filteredAdminUsers.length} de {adminUsers.length} usuário(s)
                        {adminSearch && <span>para "<strong>{adminSearch}</strong>"</span>}
                        {adminFilter !== 'all' && (
                          <button onClick={() => { setAdminSearch(''); setAdminFilter('all'); }}
                            style={{ marginLeft:4,fontSize:10,color:'var(--accent)',background:'none',border:'none',cursor:'pointer',fontFamily:'var(--font-primary)',fontWeight:600 }}>
                            Limpar filtros
                          </button>
                        )}
                      </div>
                    )}
                  </div>

                  {/* ═══ USER LIST / GRID ═══ */}
                  {adminLoading ? (
                    <div style={{ display:'flex',alignItems:'center',justifyContent:'center',gap:8,padding:40,color:'var(--text-tertiary)' }}>
                      <Loader size={18} className="spin-icon" /> Carregando...
                    </div>
                  ) : filteredAdminUsers.length === 0 ? (
                    <div style={{ padding:'40px 20px',textAlign:'center',color:'var(--text-tertiary)' }}>
                      <Search size={32} style={{ opacity:0.2,marginBottom:8 }} />
                      <p style={{ fontSize:13,margin:0 }}>Nenhum usuário encontrado</p>
                      <p style={{ fontSize:11,margin:'4px 0 0',opacity:0.6 }}>Tente alterar o filtro ou a busca</p>
                    </div>
                  ) : adminView === 'list' ? (
                    /* ─── LIST VIEW ─── */
                    <div style={{ padding:'0 16px 16px' }}>
                      {filteredAdminUsers.map(u => (
                        <div key={u.id} style={{
                          display:'flex',alignItems:'center',gap:12,padding:'11px 12px',
                          borderRadius:10,border:'1px solid var(--border-color)',marginBottom:5,
                          background:'var(--bg-secondary)',transition:'all 0.15s ease',
                        }}>
                          <div style={{
                            width:40,height:40,borderRadius:'50%',flexShrink:0,
                            background: u.role==='admin'
                              ? 'linear-gradient(135deg, var(--accent), var(--purple))'
                              : 'var(--bg-tertiary)',
                            color: u.role==='admin' ? '#fff' : 'var(--text-secondary)',
                            display:'flex',alignItems:'center',justifyContent:'center',
                            fontSize:14,fontWeight:700,
                          }}>
                            {u.name?.split(' ').map(n=>n[0]).join('').substring(0,2).toUpperCase()}
                          </div>
                          <div style={{ flex:1,minWidth:0 }}>
                            <div style={{ display:'flex',alignItems:'center',gap:6,flexWrap:'wrap' }}>
                              <span style={{ fontSize:13.5,fontWeight:600 }}>{u.name}</span>
                              {u.role==='admin' && (
                                <span style={{ fontSize:9,background:'var(--accent)',color:'#fff',padding:'2px 7px',borderRadius:10,fontWeight:700,letterSpacing:'0.3px' }}>ADMIN</span>
                              )}
                              {!u.is_active && (
                                <span style={{ fontSize:9,background:'var(--danger)',color:'#fff',padding:'2px 7px',borderRadius:10,fontWeight:600 }}>INATIVO</span>
                              )}
                              {u.id === authUser?.id && (
                                <span style={{ fontSize:9,background:'var(--success-light)',color:'var(--success)',padding:'2px 7px',borderRadius:10,fontWeight:600 }}>VOCÊ</span>
                              )}
                            </div>
                            <div style={{ fontSize:12,color:'var(--text-tertiary)',marginTop:1 }}>{u.email}</div>
                            <div style={{ fontSize:10.5,color:'var(--text-tertiary)',marginTop:2,display:'flex',gap:10,flexWrap:'wrap' }}>
                              <span>{u.email_accounts_count}{u.max_accounts > 0 ? `/${u.max_accounts}` : ''} conta(s)</span>
                              <span>
                                {formatBytes(u.storage_used_bytes || 0)}
                                {u.storage_limit_mb > 0
                                  ? ` / ${u.storage_limit_mb >= 1024 ? `${(u.storage_limit_mb/1024).toFixed(1)} GB` : `${u.storage_limit_mb} MB`}`
                                  : ' (ilimitado)'
                                }
                              </span>
                              {u.company_id && getCompanyName(u.company_id) && (
                                <span style={{ background:'rgba(139,92,246,0.1)',color:'var(--purple)',padding:'1px 6px',borderRadius:8,fontWeight:600,fontSize:10 }}>
                                  🏢 {getCompanyName(u.company_id)}
                                </span>
                              )}
                              <span>Criado {u.created_at ? new Date(u.created_at).toLocaleDateString('pt-BR') : '—'}</span>
                              {u.last_login && <span>Acesso {new Date(u.last_login).toLocaleDateString('pt-BR')}</span>}
                              <span style={{ color: (u.ai_enabled !== false && u.permissions?.perm_ai !== false) ? 'var(--success)' : 'var(--danger)' }}>
                                IA: {(u.ai_enabled !== false && u.permissions?.perm_ai !== false) ? 'Ativa' : 'Desativada'}
                                {u.ai_credits_limit > 0 && ` · ${Math.max(0, u.ai_credits_remaining)}/${u.ai_credits_limit} créditos`}
                              </span>
                            </div>
                          </div>
                          <div style={{ display:'flex',gap:2,alignItems:'center' }}>
                            {/* AI config popup (admin can configure for non-admin users) */}
                            {u.role !== 'admin' && (
                              <button className="icon-btn" title="Configurar IA"
                                style={{ width:32,height:32,color: (u.ai_enabled !== false && u.permissions?.perm_ai !== false) ? 'var(--success)' : 'var(--text-tertiary)' }}
                                onClick={() => {
                                  setShowAdminAiPopup(u);
                                  const permAi = u.permissions?.perm_ai !== false;
                                  setAdminAiForm({
                                    ai_enabled: u.ai_enabled !== false && permAi,
                                    ai_credits_limit: u.ai_credits_limit || 0,
                                  });
                                }}>
                                <Sparkles size={14} />
                              </button>
                            )}
                            {u.role !== 'admin' && (
                              <button className="icon-btn" title="Permissões"
                                style={{ width:32,height:32,color:'var(--purple)' }}
                                onClick={() => { setShowPermissionsPopup(u); setPermissionsForm(u.permissions || {}); }}>
                                <Shield size={14} />
                              </button>
                            )}
                            <button className="icon-btn" onClick={() => openAdminUserDashboard(u)} title="Dashboard" style={{ width:32,height:32 }}>
                              <BarChart3 size={14} />
                            </button>
                            <button className="icon-btn" onClick={() => openEditUser(u)} title="Editar" style={{ width:32,height:32 }}>
                              <Edit3 size={14} />
                            </button>
                            {u.id !== authUser?.id && (
                              <button className="icon-btn" onClick={() => deleteUser(u.id, u.name)} title="Excluir"
                                style={{ width:32,height:32,color:'var(--danger)' }}>
                                <Trash2 size={14} />
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    /* ─── GRID VIEW ─── */
                    <div style={{ padding:'0 16px 16px',display:'grid',gridTemplateColumns:'repeat(2,1fr)',gap:8 }}>
                      {filteredAdminUsers.map(u => (
                        <div key={u.id} style={{
                          padding:'16px',borderRadius:12,border:'1px solid var(--border-color)',
                          background:'var(--bg-secondary)',transition:'all 0.15s ease',
                          display:'flex',flexDirection:'column',alignItems:'center',textAlign:'center',gap:8,
                        }}>
                          <div style={{
                            width:48,height:48,borderRadius:'50%',
                            background: u.role==='admin'
                              ? 'linear-gradient(135deg, var(--accent), var(--purple))'
                              : 'var(--bg-tertiary)',
                            color: u.role==='admin' ? '#fff' : 'var(--text-secondary)',
                            display:'flex',alignItems:'center',justifyContent:'center',
                            fontSize:16,fontWeight:700,
                          }}>
                            {u.name?.split(' ').map(n=>n[0]).join('').substring(0,2).toUpperCase()}
                          </div>
                          <div>
                            <div style={{ fontSize:13.5,fontWeight:600,display:'flex',alignItems:'center',justifyContent:'center',gap:4,flexWrap:'wrap' }}>
                              {u.name}
                              {u.id === authUser?.id && <span style={{ fontSize:8,background:'var(--success-light)',color:'var(--success)',padding:'1px 5px',borderRadius:8,fontWeight:600 }}>VOCÊ</span>}
                            </div>
                            <div style={{ fontSize:11,color:'var(--text-tertiary)',marginTop:2 }}>{u.email}</div>
                            <div style={{ display:'flex',gap:4,justifyContent:'center',marginTop:6 }}>
                              {u.role==='admin' && (
                                <span style={{ fontSize:9,background:'var(--accent)',color:'#fff',padding:'2px 7px',borderRadius:10,fontWeight:700 }}>ADMIN</span>
                              )}
                              {!u.is_active && (
                                <span style={{ fontSize:9,background:'var(--danger)',color:'#fff',padding:'2px 7px',borderRadius:10 }}>INATIVO</span>
                              )}
                              <span style={{ fontSize:9,background:'var(--bg-tertiary)',color:'var(--text-tertiary)',padding:'2px 7px',borderRadius:10 }}>
                                {u.email_accounts_count} conta(s)
                              </span>
                            </div>
                            {u.last_login && (
                              <div style={{ fontSize:10,color:'var(--text-tertiary)',marginTop:4,opacity:0.7 }}>
                                Último acesso: {new Date(u.last_login).toLocaleDateString('pt-BR')}
                              </div>
                            )}
                          </div>
                          <div style={{ display:'flex',gap:4,marginTop:4,flexWrap:'wrap',justifyContent:'center' }}>
                            {u.role !== 'admin' && (
                              <button className="action-btn" style={{ padding:'5px 10px',fontSize:11,color: (u.ai_enabled !== false && u.permissions?.perm_ai !== false) ? 'var(--success)' : 'var(--text-tertiary)' }}
                                onClick={() => { setShowAdminAiPopup(u); const permAi = u.permissions?.perm_ai !== false; setAdminAiForm({ ai_enabled: u.ai_enabled !== false && permAi, ai_credits_limit: u.ai_credits_limit || 0 }); }}>
                                <Sparkles size={12} /> IA
                              </button>
                            )}
                            {u.role !== 'admin' && (
                              <button className="action-btn" style={{ padding:'5px 10px',fontSize:11,color:'var(--purple)' }}
                                onClick={() => { setShowPermissionsPopup(u); setPermissionsForm(u.permissions || {}); }}>
                                <Shield size={12} /> Permissões
                              </button>
                            )}
                            <button className="action-btn" onClick={() => openAdminUserDashboard(u)} style={{ padding:'5px 10px',fontSize:11 }}>
                              <BarChart3 size={12} /> Dashboard
                            </button>
                            <button className="action-btn" onClick={() => openEditUser(u)} style={{ padding:'5px 10px',fontSize:11 }}>
                              <Edit3 size={12} /> Editar
                            </button>
                            {u.id !== authUser?.id && (
                              <button className="action-btn danger" onClick={() => deleteUser(u.id, u.name)} style={{ padding:'5px 10px',fontSize:11 }}>
                                <Trash2 size={12} /> Excluir
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
                  )}

                  {/* ═══ COMPANIES TAB ═══ */}
                  {adminTab === 'companies' && (
                    <>
                      {showAddCompany ? (
                        /* Company Form */
                        <div style={{ padding:24 }}>
                          <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:20 }}>
                            <div style={{ display:'flex',alignItems:'center',gap:10 }}>
                              <button className="icon-btn" onClick={() => setShowAddCompany(false)} style={{ width:32,height:32 }}>
                                <ArrowLeft size={16} />
                              </button>
                              <h3 style={{ fontSize:16,fontWeight:700,margin:0 }}>{editingCompany ? `Editar: ${editingCompany.name}` : 'Nova Empresa'}</h3>
                            </div>
                          </div>

                          <div style={{ display:'flex',flexDirection:'column',gap:14 }}>
                            <div className="form-row">
                              <div className="form-group">
                                <label className="form-label">Nome da empresa *</label>
                                <input className="form-input" placeholder="Nome da empresa"
                                  value={companyForm.name} onChange={e => setCompanyForm(p=>({...p,name:e.target.value}))} />
                              </div>
                              <div className="form-group">
                                <label className="form-label">Plano</label>
                                <select value={companyForm.plan} onChange={e => {
                                  const plan = e.target.value;
                                  const presets = {
                                    free:         { max_users:1, max_accounts_per_user:1, storage_limit_mb:10,    ai_credits_per_user:0,   ai_enabled:false },
                                    beginner:     { max_users:1, max_accounts_per_user:1, storage_limit_mb:100,   ai_credits_per_user:10,  ai_enabled:true },
                                    starter:      { max_users:1, max_accounts_per_user:3, storage_limit_mb:150,   ai_credits_per_user:20,  ai_enabled:true },
                                    professional: { max_users:2, max_accounts_per_user:5, storage_limit_mb:500,   ai_credits_per_user:80,  ai_enabled:true },
                                    business:     { max_users:5, max_accounts_per_user:10,storage_limit_mb:3072,  ai_credits_per_user:150, ai_enabled:true },
                                    ultra:        { max_users:10,max_accounts_per_user:0, storage_limit_mb:15360, ai_credits_per_user:250, ai_enabled:true },
                                    custom:       { max_users:0, max_accounts_per_user:0, storage_limit_mb:0,     ai_credits_per_user:0,   ai_enabled:true },
                                  };
                                  const p = presets[plan] || presets.custom;
                                  setCompanyForm(prev => ({ ...prev, plan, ...p }));
                                }}
                                  style={{ width:'100%',padding:'8px 12px',border:'1px solid var(--border-color)',borderRadius:6,background:'var(--bg-tertiary)',color:'var(--text-primary)',fontFamily:'var(--font-primary)',fontSize:13,cursor:'pointer' }}>
                                  <option value="free">Free — Grátis</option>
                                  <option value="beginner">Beginner — R$ 19,90/mês</option>
                                  <option value="starter">Starter — R$ 39,90/mês</option>
                                  <option value="professional">Professional — R$ 79,90/mês ⭐</option>
                                  <option value="business">Business — R$ 149,90/mês</option>
                                  <option value="ultra">Ultra — R$ 500/mês</option>
                                  <option value="custom">Personalizado</option>
                                </select>
                              </div>
                            </div>

                            {companyForm.plan !== 'custom' && (
                              <div style={{
                                padding:'8px 12px',borderRadius:8,fontSize:11,
                                background:'var(--accent-light)',border:'1px solid rgba(59,130,246,0.2)',
                                color:'var(--accent)',display:'flex',alignItems:'center',gap:6,
                              }}>
                                <Info size={13} />
                                Valores preenchidos automaticamente pelo plano. Você pode ajustar manualmente abaixo.
                              </div>
                            )}

                            <div style={{ fontSize:11,fontWeight:700,color:'var(--text-tertiary)',textTransform:'uppercase',letterSpacing:0.5,marginTop:4 }}>
                              Limites da Empresa
                            </div>

                            <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:12 }}>
                              <div className="form-group">
                                <label className="form-label">Máx. Usuários</label>
                                <input className="form-input" type="number" min="0" placeholder="0 = ilimitado"
                                  value={companyForm.max_users || ''} onChange={e => setCompanyForm(p=>({...p,max_users:parseInt(e.target.value)||0}))} />
                                <span style={{ fontSize:10,color:'var(--text-tertiary)' }}>{companyForm.max_users === 0 ? 'Ilimitado' : `Máximo ${companyForm.max_users}`}</span>
                              </div>
                              <div className="form-group">
                                <label className="form-label">Contas email / usuário</label>
                                <input className="form-input" type="number" min="0" placeholder="0 = ilimitado"
                                  value={companyForm.max_accounts_per_user || ''} onChange={e => setCompanyForm(p=>({...p,max_accounts_per_user:parseInt(e.target.value)||0}))} />
                                <span style={{ fontSize:10,color:'var(--text-tertiary)' }}>{companyForm.max_accounts_per_user === 0 ? 'Ilimitado' : `Máximo ${companyForm.max_accounts_per_user}`}</span>
                              </div>
                              <div className="form-group">
                                <label className="form-label">Armazenamento (MB)</label>
                                <input className="form-input" type="number" min="0" placeholder="0 = ilimitado"
                                  value={companyForm.storage_limit_mb || ''} onChange={e => setCompanyForm(p=>({...p,storage_limit_mb:parseInt(e.target.value)||0}))} />
                                <span style={{ fontSize:10,color:'var(--text-tertiary)' }}>
                                  {companyForm.storage_limit_mb === 0 ? 'Ilimitado' : companyForm.storage_limit_mb >= 1024 ? `${(companyForm.storage_limit_mb/1024).toFixed(1)} GB` : `${companyForm.storage_limit_mb} MB`}
                                </span>
                              </div>
                              <div className="form-group">
                                <label className="form-label">Créditos IA / usuário</label>
                                <input className="form-input" type="number" min="0" placeholder="0 = ilimitado"
                                  value={companyForm.ai_credits_per_user || ''} onChange={e => setCompanyForm(p=>({...p,ai_credits_per_user:parseInt(e.target.value)||0}))} />
                                <span style={{ fontSize:10,color:'var(--text-tertiary)' }}>{companyForm.ai_credits_per_user === 0 ? 'Ilimitado' : `${companyForm.ai_credits_per_user} por usuário`}</span>
                              </div>
                            </div>

                            <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between',padding:'8px 0' }}>
                              <div>
                                <div style={{ fontSize:13,fontWeight:500 }}>IA Ativada para a empresa</div>
                                <div style={{ fontSize:11,color:'var(--text-tertiary)' }}>Habilitar assistente IA para todos os usuários desta empresa</div>
                              </div>
                              <button className={`toggle ${companyForm.ai_enabled ? 'on' : ''}`}
                                onClick={() => setCompanyForm(p=>({...p,ai_enabled:!p.ai_enabled}))} />
                            </div>

                            <div className="form-group">
                              <label className="form-label">Observações</label>
                              <textarea className="form-input" rows={3} placeholder="Notas internas sobre a empresa..."
                                value={companyForm.notes} onChange={e => setCompanyForm(p=>({...p,notes:e.target.value}))}
                                style={{ resize:'vertical',minHeight:60 }} />
                            </div>

                            <div style={{ display:'flex',gap:8,justifyContent:'flex-end',marginTop:4 }}>
                              <button className="btn-secondary" onClick={() => setShowAddCompany(false)}>Cancelar</button>
                              <button className="btn-primary" onClick={saveCompany} disabled={companyFormLoading}
                                style={{ display:'flex',alignItems:'center',gap:6,opacity:companyFormLoading?0.7:1 }}>
                                {companyFormLoading ? <Loader size={14} className="spin-icon" /> : <Save size={14} />}
                                {editingCompany ? 'Salvar Alterações' : 'Criar Empresa'}
                              </button>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <>
                          {/* Company header */}
                          <div style={{ padding:'12px 20px',display:'flex',alignItems:'center',justifyContent:'space-between' }}>
                            <span style={{ fontSize:13,color:'var(--text-tertiary)' }}>{companies.length} empresa(s)</span>
                            <button className="btn-primary" onClick={openAddCompany} style={{ padding:'6px 14px',fontSize:12,display:'flex',alignItems:'center',gap:4 }}>
                              <Plus size={14} /> Nova Empresa
                            </button>
                          </div>

                          {companiesLoading ? (
                            <div style={{ display:'flex',alignItems:'center',justifyContent:'center',gap:8,padding:32,color:'var(--text-tertiary)' }}>
                              <Loader size={18} className="spin-icon" /> Carregando...
                            </div>
                          ) : companies.length === 0 ? (
                            <div style={{ textAlign:'center',padding:'40px 20px',color:'var(--text-tertiary)' }}>
                              <Building2 size={40} style={{ opacity:0.3,marginBottom:12 }} />
                              <p style={{ fontSize:14,margin:'0 0 4px' }}>Nenhuma empresa cadastrada</p>
                              <p style={{ fontSize:12,margin:0 }}>Crie empresas para gerenciar pacotes de clientes</p>
                            </div>
                          ) : (
                            <div style={{ padding:'0 12px 12px' }}>
                              {companies.map(c => (
                                <div key={c.id} style={{
                                  padding:16,borderRadius:10,border:'1px solid var(--border-color)',
                                  background:'var(--bg-primary)',marginBottom:8,
                                }}>
                                  <div style={{ display:'flex',alignItems:'flex-start',justifyContent:'space-between',marginBottom:10 }}>
                                    <div style={{ display:'flex',alignItems:'center',gap:10 }}>
                                      <div style={{
                                        width:40,height:40,borderRadius:10,flexShrink:0,
                                        background:'linear-gradient(135deg, var(--accent), var(--purple))',
                                        display:'flex',alignItems:'center',justifyContent:'center',
                                      }}>
                                        <Building2 size={18} color="#fff" />
                                      </div>
                                      <div>
                                        <div style={{ fontSize:14,fontWeight:700 }}>{c.name}</div>
                                        <div style={{ fontSize:11,color:'var(--text-tertiary)',display:'flex',gap:6,marginTop:2 }}>
                                          <span style={{
                                            padding:'1px 8px',borderRadius:10,fontWeight:600,fontSize:10,
                                            background:'var(--accent-light)',color:'var(--accent)',textTransform:'uppercase',
                                          }}>{c.plan}</span>
                                          {!c.is_active && <span style={{ padding:'1px 8px',borderRadius:10,background:'var(--danger-light)',color:'var(--danger)',fontSize:10,fontWeight:600 }}>INATIVA</span>}
                                        </div>
                                      </div>
                                    </div>
                                    <div style={{ display:'flex',gap:4 }}>
                                      <button className="icon-btn" onClick={async () => {
                                        const newState = !c.ai_enabled;
                                        try {
                                          await api.updateCompany(c.id, { ai_enabled: newState, propagate: true });
                                          setCompanies(prev => prev.map(x => x.id === c.id ? { ...x, ai_enabled: newState } : x));
                                          toast(`IA ${newState ? 'ativada' : 'desativada'} para todos os usuários da empresa`, 'success');
                                          loadUsers();
                                        } catch { toast('Erro ao alterar IA', 'error'); }
                                      }} title={c.ai_enabled ? 'Desativar IA para todos' : 'Ativar IA para todos'}
                                        style={{ width:30,height:30,color: c.ai_enabled ? 'var(--success)' : 'var(--danger)' }}>
                                        <Sparkles size={14} />
                                      </button>
                                      <button className="icon-btn" onClick={() => propagateCompanyLimits(c.id)} title="Propagar limites para usuários"
                                        style={{ width:30,height:30 }}>
                                        <RefreshCw size={14} />
                                      </button>
                                      <button className="icon-btn" onClick={() => openEditCompany(c)} title="Editar" style={{ width:30,height:30 }}>
                                        <Edit3 size={14} />
                                      </button>
                                      <button className="icon-btn" onClick={() => deleteCompany(c.id, c.name)} title="Excluir"
                                        style={{ width:30,height:30,color:'var(--danger)' }}>
                                        <Trash2 size={14} />
                                      </button>
                                    </div>
                                  </div>

                                  {/* Company stats grid */}
                                  <div style={{ display:'grid',gridTemplateColumns:'repeat(5,1fr)',gap:8 }}>
                                    {[
                                      { label:'Usuários', value:`${c.users_count || 0}/${c.max_users || '∞'}`, icon:<Users size={13} /> },
                                      { label:'Contas/usr', value:c.max_accounts_per_user || '∞', icon:<Mail size={13} /> },
                                      { label:'Armaz.', value:c.storage_limit_mb ? (c.storage_limit_mb >= 1024 ? `${(c.storage_limit_mb/1024).toFixed(0)}GB` : `${c.storage_limit_mb}MB`) : '∞', icon:<HardDrive size={13} /> },
                                      { label:'Créd IA/usr', value:c.ai_credits_per_user || '∞', icon:<Sparkles size={13} /> },
                                      { label:'IA', value:c.ai_enabled ? 'Ativa' : 'Inativa', icon:<Cpu size={13} />, color:c.ai_enabled ? 'var(--success)' : 'var(--danger)' },
                                    ].map((s,i) => (
                                      <div key={i} style={{
                                        padding:'6px 8px',borderRadius:6,background:'var(--bg-tertiary)',textAlign:'center',
                                      }}>
                                        <div style={{ display:'flex',alignItems:'center',justifyContent:'center',gap:3,color:s.color||'var(--text-tertiary)',marginBottom:2 }}>
                                          {s.icon}
                                        </div>
                                        <div style={{ fontSize:13,fontWeight:700,color:s.color||'var(--text-primary)' }}>{s.value}</div>
                                        <div style={{ fontSize:9,color:'var(--text-tertiary)',fontWeight:500 }}>{s.label}</div>
                                      </div>
                                    ))}
                                  </div>

                                  {c.notes && (
                                    <div style={{ marginTop:8,fontSize:11,color:'var(--text-tertiary)',fontStyle:'italic',padding:'4px 0' }}>
                                      📝 {c.notes}
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </>
                      )}
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ===== ADMIN AI CONFIG POPUP ===== */}
      <div className={`modal-overlay ${showAdminAiPopup ? 'active' : ''}`}
        onClick={() => setShowAdminAiPopup(null)} style={{ zIndex:260 }}>
        {showAdminAiPopup && (
          <div className="modal" onClick={e => e.stopPropagation()} style={{ width:420 }}>
            <div className="modal-header">
              <span className="modal-title" style={{ display:'flex',alignItems:'center',gap:8 }}>
                <Sparkles size={18} color="var(--accent)" /> Configurar IA — {showAdminAiPopup.name}
              </span>
              <button className="modal-close-btn" onClick={() => setShowAdminAiPopup(null)}><X size={18} /></button>
            </div>
            <div className="modal-body" style={{ padding:20 }}>
              {/* User info card */}
              <div style={{
                display:'flex',alignItems:'center',gap:12,padding:12,borderRadius:10,
                background:'var(--bg-tertiary)',marginBottom:20,
              }}>
                <div style={{
                  width:40,height:40,borderRadius:'50%',
                  background:'var(--bg-hover)',color:'var(--text-secondary)',
                  display:'flex',alignItems:'center',justifyContent:'center',
                  fontSize:14,fontWeight:700,
                }}>
                  {showAdminAiPopup.name?.split(' ').map(n=>n[0]).join('').substring(0,2).toUpperCase()}
                </div>
                <div>
                  <div style={{ fontSize:13,fontWeight:600 }}>{showAdminAiPopup.name}</div>
                  <div style={{ fontSize:11,color:'var(--text-tertiary)' }}>{showAdminAiPopup.email}</div>
                </div>
              </div>

              {/* AI Enable/Disable toggle */}
              <div style={{
                display:'flex',alignItems:'center',justifyContent:'space-between',
                padding:'14px 0',borderBottom:'1px solid var(--border-light)',
              }}>
                <div>
                  <div style={{ fontSize:14,fontWeight:600 }}>Assistente IA</div>
                  <div style={{ fontSize:12,color:'var(--text-tertiary)',marginTop:2 }}>
                    {adminAiForm.ai_enabled ? 'Habilitado para este usuário' : 'Desabilitado para este usuário'}
                  </div>
                </div>
                <button
                  className={`toggle ${adminAiForm.ai_enabled ? 'on' : ''}`}
                  onClick={() => setAdminAiForm(p => ({ ...p, ai_enabled: !p.ai_enabled }))}
                />
              </div>

              {/* Credits limit */}
              <div style={{ padding:'14px 0',borderBottom:'1px solid var(--border-light)' }}>
                <div style={{ fontSize:14,fontWeight:600,marginBottom:4 }}>Limite de Créditos</div>
                <div style={{ fontSize:12,color:'var(--text-tertiary)',marginBottom:12 }}>
                  Defina a quantidade máxima de ações IA. Use 0 para ilimitado.
                </div>
                <div style={{ display:'flex',gap:8,alignItems:'center' }}>
                  {[0, 10, 25, 50, 100, 200].map(v => (
                    <button key={v} onClick={() => setAdminAiForm(p => ({ ...p, ai_credits_limit: v }))}
                      style={{
                        padding:'6px 10px',borderRadius:6,fontSize:12,fontWeight:600,
                        border: adminAiForm.ai_credits_limit === v ? '2px solid var(--accent)' : '1px solid var(--border-color)',
                        background: adminAiForm.ai_credits_limit === v ? 'var(--accent-light)' : 'var(--bg-tertiary)',
                        color: adminAiForm.ai_credits_limit === v ? 'var(--accent)' : 'var(--text-secondary)',
                        cursor:'pointer',fontFamily:'var(--font-primary)',transition:'all 0.15s',
                      }}>
                      {v === 0 ? '∞' : v}
                    </button>
                  ))}
                </div>
                <div style={{ marginTop:10,display:'flex',alignItems:'center',gap:8 }}>
                  <label style={{ fontSize:12,fontWeight:500,color:'var(--text-secondary)',whiteSpace:'nowrap' }}>Personalizado:</label>
                  <input
                    type="number" min="0" step="1"
                    className="form-input"
                    style={{ width:90,padding:'6px 10px',fontSize:13,textAlign:'center' }}
                    value={adminAiForm.ai_credits_limit}
                    onChange={e => setAdminAiForm(p => ({ ...p, ai_credits_limit: Math.max(0, parseInt(e.target.value) || 0) }))}
                  />
                  <span style={{ fontSize:12,color:'var(--text-tertiary)' }}>créditos</span>
                </div>
              </div>

              {/* Current usage info */}
              <div style={{ padding:'14px 0' }}>
                <div style={{ fontSize:12,fontWeight:600,color:'var(--text-secondary)',marginBottom:8 }}>Uso Atual</div>
                <div style={{
                  display:'flex',gap:16,alignItems:'center',
                }}>
                  <div style={{
                    padding:'10px 16px',borderRadius:10,background:'var(--bg-tertiary)',
                    textAlign:'center',flex:1,
                  }}>
                    <div style={{ fontSize:22,fontWeight:800,color:'var(--text-primary)' }}>{showAdminAiPopup.ai_credits_used || 0}</div>
                    <div style={{ fontSize:10,color:'var(--text-tertiary)',fontWeight:500,marginTop:2 }}>USADO(S)</div>
                  </div>
                  <div style={{
                    padding:'10px 16px',borderRadius:10,background:'var(--bg-tertiary)',
                    textAlign:'center',flex:1,
                  }}>
                    <div style={{ fontSize:22,fontWeight:800,color: (showAdminAiPopup.ai_credits_limit > 0 && showAdminAiPopup.ai_credits_remaining <= 0) ? 'var(--danger)' : 'var(--text-primary)' }}>
                      {showAdminAiPopup.ai_credits_limit > 0 ? Math.max(0, showAdminAiPopup.ai_credits_remaining) : '∞'}
                    </div>
                    <div style={{ fontSize:10,color:'var(--text-tertiary)',fontWeight:500,marginTop:2 }}>RESTANTE(S)</div>
                  </div>
                  <div style={{
                    padding:'10px 16px',borderRadius:10,background:'var(--bg-tertiary)',
                    textAlign:'center',flex:1,
                  }}>
                    <div style={{ fontSize:22,fontWeight:800,color:'var(--text-primary)' }}>
                      {showAdminAiPopup.ai_credits_limit > 0 ? showAdminAiPopup.ai_credits_limit : '∞'}
                    </div>
                    <div style={{ fontSize:10,color:'var(--text-tertiary)',fontWeight:500,marginTop:2 }}>LIMITE</div>
                  </div>
                </div>

                {/* Reset credits button */}
                {(showAdminAiPopup.ai_credits_used || 0) > 0 && (
                  <button
                    onClick={async () => {
                      try {
                        await api.updateUser(showAdminAiPopup.id, { ai_credits_reset: true });
                        setAdminUsers(prev => prev.map(x => x.id === showAdminAiPopup.id
                          ? { ...x, ai_credits_used: 0, ai_credits_remaining: x.ai_credits_limit || 0 }
                          : x
                        ));
                        setShowAdminAiPopup(prev => prev ? { ...prev, ai_credits_used: 0, ai_credits_remaining: prev.ai_credits_limit || 0 } : null);
                        toast('Créditos resetados', 'success');
                      } catch { toast('Erro ao resetar', 'error'); }
                    }}
                    style={{
                      marginTop:10,width:'100%',padding:'8px',borderRadius:8,
                      border:'1px solid var(--border-color)',background:'var(--bg-secondary)',
                      cursor:'pointer',fontSize:12,fontWeight:600,color:'var(--accent)',
                      fontFamily:'var(--font-primary)',transition:'all 0.15s',
                      display:'flex',alignItems:'center',justifyContent:'center',gap:6,
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background='var(--accent-light)'; e.currentTarget.style.borderColor='var(--accent)'; }}
                    onMouseLeave={e => { e.currentTarget.style.background='var(--bg-secondary)'; e.currentTarget.style.borderColor='var(--border-color)'; }}
                  >
                    <RefreshCw size={13} /> Resetar créditos usados
                  </button>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowAdminAiPopup(null)}>Cancelar</button>
              <button className="btn-primary"
                style={{ display:'flex',alignItems:'center',gap:6 }}
                onClick={async () => {
                  try {
                    // Usa as permissões mais recentes da lista (o snapshot do popup
                    // pode estar defasado e reverteria permissões salvas depois)
                    const latestUser = adminUsers.find(x => x.id === showAdminAiPopup.id) || showAdminAiPopup;
                    const updatedPerms = { ...(latestUser.permissions || {}), perm_ai: adminAiForm.ai_enabled };
                    await api.updateUser(showAdminAiPopup.id, {
                      ai_enabled: adminAiForm.ai_enabled,
                      ai_credits_limit: adminAiForm.ai_credits_limit,
                      permissions: updatedPerms,
                    });
                    setAdminUsers(prev => prev.map(x => x.id === showAdminAiPopup.id ? {
                      ...x,
                      ai_enabled: adminAiForm.ai_enabled,
                      ai_credits_limit: adminAiForm.ai_credits_limit,
                      ai_credits_remaining: adminAiForm.ai_credits_limit > 0
                        ? Math.max(0, adminAiForm.ai_credits_limit - (x.ai_credits_used || 0))
                        : -1,
                      permissions: updatedPerms,
                    } : x));
                    toast('Configurações de IA salvas', 'success');
                    setShowAdminAiPopup(null);
                  } catch { toast('Erro ao salvar', 'error'); }
                }}>
                <Save size={14} /> Salvar
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ===== ADMIN: PERMISSIONS POPUP ===== */}
      <div className={`modal-overlay ${showPermissionsPopup ? 'active' : ''}`}
        onClick={() => setShowPermissionsPopup(null)} style={{ zIndex:265 }}>
        {showPermissionsPopup && (
          <div className="modal" onClick={e => e.stopPropagation()} style={{ width:520,maxHeight:'85vh' }}>
            <div className="modal-header">
              <span className="modal-title" style={{ display:'flex',alignItems:'center',gap:8 }}>
                <Shield size={18} color="var(--purple)" /> Permissões — {showPermissionsPopup.name}
              </span>
              <button className="modal-close-btn" onClick={() => setShowPermissionsPopup(null)}><X size={18} /></button>
            </div>
            <div className="modal-body" style={{ padding:20,maxHeight:'calc(85vh - 130px)',overflow:'auto' }}>
              {/* User info */}
              <div style={{ display:'flex',alignItems:'center',gap:12,padding:12,borderRadius:10,background:'var(--bg-tertiary)',marginBottom:20 }}>
                <div style={{ width:40,height:40,borderRadius:'50%',background:'var(--bg-hover)',color:'var(--text-secondary)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:14,fontWeight:700 }}>
                  {showPermissionsPopup.name?.split(' ').map(n=>n[0]).join('').substring(0,2).toUpperCase()}
                </div>
                <div>
                  <div style={{ fontSize:13,fontWeight:600 }}>{showPermissionsPopup.name}</div>
                  <div style={{ fontSize:11,color:'var(--text-tertiary)' }}>{showPermissionsPopup.email}</div>
                </div>
              </div>

              {/* Permission toggles */}
              {[
                { key: 'perm_dashboard', label: 'Dashboard', desc: 'Acesso ao painel de estatísticas', icon: <BarChart3 size={16} /> },
                { key: 'perm_tags', label: 'Gerenciar Tags', desc: 'Criar, editar e excluir tags', icon: <Tag size={16} /> },
                { key: 'perm_auto_tag_rules', label: 'Regras automáticas de tag', desc: 'Criar regras de auto-tagging por domínio/email/assunto', icon: <Filter size={16} /> },
                { key: 'perm_spam_rules', label: 'Regras de Spam personalizadas', desc: 'Gerenciar whitelist/blacklist de spam', icon: <ShieldAlert size={16} /> },
                { key: 'perm_contacts', label: 'Contatos', desc: 'Acesso ao gerenciamento de contatos', icon: <Users size={16} /> },
                { key: 'perm_cleanup', label: 'Limpeza de Emails', desc: 'Acesso à ferramenta de limpeza de emails', icon: <Trash2 size={16} /> },
                { key: 'perm_theme', label: 'Personalização de Tema', desc: 'Alternar entre temas claro/escuro/noturno', icon: <Sun size={16} /> },
              ].map(perm => (
                <div key={perm.key} style={{
                  display:'flex',alignItems:'center',justifyContent:'space-between',
                  padding:'12px 0',borderBottom:'1px solid var(--border-light)',
                }}>
                  <div style={{ display:'flex',alignItems:'center',gap:10,flex:1 }}>
                    <span style={{ color:'var(--text-tertiary)' }}>{perm.icon}</span>
                    <div>
                      <div style={{ fontSize:13,fontWeight:600 }}>{perm.label}</div>
                      <div style={{ fontSize:11,color:'var(--text-tertiary)',marginTop:1 }}>{perm.desc}</div>
                    </div>
                  </div>
                  <button className={`toggle ${permissionsForm[perm.key] !== false ? 'on' : ''}`}
                    onClick={() => setPermissionsForm(p => ({ ...p, [perm.key]: p[perm.key] === false ? true : false }))} />
                </div>
              ))}

              {/* AI Section */}
              <div style={{ marginTop:16,padding:'14px 0',borderBottom:'1px solid var(--border-light)' }}>
                <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between' }}>
                  <div style={{ display:'flex',alignItems:'center',gap:10 }}>
                    <Sparkles size={16} color="var(--text-tertiary)" />
                    <div>
                      <div style={{ fontSize:13,fontWeight:600 }}>Funcionalidade IA</div>
                      <div style={{ fontSize:11,color:'var(--text-tertiary)',marginTop:1 }}>Habilitar ou desabilitar o assistente de IA</div>
                    </div>
                  </div>
                  <button className={`toggle ${permissionsForm.perm_ai !== false ? 'on' : ''}`}
                    onClick={() => setPermissionsForm(p => ({ ...p, perm_ai: p.perm_ai === false ? true : false }))} />
                </div>

                {/* Individual AI models - only show if AI is enabled */}
                {permissionsForm.perm_ai !== false && (
                  <div style={{ marginTop:14,paddingLeft:26 }}>
                    <div style={{ fontSize:11,fontWeight:700,color:'var(--text-tertiary)',textTransform:'uppercase',letterSpacing:0.5,marginBottom:10 }}>
                      Modelos individuais
                    </div>
                    {AI_MODELS.map(m => {
                      const permKey = `perm_ai_${m.id}`;
                      const providerLabels = { openai:'OpenAI', gemini:'Google', anthropic:'Anthropic', grok:'xAI' };
                      return (
                        <div key={m.id} style={{
                          display:'flex',alignItems:'center',justifyContent:'space-between',
                          padding:'8px 0',borderBottom:'1px solid var(--border-light)',
                        }}>
                          <div style={{ display:'flex',alignItems:'center',gap:8,flex:1 }}>
                            <span style={{
                              fontSize:9,fontWeight:700,padding:'2px 6px',borderRadius:4,
                              background:`${m.tagColor}20`,color:m.tagColor,textTransform:'uppercase',
                            }}>{providerLabels[m.provider]}</span>
                            <span style={{ fontSize:12.5,fontWeight:500 }}>{m.label}</span>
                            <span style={{ fontSize:10,color:'var(--text-tertiary)' }}>({m.credits} créd.)</span>
                          </div>
                          <button className={`toggle ${permissionsForm[permKey] !== false ? 'on' : ''}`}
                            style={{ transform:'scale(0.85)' }}
                            onClick={() => setPermissionsForm(p => ({ ...p, [permKey]: p[permKey] === false ? true : false }))} />
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowPermissionsPopup(null)}>Cancelar</button>
              <button className="btn-primary" style={{ display:'flex',alignItems:'center',gap:6 }}
                onClick={async () => {
                  try {
                    // Sync ai_enabled with perm_ai
                    const aiEnabled = permissionsForm.perm_ai !== false;
                    await api.updateUser(showPermissionsPopup.id, { permissions: permissionsForm, ai_enabled: aiEnabled });
                    setAdminUsers(prev => prev.map(x => x.id === showPermissionsPopup.id
                      ? { ...x, permissions: { ...x.permissions, ...permissionsForm }, ai_enabled: aiEnabled }
                      : x
                    ));
                    toast('Permissões salvas com sucesso', 'success');
                    setShowPermissionsPopup(null);
                  } catch { toast('Erro ao salvar permissões', 'error'); }
                }}>
                <Save size={14} /> Salvar Permissões
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ===== EDIT CONTACT MODAL ===== */}
      <div className={`modal-overlay ${editingContact ? 'active' : ''}`} onClick={() => setEditingContact(null)}>
        {editingContact && (
          <div className="modal" onClick={e => e.stopPropagation()} style={{ width: 420 }}>
            <div className="modal-header">
              <span className="modal-title">Editar Contato</span>
              <button className="modal-close-btn" onClick={() => setEditingContact(null)}><X size={18} /></button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Nome</label>
                <input className="form-input" placeholder="Nome do contato"
                  value={editContactForm.name}
                  onChange={e => setEditContactForm(p => ({ ...p, name: e.target.value }))} />
              </div>
              <div className="form-group">
                <label className="form-label">Email *</label>
                <input className="form-input" type="email" placeholder="email@exemplo.com"
                  value={editContactForm.email}
                  onChange={e => setEditContactForm(p => ({ ...p, email: e.target.value }))} />
              </div>
              <div style={{ fontSize:12,color:'var(--text-tertiary)',marginTop:8 }}>
                Contatado {editingContact.times_contacted}x
                {editingContact.last_contacted && ` · Último: ${new Date(editingContact.last_contacted).toLocaleDateString('pt-BR')}`}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setEditingContact(null)}>Cancelar</button>
              <button className="btn-primary" onClick={saveEditContact} disabled={editContactLoading}
                style={{ display:'flex',alignItems:'center',gap:6,opacity:editContactLoading?0.6:1 }}>
                {editContactLoading ? <Loader size={14} className="spin-icon" /> : <Save size={14} />}
                Salvar
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ===== CONFIRM DELETE DIALOG ===== */}
      <div className={`modal-overlay ${confirmDelete ? 'active' : ''}`}
        onClick={() => setConfirmDelete(null)}
        style={{ zIndex: 250 }}
      >
        {confirmDelete && (
          <div className="modal" onClick={e => e.stopPropagation()} style={{ width: 420 }}>
            <div className="modal-header">
              <span className="modal-title">Confirmar Exclusão</span>
              <button className="modal-close-btn" onClick={() => setConfirmDelete(null)}>
                <X size={18} />
              </button>
            </div>
            <div className="modal-body" style={{ padding: '20px' }}>
              <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                <div style={{
                  width: 40, height: 40, borderRadius: '50%', flexShrink: 0,
                  background: 'var(--danger-light)', display: 'flex',
                  alignItems: 'center', justifyContent: 'center',
                }}>
                  <Trash2 size={18} color="var(--danger)" />
                </div>
                <div>
                  <p style={{ fontSize: 14, fontWeight: 500, marginBottom: 6 }}>
                    {confirmDelete.permanent
                      ? 'Este email será excluído permanentemente e não poderá ser recuperado.'
                      : 'Este email será movido para a lixeira.'}
                  </p>
                  <p style={{
                    fontSize: 13, color: 'var(--text-tertiary)',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 300,
                  }}>
                    "{confirmDelete.subject}"
                  </p>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setConfirmDelete(null)}>
                Cancelar
              </button>
              <button className="btn-primary" onClick={confirmDeleteEmail}
                style={{ background: 'var(--danger)', display: 'flex', alignItems: 'center', gap: 6 }}>
                <Trash2 size={14} /> {confirmDelete.label}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ===== BULK ACTION CONFIRMATION ===== */}
      <div className={`modal-overlay ${bulkConfirm ? 'active' : ''}`}
        onClick={() => setBulkConfirm(null)}
        style={{ zIndex: 260 }}
      >
        {bulkConfirm && (
          <div className="modal" onClick={e => e.stopPropagation()} style={{ width: 440 }}>
            <div className="modal-header">
              <span className="modal-title">Confirmar Ação em Massa</span>
              <button className="modal-close-btn" onClick={() => setBulkConfirm(null)}>
                <X size={18} />
              </button>
            </div>
            <div className="modal-body" style={{ padding: '20px' }}>
              <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                <div style={{
                  width: 40, height: 40, borderRadius: '50%', flexShrink: 0,
                  background: 'var(--warning-light)', display: 'flex',
                  alignItems: 'center', justifyContent: 'center',
                }}>
                  <AlertTriangle size={18} color="var(--warning)" />
                </div>
                <div>
                  <p style={{ fontSize: 14, fontWeight: 600, marginBottom: 6 }}>
                    {bulkConfirm.label}
                  </p>
                  <p style={{ fontSize: 13, color: 'var(--text-tertiary)' }}>
                    {bulkConfirm.description}
                  </p>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setBulkConfirm(null)}>Cancelar</button>
              <button className="btn-primary" onClick={executeBulkAction}
                style={{
                  background: bulkConfirm.action === 'bulk_delete' ? 'var(--danger)' : undefined,
                  display: 'flex', alignItems: 'center', gap: 6,
                }}>
                <Check size={14} /> Confirmar
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ===== ATTACHMENT PREVIEW MODAL ===== */}
      <div className={`modal-overlay ${attachPreview ? 'active' : ''}`}
        onClick={() => setAttachPreview(null)}
        style={{ zIndex: 300 }}
      >
        {attachPreview && (
          <div onClick={e => e.stopPropagation()} style={{
            width: '90vw', maxWidth: 900, maxHeight: '90vh',
            background: 'var(--bg-secondary)', borderRadius: 'var(--radius-lg)',
            boxShadow: 'var(--shadow-xl)', display: 'flex', flexDirection: 'column',
            overflow: 'hidden',
          }}>
            {/* Header */}
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '12px 18px', borderBottom: '1px solid var(--border-color)',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                <Paperclip size={16} color="var(--text-tertiary)" />
                <span style={{ fontSize: 14, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {attachPreview.filename}
                </span>
                {attachPreview.size && (
                  <span style={{ fontSize: 12, color: 'var(--text-tertiary)', flexShrink: 0 }}>
                    ({formatBytes(attachPreview.size)})
                  </span>
                )}
              </div>
              <div style={{ display: 'flex', gap: 4 }}>
                {attachPreview.dataUrl && (
                  <a href={attachPreview.dataUrl} download={attachPreview.filename}
                    className="icon-btn" style={{ width: 32, height: 32, textDecoration: 'none', color: 'inherit' }}
                    title="Baixar">
                    <Download size={16} />
                  </a>
                )}
                <button className="icon-btn" style={{ width: 32, height: 32 }}
                  onClick={() => setAttachPreview(null)} title="Fechar">
                  <X size={16} />
                </button>
              </div>
            </div>

            {/* Body */}
            <div style={{
              flex: 1, overflow: 'auto', display: 'flex', alignItems: 'center',
              justifyContent: 'center', padding: 16, minHeight: 200,
              background: 'var(--bg-primary)',
            }}>
              {attachPreview.loading ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: 'var(--text-tertiary)' }}>
                  <Loader size={20} className="spin-icon" /> Carregando...
                </div>
              ) : attachPreview.mime?.startsWith('image/') ? (
                <img src={attachPreview.dataUrl} alt={attachPreview.filename}
                  style={{ maxWidth: '100%', maxHeight: '75vh', objectFit: 'contain', borderRadius: 6 }} />
              ) : attachPreview.mime === 'application/pdf' ? (
                <iframe src={attachPreview.dataUrl} title={attachPreview.filename}
                  style={{ width: '100%', height: '75vh', border: 'none', borderRadius: 6 }} />
              ) : attachPreview.mime?.startsWith('text/') ? (
                <pre style={{
                  width: '100%', maxHeight: '75vh', overflow: 'auto', padding: 16,
                  background: 'var(--bg-tertiary)', borderRadius: 6, fontSize: 13,
                  fontFamily: 'var(--font-mono)', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                  color: 'var(--text-primary)',
                }}>
                  {/* base64ToUtf8 decodifica UTF-8 corretamente (atob corrompia
                      acentos) e nunca lança — base64 inválido não derruba o app */}
                  {base64ToUtf8(attachPreview.dataUrl?.split(',')[1] || '') ?? '(Não foi possível decodificar o conteúdo)'}
                </pre>
              ) : (
                <div style={{ textAlign: 'center', color: 'var(--text-tertiary)' }}>
                  <FileText size={48} style={{ opacity: 0.3, marginBottom: 12 }} />
                  <p style={{ fontSize: 14 }}>Preview não disponível para este formato</p>
                  <a href={attachPreview.dataUrl} download={attachPreview.filename}
                    className="btn-primary" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 12, textDecoration: 'none' }}>
                    <Download size={14} /> Baixar arquivo
                  </a>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ===== SPAM RULES MODAL ===== */}
      <div className={`modal-overlay ${showSpamRules ? 'active' : ''}`} onClick={() => setShowSpamRules(false)}>
        <div className="modal" onClick={e => e.stopPropagation()} style={{ width:560 }}>
          <div className="modal-header">
            <span className="modal-title"><ShieldAlert size={18} style={{ marginRight:6 }} /> Regras de Spam</span>
            <button className="modal-close-btn" onClick={() => setShowSpamRules(false)}><X size={18} /></button>
          </div>
          <div className="modal-body">
            <p style={{ fontSize:13,color:'var(--text-secondary)',marginBottom:16,lineHeight:1.5 }}>
              Emails de domínios, remetentes ou com palavras-chave listados serão movidos para Spam automaticamente.
            </p>
            <div style={{ display:'flex',gap:8,marginBottom:16,alignItems:'flex-end' }}>
              <div style={{ flex:'0 0 110px' }}>
                <label style={{ display:'block',fontSize:11,fontWeight:600,color:'var(--text-tertiary)',marginBottom:3 }}>Tipo</label>
                <select value={newSpamRule.type} onChange={e => setNewSpamRule(p => ({...p,type:e.target.value}))}
                  style={{ width:'100%',padding:'7px',fontSize:12,border:'1px solid var(--border-color)',borderRadius:6,background:'var(--bg-tertiary)',color:'var(--text-primary)',fontFamily:'var(--font-primary)' }}>
                  <option value="domain">Domínio</option>
                  <option value="email">Email</option>
                  <option value="keyword">Palavra</option>
                </select>
              </div>
              <div style={{ flex:1 }}>
                <label style={{ display:'block',fontSize:11,fontWeight:600,color:'var(--text-tertiary)',marginBottom:3 }}>Padrão</label>
                <input className="form-input" placeholder={newSpamRule.type==='domain'?'ex: spam.xyz':newSpamRule.type==='email'?'ex: spammer@evil.com':'ex: ganhe dinheiro'}
                  value={newSpamRule.pattern} onChange={e => setNewSpamRule(p => ({...p,pattern:e.target.value}))}
                  onKeyDown={e => e.key==='Enter' && addSpamRule()} style={{ fontSize:12 }} />
              </div>
              <button className="btn-primary" onClick={addSpamRule} style={{ flexShrink:0,padding:'7px 12px',fontSize:12 }}>Adicionar</button>
            </div>
            {spamRulesLoading ? (
              <div style={{ display:'flex',alignItems:'center',gap:8,padding:20,color:'var(--text-tertiary)',justifyContent:'center' }}><Loader size={16} className="spin-icon" /> Carregando...</div>
            ) : spamRules.length === 0 ? (
              <div style={{ textAlign:'center',padding:20,color:'var(--text-tertiary)',fontSize:13 }}>Nenhuma regra personalizada.</div>
            ) : spamRules.map(rule => (
              <div key={rule.id} style={{ display:'flex',alignItems:'center',gap:10,padding:'8px 0',borderBottom:'1px solid var(--border-light)' }}>
                <span style={{ fontSize:10,fontWeight:700,padding:'2px 8px',borderRadius:4,textTransform:'uppercase',background:rule.rule_type==='domain'?'var(--tag-blue)':rule.rule_type==='email'?'var(--tag-red)':'var(--tag-yellow)',color:rule.rule_type==='domain'?'var(--tag-blue-text)':rule.rule_type==='email'?'var(--tag-red-text)':'var(--tag-yellow-text)' }}>
                  {rule.rule_type==='domain'?'Domínio':rule.rule_type==='email'?'Email':'Palavra'}
                </span>
                <span style={{ flex:1,fontSize:13,fontFamily:'var(--font-mono)' }}>{rule.pattern}</span>
                {rule.user_id===null ? <span style={{ fontSize:10,color:'var(--text-tertiary)',fontWeight:600 }}>GLOBAL</span>
                  : <button className="icon-btn" style={{ width:26,height:26 }} onClick={() => removeSpamRule(rule.id)}><X size={14} color="var(--danger)" /></button>}
              </div>
            ))}
          </div>
          <div className="modal-footer"><button className="btn-secondary" onClick={() => setShowSpamRules(false)}>Fechar</button></div>
        </div>
      </div>

      {/* ===== ADMIN: USER DASHBOARD MODAL ===== */}
      <div className={`modal-overlay ${adminViewDashboard ? 'active' : ''}`} onClick={() => { setAdminViewDashboard(null); setAdminDashboardData(null); }} style={{ zIndex:260 }}>
        {adminViewDashboard && (
          <div className="modal" onClick={e => e.stopPropagation()} style={{ width:780,maxHeight:'90vh' }}>
            <div className="modal-header">
              <span className="modal-title"><BarChart3 size={18} style={{ marginRight:6 }} /> Dashboard: {adminViewDashboard.userName}</span>
              <button className="modal-close-btn" onClick={() => { setAdminViewDashboard(null); setAdminDashboardData(null); }}><X size={18} /></button>
            </div>
            <div className="modal-body" style={{ maxHeight:'calc(90vh - 100px)',overflow:'auto' }}>
              {adminDashboardLoading ? (
                <div style={{ display:'flex',alignItems:'center',justifyContent:'center',gap:10,padding:40,color:'var(--text-tertiary)' }}><Loader size={20} className="spin-icon" /> Carregando...</div>
              ) : adminDashboardData ? (
                <>
                  {adminDashboardData.user && (
                    <div style={{ display:'flex',alignItems:'center',gap:12,padding:12,background:'var(--bg-tertiary)',borderRadius:8,marginBottom:16 }}>
                      <div style={{ width:40,height:40,borderRadius:'50%',background:'var(--accent)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:15,fontWeight:700,color:'#fff' }}>{(adminDashboardData.user.name||'?')[0].toUpperCase()}</div>
                      <div>
                        <div style={{ fontSize:14,fontWeight:600 }}>{adminDashboardData.user.name}</div>
                        <div style={{ fontSize:12,color:'var(--text-tertiary)' }}>{adminDashboardData.user.email}</div>
                      </div>
                    </div>
                  )}
                  <div style={{ display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(120px,1fr))',gap:8,marginBottom:16 }}>
                    {[{l:'Contas',v:adminDashboardData.summary.total_accounts},{l:'Emails',v:adminDashboardData.summary.total_emails.toLocaleString()},{l:'Não Lidos',v:adminDashboardData.summary.total_unread},{l:'Enviados',v:adminDashboardData.summary.total_sent},{l:'Spam',v:adminDashboardData.summary.total_spam},{l:'Contatos',v:adminDashboardData.summary.total_contacts},{l:'Tamanho',v:adminDashboardData.summary.total_size_formatted}].map((c,i) => (
                      <div key={i} style={{ background:'var(--bg-secondary)',border:'1px solid var(--border-color)',borderRadius:8,padding:10,textAlign:'center' }}>
                        <div style={{ fontSize:9,fontWeight:700,color:'var(--text-tertiary)',textTransform:'uppercase',marginBottom:4 }}>{c.l}</div>
                        <div style={{ fontSize:18,fontWeight:700 }}>{c.v}</div>
                      </div>
                    ))}
                  </div>
                  {adminDashboardData.accounts.map((acc,i) => (
                    <div key={i} style={{ display:'flex',alignItems:'center',gap:12,padding:'8px 12px',background:'var(--bg-tertiary)',borderRadius:8,marginBottom:4,fontSize:13 }}>
                      <div style={{ flex:1 }}><div style={{ fontWeight:600 }}>{acc.email}</div><div style={{ fontSize:11,color:'var(--text-tertiary)' }}>IMAP: {acc.imap_host || 'N/A'}</div></div>
                      <div style={{ fontWeight:600 }}>{acc.total_emails} emails</div>
                      <div style={{ color:'var(--text-tertiary)',fontSize:12 }}>{acc.estimated_size_formatted}</div>
                    </div>
                  ))}
                </>
              ) : <div style={{ textAlign:'center',padding:40,color:'var(--text-tertiary)' }}>Erro ao carregar</div>}
            </div>
            <div className="modal-footer"><button className="btn-secondary" onClick={() => { setAdminViewDashboard(null); setAdminDashboardData(null); }}>Fechar</button></div>
          </div>
        )}
      </div>

      {/* ===== ACTIVE REQUESTS INDICATOR ===== */}
      {activeRequests.length > 0 && (
        <div style={{
          position:'fixed',bottom:80,left:'50%',transform:'translateX(-50%)',zIndex:350,
          background:'var(--bg-sidebar)',color:'#fff',borderRadius:10,padding:'8px 16px',
          boxShadow:'var(--shadow-xl)',display:'flex',alignItems:'center',gap:10,
          fontSize:12,fontWeight:500,maxWidth:480,fontFamily:'var(--font-primary)',
          animation:'fadeInUp 0.2s ease both',
        }}>
          <Loader size={14} className="spin-icon" style={{ flexShrink:0 }} />
          <div style={{ flex:1,minWidth:0 }}>
            {activeRequests.length === 1 ? (
              <span>{activeRequests[0].label}...</span>
            ) : (
              <span>
                {activeRequests.length} operações em andamento
                <span style={{ marginLeft:6,fontSize:10,color:'rgba(255,255,255,0.5)' }}>
                  ({activeRequests.map(r => r.label).slice(0, 3).join(', ')}{activeRequests.length > 3 ? '...' : ''})
                </span>
              </span>
            )}
          </div>
          <div style={{ width:40,height:3,borderRadius:2,background:'rgba(255,255,255,0.15)',overflow:'hidden',flexShrink:0 }}>
            <div style={{
              height:'100%',borderRadius:2,
              background:'var(--accent)',
              animation:'reqPulse 1.5s ease-in-out infinite',
            }} />
          </div>
        </div>
      )}

      {/* ===== TOASTS ===== */}
      <div className="toast-container">
        {toasts.map(t => (
          <div key={t.id} className={`toast show`}>
            <span className={`toast-icon ${t.type}`}>
              {t.type === 'success' && <CheckCircle size={18} />}
              {t.type === 'error' && <AlertCircle size={18} />}
              {t.type === 'info' && <Info size={18} />}
            </span>
            {t.message}
          </div>
        ))}
      </div>
    </div>
  );
}
